<template>
  <div class="flow-builder-container">
    <!-- Top Header -->
    <div class="flow-header">
      <div class="header-left">
        <!-- Logo -->
        <img src="https://dashboard.quali-d.com/img/logo.fb8dd5af.png" alt="Quali-D" class="header-logo" />
        <div class="header-divider"></div>
        <!-- Exit button -->
        <button @click="goBack" class="btn-back">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Exit Flow Builder
        </button>
        <div class="header-divider"></div>
        <!-- Flow name display -->
        <div class="flow-title-container">
          <span class="flow-name-display">{{ flowName || 'Untitled Flow' }}</span>
          <button @click="openRenameModal" class="btn-rename" title="Rename flow">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
          </button>
          <span v-if="flowId" class="flow-id-badge">{{ flowId }}</span>
        </div>
        <!-- Target user banner -->
        <div v-if="targetOwnerId" class="target-user-banner">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
          Building for: <strong>{{ targetOwnerCompany || targetOwnerName }}</strong>
        </div>
      </div>

      <div class="header-actions">
        <!-- Undo / Redo -->
        <div class="btn-group">
          <button @click="undoAction" :disabled="!canUndo" class="hdr-btn hdr-btn-icon" title="Undo (Ctrl+Z)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 7v6h6"/>
              <path d="M21 17a9 9 0 00-9-9 9 9 0 00-9 9"/>
            </svg>
          </button>
          <button @click="redoAction" :disabled="!canRedo" class="hdr-btn hdr-btn-icon" title="Redo (Ctrl+Y)">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 7v6h-6"/>
              <path d="M3 17a9 9 0 019-9 9 9 0 019 9"/>
            </svg>
          </button>
        </div>
        <div class="header-divider"></div>
        <!-- Autosave: toggle + silent flash -->
        <div class="autosave-group">
          <label class="toggle-switch" title="Toggle autosave">
            <input type="checkbox" v-model="autosaveEnabled" @change="toggleAutosave">
            <span class="toggle-slider"></span>
          </label>
          <span class="autosave-label">Autosave</span>
          <transition name="autosave-flash">
            <span v-if="autosaveFeedback" class="autosave-saved-badge">
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Saved
            </span>
          </transition>
        </div>
        <div class="header-divider"></div>
        <!-- Arrange -->
        <button @click="autoArrange" class="hdr-btn" title="Auto-arrange nodes on canvas">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
            <rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/>
          </svg>
          Arrange
        </button>
        <!-- Save -->
        <button @click="saveFlow" class="hdr-btn hdr-btn-save" :disabled="saving || !flowValidation.isValid" :title="!flowValidation.isValid ? `Fix ${flowValidation.errors.length} error(s) before saving` : 'Save flow'">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          {{ saving ? 'Saving…' : 'Save' }}
        </button>
        <!-- Simulate -->
        <button @click="showSimulator = true" class="hdr-btn hdr-btn-simulate" :disabled="!flowId" title="Simulate this flow step by step">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="5 3 19 12 5 21 5 3"/>
          </svg>
          Simulate
        </button>
        <div class="header-divider"></div>
        <!-- Fix errors with AI (shown when validation errors exist) -->
        <button v-if="flowValidation.errors.length" @click="aiFixErrors" class="hdr-btn hdr-btn-fix-errors" :title="`${flowValidation.errors.length} error(s) — click to fix with AI`">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          Fix {{ flowValidation.errors.length }} error{{ flowValidation.errors.length > 1 ? 's' : '' }}
        </button>
        <!-- AI Flow Builder -->
        <button @click="aiPanelOpen = !aiPanelOpen" :class="['hdr-btn hdr-btn-ai', { 'hdr-btn-ai--active': aiPanelOpen }]" title="AI Flow Builder">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a10 10 0 0110 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
          AI
        </button>
      </div>
    </div>

    <!-- Rename Flow Modal -->
    <div v-if="showRenameModal" class="modal-overlay" style="background:rgba(0,0,0,0.6);backdrop-filter:blur(2px);" @click.self="showRenameModal = false">
      <div class="modal-content rename-flow-modal" @click.stop>
        <div class="modal-header" style="background:var(--card-background,#1f2937);">
          <h2>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:middle;margin-right:8px;">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Rename Flow
          </h2>
          <button @click="showRenameModal = false" class="btn-close">×</button>
        </div>
        <div class="modal-body" style="background:var(--card-background,#1f2937);padding:20px;">
          <input
            v-model="renameFlowName"
            type="text"
            class="form-input"
            placeholder="Enter flow name…"
            @keyup.enter="confirmRename"
            ref="renameInput"
          />
        </div>
        <div class="modal-footer" style="background:var(--card-background,#1f2937);display:flex;justify-content:flex-end;gap:8px;padding:12px 20px;border-top:1px solid var(--border-primary);">
          <button @click="showRenameModal = false" class="hdr-btn">Cancel</button>
          <button @click="confirmRename" class="hdr-btn hdr-btn-save">Save Name</button>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="flow-content">
      <!-- Left: Activity Bar + Sidebar Panel -->
      <div class="left-sidebar-wrapper">

        <!-- Vertical Tab Strip -->
        <div class="activity-bar">
          <button
            @click="activeSidebarTab = 'nodes'"
            :class="{ active: activeSidebarTab === 'nodes' }"
            class="activity-tab"
            title="Nodes"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
            <span>Nodes</span>
          </button>
          <button
            @click="activeSidebarTab = 'variables'"
            :class="{ active: activeSidebarTab === 'variables' }"
            class="activity-tab"
            title="Variables"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <rect x="2" y="3" width="20" height="18" rx="2" ry="2"/>
              <line x1="8" y1="9" x2="16" y2="9"/>
              <line x1="8" y1="15" x2="16" y2="15"/>
              <line x1="8" y1="12" x2="16" y2="12"/>
            </svg>
            <span>Variables</span>
          </button>
          <button
            @click="activeSidebarTab = 'settings'"
            :class="{ active: activeSidebarTab === 'settings' }"
            class="activity-tab"
            title="Flow Settings"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
            <span>Settings</span>
          </button>
        </div>

        <!-- Panel Content -->
        <div class="sidebar-panel">

          <!-- Nodes Panel -->
          <div v-show="activeSidebarTab === 'nodes'" class="panel-inner">
            <div class="sidebar-header">
              <h3>Nodes</h3>
              <input
                v-model="nodeSearchQuery"
                type="text"
                placeholder="Search nodes..."
                class="node-search"
              />
            </div>
            <div class="node-categories">
              <!-- Messages -->
              <div class="node-category">
                <div class="category-header" @click="toggleCategory('messages')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Messages</span>
                  <svg :class="{ rotated: expandedCategories.messages }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.messages" class="category-nodes">
                  <div v-for="node in getNodesByCategory('messages')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item" :data-node-type="node.type">
                    <span v-if="getNodeIconSrcs(node.type).length" class="node-icon-multi">
                      <img v-for="(src, i) in getNodeIconSrcs(node.type)" :key="i" :src="src" class="node-icon-img" :alt="node.name" />
                    </span>
                    <span v-else class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Input -->
              <div class="node-category">
                <div class="category-header" @click="toggleCategory('inputs')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Input</span>
                  <svg :class="{ rotated: expandedCategories.inputs }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.inputs" class="category-nodes">
                  <div v-for="node in getNodesByCategory('inputs')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item" :data-node-type="node.type">
                    <span v-if="getNodeIconSrcs(node.type).length" class="node-icon-multi">
                      <img v-for="(src, i) in getNodeIconSrcs(node.type)" :key="i" :src="src" class="node-icon-img" :alt="node.name" />
                    </span>
                    <span v-else class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Logic -->
              <div class="node-category">
                <div class="category-header" @click="toggleCategory('logic')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="16 18 22 12 16 6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    <polyline points="8 6 2 12 8 18" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Logic</span>
                  <svg :class="{ rotated: expandedCategories.logic }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.logic" class="category-nodes">
                  <div v-for="node in getNodesByCategory('logic')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item" :data-node-type="node.type">
                    <span class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Actions -->
              <div class="node-category">
                <div class="category-header" @click="toggleCategory('actions')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                  <span>Actions</span>
                  <svg :class="{ rotated: expandedCategories.actions }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.actions" class="category-nodes">
                  <div v-for="node in getNodesByCategory('actions')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item" :data-node-type="node.type">
                    <img v-if="getNodeIconSrc(node.type)" :src="getNodeIconSrc(node.type)" class="node-icon-img" :alt="node.name" />
                    <span v-else class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- CRM -->
              <div class="node-category node-category-crm">
                <div class="category-header category-header-crm" @click="toggleCategory('crm')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                  <span>Customer Actions</span>
                  <svg :class="{ rotated: expandedCategories.crm }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.crm" class="category-nodes">
                  <div v-for="node in getNodesByCategory('crm')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item" :data-node-type="node.type">
                    <span class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Google Sheets -->
              <div class="node-category node-category-google">
                <div class="category-header category-header-google" @click="toggleCategory('google_sheets')">
                  <img src="@/assets/google-sheets.svg" class="google-brand-icon" alt="Google Sheets" />
                  <span>Google Sheets</span>
                  <svg :class="{ rotated: expandedCategories.google_sheets }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.google_sheets" class="category-nodes">
                  <div v-for="node in getNodesByCategory('google_sheets')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item node-item-google" :data-node-type="node.type">
                    <img src="@/assets/google-sheets.svg" class="node-icon-img" alt="Google Sheets" />
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Google Drive -->
              <div class="node-category node-category-google">
                <div class="category-header category-header-google" @click="toggleCategory('google_drive')">
                  <img src="@/assets/google-drive.svg" class="google-brand-icon" alt="Google Drive" />
                  <span>Google Drive</span>
                  <svg :class="{ rotated: expandedCategories.google_drive }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.google_drive" class="category-nodes">
                  <div v-for="node in getNodesByCategory('google_drive')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item node-item-google" :data-node-type="node.type">
                    <img src="@/assets/google-drive.svg" class="node-icon-img" alt="Google Drive" />
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Google Docs -->
              <div class="node-category node-category-google">
                <div class="category-header category-header-google" @click="toggleCategory('google_docs')">
                  <img src="@/assets/google-docs.svg" class="google-brand-icon" alt="Google Docs" />
                  <span>Google Docs</span>
                  <svg :class="{ rotated: expandedCategories.google_docs }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.google_docs" class="category-nodes">
                  <div v-for="node in getNodesByCategory('google_docs')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item node-item-google" :data-node-type="node.type">
                    <img src="@/assets/google-docs.svg" class="node-icon-img" alt="Google Docs" />
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Microsoft Excel -->
              <div class="node-category node-category-google">
                <div class="category-header category-header-google" @click="toggleCategory('excel')">
                  <img src="@/assets/excel.svg" class="google-brand-icon" alt="Excel" />
                  <span>Excel Online</span>
                  <svg :class="{ rotated: expandedCategories.excel }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.excel" class="category-nodes">
                  <div v-for="node in getNodesByCategory('excel')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item node-item-google" :data-node-type="node.type">
                    <img src="@/assets/excel.svg" class="node-icon-img" alt="Excel" />
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Microsoft OneDrive -->
              <div class="node-category node-category-google">
                <div class="category-header category-header-google" @click="toggleCategory('onedrive')">
                  <img src="@/assets/onedrive.svg" class="google-brand-icon" alt="OneDrive" />
                  <span>OneDrive</span>
                  <svg :class="{ rotated: expandedCategories.onedrive }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.onedrive" class="category-nodes">
                  <div v-for="node in getNodesByCategory('onedrive')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item node-item-google" :data-node-type="node.type">
                    <img src="@/assets/onedrive.svg" class="node-icon-img" alt="OneDrive" />
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- AI -->
              <div class="node-category node-category-ai">
                <div class="category-header category-header-ai" @click="toggleCategory('ai')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M12 2a2 2 0 0 1 2 2c0 .74-.4 1.39-1 1.73V7h1a7 7 0 0 1 7 7h1a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1h-1v1a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-1H2a1 1 0 0 1-1-1v-3a1 1 0 0 1 1-1h1a7 7 0 0 1 7-7h1V5.73c-.6-.34-1-.99-1-1.73a2 2 0 0 1 2-2z" stroke-linecap="round" stroke-linejoin="round"/>
                    <circle cx="8.5" cy="13.5" r="1.5" fill="currentColor" stroke="none"/>
                    <circle cx="15.5" cy="13.5" r="1.5" fill="currentColor" stroke="none"/>
                  </svg>
                  <span>AI</span>
                  <svg :class="{ rotated: expandedCategories.ai }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.ai" class="category-nodes">
                  <div v-for="node in getNodesByCategory('ai')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item node-item-ai" :data-node-type="node.type">
                    <img :src="getNodeIconSrc(node.type)" class="node-icon-img ai-icon-img" :alt="node.name" />
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Leads -->
              <div class="node-category node-category-leads">
                <div class="category-header category-header-leads" @click="toggleCategory('leads')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>
                  </svg>
                  <span>Leads</span>
                  <svg :class="{ rotated: expandedCategories.leads }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.leads" class="category-nodes">
                  <div v-for="node in getNodesByCategory('leads')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item" :data-node-type="node.type">
                    <span class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Advertiser -->
              <div class="node-category node-category-advertiser">
                <div class="category-header category-header-advertiser" @click="toggleCategory('advertiser')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
                  </svg>
                  <span>Advertiser</span>
                  <svg :class="{ rotated: expandedCategories.advertiser }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.advertiser" class="category-nodes">
                  <div v-for="node in getNodesByCategory('advertiser')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item node-item-advertiser" :data-node-type="node.type">
                    <span class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Appointments -->
              <div class="node-category node-category-appointments">
                <div class="category-header category-header-appointments" @click="toggleCategory('appointments')">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                  </svg>
                  <span>Appointments</span>
                  <svg :class="{ rotated: expandedCategories.appointments }" class="chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <polyline points="6 9 12 15 18 9" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </div>
                <div v-show="expandedCategories.appointments" class="category-nodes">
                  <div v-for="node in getNodesByCategory('appointments')" :key="node.type" :draggable="true" @dragstart="onNodeDragStart($event, node)" class="node-item" :data-node-type="node.type">
                    <span class="node-icon">{{ node.icon }}</span>
                    <div class="node-info">
                      <div class="node-name">{{ node.name }}</div>
                      <div class="node-desc">{{ node.description }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Variables Panel -->
          <div v-show="activeSidebarTab === 'variables'" class="panel-inner">
            <div class="sidebar-header">
              <div class="sidebar-header-row">
                <h3>Variables</h3>
                <span class="variable-count">{{ Object.keys(flowVariables).length }}</span>
              </div>
            </div>
            <div class="vars-sidebar-content">
              <div class="vars-actions">
                <button @click="openVariableModal()" class="btn-add-variable">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                  </svg>
                  <span>New Variable</span>
                </button>
              </div>
              <div v-if="Object.keys(flowVariables).length > 0" class="vars-list">
                <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                  <div v-if="Object.keys(group.vars).length > 0" class="var-group">
                    <div class="var-group-header" :class="'vg-' + srcKey">
                      <span class="var-group-icon">{{ group.icon }}</span>
                      <div class="var-group-info">
                        <span class="var-group-label">{{ group.label }}</span>
                        <span class="var-group-desc">{{ group.desc }}</span>
                      </div>
                      <span class="var-group-count">{{ Object.keys(group.vars).length }}</span>
                    </div>
                    <div v-for="(variable, name) in group.vars" :key="name" class="variable-card" :class="'card-src-' + srcKey">
                      <div class="variable-card-header">
                        <div class="variable-title">
                          <code class="variable-syntax">&#123;&#123;{{ name }}&#125;&#125;</code>
                          <button @click="copyVariableToClipboard(name)" class="btn-copy-var" title="Copy to clipboard">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
                            </svg>
                          </button>
                        </div>
                        <span v-if="variable.secret" class="type-badge type-secret" title="Encrypted on server">🔒 secret</span>
                        <span v-else class="type-badge" :class="'type-' + variable.type">{{ variable.type }}</span>
                      </div>
                      <div v-if="variable.secret" class="variable-value-masked">••••••••••••</div>
                      <div v-else-if="variable.description" class="variable-description">{{ variable.description }}</div>
                      <button v-if="variable.nodeId" class="var-source-node-btn" @click="selectNode(variable.nodeId)" :title="'Jump to: ' + variable.nodeName">
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><rect x="3" y="3" width="18" height="18" rx="3"/><path d="M9 12h6M12 9v6"/></svg>
                        {{ variable.nodeName }}
                      </button>
                      <div v-if="variable.source === 'custom' || variable.source === 'api'" class="variable-card-actions">
                        <button @click="editCustomVariable(name, variable.value, variable.secret)" class="btn-action btn-edit" title="Edit">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
                            <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
                          </svg>
                          Edit
                        </button>
                        <button @click="deleteCustomVariable(name)" class="btn-action btn-delete" title="Delete">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <polyline points="3 6 5 6 21 6"/>
                            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                          </svg>
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </template>
              </div>

              <!-- Broken / orphaned variable references -->
              <div v-if="orphanedVarNames.size > 0" class="var-group broken-vars-group">
                <div class="var-group-header vg-broken">
                  <span class="var-group-icon">⚠️</span>
                  <div class="var-group-info">
                    <span class="var-group-label">Broken References</span>
                    <span class="var-group-desc">Used in nodes but no longer produced</span>
                  </div>
                  <span class="var-group-count">{{ orphanedVarNames.size }}</span>
                </div>
                <div v-for="name in [...orphanedVarNames]" :key="name" class="variable-card card-broken">
                  <div class="variable-card-header">
                    <div class="variable-title">
                      <code class="variable-syntax broken-syntax">&#123;&#123;{{ name }}&#125;&#125;</code>
                    </div>
                    <span class="type-badge type-broken">broken</span>
                  </div>
                  <div class="variable-description" style="color: #f87171;">Referenced but source node was deleted</div>
                  <div class="variable-card-actions">
                    <button @click="removeOrphanedVar(name)" class="btn-action btn-delete" title="Remove all references to this variable">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div v-else class="vars-empty">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="2" y="3" width="20" height="18" rx="2" ry="2"/>
                  <line x1="8" y1="9" x2="16" y2="9"/>
                  <line x1="8" y1="15" x2="16" y2="15"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                </svg>
                <p>No variables yet</p>
                <small>Auto-created from inputs &amp; API calls, or add custom ones.</small>
              </div>
            </div>
          </div>

          <!-- Settings Panel -->
          <div v-show="activeSidebarTab === 'settings'" class="panel-inner settings-panel-inner">
            <div class="sidebar-header settings-sticky-header">
              <h3>Flow Settings</h3>
            </div>
            <div class="settings-content">

              <!-- Settings validation errors -->
              <div v-if="flowValidation.errors.some(e => e.nodeId === '__settings__')" class="settings-error-banner">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style="flex-shrink:0;margin-top:1px"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                <div>
                  <div v-for="err in flowValidation.errors.filter(e => e.nodeId === '__settings__')" :key="err.type" class="settings-error-item">{{ err.message }}</div>
                </div>
              </div>

              <!-- Session Timeout -->
              <div class="settings-section">
                <div class="settings-section-header">
                  <span class="settings-section-icon">⏱</span>
                  <div>
                    <strong>Session Timeout</strong>
                    <p>How long a customer can be inactive before their session expires and they restart from the beginning. The timer resets on every interaction.</p>
                  </div>
                </div>
                <div class="settings-field">
                  <label class="settings-label">Expire after last interaction</label>
                  <div style="display:flex;gap:8px;align-items:center;" :style="{ opacity: flowDefinition.settings.never_expire ? 0.4 : 1, pointerEvents: flowDefinition.settings.never_expire ? 'none' : 'auto' }">
                    <input
                      v-model.number="flowDefinition.settings.timeout_value"
                      type="number"
                      min="1"
                      class="property-input"
                      style="flex:1;min-width:0;"
                      @input="onNodePropertyChange"
                    />
                    <select
                      v-model="flowDefinition.settings.timeout_unit"
                      class="property-input"
                      style="flex:1;min-width:0;"
                      @change="onNodePropertyChange"
                    >
                      <option value="minutes">Minutes</option>
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                    </select>
                  </div>
                  <label class="property-checkbox" style="margin-top:6px;">
                    <input
                      type="checkbox"
                      v-model="flowDefinition.settings.never_expire"
                      @change="onNodePropertyChange"
                    />
                    <span>Never expire — session stays active indefinitely</span>
                  </label>
                  <small class="property-hint">
                    Current: <strong>{{ sessionTimeoutLabel }}</strong>. Use the
                    <em>Override Session Timeout</em> node to change it mid-flow.
                  </small>
                </div>
              </div>

              <!-- Department Handoff Mode -->
              <div class="settings-section">
                <div class="settings-section-header">
                  <span class="settings-section-icon">🏢</span>
                  <div>
                    <strong>Department Handoff Mode</strong>
                    <p>When enabled, the flow pauses once a customer is assigned to a department. Agents handle the conversation manually. The flow resumes automatically when the customer next messages after the timeout.</p>
                  </div>
                </div>
                <div class="settings-field">
                  <label class="property-checkbox">
                    <input
                      type="checkbox"
                      v-model="flowDefinition.settings.department_handoff_mode"
                      @change="onNodePropertyChange"
                    />
                    <span>Enable department handoff mode</span>
                  </label>
                </div>
                <template v-if="flowDefinition.settings.department_handoff_mode">
                  <div class="settings-field">
                    <label class="settings-label">Max agent handle time</label>
                    <div style="display:flex;gap:8px;align-items:center;">
                      <input
                        v-model.number="flowDefinition.settings.max_agent_handle_value"
                        type="number"
                        min="1"
                        :max="flowDefinition.settings.max_agent_handle_unit === 'minutes' ? 1440 : 24"
                        class="property-input"
                        style="flex:1;min-width:0;"
                        @input="onNodePropertyChange"
                      />
                      <select
                        v-model="flowDefinition.settings.max_agent_handle_unit"
                        class="property-input"
                        style="flex:1;min-width:0;"
                        @change="onAgentHandleUnitChange"
                      >
                        <option value="minutes">Minutes</option>
                        <option value="hours">Hours</option>
                      </select>
                    </div>
                    <small class="property-hint">If the customer messages again after this period with no resolution, the flow restarts from the beginning.</small>
                  </div>
                  <div class="settings-field">
                    <label class="settings-label">Resolve message</label>
                    <label class="property-checkbox" style="margin-bottom:6px;">
                      <input
                        type="checkbox"
                        v-model="flowDefinition.settings.resolve_message_enabled"
                        @change="onNodePropertyChange"
                      />
                      <span>Send a message when agent marks as resolved</span>
                    </label>
                    <textarea
                      v-if="flowDefinition.settings.resolve_message_enabled"
                      v-model="flowDefinition.settings.resolve_message"
                      class="property-input"
                      rows="3"
                      placeholder="e.g. Thank you for contacting us. We hope we were able to help!"
                      style="resize:vertical;"
                      @input="onNodePropertyChange"
                    ></textarea>
                  </div>

                  <!-- Late Response Tracking -->
                  <div class="settings-field">
                    <label class="settings-label">Mark conversation as LATE after</label>
                    <div style="display:flex;gap:8px;align-items:center;">
                      <input
                        v-model.number="flowDefinition.settings.handoff_late_threshold_minutes"
                        type="number"
                        min="1"
                        class="property-input"
                        placeholder="30"
                        @input="onNodePropertyChange"
                      />
                      <span style="color:var(--text-secondary);font-size:13px;white-space:nowrap;">minutes with no agent reply</span>
                    </div>
                    <small class="property-hint">Once exceeded, the conversation appears as LATE in the dashboard. The agent's LATE counter increments in performance reports.</small>
                  </div>

                  <!-- 24h Window Warning -->
                  <div class="settings-field">
                    <label class="settings-label">Send 24h window warning</label>
                    <div style="display:flex;gap:8px;align-items:center;">
                      <input
                        v-model.number="flowDefinition.settings.handoff_warn_before_24h_minutes"
                        type="number"
                        min="1"
                        class="property-input"
                        placeholder="120"
                        @input="onNodePropertyChange"
                      />
                      <span style="color:var(--text-secondary);font-size:13px;white-space:nowrap;">minutes before window closes</span>
                    </div>
                    <small class="property-hint">An interactive message with a reply button is sent to the customer this many minutes before the 24h free messaging window closes.</small>
                  </div>

                  <!-- Extend Window Message -->
                  <div class="settings-field">
                    <label class="settings-label">Extend window message</label>
                    <textarea
                      v-model="flowDefinition.settings.handoff_extend_message"
                      class="property-input"
                      rows="3"
                      placeholder="Our team is still available to assist you. Please reply to keep this conversation open."
                      style="resize:vertical;"
                      @input="onNodePropertyChange"
                    ></textarea>
                    <label class="settings-label" style="margin-top:8px;">Reply button text</label>
                    <input
                      v-model="flowDefinition.settings.handoff_extend_button_text"
                      type="text"
                      class="property-input"
                      placeholder="I'm still here"
                      maxlength="20"
                      @input="onNodePropertyChange"
                    />
                    <small class="property-hint">WhatsApp reply buttons are limited to 20 characters.</small>
                  </div>
                </template>
              </div>

            </div>
          </div>

        </div>
      </div>

      <!-- Center - Flow Canvas -->
      <div class="canvas-container" @mousedown="onCanvasMouseDown" @wheel.prevent="onCanvasWheel">
        <div
          ref="canvas"
          class="flow-canvas show-grid"
          :style="canvasTransform"
          @drop="onCanvasDrop"
          @dragover.prevent
          @click="deselectAll"
        >
          <div
            v-for="node in flowDefinition.nodes"
            :key="node.id"
            :ref="`node-${node.id}`"
            :class="[
              'flow-node',
              `node-type-${node.type}`,
              { selected: selectedNodeId === node.id },
              { 'multi-selected': selectedNodeIds.includes(node.id) },
              { 'node-has-error': getNodeValidationStatus(node.id).hasError },
              { 'node-has-warning': !getNodeValidationStatus(node.id).hasError && getNodeValidationStatus(node.id).hasWarning }
            ]"
            :style="{
              left: node.position.x + 'px',
              top: node.position.y + 'px'
            }"
            :data-node-id="node.id"
            @click.stop="selectNode(node.id)"
            @mousedown="startNodeDrag($event, node.id)"
          >
            <div class="node-header">
              <div class="node-type-label">
                <template v-if="getNodeIconSrcs(node.type).length">
                  <img v-for="(src, i) in getNodeIconSrcs(node.type)" :key="i" :src="src" class="canvas-node-icon-img" :alt="node.type" />
                </template>
                <span v-else class="canvas-node-icon-emoji">{{ getNodeTypeIcon(node.type) }}</span>
                {{ getNodeTypeName(node.type) }}
              </div>
              <span v-if="getNodeValidationStatus(node.id).hasError" class="node-status-badge error" title="Error">
                🔴
              </span>
              <span v-else-if="getNodeValidationStatus(node.id).hasWarning" class="node-status-badge warning" title="Warning">
                🟡
              </span>
              <span v-else-if="hasConnection(node)" class="node-status-badge success" title="Valid">
                🟢
              </span>
              <span v-if="getNodeOrphanedVars(node).length > 0" class="node-status-badge broken-badge" :title="'Broken variables: ' + getNodeOrphanedVars(node).join(', ')">
                ⚠️
              </span>
              <button @click.stop="selectSubtree(node.id)" class="btn-select-subtree" title="Select this node + all connected downstream nodes">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="6" cy="6" r="3"/><circle cx="18" cy="6" r="3"/><circle cx="12" cy="18" r="3"/>
                  <line x1="6" y1="9" x2="12" y2="15"/><line x1="18" y1="9" x2="12" y2="15"/>
                </svg>
              </button>
              <button @click.stop="openDuplicateMenu($event, node.id)" class="btn-duplicate-node" :class="{ active: duplicateMenuNodeId === node.id }" title="Duplicate node">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <button v-if="!node.isWelcomeMessage" @click.stop="deleteNode(node.id)" class="btn-delete-node">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="3 6 5 6 21 6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </button>
              <span v-else class="welcome-badge" title="Welcome Message (Cannot be deleted)">
                🏠
              </span>
            </div>
            <div v-if="node.name" class="node-name-section">
              <div class="node-name-label">{{ node.name }}</div>
            </div>
            <div class="node-content" v-if="node.type !== 'api_call'">
              <div class="node-preview">{{ getNodePreview(node) }}</div>
            </div>
            <div class="node-ports">
              <div
                v-if="!isStartNode(node.id)"
                class="port port-input"
                :class="{ 'port-connected': isInputPortConnected(node.id) }"
                :data-node-id="node.id"
                :data-port-type="'input'"
              ></div>

              <!-- Multiple output ports for button/list nodes -->
              <template v-if="hasMultipleOutputs(node)">
                <div
                  v-for="(option, index) in getNodeOptions(node)"
                  :key="`port-${node.id}-${index}`"
                  class="port port-output port-multi"
                  :class="[`port-index-${index}`, { 'port-connected': isPortConnected(node, option.id) }]"
                  :data-node-id="node.id"
                  :data-port-type="'output'"
                  :data-port-index="index"
                  :data-port-id="option.id"
                  :style="getMultiPortStyle(node, index)"
                  @mousedown.stop="startConnectionDrag($event, node.id, option.id)"
                >
                  <div class="port-label">{{ option.label }}</div>
                </div>
              </template>

              <!-- On Resolve port for assign_department with post_resolve_enabled -->
              <div
                v-else-if="node.type === 'assign_department' && node.config?.post_resolve_enabled && flowDefinition?.settings?.department_handoff_mode"
                class="port port-output port-multi"
                :class="{ 'port-connected': !!node.config.post_resolve_first_node_id }"
                :data-node-id="node.id"
                :data-port-type="'output'"
                :data-port-id="'post_resolve'"
                @mousedown.stop="startConnectionDrag($event, node.id, 'post_resolve')"
              >
                <div class="port-label">On Resolve</div>
              </div>

              <!-- Single output port for other nodes -->
              <div
                v-else-if="canHaveOutput(node)"
                class="port port-output"
                :data-node-id="node.id"
                :data-port-type="'output'"
                @mousedown.stop="startConnectionDrag($event, node.id)"
              ></div>
            </div>

            <!-- AI Function Tool Ports (bottom) — always visible for AI nodes -->
            <template v-if="['openai_call','claude_call','gemini_call'].includes(node.type)">
              <!-- One port per configured tool -->
              <div
                v-for="(tool, tidx) in (node.config?.tools || [])"
                :key="`tool-port-${node.id}-${tidx}`"
                class="port port-tool"
                :class="{ 'port-connected': !!tool.node_id }"
                :data-node-id="node.id"
                :data-port-type="'output'"
                :data-port-id="`tool__${tidx}`"
                :style="getToolPortStyle(node, tidx, false)"
                @mousedown.stop="startConnectionDrag($event, node.id, `tool__${tidx}`)"
              >
                <div class="port-tool-label">{{ tool.name || 'tool' }}</div>
              </div>
              <!-- "+" add-tool port — always shown -->
              <div
                class="port port-tool port-tool-add"
                :data-node-id="node.id"
                :data-port-type="'output'"
                :data-port-id="'tool__new'"
                :style="getToolPortStyle(node, (node.config?.tools || []).length, true)"
                @mousedown.stop="startConnectionDrag($event, node.id, 'tool__new')"
              >
                <div class="port-tool-label">+ add tool</div>
              </div>
            </template>

            <!-- Advertiser Message Slot Ports (bottom) -->
            <template v-if="node.type === 'advertiser'">
              <div
                v-for="i in (node.config?.count || 2)"
                :key="`adv-port-${node.id}-${i-1}`"
                class="port port-tool port-adv-msg"
                :class="{ 'port-connected': !!(node.config?.message_nodes || [])[i-1] }"
                :data-node-id="node.id"
                :data-port-type="'output'"
                :data-port-id="`msg__${i-1}`"
                :style="getAdvPortStyle(node, i-1)"
                @mousedown.stop="startConnectionDrag($event, node.id, `msg__${i-1}`)"
              >
                <div class="port-tool-label">Msg {{ i }}</div>
              </div>
            </template>
          </div>

          <!-- Connections/Edges -->
          <svg class="connections-layer">
            <path
              v-for="(edge, index) in edges"
              :key="`${edge.from}-${edge.to}-${index}`"
              :d="getEdgePath(edge)"
              :class="['connection-path', { 'connection-tool': edge.isTool, selected: selectedEdge && selectedEdge.from === edge.from && selectedEdge.to === edge.to && selectedEdge.portId === edge.portId }]"
              @click.stop="selectEdge(edge)"
            />
            <!-- Temporary connection while dragging -->
            <path
              v-if="tempConnection"
              :d="tempConnection.path"
              class="connection-path temp"
              stroke-dasharray="5,5"
            />
          </svg>

          <!-- Delete buttons for connections -->
          <div
            v-for="(edge, index) in edges"
            :key="`delete-${edge.from}-${edge.to}-${index}`"
            class="connection-delete-btn"
            :style="getEdgeDeleteButtonPosition(edge)"
            @click.stop="deleteEdge(edge)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke-width="2" fill="white"/>
              <line x1="8" y1="8" x2="16" y2="16" stroke-width="2" stroke-linecap="round"/>
              <line x1="16" y1="8" x2="8" y2="16" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </div>
        </div>

        <!-- Canvas Controls -->
        <div class="canvas-controls">
          <button @click="zoomIn" class="btn-zoom">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M21 21l-4.35-4.35M11 8v6M8 11h6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <div class="zoom-level">{{ Math.round(zoomLevel * 100) }}%</div>
          <button @click="zoomOut" class="btn-zoom">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M21 21l-4.35-4.35M8 11h6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
          <button @click="resetZoom" class="btn-zoom">Reset</button>
          <div class="canvas-controls-divider"></div>
          <!-- Multi-select toggle -->
          <div class="multiselect-controls">
            <button
              :class="['btn-multiselect', { active: multiSelectMode }]"
              @click="toggleMultiSelect"
              :title="multiSelectMode ? 'Exit multi-select mode' : 'Multi-select mode: click nodes to select, then drag any selected node to move them all'"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              <span>{{ multiSelectMode ? (selectedNodeIds.length ? `${selectedNodeIds.length} selected` : 'Click nodes to select') : 'Multi-select' }}</span>
            </button>
            <button
              v-if="multiSelectMode && selectedNodeIds.length"
              class="btn-multiselect-clear"
              @click="selectedNodeIds = []; multiSelectMode = false"
              title="Clear selection and exit multi-select"
            >✕</button>
          </div>
        </div>
      </div>

      <!-- Right Sidebar - Node Properties -->
      <div v-if="selectedNode" class="right-sidebar" :style="{ width: propertiesPanelWidth + 'px' }">
        <div class="resize-handle" @mousedown="startPanelResize"></div>
        <div class="properties-panel">
          <div class="panel-header">
            <h3>Node Properties</h3>
            <button @click="deselectAll" class="btn-close-panel">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                <line x1="6" y1="6" x2="18" y2="18" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
          </div>

          <div class="properties-content">
            <!-- Common Properties -->
            <div class="property-group">
              <label class="property-label">Node Name</label>
              <input
                v-model="selectedNode.name"
                type="text"
                class="property-input"
                placeholder="Enter node name"
                @input="onNodePropertyChange"
              />
            </div>

            <div class="property-group">
              <label class="property-label">Node Type</label>
              <div class="property-value-readonly">{{ getNodeTypeName(selectedNode.type) }}</div>
            </div>

            <!-- Validation Errors for this node -->
            <div v-if="getNodeValidationStatus(selectedNode.id).hasError" class="node-validation-errors">
              <div
                v-for="err in flowValidation.errors.filter(e => e.nodeId === selectedNode.id)"
                :key="err.type"
                class="node-validation-error-item"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
                  <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                </svg>
                {{ err.message }}
              </div>
            </div>

            <!-- Validation Warnings for this node -->
            <div v-if="getNodeValidationStatus(selectedNode.id).hasWarning" class="node-validation-warnings">
              <div
                v-for="warn in flowValidation.warnings.filter(w => w.nodeId === selectedNode.id)"
                :key="warn.type + warn.message"
                class="node-validation-warning-item"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="flex-shrink:0">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                {{ warn.message }}
              </div>
            </div>

            <!-- Type-specific Properties -->

            <!-- Message Node Editor -->
            <div v-if="selectedNode.type === 'message'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Header <span class="field-optional">optional · max 60 chars</span></label>
                <VarTextEditor v-model="ensureHeader(selectedNode).en" placeholder="Bold title above the message..." :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Message Text</label>
                <VarTextEditor v-model="selectedNode.config.text.en" placeholder="Enter your message..." :rows="3" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Footer <span class="field-optional">optional · max 60 chars</span></label>
                <VarTextEditor v-model="ensureFooter(selectedNode).en" placeholder="Small grey note below the message..." :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>
            </div>

            <!-- List Message Node Editor -->
            <div v-if="selectedNode.type === 'list_message'" class="node-editor">
              <div class="list-msg-banner">📋 List Message — sends a formatted list from an array variable</div>

              <!-- Array variable picker -->
              <div class="property-group">
                <label class="property-label">Array Variable <span class="field-required">required</span></label>
                <select v-model="selectedNode.config.array_variable" class="property-input" @change="onNodePropertyChange">
                  <option value="">— Select array variable —</option>
                  <option v-for="(info, varName) in getArrayVariables()" :key="varName" :value="varName">
                    {{ varName }} ({{ info.arrayLength || '?' }} items)
                  </option>
                </select>
                <small v-if="!Object.keys(getArrayVariables()).length" class="property-hint warn-hint">No array variables detected yet. Run an API Call node with a test first.</small>
              </div>

              <!-- Field chips — shown once an array variable with known fields is selected -->
              <div v-if="selectedNode.config.array_variable && getArrayVariables()[selectedNode.config.array_variable]?.objectFields?.length" class="property-group">
                <label class="property-label">Item Fields <span class="field-optional">click to insert into template</span></label>
                <div class="list-field-chips">
                  <span class="list-field-chip list-field-chip--index" @mousedown.prevent="insertListField('#')">&#123;&#123;#&#125;&#125; number</span>
                  <span
                    v-for="field in getArrayVariables()[selectedNode.config.array_variable].objectFields"
                    :key="field"
                    class="list-field-chip"
                    @mousedown.prevent="insertListField(field)"
                  >{{ field }}</span>
                </div>
              </div>

              <!-- Header -->
              <div class="property-group">
                <label class="property-label">Header <span class="field-optional">optional</span></label>
                <VarTextEditor v-model="ensureHeader(selectedNode).en" placeholder="e.g. 📋 Your Transactions" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>

              <!-- Item template -->
              <div class="property-group">
                <label class="property-label">Item Template <span class="field-required">required</span></label>
                <VarTextEditor v-model="selectedNode.config.item_template.en" placeholder="• {{name}} — {{amount}} on {{date}}" :rows="3" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Click field chips above to insert. Use <code v-pre>{{#}}</code> for row number.</small>
              </div>

              <!-- Separator -->
              <div class="property-group">
                <label class="property-label">Item Spacing</label>
                <div class="list-separator-row">
                  <label class="list-sep-option">
                    <input type="radio" v-model="selectedNode.config.separator" value="compact" @change="onNodePropertyChange">
                    <span>Compact <small>(new line)</small></span>
                  </label>
                  <label class="list-sep-option">
                    <input type="radio" v-model="selectedNode.config.separator" value="spacious" @change="onNodePropertyChange">
                    <span>Spacious <small>(blank line)</small></span>
                  </label>
                  <label class="list-sep-option">
                    <input type="radio" v-model="selectedNode.config.separator" value="divider" @change="onNodePropertyChange">
                    <span>Divider <small>(─── line)</small></span>
                  </label>
                </div>
              </div>

              <!-- Max items -->
              <div class="property-group">
                <label class="property-label">Max Items <span class="field-optional">0 = show all</span></label>
                <input type="number" v-model.number="selectedNode.config.max_items" class="property-input" min="0" placeholder="0" @change="onNodePropertyChange" />
              </div>

              <!-- Footer -->
              <div class="property-group">
                <label class="property-label">Footer <span class="field-optional">optional</span></label>
                <VarTextEditor v-model="ensureFooter(selectedNode).en" placeholder="e.g. Total: {{total_amount}}" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>

              <!-- Empty fallback -->
              <div class="property-group">
                <label class="property-label">Empty Fallback <span class="field-optional">shown when array is empty</span></label>
                <input type="text" v-model="selectedNode.config.empty_fallback.en" class="property-input" placeholder="No items found." @change="onNodePropertyChange" />
              </div>
            </div>

            <!-- Image Message Node Editor -->
            <div v-if="selectedNode.type === 'image_message'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Image URL</label>
                <VarTextEditor v-model="selectedNode.config.image_url" placeholder="https://example.com/image.jpg" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Enter a public image URL (JPG, PNG, WebP)</small>
              </div>
              <div class="property-group">
                <label class="property-label">Caption - Optional</label>
                <VarTextEditor v-model="ensureCaption(selectedNode).en" placeholder="Image description..." :rows="2" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>
            </div>

            <!-- Document Message Node Editor -->
            <div v-if="selectedNode.type === 'document_message'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Document URL</label>
                <VarTextEditor v-model="selectedNode.config.document_url" placeholder="https://example.com/file.pdf" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Enter a public URL to a PDF or document file</small>
              </div>
              <div class="property-group">
                <label class="property-label">Caption - Optional</label>
                <VarTextEditor v-model="ensureCaption(selectedNode).en" placeholder="Document description..." :rows="2" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>
            </div>

            <!-- Button Selection Node Editor -->
            <div v-if="selectedNode.type === 'button_selection'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Header <span class="field-optional">optional · max 60 chars</span></label>
                <VarTextEditor v-model="ensureHeader(selectedNode).en" placeholder="Bold title above the message..." :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Body Text</label>
                <VarTextEditor v-model="selectedNode.config.body.en" placeholder="What is your question?" :rows="2" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Footer <span class="field-optional">optional · max 60 chars</span></label>
                <VarTextEditor v-model="ensureFooter(selectedNode).en" placeholder="Small grey note below the buttons..." :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>

              <!-- Dynamic Buttons Toggle (hidden on Welcome Message — no session data exists yet) -->
              <div v-if="!selectedNode.isWelcomeMessage" class="property-group">
                <label class="property-checkbox dynamic-toggle">
                  <input
                    type="checkbox"
                    v-model="selectedNode.config.use_dynamic_buttons"
                    @change="onNodePropertyChange"
                  />
                  <span>🔄 Generate buttons dynamically from array</span>
                </label>
                <small class="property-hint">When enabled, buttons are created from an API response array</small>
              </div>

              <!-- Dynamic Button Configuration -->
              <div v-if="!selectedNode.isWelcomeMessage && selectedNode.config.use_dynamic_buttons" class="dynamic-config-section">
                <div class="property-group">
                  <label class="property-label">Array Variable</label>
                  <select v-model="selectedNode.config.dynamic_array_variable" class="property-input" @change="onNodePropertyChange">
                    <option value="">Select array variable...</option>
                    <option v-for="(varData, varName) in getArrayVariables()" :key="varName" :value="varName">
                      {{ varName }} - {{ varData.arrayItemType }}[{{ varData.arrayLength }}]
                    </option>
                  </select>
                  <small class="property-hint">Choose which array variable to use for generating buttons</small>
                </div>

                <!-- Field Mapping for Object Arrays -->
                <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayItemType === 'object'" class="property-group">
                  <label class="property-label">Button Text Field</label>
                  <select v-model="selectedNode.config.button_text_field" class="property-input" @change="onNodePropertyChange">
                    <option value="">Select field for button text...</option>
                    <option v-for="field in getSelectedArrayMetadata().objectFields" :key="field" :value="field">
                      {{ field }}
                    </option>
                  </select>
                  <small class="property-hint">Which field to use as button text</small>
                </div>

                <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayItemType === 'object' && selectedNode.config.button_text_field" class="property-group">
                  <label class="property-label">Format button text as date/time <span class="field-optional">optional</span></label>
                  <select v-model="selectedNode.config.button_text_date_format" class="property-input" @change="onNodePropertyChange">
                    <option value="">No — use value as-is</option>
                    <option value="g:i A">9:15 AM</option>
                    <option value="H:i">09:15</option>
                    <option value="h:i A">09:15 AM</option>
                    <option value="M j · g:i A">Apr 21 · 9:15 AM</option>
                    <option value="D, M j · g:i A">Tue, Apr 21 · 9:15 AM</option>
                    <option value="l, F j">Tuesday, April 21</option>
                    <option value="M j, Y">Apr 21, 2026</option>
                    <option value="d/m/Y">21/04/2026</option>
                    <option value="m/d/Y">04/21/2026</option>
                  </select>
                  <small class="property-hint">If the field contains an ISO date like <code>2026-04-21T09:15:00</code>, pick a format to display it readably.</small>
                </div>

                <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayItemType === 'object'" class="property-group">
                  <label class="property-label">Button ID Field</label>
                  <select v-model="selectedNode.config.button_id_field" class="property-input" @change="onNodePropertyChange">
                    <option value="">Select field for button ID...</option>
                    <option v-for="field in getSelectedArrayMetadata().objectFields" :key="field" :value="field">
                      {{ field }}
                    </option>
                  </select>
                  <small class="property-hint">Which field to use as button value/identifier</small>
                </div>

                <!-- Routing Mode Selection -->
                <div class="property-group" style="background: rgba(249, 115, 22, 0.08); padding: 12px; border-radius: 8px; border: 1.5px solid rgba(249, 115, 22, 0.2);">
                  <label class="property-label" style="color: #f97316; font-weight: 600;">🔀 After User Selects, What Happens?</label>
                  <small class="property-hint" style="margin-bottom: 12px; display: block;">Choose how to route users after they click a dynamic button</small>

                  <div style="display: flex; gap: 12px;">
                    <label class="routing-option" :class="{ active: selectedNode.config.dynamic_routing_mode === 'end' }">
                      <input
                        type="radio"
                        value="end"
                        v-model="selectedNode.config.dynamic_routing_mode"
                        @change="onNodePropertyChange"
                      />
                      <div class="routing-option-content">
                        <div class="routing-option-title">🏁 End Here</div>
                        <small>Connect to a final message/action node</small>
                      </div>
                    </label>

                    <label class="routing-option" :class="{ active: selectedNode.config.dynamic_routing_mode === 'conditional' }">
                      <input
                        type="radio"
                        value="conditional"
                        v-model="selectedNode.config.dynamic_routing_mode"
                        @change="onNodePropertyChange"
                      />
                      <div class="routing-option-content">
                        <div class="routing-option-title">⚡ Use Conditional Router</div>
                        <small>Route based on what was selected</small>
                      </div>
                    </label>
                  </div>
                </div>

                <!-- Auto-Generated Variable Info (always shown for dynamic buttons) -->
                <div v-if="selectedNode.config.dynamic_array_variable" class="property-group" style="background: rgba(16, 185, 129, 0.08); padding: 12px; border-radius: 8px; border: 1.5px solid rgba(16, 185, 129, 0.2);">
                  <label class="property-label" style="color: #10b981; font-weight: 600;">💾 Auto-Stored Variables After Selection</label>
                  <small class="property-hint" style="margin-bottom: 12px; display: block;">When the user picks a button, ALL fields of the selected item are saved automatically</small>

                  <div style="display: flex; flex-direction: column; gap: 8px;">
                    <div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: rgba(16, 185, 129, 0.1); border-radius: 6px;">
                      <code style="font-size: 12px; color: #10b981; font-weight: 600;">{{ getAutoVarName(selectedNode, '_label') }}</code>
                      <span style="font-size: 11px; color: var(--text-secondary);">= Button text the user selected</span>
                    </div>
                    <div v-if="selectedNode.config.dynamic_routing_mode === 'conditional'" style="display: flex; align-items: center; gap: 8px; padding: 8px; background: rgba(16, 185, 129, 0.1); border-radius: 6px;">
                      <code style="font-size: 12px; color: #10b981; font-weight: 600;">{{ getAutoVarName(selectedNode, '_id') }}</code>
                      <span style="font-size: 11px; color: var(--text-secondary);">= Button ID value</span>
                    </div>
                    <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().objectFields" style="display: flex; flex-direction: column; gap: 4px; padding: 8px; background: rgba(16, 185, 129, 0.1); border-radius: 6px;">
                      <span style="font-size: 11px; color: #10b981; font-weight: 600;">All item fields:</span>
                      <div v-for="field in getSelectedArrayMetadata().objectFields" :key="field" style="font-size: 11px; color: var(--text-secondary); padding-left: 8px;">
                        <code style="color: #10b981;">{{ getAutoVarName(selectedNode, '_' + field) }}</code>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Fallback Message -->
                <div class="property-group">
                  <label class="property-label">Fallback Message (if array is empty)</label>
                  <input
                    v-model="selectedNode.config.empty_array_fallback.en"
                    class="property-input"
                    placeholder="No options available"
                    @input="onNodePropertyChange"
                  />
                </div>
              </div>

              <!-- Static Buttons (Only show when NOT using dynamic) -->
              <div v-if="!selectedNode.config.use_dynamic_buttons" class="property-group">
                <label class="property-label">Buttons</label>
                <small class="property-hint" style="color: #3b82f6;">
                  💡 1–3 buttons = Reply Buttons, 4+ = List Menu (auto-switches)
                </small>
                <div
                  v-for="(button, index) in selectedNode.config.buttons"
                  :key="button.id"
                  class="button-item"
                  draggable="true"
                  @dragstart="onButtonDragStart(index)"
                  @dragover.prevent="onButtonDragOver(index)"
                  @dragend="onButtonDragEnd"
                >
                  <span class="drag-handle">⠿</span>
                  <div class="button-item-fields">
                    <div class="btn-text-wrap">
                      <input
                        v-model="button.text.en"
                        :class="['property-input', { 'input-over-limit': button.text.en && button.text.en.length > (selectedNode.config.buttons.length > 3 ? 24 : 20) }]"
                        :placeholder="selectedNode.config.buttons.length > 3 ? 'Row title (max 24)' : 'Button text (max 20)'"
                        :maxlength="selectedNode.config.buttons.length > 3 ? 24 : 20"
                        @input="onNodePropertyChange"
                      />
                      <span
                        v-if="button.text.en && button.text.en.length > (selectedNode.config.buttons.length > 3 ? 24 : 20)"
                        class="btn-char-error"
                      >{{ button.text.en.length }}/{{ selectedNode.config.buttons.length > 3 ? 24 : 20 }}</span>
                    </div>
                    <input
                      v-if="selectedNode.config.buttons.length > 3"
                      v-model="button.description.en"
                      class="property-input"
                      placeholder="Description (optional, max 72)"
                      maxlength="72"
                      @input="onNodePropertyChange"
                    />
                  </div>
                  <button @click="removeButton(index)" class="btn-remove">×</button>
                </div>
                <button @click="addButton" class="btn-add">
                  + Add Button
                  <span v-if="selectedNode.config.buttons.length >= 3" style="font-size: 11px; opacity: 0.7;"> (will convert to list)</span>
                </button>
              </div>

              <!-- Auto Variable Hint -->
              <div v-if="selectedNode.name" class="property-group">
                <label class="property-label">Auto Variable <span class="field-optional">set when customer taps a button</span></label>
                <span
                  class="var-pick-chip chip-src-auto"
                  style="cursor:pointer;"
                  title="Click to copy"
                  @mousedown.prevent="copyVariableToClipboard(selectedNode.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') + '_label')"
                >{{ selectedNode.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') }}_label</span>
                <small class="property-hint" style="margin-top:4px;">Automatically holds the label of whichever button the customer tapped. No setup needed.</small>
              </div>

              <!-- Back Button Toggle (hidden on Welcome Message — first node has no previous node to go back to) -->
              <div v-if="!selectedNode.isWelcomeMessage" class="property-group">
                <label class="property-checkbox">
                  <input
                    type="checkbox"
                    v-model="selectedNode.config.show_back_button"
                    @change="onNodePropertyChange"
                  />
                  <span>⬅️ Show "Back" button</span>
                </label>
                <small class="property-hint">Adds a button to return to the previous node</small>
              </div>

            </div>

            <!-- Text Input Node Editor -->
            <div v-if="selectedNode.type === 'text_input'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Input Type</label>
                <select v-model="selectedNode.config.input_type" class="property-input" @change="onNodePropertyChange">
                  <option value="custom">Custom Text</option>
                  <option value="name">Name (validates format)</option>
                  <option value="email">Email Address (validates format)</option>
                  <option value="phone_number">Phone Number (validates format)</option>
                </select>
                <small class="property-hint">Choose input type for automatic validation</small>
              </div>
              <div class="property-group">
                <label class="property-label">Message <span class="field-optional">optional</span></label>
                <VarTextEditor v-model="selectedNode.config.prompt.en" placeholder="What is your name?" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Variable Name</label>
                <input
                  v-model="selectedNode.config.variable_name"
                  class="property-input"
                  placeholder="customer_name"
                  @input="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-checkbox">
                  <input
                    type="checkbox"
                    v-model="ensureValidation(selectedNode).required"
                    @change="onNodePropertyChange"
                  />
                  <span>Required field</span>
                </label>
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>
            </div>

            <!-- Date Picker Node Editor -->
            <div v-if="selectedNode.type === 'date_selection'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Prompt</label>
                <VarTextEditor v-model="ensurePrompt(selectedNode).en" placeholder="Please choose an available date:" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Button Label</label>
                <input v-model="selectedNode.config.button_label" class="property-input" placeholder="View Dates" @input="onNodePropertyChange" />
                <small class="property-hint">Text on the WhatsApp interactive list button (used when ≤10 dates)</small>
              </div>
              <div class="property-row-2">
                <div class="property-group">
                  <label class="property-label">Max Future Days</label>
                  <input type="number" v-model.number="selectedNode.config.max_future_days" class="property-input" min="0" max="365" @input="onNodePropertyChange" />
                </div>
                <div class="property-group">
                  <label class="property-label">Max Past Days</label>
                  <input type="number" v-model.number="selectedNode.config.max_past_days" class="property-input" min="0" max="365" @input="onNodePropertyChange" />
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Skip Days</label>
                <div class="day-skip-grid">
                  <label
                    v-for="(dayName, dayNum) in ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']"
                    :key="dayNum"
                    class="day-skip-item"
                    :class="{ active: Array.isArray(selectedNode.config.skip_days) && selectedNode.config.skip_days.map(Number).includes(dayNum) }"
                  >
                    <input
                      type="checkbox"
                      :value="dayNum"
                      :checked="Array.isArray(selectedNode.config.skip_days) && selectedNode.config.skip_days.map(Number).includes(dayNum)"
                      @change="toggleSkipDay(selectedNode, dayNum, $event)"
                    />
                    <span>{{ dayName }}</span>
                  </label>
                </div>
                <small class="property-hint">Checked days are excluded from the date list</small>
              </div>
              <div class="property-group">
                <label class="property-checkbox">
                  <input type="checkbox" v-model="selectedNode.config.skip_today" @change="onNodePropertyChange" />
                  <span>Skip today</span>
                </label>
              </div>
              <div class="property-group">
                <label class="property-label">Display Format</label>
                <select v-model="selectedNode.config.display_format" class="property-input" @change="onNodePropertyChange">
                  <option value="D, d M Y">Mon, 02 Jun 2025</option>
                  <option value="l, d F Y">Monday, 02 June 2025</option>
                  <option value="d M Y">02 Jun 2025</option>
                  <option value="d/m/Y">02/06/2025</option>
                  <option value="m/d/Y">06/02/2025</option>
                </select>
                <small class="property-hint">How dates appear in the WhatsApp list shown to the customer</small>
              </div>
              <div class="property-group">
                <label class="property-label">Output Format</label>
                <select v-model="selectedNode.config.output_format" class="property-input" @change="onNodePropertyChange">
                  <option value="d/m/Y">DD/MM/YYYY (e.g. 05/03/2026)</option>
                  <option value="m/d/Y">MM/DD/YYYY (e.g. 03/05/2026)</option>
                  <option value="Y-m-d">YYYY-MM-DD (e.g. 2026-03-05)</option>
                  <option value="d-m-Y">DD-MM-YYYY (e.g. 05-03-2026)</option>
                  <option value="d M Y">DD Mon YYYY (e.g. 05 Mar 2026)</option>
                  <option value="j F Y">D Month YYYY (e.g. 5 March 2026)</option>
                </select>
                <small class="property-hint">Format stored in the variable when customer selects a date</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save as Variable</label>
                <input v-model="selectedNode.config.variable_name" class="property-input" placeholder="appointment_date" @input="onNodePropertyChange" />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>
            </div>

            <!-- Time Picker Node Editor -->
            <div v-if="selectedNode.type === 'time_selection'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Prompt</label>
                <VarTextEditor v-model="ensurePrompt(selectedNode).en" placeholder="Please enter a time (e.g. 2:30 PM)" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Time Format</label>
                <select v-model="selectedNode.config.time_format" class="property-input" @change="onNodePropertyChange">
                  <option value="12h">12-hour (e.g. 2:30 PM)</option>
                  <option value="24h">24-hour (e.g. 14:30)</option>
                </select>
                <small class="property-hint">Customer must type a time in this format</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save as Variable</label>
                <input
                  v-model="selectedNode.config.variable_name"
                  class="property-input"
                  placeholder="appointment_time"
                  @input="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-checkbox">
                  <input
                    type="checkbox"
                    v-model="ensureValidation(selectedNode).required"
                    @change="onNodePropertyChange"
                  />
                  <span>Required field</span>
                </label>
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>
            </div>

            <!-- Location Input Node Editor -->
            <div v-if="selectedNode.type === 'location_input'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Prompt</label>
                <VarTextEditor v-model="ensurePrompt(selectedNode).en" placeholder="📍 Please share your location" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Save as Variable</label>
                <input
                  v-model="selectedNode.config.variable_name"
                  class="property-input"
                  placeholder="customer_location"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">
                  e.g. <code>customer_location</code> saves:<br>
                  <code>{{customer_location}}</code> summary · <code>{{raw_customer_location}}</code> full JSON<br>
                  <code>{{customer_latitude}}</code> · <code>{{customer_longitude}}</code><br>
                  <code>{{customer_location_address}}</code> · <code>{{customer_location_name}}</code> · <code>{{customer_location_maps_url}}</code>
                </small>
              </div>
            </div>

            <!-- Store Locator Node Editor -->
            <div v-if="selectedNode.type === 'store_locator'" class="node-editor">
              <!-- User location variable -->
              <div class="property-group">
                <label class="property-label">Customer Location Variable</label>
                <select v-model="selectedNode.config.user_location_variable" class="property-input" @change="onNodePropertyChange">
                  <option v-for="n in flowDefinition.nodes.filter(n => n.type === 'location_input')" :key="n.id" :value="n.config.variable_name || 'customer_location'">
                    {{ n.name || 'Location Input' }} → {{ n.config.variable_name || 'customer_location' }}
                  </option>
                  <option value="">— type manually below —</option>
                </select>
                <input
                  v-if="!flowDefinition.nodes.some(n => n.type === 'location_input')"
                  v-model="selectedNode.config.user_location_variable"
                  class="property-input"
                  placeholder="customer_location"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">Variable name from a Location Input node (e.g. <code>customer_location</code>)</small>
              </div>

              <!-- Store source toggle -->
              <div class="property-group">
                <label class="property-label">Store List Source</label>
                <div class="ai-mode-pills">
                  <button :class="['ai-mode-pill', selectedNode.config.store_source === 'manual' ? 'ai-mode-pill--active' : '']" @click="selectedNode.config.store_source = 'manual'; onNodePropertyChange()">Manual List</button>
                  <button :class="['ai-mode-pill', selectedNode.config.store_source === 'dynamic' ? 'ai-mode-pill--active' : '']" @click="selectedNode.config.store_source = 'dynamic'; onNodePropertyChange()">From Variable (API)</button>
                </div>
              </div>

              <!-- Manual stores list -->
              <template v-if="selectedNode.config.store_source === 'manual'">
                <div class="property-group">
                  <label class="property-label">Stores</label>
                  <div v-for="(store, idx) in (selectedNode.config.stores || [])" :key="idx" class="store-locator-row">
                    <div style="display:flex;align-items:center;gap:6px;margin-bottom:4px">
                      <span style="font-size:11px;font-weight:600;color:var(--text-secondary);flex:1">Store {{ idx + 1 }}</span>
                      <button @click="selectedNode.config.stores.splice(idx,1); onNodePropertyChange()" class="btn-remove-field" title="Remove store">×</button>
                    </div>
                    <input v-model="store.name" class="property-input" placeholder="Store name" @input="onNodePropertyChange" />
                    <input v-model="store.address" class="property-input" placeholder="Address (optional)" style="margin-top:4px" @input="onNodePropertyChange" />
                    <div style="display:flex;gap:6px;margin-top:4px">
                      <input v-model="store.lat" class="property-input" placeholder="Latitude" @input="onNodePropertyChange" />
                      <input v-model="store.lng" class="property-input" placeholder="Longitude" @input="onNodePropertyChange" />
                    </div>
                    <!-- Custom fields -->
                    <div v-for="(cf, fidx) in (store.custom_fields || [])" :key="fidx" style="display:flex;gap:6px;margin-top:4px;align-items:center">
                      <input v-model="cf.key" class="property-input" placeholder="field key (e.g. store_id)" @input="onNodePropertyChange" style="flex:1" />
                      <input v-model="cf.value" class="property-input" placeholder="value" @input="onNodePropertyChange" style="flex:1" />
                      <button @click="store.custom_fields.splice(fidx,1); onNodePropertyChange()" class="btn-remove-field" title="Remove field">×</button>
                    </div>
                    <button @click="if (!store.custom_fields) store.custom_fields = []; store.custom_fields.push({key:'',value:''}); onNodePropertyChange()" class="btn-add-field" style="margin-top:6px;font-size:11px">+ Custom field</button>
                  </div>
                  <button @click="selectedNode.config.stores = [...(selectedNode.config.stores||[]), {name:'',address:'',lat:'',lng:'',custom_fields:[]}]; onNodePropertyChange()" class="btn-add-field">+ Add Store</button>
                </div>
              </template>

              <!-- Dynamic store list from session array -->
              <template v-if="selectedNode.config.store_source === 'dynamic'">
                <div class="property-group">
                  <label class="property-label">Array Variable (from API node)</label>
                  <select v-model="selectedNode.config.stores_array_variable" class="property-input" @change="onNodePropertyChange">
                    <option value="">— select variable —</option>
                    <option v-for="(meta, vname) in getArrayVariables()" :key="vname" :value="vname">{{ vname }}</option>
                  </select>
                </div>
                <div class="property-group">
                  <label class="property-label">Field Mapping</label>
                  <div class="filter-row">
                    <span class="filter-label">Name field</span>
                    <input v-model="selectedNode.config.name_field" class="property-input" placeholder="name" @input="onNodePropertyChange" />
                  </div>
                  <div class="filter-row">
                    <span class="filter-label">Address field</span>
                    <input v-model="selectedNode.config.address_field" class="property-input" placeholder="address" @input="onNodePropertyChange" />
                  </div>
                  <div class="filter-row">
                    <span class="filter-label">Lat field</span>
                    <input v-model="selectedNode.config.lat_field" class="property-input" placeholder="latitude" @input="onNodePropertyChange" />
                  </div>
                  <div class="filter-row">
                    <span class="filter-label">Lng field</span>
                    <input v-model="selectedNode.config.lng_field" class="property-input" placeholder="longitude" @input="onNodePropertyChange" />
                  </div>
                </div>
              </template>

              <!-- Result variable prefix -->
              <div class="property-group">
                <label class="property-label">Result Variable Prefix</label>
                <input v-model="selectedNode.config.result_variable" class="property-input" placeholder="nearest_store" @input="onNodePropertyChange" />
                <small class="property-hint" v-if="selectedNode.config.result_variable" v-html="storeLocatorHintHtml(selectedNode.config.result_variable)"></small>
              </div>

              <!-- Output ports info -->
              <div class="property-group">
                <label class="property-label">Output Ports</label>
                <div class="condition-port-hint">
                  <b>✅ Store found</b> — nearest store variables are set<br>
                  <b>❌ No location / no stores</b> — customer location not captured or store list is empty
                </div>
              </div>
            </div>

            <!-- Image Input Node Editor -->
            <div v-if="selectedNode.type === 'image_input'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Prompt</label>
                <VarTextEditor v-model="selectedNode.config.prompt.en" placeholder="🖼 Please send a photo" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Save as Variable</label>
                <input
                  v-model="selectedNode.config.variable_name"
                  class="property-input"
                  placeholder="customer_image"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">
                  e.g. <code>customer_image</code> saves:<br>
                  <code>{{customer_image}}</code> — the image URL<br>
                  <code>{{customer_image_caption}}</code> — caption sent with the image (if any)
                </small>
              </div>
              <div class="property-group">
                <label class="property-label">Invalid message — when not an image</label>
                <VarTextEditor v-model="selectedNode.config.invalid_message.en" placeholder="⚠️ Please send an image, not text." :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Output Ports</label>
                <div class="condition-port-hint" style="margin-bottom:6px">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity:0.6"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Drag <strong>🖼 Image received</strong> → runs when customer sends a valid image
                </div>
                <div class="condition-port-hint">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity:0.6"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Drag <strong>✗ Not an image</strong> → runs when customer sends text/document/etc.
                </div>
              </div>
            </div>

            <!-- Action Node Editor -->
            <div v-if="selectedNode.type === 'action'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Success Message</label>
                <textarea
                  v-model="selectedNode.config.success_message.en"
                  class="property-textarea"
                  placeholder="Thank you! Your response has been saved."
                  rows="2"
                  @input="onNodePropertyChange"
                ></textarea>
              </div>
            </div>

            <!-- ═══════════════════════════════════════ -->
            <!-- CSAT Rating Node Editor              -->
            <!-- ═══════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'csat_rating'" class="node-editor">

              <!-- Rating question -->
              <div class="property-group">
                <label class="property-label">Rating Question</label>
                <textarea
                  v-model="selectedNode.config.question.en"
                  class="property-textarea"
                  placeholder="How would you rate your experience?"
                  rows="2"
                  @input="onNodePropertyChange"
                ></textarea>
              </div>

              <!-- Scale selector -->
              <div class="property-group">
                <label class="property-label">Rating Scale</label>
                <div class="csat-scale-cards">
                  <button
                    type="button"
                    :class="['csat-scale-card', selectedNode.config.scale === 5 ? 'csat-scale-card--active' : '']"
                    @click="selectedNode.config.scale = 5; onNodePropertyChange()"
                  >
                    <span class="csat-scale-stars">⭐⭐⭐⭐⭐</span>
                    <strong class="csat-scale-label">1 – 5 Stars</strong>
                    <span class="csat-scale-desc">Most common · easy to answer</span>
                  </button>
                  <button
                    type="button"
                    :class="['csat-scale-card', selectedNode.config.scale === 10 ? 'csat-scale-card--active' : '']"
                    @click="selectedNode.config.scale = 10; onNodePropertyChange()"
                  >
                    <span class="csat-scale-stars csat-scale-stars--ten">1 · · · · · · · · 10</span>
                    <strong class="csat-scale-label">1 – 10 Scale</strong>
                    <span class="csat-scale-desc">More granular · NPS-style</span>
                  </button>
                </div>
              </div>

              <!-- Output variable -->
              <div class="property-group">
                <label class="property-label">Save Rating To Variable</label>
                <input
                  type="text"
                  v-model="selectedNode.config.variable_name"
                  class="property-input"
                  placeholder="customer_csat_rating"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">
                  Saved as a number (1 – {{ selectedNode.config.scale }}).
                  Reference it as <code>&#123;&#123; {{ selectedNode.config.variable_name || 'customer_csat_rating' }} &#125;&#125;</code> in later nodes.
                </small>
              </div>

              <!-- CSAT info banner -->
              <div class="csat-info-banner">
                <span class="csat-info-icon">💾</span>
                <div>
                  <strong>Auto-saved to analytics</strong>
                  <p>Ratings are also recorded in your CSAT analytics dashboard, linked to the resolved department and agent.</p>
                </div>
              </div>

            </div>

            <!-- Restart Flow Node Editor -->
            <div v-if="selectedNode.type === 'restart_flow'" class="node-editor">
              <div class="restart-flow-info">
                <span class="restart-flow-icon">🔄</span>
                <div>
                  <strong>Restart Flow</strong>
                  <p>When the customer reaches this node, their current session ends and the conversation restarts from the beginning.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Message before restarting <small>optional</small></label>
                <textarea
                  v-model="selectedNode.config.message.en"
                  class="property-textarea"
                  placeholder="e.g. Let's start over! 🔄"
                  rows="2"
                  @input="onNodePropertyChange"
                ></textarea>
              </div>
            </div>

            <!-- ═══════════════════════════════════════ -->
            <!-- QR Generator Node Editor              -->
            <!-- ═══════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'qr_generator'" class="node-editor node-editor-qr">

              <!-- What to encode -->
              <div class="property-group">
                <label class="property-label">What to encode in the QR?</label>

                <!-- Quick preset buttons -->
                <div class="qr-presets">
                  <button type="button" class="qr-preset-btn" @click="selectedNode.config.qr_data = '{{website_url}}'; onNodePropertyChange()" title="Website URL">🔗 Website</button>
                  <button type="button" class="qr-preset-btn" @click="selectedNode.config.qr_data = 'https://wa.me/{{phone_number}}'; onNodePropertyChange()" title="WhatsApp chat link">💬 WhatsApp</button>
                  <button type="button" class="qr-preset-btn" @click="selectedNode.config.qr_data = 'tel:{{phone_number}}'; onNodePropertyChange()" title="Phone number">📞 Phone</button>
                  <button type="button" class="qr-preset-btn" @click="selectedNode.config.qr_data = ''; onNodePropertyChange()" title="Custom text">✏️ Custom</button>
                </div>

                <VarTextEditor
                  v-model="selectedNode.config.qr_data"
                  placeholder="https://... or any text — use {{variables}}"
                  :rows="3"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>

              <!-- Size -->
              <div class="property-group">
                <label class="property-label">QR Code Size</label>
                <div class="qr-size-pills">
                  <button
                    v-for="s in [{label:'S', value:200},{label:'M', value:300},{label:'L', value:400},{label:'XL', value:500}]"
                    :key="s.value"
                    type="button"
                    class="qr-size-pill"
                    :class="{'qr-size-pill--active': (selectedNode.config.size || 300) === s.value}"
                    @click="selectedNode.config.size = s.value; onNodePropertyChange()"
                  >
                    {{ s.label }}
                    <span class="qr-size-px">{{ s.value }}px</span>
                  </button>
                </div>
              </div>

              <!-- Send to customer toggle -->
              <div class="property-group">
                <label class="qr-send-toggle" @click="selectedNode.config.send_to_customer = !selectedNode.config.send_to_customer; onNodePropertyChange()">
                  <div class="qr-toggle-track" :class="{'qr-toggle-on': selectedNode.config.send_to_customer}">
                    <div class="qr-toggle-thumb"></div>
                  </div>
                  <div class="qr-toggle-text">
                    <span>Send QR image to customer</span>
                    <small>{{ selectedNode.config.send_to_customer ? 'Will automatically send the QR as a WhatsApp image' : 'Only saves the URL — connect an Image node to send it' }}</small>
                  </div>
                </label>

                <!-- Caption — only shown when sending -->
                <div v-if="selectedNode.config.send_to_customer" class="qr-caption-row">
                  <VarTextEditor
                    v-model="selectedNode.config.caption"
                    placeholder="Optional caption below the QR image…"
                    :single-line="true"
                    @focus="activeVarEditor = $event"
                    @update:modelValue="onNodePropertyChange"
                  />
                </div>
              </div>

              <!-- Save URL as -->
              <div class="property-group">
                <label class="property-label">Save image URL as <span class="field-optional">(use in Image nodes or API calls)</span></label>
                <input
                  v-model="selectedNode.config.result_variable"
                  class="property-input"
                  placeholder="qr_code_url"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">The direct image URL is saved here — paste <code>&#123;&#123;{{ selectedNode.config.result_variable || 'qr_code_url' }}&#125;&#125;</code> anywhere.</small>
              </div>
            </div>

            <!-- ═══════════════════════════════════════ -->
            <!-- Calculator Node Editor                -->
            <!-- ═══════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'calculator'" class="node-editor node-editor-calc">

              <!-- Calculation cards -->
              <div class="calc-cards">
                <div
                  v-for="(calc, idx) in (selectedNode.config.calculations || [])"
                  :key="idx"
                  class="calc-card"
                >
                  <!-- Top: equation inputs -->
                  <div class="calc-eq-row">
                    <!-- Operand A -->
                    <input
                      v-model="calc.operand_a"
                      class="calc-operand"
                      :class="{ 'calc-operand--active': activeCalcInput && activeCalcInput.idx === idx && activeCalcInput.field === 'a' }"
                      placeholder="{{var}} or number"
                      @focus="activeCalcInput = { idx, field: 'a' }"
                      @input="onNodePropertyChange"
                    />

                    <!-- Operation selector -->
                    <select
                      v-model="calc.operator"
                      class="calc-op-select"
                      @change="onNodePropertyChange"
                    >
                      <optgroup label="Arithmetic">
                        <option value="+">＋  Add</option>
                        <option value="-">－  Subtract</option>
                        <option value="*">×  Multiply</option>
                        <option value="/">÷  Divide</option>
                        <option value="%">%  Remainder</option>
                      </optgroup>
                      <optgroup label="Rounding">
                        <option value="round">↻  Round  (B = decimals)</option>
                        <option value="floor">⌊⌋  Round Down</option>
                        <option value="ceil">⌈⌉  Round Up</option>
                        <option value="abs">|x|  Absolute value</option>
                      </optgroup>
                      <optgroup label="Compare">
                        <option value="min">↓  Keep smallest</option>
                        <option value="max">↑  Keep largest</option>
                      </optgroup>
                    </select>

                    <!-- Operand B (hidden for floor/ceil/abs) -->
                    <input
                      v-if="calcOpNeedsB(calc.operator)"
                      v-model="calc.operand_b"
                      class="calc-operand"
                      :class="{ 'calc-operand--active': activeCalcInput && activeCalcInput.idx === idx && activeCalcInput.field === 'b' }"
                      :placeholder="calc.operator === 'round' ? 'decimals (0)' : '{{var}} or number'"
                      @focus="activeCalcInput = { idx, field: 'b' }"
                      @input="onNodePropertyChange"
                    />
                  </div>

                  <!-- Bottom: result variable + remove -->
                  <div class="calc-result-row">
                    <span class="calc-save-label">save as</span>
                    <input
                      v-model="calc.result_variable"
                      class="calc-result-input"
                      placeholder="result_variable"
                      @input="onNodePropertyChange"
                    />
                    <button class="calc-remove-btn" type="button" @click="removeCalcRow(selectedNode, idx)" title="Remove">✕</button>
                  </div>
                </div>

                <!-- Empty state -->
                <div v-if="!(selectedNode.config.calculations || []).length" class="calc-empty">
                  Click "+ Add" to create your first calculation
                </div>
              </div>

              <button class="btn-add-calc" type="button" @click="addCalcRow(selectedNode)">
                <span>＋</span> Add Calculation
              </button>

              <!-- Variable picker — click to insert into focused operand -->
              <div v-if="Object.keys(flowVariables).length > 0" class="calc-var-picker">
                <div class="calc-var-picker-label">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V9z"/><polyline points="13 2 13 9 20 9"/></svg>
                  {{ activeCalcInput ? 'Click a variable to insert it' : 'Focus an input above, then click to insert' }}
                </div>
                <div class="calc-var-pills">
                  <span
                    v-for="(varInfo, varName) in flowVariables"
                    :key="varName"
                    class="calc-var-pill"
                    :class="{ 'calc-var-pill--ready': !!activeCalcInput }"
                    :title="varInfo.description"
                    @mousedown.prevent="insertCalcVar(varName)"
                  >{{ varName }}</span>
                </div>
              </div>

              <small class="calc-chain-hint">Rows run top to bottom — you can use a result from row 1 in row 2.</small>
            </div>

            <!-- Delay Node Editor -->
            <div v-if="selectedNode.type === 'delay'" class="node-editor">
              <div class="delay-info">
                <span class="delay-icon">⏱️</span>
                <div>
                  <strong>Delay</strong>
                  <p>Pauses the flow for the set duration before continuing to the next node.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Wait for</label>
                <div class="delay-input-row">
                  <input
                    v-model.number="selectedNode.config.delay_value"
                    type="number"
                    min="1"
                    :max="selectedNode.config.delay_unit === 'seconds' ? 30 : selectedNode.config.delay_unit === 'minutes' ? 10 : 1"
                    class="property-input delay-value-input"
                    @input="onNodePropertyChange"
                  />
                  <select
                    v-model="selectedNode.config.delay_unit"
                    class="property-input delay-unit-select"
                    @change="onNodePropertyChange"
                  >
                    <option value="seconds">Seconds</option>
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                  </select>
                </div>
                <small class="delay-note">
                  Max: 30 seconds · 10 minutes · 1 hour. Long delays may affect response time.
                </small>
              </div>
            </div>

            <!-- Current Date / Time -->
            <div v-if="selectedNode.type === 'current_datetime'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">🕐</span>
                <div>
                  <strong>Current Date / Time</strong>
                  <p>Reads the current date and time using your account's country timezone and stores them as flow variables.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Variable prefix <span class="required-star">*</span></label>
                <input
                  v-model="selectedNode.config.variable_prefix"
                  type="text"
                  class="property-input"
                  placeholder="now"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">
                  Outputs:
                  <code>&#123;&#123;{{ selectedNode.config.variable_prefix || 'now' }}_date&#125;&#125;</code>
                  <code>&#123;&#123;{{ selectedNode.config.variable_prefix || 'now' }}_time&#125;&#125;</code>
                  <code>&#123;&#123;{{ selectedNode.config.variable_prefix || 'now' }}_datetime&#125;&#125;</code>
                  <code>&#123;&#123;{{ selectedNode.config.variable_prefix || 'now' }}_day&#125;&#125;</code>
                  <code>&#123;&#123;{{ selectedNode.config.variable_prefix || 'now' }}_day_number&#125;&#125;</code>
                  <code>&#123;&#123;{{ selectedNode.config.variable_prefix || 'now' }}_hour&#125;&#125;</code>
                  <code>&#123;&#123;{{ selectedNode.config.variable_prefix || 'now' }}_minute&#125;&#125;</code>
                </small>
              </div>
              <div class="property-group">
                <label class="property-label">Day offset</label>
                <div style="display:flex;align-items:center;gap:10px">
                  <input
                    v-model.number="selectedNode.config.offset_days"
                    type="number"
                    class="property-input"
                    style="width:90px"
                    placeholder="0"
                    @input="onNodePropertyChange"
                  />
                  <span style="font-size:12px;color:#6b7280;font-style:italic">{{ offsetDayLabel(selectedNode.config.offset_days) }}</span>
                </div>
                <small class="property-hint">0 = today · 1 = tomorrow · 2 = day after · -1 = yesterday · -2 = day before</small>
              </div>

              <div class="property-group">
                <label class="property-checkbox">
                  <input
                    type="checkbox"
                    v-model="selectedNode.config.skip_weekends"
                    @change="onSkipWeekendsToggle(selectedNode); onNodePropertyChange()"
                  />
                  <span>Skip certain days — advance forward if the result lands on a selected day</span>
                </label>
              </div>

              <div v-if="selectedNode.config.skip_weekends" class="property-group">
                <label class="property-label">Days to skip</label>
                <div class="weekend-days-picker">
                  <label
                    v-for="day in [{n:'Mon',v:1},{n:'Tue',v:2},{n:'Wed',v:3},{n:'Thu',v:4},{n:'Fri',v:5},{n:'Sat',v:6},{n:'Sun',v:0}]"
                    :key="day.v"
                    class="weekend-day-chip"
                    :class="{ active: Array.isArray(selectedNode.config.weekend_days) && selectedNode.config.weekend_days.map(Number).includes(day.v) }"
                  >
                    <input
                      type="checkbox"
                      :value="day.v"
                      :checked="Array.isArray(selectedNode.config.weekend_days) && selectedNode.config.weekend_days.map(Number).includes(day.v)"
                      @change="toggleWeekendDay(selectedNode, day.v, $event)"
                    />
                    {{ day.n }}
                  </label>
                </div>
                <small class="property-hint">If the result lands on a checked day, it keeps advancing by 1 day until it lands on an unchecked day.</small>
              </div>

              <div class="property-group">
                <label class="property-label">Date format</label>
                <select
                  v-model="selectedNode.config.date_format"
                  class="property-input"
                  @change="onNodePropertyChange"
                >
                  <option value="d/m/Y">DD/MM/YYYY (e.g. 05/03/2026)</option>
                  <option value="m/d/Y">MM/DD/YYYY (e.g. 03/05/2026)</option>
                  <option value="Y-m-d">YYYY-MM-DD (e.g. 2026-03-05)</option>
                  <option value="d-m-Y">DD-MM-YYYY (e.g. 05-03-2026)</option>
                  <option value="d M Y">DD Mon YYYY (e.g. 05 Mar 2026)</option>
                  <option value="j F Y">D Month YYYY (e.g. 5 March 2026)</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Time format</label>
                <select
                  v-model="selectedNode.config.time_format"
                  class="property-input"
                  @change="onNodePropertyChange"
                >
                  <option value="H:i">24-hour (e.g. 14:30)</option>
                  <option value="H:i:s">24-hour with seconds (e.g. 14:30:00)</option>
                  <option value="h:i A">12-hour AM/PM (e.g. 02:30 PM)</option>
                  <option value="h:i:s A">12-hour with seconds (e.g. 02:30:00 PM)</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Output preview <span class="field-optional">based on your browser time</span></label>
                <div class="datetime-preview-table">
                  <div v-for="row in datetimeSampleValues(selectedNode)" :key="row.key" class="datetime-preview-row">
                    <code class="datetime-preview-key">&#123;&#123;{{ row.key }}&#125;&#125;</code>
                    <span class="datetime-preview-val">{{ row.val }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Override Session Timeout -->
            <div v-if="selectedNode.type === 'set_session_timeout'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">⏱</span>
                <div>
                  <strong>Override Session Timeout</strong>
                  <p>Changes how long the session stays active from this point in the flow. Overrides the flow-level setting.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">New timeout duration</label>
                <div style="display:flex;gap:8px;align-items:center;" :style="{ opacity: selectedNode.config.never_expire ? 0.4 : 1, pointerEvents: selectedNode.config.never_expire ? 'none' : 'auto' }">
                  <input
                    v-model.number="selectedNode.config.timeout_value"
                    type="number"
                    min="1"
                    class="property-input"
                    style="flex:1;"
                    @input="onNodePropertyChange"
                  />
                  <select
                    v-model="selectedNode.config.timeout_unit"
                    class="property-input"
                    style="flex:1;"
                    @change="onNodePropertyChange"
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                  </select>
                </div>
                <label class="property-checkbox" style="margin-top:6px;">
                  <input
                    type="checkbox"
                    v-model="selectedNode.config.never_expire"
                    @change="onNodePropertyChange"
                  />
                  <span>Never expire — session stays active indefinitely</span>
                </label>
                <small class="property-hint">
                  <span v-if="selectedNode.config.never_expire">Session will never expire after this node.</span>
                  <span v-else>Session will expire <strong>{{ selectedNode.config.timeout_value }} {{ selectedNode.config.timeout_unit }}</strong> after last interaction.</span>
                </small>
              </div>
            </div>

            <!-- Check 24h Window -->
            <div v-if="selectedNode.type === 'check_24h_window'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">⏰</span>
                <div>
                  <strong>Check 24h Window</strong>
                  <p>Checks whether the customer's last inbound message was within the allowed time window. Use this before sending free-form messages — WhatsApp only allows them within 24 hours of the customer's last message.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Hours limit</label>
                <input
                  v-model.number="selectedNode.config.hours_limit"
                  type="number"
                  min="1"
                  max="168"
                  class="property-input"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">Default 24. The ✅ port fires if the customer messaged within this many hours.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save hours-since to variable <span class="field-optional">optional</span></label>
                <input
                  v-model="selectedNode.config.result_variable"
                  type="text"
                  class="property-input"
                  placeholder="e.g. hours_since_last_msg"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">Stores how many hours ago the customer last messaged (decimal). Useful for messages like "You last contacted us X hours ago."</small>
              </div>
            </div>

            <!-- Check Working Hours -->
            <div v-if="selectedNode.type === 'check_working_hours'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">🏢</span>
                <div>
                  <strong>Check Working Hours</strong>
                  <p>Checks if the current time falls within your company's working hours and working days (set in Company Profile). Routes to <strong>✅ Open</strong> if inside, <strong>🔴 Closed</strong> if outside.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Timezone</label>
                <select
                  v-model="selectedNode.config.timezone"
                  class="property-input"
                  @change="onNodePropertyChange"
                >
                  <option value="UTC">UTC</option>
                  <option value="Africa/Cairo">Africa/Cairo (EET, UTC+2/+3)</option>
                  <option value="Africa/Johannesburg">Africa/Johannesburg (SAST, UTC+2)</option>
                  <option value="Africa/Lagos">Africa/Lagos (WAT, UTC+1)</option>
                  <option value="Africa/Nairobi">Africa/Nairobi (EAT, UTC+3)</option>
                  <option value="America/New_York">America/New_York (ET)</option>
                  <option value="America/Chicago">America/Chicago (CT)</option>
                  <option value="America/Denver">America/Denver (MT)</option>
                  <option value="America/Los_Angeles">America/Los_Angeles (PT)</option>
                  <option value="America/Sao_Paulo">America/Sao_Paulo (BRT)</option>
                  <option value="Asia/Dubai">Asia/Dubai (GST, UTC+4)</option>
                  <option value="Asia/Riyadh">Asia/Riyadh (AST, UTC+3)</option>
                  <option value="Asia/Kuwait">Asia/Kuwait (AST, UTC+3)</option>
                  <option value="Asia/Beirut">Asia/Beirut (EET)</option>
                  <option value="Asia/Amman">Asia/Amman (EET)</option>
                  <option value="Asia/Baghdad">Asia/Baghdad (AST, UTC+3)</option>
                  <option value="Asia/Karachi">Asia/Karachi (PKT, UTC+5)</option>
                  <option value="Asia/Kolkata">Asia/Kolkata (IST, UTC+5:30)</option>
                  <option value="Asia/Dhaka">Asia/Dhaka (BST, UTC+6)</option>
                  <option value="Asia/Bangkok">Asia/Bangkok (ICT, UTC+7)</option>
                  <option value="Asia/Singapore">Asia/Singapore (SGT, UTC+8)</option>
                  <option value="Asia/Tokyo">Asia/Tokyo (JST, UTC+9)</option>
                  <option value="Asia/Seoul">Asia/Seoul (KST, UTC+9)</option>
                  <option value="Australia/Sydney">Australia/Sydney (AEST)</option>
                  <option value="Europe/London">Europe/London (GMT/BST)</option>
                  <option value="Europe/Paris">Europe/Paris (CET/CEST)</option>
                  <option value="Europe/Istanbul">Europe/Istanbul (TRT, UTC+3)</option>
                  <option value="Europe/Moscow">Europe/Moscow (MSK, UTC+3)</option>
                </select>
                <small class="property-hint">The timezone used to check the current time against your working hours. Make sure it matches your company's local time.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save current time to variable <span class="field-optional">optional</span></label>
                <input
                  v-model="selectedNode.config.result_variable"
                  type="text"
                  class="property-input"
                  placeholder="e.g. current_time"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">Stores the current day and time as seen by this node, e.g. <code>Wednesday 14:35</code>. Useful for debugging or sending to the customer.</small>
              </div>
              <div class="working-hours-setup-hint">
                <span>⚙️ Haven't set your working hours yet?</span>
                <router-link to="/company-profile" class="btn-goto-profile">Go to Company Profile →</router-link>
              </div>
            </div>

            <!-- Send WhatsApp Template -->
            <div v-if="selectedNode.type === 'send_template'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">📋</span>
                <div>
                  <strong>Send WhatsApp Template</strong>
                  <p>Sends an approved WhatsApp message template to the customer. You can use flow variables as parameter values.</p>
                </div>
              </div>

              <!-- Template selector -->
              <div class="property-group">
                <label class="property-label">Template <span class="required-star">*</span></label>
                <select
                  v-model="selectedNode.config.template_name"
                  class="property-input"
                  @change="onSendTemplateSelected(selectedNode)"
                >
                  <option value="">— select an approved template —</option>
                  <option
                    v-for="tpl in approvedTemplates"
                    :key="tpl.id"
                    :value="tpl.name"
                  >
                    {{ tpl.name }} · {{ tpl.language }} · {{ tpl.category }}
                  </option>
                </select>
                <small v-if="approvedTemplates.length === 0" class="property-hint" style="color:#e67e22">
                  No approved templates found. Make sure your WhatsApp account is connected and you have at least one APPROVED template.
                </small>
              </div>

              <!-- Template preview (header, body, footer) -->
              <div v-if="selectedNode.config.template_name && selectedNode.config.template_components.length" class="template-node-preview">
                <div
                  v-for="comp in selectedNode.config.template_components"
                  :key="comp.type"
                  class="template-comp-row"
                >
                  <span class="template-comp-label">{{ comp.type }}</span>
                  <span v-if="comp.type === 'HEADER' && comp.format !== 'TEXT'" class="template-comp-media">
                    {{ comp.format }} media
                  </span>
                  <span v-else-if="comp.type === 'BUTTONS' && comp.buttons">
                    {{ comp.buttons.map(b => b.text).join(' · ') }}
                  </span>
                  <span v-else class="template-comp-text">{{ (comp.text || '').substring(0, 80) }}</span>
                </div>
              </div>

              <!-- Header: TEXT with variables -->
              <template v-if="selectedNode.config.header_type === 'TEXT' && selectedNode.config.header_params.length > 0">
                <div class="property-group-title">Header Variables</div>
                <div
                  v-for="(param, idx) in selectedNode.config.header_params"
                  :key="'hp' + idx"
                  class="property-group"
                >
                  <label class="property-label">Header &#123;&#123;{{ idx + 1 }}&#125;&#125;</label>
                  <input
                    v-model="selectedNode.config.header_params[idx].value"
                    type="text"
                    class="property-input"
                    placeholder="static value or {{variable_name}}"
                    @input="onNodePropertyChange"
                  />
                </div>
              </template>

              <!-- Header: media (IMAGE / VIDEO / DOCUMENT) -->
              <template v-if="['IMAGE','VIDEO','DOCUMENT'].includes(selectedNode.config.header_type)">
                <div class="property-group-title">Header Media</div>
                <div class="property-group">
                  <label class="property-label">{{ selectedNode.config.header_type }} URL <span class="required-star">*</span></label>
                  <input
                    v-model="selectedNode.config.header_media_url"
                    type="text"
                    class="property-input"
                    placeholder="https://... or {{variable_with_url}}"
                    @input="onNodePropertyChange"
                  />
                  <small class="property-hint">Publicly accessible URL. Supports <code>{{variable}}</code> substitution.</small>
                </div>
              </template>

              <!-- Body variables -->
              <template v-if="selectedNode.config.body_params.length > 0">
                <div class="property-group-title">Body Variables</div>
                <div
                  v-for="(param, idx) in selectedNode.config.body_params"
                  :key="'bp' + idx"
                  class="property-group"
                >
                  <label class="property-label">Body &#123;&#123;{{ idx + 1 }}&#125;&#125;</label>
                  <input
                    v-model="selectedNode.config.body_params[idx].value"
                    type="text"
                    class="property-input"
                    placeholder="static value or {{variable_name}}"
                    @input="onNodePropertyChange"
                  />
                </div>
              </template>

              <!-- Button URL variables -->
              <template v-if="selectedNode.config.button_params.length > 0">
                <div class="property-group-title">Button URL Variables</div>
                <div
                  v-for="(btnParam, idx) in selectedNode.config.button_params"
                  :key="'btp' + idx"
                  class="property-group"
                >
                  <label class="property-label">Button "{{ btnParam.button_text }}" &#123;&#123;1&#125;&#125;</label>
                  <input
                    v-model="selectedNode.config.button_params[idx].value"
                    type="text"
                    class="property-input"
                    placeholder="static value or {{variable_name}}"
                    @input="onNodePropertyChange"
                  />
                </div>
              </template>

              <!-- No parameters needed -->
              <div
                v-if="selectedNode.config.template_name && selectedNode.config.template_components.length && selectedNode.config.header_params.length === 0 && selectedNode.config.body_params.length === 0 && selectedNode.config.button_params.length === 0 && !['IMAGE','VIDEO','DOCUMENT'].includes(selectedNode.config.header_type)"
                class="property-hint-box"
              >
                This template has no variable parameters — it will be sent as-is.
              </div>
            </div>

            <!-- CRM: Add Note -->
            <div v-if="selectedNode.type === 'crm_add_note'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">📝</span>
                <div>
                  <strong>Add Customer Note</strong>
                  <p>Saves a note to the customer's CRM profile. Supports <code>{{variable}}</code> substitution.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Note text <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.note"
                  placeholder="e.g. Customer requested callback. Product: {{product_name}}"
                  :rows="3"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
                <small class="property-hint">You can embed any flow variable using <code>{{variable_name}}</code></small>
              </div>
            </div>

            <!-- CRM: Update Category -->
            <div v-if="selectedNode.type === 'crm_update_category'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">🏷️</span>
                <div>
                  <strong>Update Customer Category</strong>
                  <p>Moves the customer to a different category in the CRM.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Category (fixed)</label>
                <select
                  v-model="selectedNode.config.category_id_static"
                  class="property-input"
                  :disabled="!!selectedNode.config.category_id_var"
                  @change="onNodePropertyChange"
                >
                  <option value="">— select a category —</option>
                  <option v-for="cat in customerCategories" :key="cat.id" :value="String(cat.id)">
                    {{ cat.name }}
                  </option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">— or — Category ID from variable</label>
                <select
                  v-model="selectedNode.config.category_id_var"
                  class="property-input"
                  @change="onNodePropertyChange"
                >
                  <option value="">— pick from flow variables —</option>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">
                        {{ varName }}
                      </option>
                    </optgroup>
                  </template>
                </select>
                <small class="property-hint">Variable takes priority over fixed category if both are set</small>
              </div>
            </div>

            <!-- CRM: Update Customer -->
            <div v-if="selectedNode.type === 'crm_update_customer'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">👤</span>
                <div>
                  <strong>Update Customer</strong>
                  <p>Updates customer fields. Leave a field blank to keep its current value unchanged.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Full Name <small>optional</small></label>
                <VarTextEditor
                  v-model="selectedNode.config.full_name"
                  placeholder="e.g. {{customer_name}}"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">Address <small>optional</small></label>
                <VarTextEditor
                  v-model="selectedNode.config.address"
                  placeholder="e.g. {{customer_address}}"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">Mark as VIP <small>optional</small></label>
                <select v-model="selectedNode.config.vip" class="property-input" @change="onNodePropertyChange">
                  <option value="">— no change —</option>
                  <option value="1">Yes — mark as VIP</option>
                  <option value="0">No — remove VIP</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Case Resolved <small>optional</small></label>
                <select v-model="selectedNode.config.case_resolved" class="property-input" @change="onNodePropertyChange">
                  <option value="">— no change —</option>
                  <option value="1">Yes — mark as resolved</option>
                  <option value="0">No — mark as unresolved</option>
                </select>
              </div>
            </div>

            <!-- CRM: Assign Tag -->
            <div v-if="selectedNode.type === 'assign_tag'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">🏷️</span>
                <div>
                  <strong>Assign Tag</strong>
                  <p>Assigns the selected tag to the current customer.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Tag to Assign</label>
                <select v-model="selectedNode.config.tag_id" class="property-input" @change="onNodePropertyChange">
                  <option value="">— Select a tag —</option>
                  <option v-for="tag in availableTags" :key="tag.id" :value="tag.id">{{ tag.name }}</option>
                </select>
                <p v-if="!availableTags.length" class="property-hint">No tags found. Create tags in the Customer Tags page first.</p>
              </div>
            </div>

            <!-- CRM: Remove Tag -->
            <div v-if="selectedNode.type === 'remove_tag'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">🗑️</span>
                <div>
                  <strong>Remove Tag</strong>
                  <p>Removes the selected tag from the current customer.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Tag to Remove</label>
                <select v-model="selectedNode.config.tag_id" class="property-input" @change="onNodePropertyChange">
                  <option value="">— Select a tag —</option>
                  <option v-for="tag in availableTags" :key="tag.id" :value="tag.id">{{ tag.name }}</option>
                </select>
                <p v-if="!availableTags.length" class="property-hint">No tags found. Create tags in the Customer Tags page first.</p>
              </div>
            </div>

            <!-- Logic: Check Tag -->
            <div v-if="selectedNode.type === 'check_tag'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">🔍</span>
                <div>
                  <strong>Check Tag</strong>
                  <p>Routes the customer to the port matching their assigned tag. If they have no matching tag, the <strong>No Tag</strong> port is used.</p>
                </div>
              </div>
              <div v-if="availableTags.length" class="property-group">
                <label class="property-label">Output Ports</label>
                <div v-for="tag in availableTags" :key="tag.id" class="property-hint" style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
                  <span :style="{ background: tag.color, width: '10px', height: '10px', borderRadius: '50%', display: 'inline-block', flexShrink: 0 }"></span>
                  <span>{{ tag.name }}</span>
                </div>
                <div class="property-hint" style="margin-top:8px;">+ <strong>No Tag</strong> fallback port</div>
              </div>
              <p v-else class="property-hint">No tags found. Create tags in the Customer Tags page first — each tag will become an output port.</p>
            </div>

            <!-- CRM: Notify Team Member -->
            <div v-if="selectedNode.type === 'notify_team'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">🔔</span>
                <div>
                  <strong>Notify Team Member</strong>
                  <p>Sends an internal message (+ push notification) to one or all agents. Supports <code>{{variable}}</code> in the message.</p>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Send to</label>
                <select v-model="selectedNode.config.send_to" class="property-input" @change="onNodePropertyChange">
                  <option value="specific_agent">Specific agent</option>
                  <option value="all_team">All team members</option>
                  <option value="random_active">Random active agent</option>
                </select>
              </div>

              <!-- Specific agent options -->
              <template v-if="selectedNode.config.send_to === 'specific_agent'">
                <div class="property-group">
                  <label class="property-label">Agent (fixed)</label>
                  <select
                    v-model="selectedNode.config.agent_id_static"
                    class="property-input"
                    :disabled="!!selectedNode.config.agent_id_var"
                    @change="onNodePropertyChange"
                  >
                    <option value="">— select an agent —</option>
                    <option v-for="agent in teamAgents" :key="agent.id" :value="String(agent.id)">
                      {{ agent.name }}{{ agent.active ? '' : ' (inactive)' }}
                    </option>
                  </select>
                </div>
                <div class="property-group">
                  <label class="property-label">— or — Agent ID from variable</label>
                  <select
                    v-model="selectedNode.config.agent_id_var"
                    class="property-input"
                    @change="onNodePropertyChange"
                  >
                    <option value="">— pick from flow variables —</option>
                    <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                      <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                        <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">
                          {{ varName }}
                        </option>
                      </optgroup>
                    </template>
                  </select>
                  <small class="property-hint">Variable takes priority over fixed agent if both are set</small>
                </div>
              </template>

              <div v-if="selectedNode.config.send_to === 'random_active'" class="property-hint-box">
                A random agent with <strong>Active</strong> status will be picked at runtime. The selected agent's ID and name are saved as <code>notified_agent_id</code> and <code>notified_agent_name</code>.
              </div>

              <div class="property-group">
                <label class="property-label">Message <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.message"
                  placeholder="e.g. 📩 New lead: {{customer_name}} — {{phone_number}}"
                  :rows="3"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span v-for="(varInfo, varName) in flowVariables" :key="varName"
                  class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)">{{ varName }}</span>
              </div>
            </div>

            <!-- Todo: Create Todo -->
            <div v-if="selectedNode.type === 'todo_create'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">✅</span>
                <div>
                  <strong>Create Todo</strong>
                  <p>Creates a task automatically linked to the current customer. Supports <code>{{variable}}</code> in title and description.</p>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Title <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.title"
                  placeholder="e.g. Follow up with {{customer_name}}"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span v-for="(varInfo, varName) in flowVariables" :key="varName"
                  class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)">{{ varName }}</span>
              </div>

              <div class="property-group">
                <label class="property-label">Description <small>optional</small></label>
                <VarTextEditor
                  v-model="selectedNode.config.description"
                  placeholder="e.g. Customer requested: {{product_name}}"
                  :rows="2"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span v-for="(varInfo, varName) in flowVariables" :key="varName"
                  class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)">{{ varName }}</span>
              </div>

              <div class="property-group">
                <label class="property-label">Priority</label>
                <select v-model="selectedNode.config.priority" class="property-input" @change="onNodePropertyChange">
                  <option value="low">🟢 Low</option>
                  <option value="medium">🟡 Medium</option>
                  <option value="high">🟠 High</option>
                  <option value="urgent">🔴 Urgent</option>
                </select>
              </div>

              <div class="property-group">
                <label class="property-label">Assign to Agent <small>optional</small></label>
                <select v-model="selectedNode.config.agent_mode" class="property-input" @change="onNodePropertyChange">
                  <option value="specific">Specific agent (or none)</option>
                  <option value="random_active">🎲 Random active agent</option>
                </select>
              </div>

              <template v-if="selectedNode.config.agent_mode !== 'random_active'">
                <div class="property-group">
                  <select
                    v-model="selectedNode.config.agent_id_static"
                    class="property-input"
                    :disabled="!!selectedNode.config.agent_id_var"
                    @change="onNodePropertyChange"
                  >
                    <option value="">— no agent —</option>
                    <option v-for="agent in teamAgents" :key="agent.id" :value="String(agent.id)">
                      {{ agent.name }}{{ agent.active ? '' : ' (inactive)' }}
                    </option>
                  </select>
                </div>
                <div class="property-group">
                  <label class="property-label">— or — Agent ID from variable</label>
                  <select
                    v-model="selectedNode.config.agent_id_var"
                    class="property-input"
                    @change="onNodePropertyChange"
                  >
                    <option value="">— pick from flow variables —</option>
                    <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                      <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                        <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                      </optgroup>
                    </template>
                  </select>
                </div>
              </template>

              <div v-else class="property-hint-box">
                A random agent with <strong>Active</strong> status will be assigned. If no active agents are found, any available agent will be assigned instead.
              </div>

              <div class="property-group">
                <label class="property-label">Deadline <small>optional</small></label>
                <select v-model="selectedNode.config.deadline_mode" class="property-input" @change="onNodePropertyChange">
                  <option value="none">— no deadline —</option>
                  <option value="days_from_now">📅 X days from now</option>
                  <option value="variable">📋 From variable</option>
                </select>
              </div>

              <div v-if="selectedNode.config.deadline_mode === 'days_from_now'" class="property-group">
                <label class="property-label">Days from now</label>
                <div class="delay-input-row">
                  <input
                    v-model.number="selectedNode.config.deadline_days"
                    type="number"
                    min="1"
                    max="365"
                    class="property-input delay-value-input"
                    @input="onNodePropertyChange"
                  />
                  <span class="deadline-days-label">days from now</span>
                </div>
                <small class="property-hint">Deadline will be set to today + {{ selectedNode.config.deadline_days || 1 }} day(s)</small>
              </div>

              <div v-if="selectedNode.config.deadline_mode === 'variable'" class="property-group">
                <label class="property-label">Variable</label>
                <select v-model="selectedNode.config.deadline_var" class="property-input" @change="onNodePropertyChange">
                  <option value="">— pick variable —</option>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                    </optgroup>
                  </template>
                </select>
                <small class="property-hint">Variable should contain a date value (e.g. from a Date Picker node)</small>
              </div>

              <div class="property-group">
                <label class="property-label">Save Todo ID as</label>
                <input
                  v-model="selectedNode.config.result_variable"
                  type="text"
                  class="property-input"
                  placeholder="todo_id"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">Use this variable in an "Update Todo Status" node later</small>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════════════ -->
            <!-- Appointments Nodes                                   -->
            <!-- ═══════════════════════════════════════════════════ -->

            <!-- Get My Services -->
            <div v-if="selectedNode.type === 'get_services'" class="node-editor">
              <div class="appt-node-banner appt-banner-services">
                <span>🛎️</span>
                <div>
                  <strong>Get My Services</strong>
                  <p>Sends a list of your services (from Products &amp; Services) and waits for the customer to pick one. Use the result as the service in <strong>Book Appointment</strong>. Automatically paginates if you have more than 8 services.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Message <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.message"
                  placeholder="Please choose a service:"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">List button label</label>
                <input v-model="selectedNode.config.button_text" type="text" class="property-input" placeholder="View Services" @input="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Save selected service as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="service" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable) }}</code> <span>Service ID → feed into Book Appointment's service field</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_name') }}</code> <span>Service name — e.g. <em>Haircut</em></span></div>
              </div>
              <div class="branch-info-list">
                <div class="branch-item branch-found">✅ Service selected</div>
                <div class="branch-item branch-none">❌ No services found in your Products &amp; Services</div>
              </div>
            </div>

            <!-- Get Departments -->
            <div v-if="selectedNode.type === 'get_departments'" class="node-editor">
              <div class="appt-node-banner" style="background:linear-gradient(135deg,#1e3a5f,#2563eb);color:#fff">
                <span>🏢</span>
                <div>
                  <strong>Get Departments</strong>
                  <p>Sends a list of your departments and waits for the customer to pick one. Useful before <strong>Assign to Department</strong>.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Message <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.message"
                  placeholder="Please choose a department:"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">List button label</label>
                <input v-model="selectedNode.config.button_text" type="text" class="property-input" placeholder="View Departments" @input="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Save selected department as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="department" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable) }}</code> <span>Department ID → feed into Assign to Department</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_name') }}</code> <span>Department name — e.g. <em>Sales</em></span></div>
              </div>
              <div class="branch-info-list">
                <div class="branch-item branch-found">✅ Department selected</div>
                <div class="branch-item branch-none">❌ No departments found</div>
              </div>
            </div>

            <!-- Check Available Dates -->
            <div v-if="selectedNode.type === 'check_available_dates'" class="node-editor">
              <div class="appt-node-banner appt-banner-dates">
                <span>📅</span>
                <div>
                  <strong>Check Available Dates</strong>
                  <p>Scans your schedule for the next N days, finds dates with open slots, and sends them to the customer as a WhatsApp list. The customer picks a date — then feed it into <strong>Check Available Slots</strong>.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Message <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.message"
                  placeholder="Please choose a date for your appointment:"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">List button label</label>
                <input v-model="selectedNode.config.button_text" type="text" class="property-input" placeholder="View Dates" @input="onNodePropertyChange" />
                <small class="property-hint">The text on the button the customer taps to open the date list</small>
              </div>
              <div class="property-group">
                <label class="property-label">Look ahead (days)</label>
                <div class="delay-input-row">
                  <input v-model.number="selectedNode.config.days_ahead" type="number" min="1" max="60" class="property-input" style="width:80px" @input="onNodePropertyChange" />
                  <span class="deadline-days-label">days from today</span>
                </div>
                <small class="property-hint">How many days ahead to search for available dates</small>
              </div>
              <div class="property-group">
                <label class="property-label">Max dates to show</label>
                <input v-model.number="selectedNode.config.max_dates" type="number" min="1" max="10" class="property-input" style="width:80px" @input="onNodePropertyChange" />
                <small class="property-hint">Maximum number of dates in the list (1–10, WhatsApp limit)</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save selected date as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="selected_date" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable) }}</code> <span>Raw date — e.g. <em>2026-03-10</em> → feed into Check Available Slots</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_formatted') }}</code> <span>Human readable — e.g. <em>Monday, Mar 10</em></span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_day') }}</code> <span>Day name — e.g. <em>Monday</em></span></div>
              </div>
              <div class="branch-info-list">
                <div class="branch-item branch-found">✅ Date selected — customer chose a date</div>
                <div class="branch-item branch-none">❌ No available dates in the next {{ selectedNode.config.days_ahead || 14 }} days</div>
              </div>
            </div>

            <!-- Check Available Slots -->
            <div v-if="selectedNode.type === 'check_available_slots'" class="node-editor">
              <div class="appt-node-banner appt-banner-slots">
                <span>🗓️</span>
                <div>
                  <strong>Check Available Slots</strong>
                  <p>Fetches open time slots for the chosen date and sends them as a WhatsApp list. The customer picks a slot — feed the result directly into <strong>Book Appointment</strong>.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">
                  Date variable <span class="required-star">*</span>
                  <span v-if="upstreamDateNode && selectedNode.config.date_var === upstreamDateNode.config.result_variable" class="auto-connect-badge">
                    ⚡ Auto-connected from "{{ upstreamDateNode.name || 'Check Available Dates' }}"
                  </span>
                </label>
                <div v-if="upstreamDateNode && selectedNode.config.date_var !== upstreamDateNode.config.result_variable" class="auto-connect-hint">
                  <span>📅 "{{ upstreamDateNode.name || 'Check Available Dates' }}" detected upstream</span>
                  <button class="auto-connect-btn" @click.prevent="selectedNode.config.date_var = upstreamDateNode.config.result_variable; onNodePropertyChange()">
                    Auto-connect
                  </button>
                </div>
                <select v-model="selectedNode.config.date_var" class="property-input" @change="onNodePropertyChange">
                  <option value="">— pick a date variable —</option>
                  <template v-if="upstreamDateNode">
                    <optgroup label="⚡ Upstream Date Node">
                      <option :value="upstreamDateNode.config.result_variable">
                        {{ upstreamDateNode.config.result_variable }} — from "{{ upstreamDateNode.name || 'Check Available Dates' }}"
                      </option>
                    </optgroup>
                  </template>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                    </optgroup>
                  </template>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Message <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.message"
                  placeholder="Please choose a time slot:"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">List button label</label>
                <input v-model="selectedNode.config.button_text" type="text" class="property-input" placeholder="View Slots" @input="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Save selected slot as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="slots" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row appt-var-highlight"><code>{{ varTag(selectedNode.config.result_variable) }}</code> <span>Selected time slot — e.g. <em>9:00 AM</em> → feed into Book Appointment</span></div>
              </div>
              <div class="branch-info-list">
                <div class="branch-item branch-found">✅ Slot selected — customer chose a time</div>
                <div class="branch-item branch-none">❌ No slots available for this date</div>
              </div>
            </div>

            <!-- Book Appointment -->
            <div v-if="selectedNode.type === 'book_appointment'" class="node-editor">
              <div class="appt-node-banner appt-banner-book">
                <span>✅</span>
                <div>
                  <strong>Book Appointment</strong>
                  <p>Books an appointment for the current customer using a date and time from flow variables.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">
                  Date variable <span class="required-star">*</span>
                  <span v-if="upstreamDatesNodeForBooking && selectedNode.config.date_var === upstreamDatesNodeForBooking.config.result_variable" class="auto-connect-badge">
                    ⚡ Auto-connected from "{{ upstreamDatesNodeForBooking.name || 'Check Available Dates' }}"
                  </span>
                </label>
                <div v-if="upstreamDatesNodeForBooking && selectedNode.config.date_var !== upstreamDatesNodeForBooking.config.result_variable" class="auto-connect-hint">
                  <span>📅 "{{ upstreamDatesNodeForBooking.name || 'Check Available Dates' }}" detected upstream</span>
                  <button class="auto-connect-btn" @click.prevent="selectedNode.config.date_var = upstreamDatesNodeForBooking.config.result_variable; onNodePropertyChange()">
                    Auto-connect
                  </button>
                </div>
                <select v-model="selectedNode.config.date_var" class="property-input" @change="onNodePropertyChange">
                  <option value="">— pick a date variable —</option>
                  <template v-if="upstreamDatesNodeForBooking">
                    <optgroup label="⚡ Upstream Dates Node">
                      <option :value="upstreamDatesNodeForBooking.config.result_variable">
                        {{ upstreamDatesNodeForBooking.config.result_variable }} — from "{{ upstreamDatesNodeForBooking.name || 'Check Available Dates' }}"
                      </option>
                    </optgroup>
                  </template>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                    </optgroup>
                  </template>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">
                  Time variable <span class="required-star">*</span>
                  <span v-if="upstreamSlotsNode && selectedNode.config.time_var === upstreamSlotsNode.config.result_variable" class="auto-connect-badge">
                    ⚡ Auto-connected from "{{ upstreamSlotsNode.name || 'Check Available Slots' }}"
                  </span>
                </label>
                <div v-if="upstreamSlotsNode && selectedNode.config.time_var !== upstreamSlotsNode.config.result_variable" class="auto-connect-hint">
                  <span>🗓️ "{{ upstreamSlotsNode.name || 'Check Available Slots' }}" detected upstream</span>
                  <button class="auto-connect-btn" @click.prevent="selectedNode.config.time_var = upstreamSlotsNode.config.result_variable; onNodePropertyChange()">
                    Auto-connect
                  </button>
                </div>
                <select v-model="selectedNode.config.time_var" class="property-input" @change="onNodePropertyChange">
                  <option value="">— pick a time variable —</option>
                  <template v-if="upstreamSlotsNode">
                    <optgroup label="⚡ Upstream Slots Node">
                      <option :value="upstreamSlotsNode.config.result_variable">
                        {{ upstreamSlotsNode.config.result_variable }} — from "{{ upstreamSlotsNode.name || 'Check Available Slots' }}"
                      </option>
                    </optgroup>
                  </template>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                    </optgroup>
                  </template>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">
                  Service <small>optional</small>
                  <span v-if="upstreamServicesNode && selectedNode.config.service_id_var === upstreamServicesNode.config.result_variable" class="auto-connect-badge">
                    ⚡ Auto-connected from "{{ upstreamServicesNode.name || 'Get My Services' }}"
                  </span>
                </label>
                <div v-if="upstreamServicesNode && selectedNode.config.service_id_var !== upstreamServicesNode.config.result_variable" class="auto-connect-hint">
                  <span>🛎️ "{{ upstreamServicesNode.name || 'Get My Services' }}" detected upstream</span>
                  <button class="auto-connect-btn" @click.prevent="selectedNode.config.service_id_var = upstreamServicesNode.config.result_variable; selectedNode.config.service_id_static = ''; onNodePropertyChange()">
                    Auto-connect
                  </button>
                </div>
                <div v-if="!upstreamServicesNode && !selectedNode.config.service_id_var" class="property-hint-box">
                  💡 Want the customer to pick a service? Add a <strong>Get My Services</strong> node before this one — it will auto-connect here.
                </div>
                <!-- Fixed service (shown when no variable is set) -->
                <select v-if="!selectedNode.config.service_id_var" v-model="selectedNode.config.service_id_static" class="property-input" @change="onNodePropertyChange">
                  <option value="">— no specific service —</option>
                  <option v-for="svc in bookingServices" :key="svc.id" :value="String(svc.id)">{{ svc.name }}</option>
                </select>
                <!-- From variable (shown when service_id_var is set, or when upstream node exists) -->
                <div v-if="selectedNode.config.service_id_var || upstreamServicesNode" class="property-group" style="margin-top:6px">
                  <label class="property-label" style="font-size:11px;color:#64748b">From variable</label>
                  <select v-model="selectedNode.config.service_id_var" class="property-input" @change="onNodePropertyChange">
                    <option value="">— no variable (use fixed above) —</option>
                    <template v-if="upstreamServicesNode">
                      <optgroup label="⚡ Upstream Services Node">
                        <option :value="upstreamServicesNode.config.result_variable">
                          {{ upstreamServicesNode.config.result_variable }} — from "{{ upstreamServicesNode.name || 'Get My Services' }}"
                        </option>
                      </optgroup>
                    </template>
                    <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                      <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                        <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                      </optgroup>
                    </template>
                  </select>
                </div>
                <!-- Show variable picker toggle when neither is set and no upstream -->
                <div v-if="!selectedNode.config.service_id_var && !upstreamServicesNode" style="margin-top:4px">
                  <button class="auto-connect-btn" style="background:#64748b" @click.prevent="selectedNode.config.service_id_var = ' '; onNodePropertyChange()">
                    + Use from variable instead
                  </button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Save results as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="appointment" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_id') }}</code> <span>Appointment ID</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_date') }}</code> <span>Formatted date</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_time') }}</code> <span>Formatted time</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_payment_status') }}</code> <span>pending / verified</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_summary') }}</code> <span>Ready-to-send confirmation message</span></div>
              </div>
              <div class="branch-info-list">
                <div class="branch-item branch-found">✅ Booked successfully</div>
                <div class="branch-item branch-none">❌ Failed (slot taken or unavailable)</div>
              </div>
            </div>

            <!-- Cancel Appointment -->
            <div v-if="selectedNode.type === 'cancel_appointment'" class="node-editor">
              <div class="appt-node-banner appt-banner-cancel">
                <span>❌</span>
                <div>
                  <strong>Cancel Appointment</strong>
                  <p>Lists the customer's upcoming appointments as a WhatsApp list. They pick one and it gets cancelled.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Prompt message</label>
                <input v-model="selectedNode.config.message" type="text" class="property-input" placeholder="Which appointment would you like to cancel?" @input="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">List button text</label>
                <input v-model="selectedNode.config.button_text" type="text" class="property-input" placeholder="My Appointments" @input="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">No appointments message</label>
                <input v-model="selectedNode.config.message_none" type="text" class="property-input" placeholder="You have no upcoming appointments to cancel." @input="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Cancellation confirmation message <small>optional</small></label>
                <input v-model="selectedNode.config.message_cancelled" type="text" class="property-input" placeholder="Default: ❌ Appointment cancelled. 📅 date ⏰ time" @input="onNodePropertyChange" />
                <small class="property-hint">Leave empty for the default summary. You can use {{ varTag(selectedNode.config.result_variable || 'cancelled') }}_date, {{ varTag(selectedNode.config.result_variable || 'cancelled') }}_time, {{ varTag(selectedNode.config.result_variable || 'cancelled') }}_summary</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save results as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="cancelled" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_date') }}</code> <span>The cancelled appointment's date</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_time') }}</code> <span>The cancelled appointment's time</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_summary') }}</code> <span>Ready-to-send cancellation confirmation</span></div>
              </div>
              <div class="branch-info-list">
                <div class="branch-item branch-found">✅ Cancelled successfully</div>
                <div class="branch-item branch-none">❌ No upcoming appointments found</div>
              </div>
            </div>

            <!-- Get Customer Appointments -->
            <div v-if="selectedNode.type === 'get_customer_appointments'" class="node-editor">
              <div class="appt-node-banner appt-banner-list">
                <span>📋</span>
                <div>
                  <strong>Get My Appointments</strong>
                  <p>Sends the customer a text message listing their upcoming appointments. No input required from the customer.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Max appointments to show</label>
                <input v-model.number="selectedNode.config.max_shown" type="number" min="1" max="10" class="property-input" @input="onNodePropertyChange" />
                <small class="property-hint">How many appointments to list (1–10)</small>
              </div>
              <div class="property-group">
                <label class="property-label">No appointments message</label>
                <input v-model="selectedNode.config.message_none" type="text" class="property-input" placeholder="You have no upcoming appointments." @input="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Save results as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="my_appointments" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_count') }}</code> <span>Total upcoming appointments</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_next_date') }}</code> <span>Next appointment's date</span></div>
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_next_time') }}</code> <span>Next appointment's time</span></div>
              </div>
            </div>

            <!-- Submit Rating -->
            <div v-if="selectedNode.type === 'submit_rating'" class="node-editor">
              <div class="appt-node-banner appt-banner-rating">
                <span>⭐</span>
                <div>
                  <strong>Submit Rating</strong>
                  <p>Saves a star rating for the customer's most recently completed appointment.</p>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Rating variable <span class="required-star">*</span></label>
                <select v-model="selectedNode.config.rating_var" class="property-input" @change="onNodePropertyChange">
                  <option value="">— pick a variable (should be 1–5) —</option>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                    </optgroup>
                  </template>
                </select>
                <small class="property-hint">Collect this via a button node with options 1, 2, 3, 4, 5</small>
              </div>
              <div class="property-group">
                <label class="property-label">Feedback variable <small>optional</small></label>
                <select v-model="selectedNode.config.feedback_var" class="property-input" @change="onNodePropertyChange">
                  <option value="">— no feedback —</option>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">{{ varName }}</option>
                    </optgroup>
                  </template>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Save result as</label>
                <input v-model="selectedNode.config.result_variable" type="text" class="property-input" placeholder="rating" @input="onNodePropertyChange" />
              </div>
              <div class="appt-vars-preview" v-if="selectedNode.config.result_variable">
                <div class="appt-var-row"><code>{{ varTag(selectedNode.config.result_variable + '_text') }}</code> <span>Poor / Fair / Good / Very Good / Excellent</span></div>
              </div>
              <div class="branch-info-list">
                <div class="branch-item branch-found">✅ Rating saved</div>
                <div class="branch-item branch-none">❌ No completed appointment to rate</div>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════════════ -->
            <!-- Leads: Save Lead Field Node Editor                  -->
            <!-- ═══════════════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'save_lead_field'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">💾</span>
                <div>
                  <strong>Save Lead Field</strong>
                  <p>Writes a flow variable value into a lead field for the current customer. The customer is automatically identified by their WhatsApp phone number.</p>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Lead Field <span class="required-star">*</span></label>
                <select v-model="selectedNode.config.field_id" class="property-input" @change="onNodePropertyChange">
                  <option value="">— select a field —</option>
                  <option v-for="field in allLeadFields" :key="field.id" :value="String(field.id)">
                    {{ field.name }}{{ field.is_custom ? ' (custom)' : '' }}
                  </option>
                </select>
                <small v-if="allLeadFields.length === 0" class="property-hint">No lead fields assigned. Go to <strong>Company Profile → Lead Fields</strong> to set up fields first.</small>
              </div>

              <div class="property-group">
                <label class="property-label">Value <span class="required-star">*</span></label>
                <VarTextEditor
                  v-model="selectedNode.config.value_var"
                  placeholder="Type a value or use {{variable}}"
                  :single-line="true"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
                <small class="property-hint">Enter a fixed value, a <code>{{variable}}</code>, or mix both — e.g. <code>Mr. {{customer_name}}</code>.</small>
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════════════ -->
            <!-- Leads: Check Lead Complete Node Editor              -->
            <!-- ═══════════════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'check_lead_complete'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">📋</span>
                <div>
                  <strong>Check Lead Complete</strong>
                  <p>Checks if all required lead fields for this customer are filled. Branches to <em>complete</em> or <em>incomplete</em>.</p>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Save Missing Fields as <small>optional</small></label>
                <input
                  v-model="selectedNode.config.missing_fields_var"
                  type="text"
                  class="property-input"
                  placeholder="e.g. missing_fields"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">If set, a comma-separated list of missing field names is stored in this variable on the <em>incomplete</em> branch.</small>
              </div>

              <div class="property-group">
                <label class="property-label">Output branches</label>
                <div class="branch-info-list">
                  <div class="branch-info-item branch-complete">✅ <strong>complete</strong> — all required fields are filled</div>
                  <div class="branch-info-item branch-incomplete">❌ <strong>incomplete</strong> — one or more fields are missing</div>
                </div>
              </div>
            </div>

            <!-- ════════════════════════════════════════════════════ -->
            <!-- Leads: Get Lead Data Node Editor                    -->
            <!-- ════════════════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'get_lead_data'" class="node-editor">
              <div class="crm-node-info">
                <span class="crm-node-icon">📥</span>
                <div>
                  <strong>Get Lead Data</strong>
                  <p>Loads the customer's current lead field values into flow variables so you can use them later.</p>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Field → Variable Mappings</label>
                <div
                  v-for="(mapping, idx) in selectedNode.config.mappings"
                  :key="idx"
                  class="lead-mapping-row"
                >
                  <select
                    v-model="mapping.field_id"
                    class="property-input mapping-field-select"
                    @change="onNodePropertyChange"
                  >
                    <option value="">— field —</option>
                    <option v-for="field in allLeadFields" :key="field.id" :value="String(field.id)">
                      {{ field.name }}{{ field.is_custom ? ' ✦' : '' }}
                    </option>
                  </select>
                  <span class="mapping-arrow">→</span>
                  <input
                    v-model="mapping.variable_name"
                    type="text"
                    class="property-input mapping-var-input"
                    placeholder="variable name"
                    @input="onNodePropertyChange"
                  />
                  <button
                    v-if="selectedNode.config.mappings.length > 1"
                    class="btn-remove-mapping"
                    @click="selectedNode.config.mappings.splice(idx, 1); onNodePropertyChange()"
                    title="Remove"
                  >✕</button>
                </div>
                <button class="btn-add-mapping" @click="selectedNode.config.mappings.push({ field_id: '', variable_name: '' }); onNodePropertyChange()">
                  + Add Mapping
                </button>
                <small v-if="allLeadFields.length === 0" class="property-hint">No lead fields assigned. Go to <strong>Company Profile → Lead Fields</strong> first.</small>
              </div>
            </div>

            <!-- Assign to Department Node Editor -->
            <div v-if="selectedNode.type === 'assign_department'" class="node-editor">
              <div class="assign-dept-info">
                <span class="assign-dept-icon">🏢</span>
                <div>
                  <strong>Assign to Department</strong>
                  <p>Assigns the customer to a department so agents can pick up the conversation. Run this after the customer has chosen a department.</p>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Department ID — from variable</label>
                <select
                  v-model="selectedNode.config.department_id_var"
                  class="property-input"
                  @change="onNodePropertyChange"
                >
                  <option value="">— pick from flow variables —</option>
                  <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                    <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                      <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">
                        {{ varName }}
                      </option>
                    </optgroup>
                  </template>
                </select>
                <small class="property-hint">Pick the variable that holds the selected department's ID (e.g. <code>choose_department_id</code>)</small>
              </div>

              <div class="property-group">
                <label class="property-label">— or — Department (fixed)</label>
                <select
                  v-model="selectedNode.config.department_id_static"
                  class="property-input"
                  :disabled="!!selectedNode.config.department_id_var"
                  @change="onNodePropertyChange"
                >
                  <option value="">— select a department —</option>
                  <option v-for="dept in departments" :key="dept.id" :value="String(dept.id)">
                    {{ dept.name }}
                  </option>
                </select>
                <small class="property-hint">Only used if no variable is selected above</small>
              </div>

              <div class="property-group">
                <label class="property-label">Confirmation message <small>optional</small></label>
                <textarea
                  v-model="selectedNode.config.message.en"
                  class="property-textarea"
                  placeholder="e.g. You've been connected to our Sales team. An agent will be with you shortly."
                  rows="2"
                  @input="onNodePropertyChange"
                ></textarea>
              </div>

              <div class="property-group" v-if="flowDefinition.settings && flowDefinition.settings.department_handoff_mode">
                <label class="property-label">After agent resolves</label>
                <div class="resolve-action-pills">
                  <label
                    class="resolve-action-pill"
                    :class="{ active: (selectedNode.config.resolve_action || 'restart') === 'restart' }"
                  >
                    <input
                      type="radio"
                      :checked="(selectedNode.config.resolve_action || 'restart') === 'restart'"
                      @change="selectedNode.config.resolve_action = 'restart'; selectedNode.next = null; selectedNode.config.post_resolve_enabled = false; selectedNode.config.post_resolve_first_node_id = null; onNodePropertyChange()"
                      style="display:none"
                    />
                    🔄 Restart flow
                  </label>
                  <label
                    class="resolve-action-pill"
                    :class="{ active: selectedNode.config.resolve_action === 'continue' }"
                  >
                    <input
                      type="radio"
                      :checked="selectedNode.config.resolve_action === 'continue'"
                      @change="selectedNode.config.resolve_action = 'continue'; onNodePropertyChange()"
                      style="display:none"
                    />
                    ➡️ Continue to next node
                  </label>
                </div>
                <small class="property-hint">
                  <b>Restart:</b> flow resets to the beginning on next contact.<br>
                  <b>Continue:</b> flow immediately advances to the connected output node after resolve.
                </small>
              </div>

              <!-- Post-resolve actions toggle — only available when resolve_action = continue -->
              <div
                class="property-group"
                v-if="flowDefinition.settings && flowDefinition.settings.department_handoff_mode && selectedNode.config.resolve_action === 'continue'"
              >
                <div class="post-resolve-toggle-row">
                  <label class="toggle-switch-label">
                    <input
                      type="checkbox"
                      :checked="!!selectedNode.config.post_resolve_enabled"
                      @change="selectedNode.config.post_resolve_enabled = $event.target.checked; if (!$event.target.checked) { selectedNode.config.post_resolve_first_node_id = null; } onNodePropertyChange()"
                    />
                    <span class="toggle-switch-track"></span>
                    <span class="toggle-switch-text">Run post-resolve actions</span>
                  </label>
                </div>
                <small class="property-hint" v-if="!selectedNode.config.post_resolve_enabled">
                  When enabled, a dedicated chain of nodes runs immediately after the agent resolves this handoff — before the customer can send another message. Use it to collect a satisfaction rating, send a closing message, or trigger a CRM update.
                </small>
                <div class="post-resolve-active-hint" v-else>
                  <span class="post-resolve-hint-icon">↗️</span>
                  <span>Connect the <b>On Resolve</b> port on this node to the first action you want to run after resolve. End the chain with a <b>Save &amp; Finish</b> node.</span>
                </div>
              </div>
            </div>

            <!-- Send Email Node Editor -->
            <div v-if="selectedNode.type === 'send_email'" class="node-editor">
              <div class="email-node-banner">
                <img src="@/assets/mail-svgrepo-com.svg" class="email-node-logo" alt="Email" />
                <div>
                  <strong>Send Email</strong>
                  <small>Delivered via s1notification@quali-d.com</small>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">To <span style="color:#e53e3e">*</span></label>
                <VarTextEditor v-model="selectedNode.config.to" placeholder="{{customer_email}} or name@example.com" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Supports flow variables, e.g. <code>{{customer_email}}</code></small>
              </div>

              <div class="property-group">
                <label class="property-label">Subject <span style="color:#e53e3e">*</span></label>
                <VarTextEditor v-model="selectedNode.config.subject" placeholder="Your order has been confirmed" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>

              <div class="property-group">
                <label class="property-label" style="display:flex;align-items:center;justify-content:space-between">
                  Body
                  <label class="email-html-toggle">
                    <input type="checkbox" v-model="selectedNode.config.html_mode" @change="onNodePropertyChange" />
                    <span class="email-html-toggle-label">HTML mode</span>
                  </label>
                </label>
                <VarTextEditor
                  v-model="selectedNode.config.body"
                  :placeholder="selectedNode.config.html_mode ? '<p>Hello <strong>{{customer_name}}</strong>,</p>\n<p>Your request has been received.</p>' : 'Hello {{customer_name}}, your request has been received...'"
                  :rows="8"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
                <small v-if="selectedNode.config.html_mode" class="property-hint">
                  ✅ HTML mode — use tags like <code>&lt;b&gt;</code>, <code>&lt;a href&gt;</code>, <code>&lt;br&gt;</code>. Variables like <code>{{customer_name}}</code> are resolved before sending.
                </small>
                <small v-else class="property-hint">Plain text mode — no HTML tags. Variables like <code>{{customer_name}}</code> still work.</small>
              </div>

              <div class="property-group">
                <label class="property-label">From Name <small>optional</small></label>
                <VarTextEditor v-model="selectedNode.config.from_name" placeholder="Quali-D (leave empty for default)" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Overrides the default sender name. Supports variables.</small>
              </div>

              <div class="property-group">
                <label class="property-label">Reply-To <small>optional</small></label>
                <VarTextEditor v-model="selectedNode.config.reply_to" placeholder="support@yourcompany.com" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
            </div>

            <!-- Currency Exchange Node Editor -->
            <div v-if="selectedNode.type === 'currency_exchange'" class="node-editor">
              <div class="currency-node-banner">
                <span class="currency-node-icon">💱</span>
                <div>
                  <strong>Currency Exchange</strong>
                  <small>Live rates via frankfurter.app (ECB · updated daily · no API key)</small>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Amount <span style="color:#e53e3e">*</span></label>
                <VarTextEditor v-model="selectedNode.config.amount" placeholder="{{product_price}} or 100" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Supports flow variables, e.g. <code>{{price}}</code></small>
              </div>

              <div class="currency-row">
                <div class="property-group currency-col">
                  <label class="property-label">From</label>
                  <select v-model="selectedNode.config.from_currency" class="property-input" @change="onNodePropertyChange">
                    <option v-for="c in currencyList" :key="c.code" :value="c.code">{{ c.code }} — {{ c.name }}</option>
                  </select>
                </div>
                <div class="currency-arrow">→</div>
                <div class="property-group currency-col">
                  <label class="property-label">To</label>
                  <select v-model="selectedNode.config.to_currency" class="property-input" @change="onNodePropertyChange">
                    <option v-for="c in currencyList" :key="c.code" :value="c.code">{{ c.code }} — {{ c.name }}</option>
                  </select>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Decimal places</label>
                <input v-model.number="selectedNode.config.decimal_places" type="number" min="0" max="8" class="property-input" style="width:80px" @input="onNodePropertyChange" />
              </div>

              <div class="property-group">
                <label class="property-label">Result variable <span style="color:#e53e3e">*</span></label>
                <input v-model="selectedNode.config.result_variable" class="property-input" placeholder="converted_amount" @input="onNodePropertyChange" />
                <small class="property-hint">
                  Saves: <code>{{selectedNode.config.result_variable || 'converted_amount'}}</code> (number),
                  <code>{{selectedNode.config.result_variable || 'converted_amount'}}_rate</code> (exchange rate),
                  <code>{{selectedNode.config.result_variable || 'converted_amount'}}_date</code> (rate date)
                </small>
              </div>
            </div>

            <!-- Set Variable Node Editor -->
            <div v-if="selectedNode.type === 'set_variable'" class="node-editor">

              <!-- STEP 1 — INPUT -->
              <div class="setvar-section-label">
                <span class="setvar-step">① INPUT</span>
                <span class="setvar-step-desc">What value do you want to work with?</span>
              </div>
              <div class="property-group">
                <label class="property-label">Source value <span style="color:#e53e3e">*</span></label>
                <VarTextEditor v-model="selectedNode.config.value" placeholder="e.g. {{customer_phone}} or plain text" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Pick a flow variable below, or type a fixed value.</small>
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert variable:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>

              <!-- STEP 2 — PROCESSING -->
              <div class="setvar-section-label" style="margin-top:14px">
                <span class="setvar-step">② PROCESS</span>
                <span class="setvar-step-desc">Do you want to modify the value before saving?</span>
              </div>
              <div class="property-group">
                <label class="property-label">Transform</label>
                <select v-model="selectedNode.config.transform" class="property-input" @change="onNodePropertyChange">
                  <option value="none">None — save as-is</option>
                  <option value="remove_prefix">Remove Prefix — strip characters from the start</option>
                  <option value="format_date">Format Date/Time — convert ISO date to readable text</option>
                  <option value="array_map_date">Format Date Field in Array — add readable label to each item</option>
                  <option value="uppercase">Uppercase</option>
                  <option value="lowercase">Lowercase</option>
                  <option value="trim">Trim whitespace</option>
                </select>
              </div>

              <!-- remove_prefix params -->
              <div v-if="selectedNode.config.transform === 'remove_prefix'" class="property-group">
                <label class="property-label">Prefix to strip <span style="color:#e53e3e">*</span></label>
                <VarTextEditor v-model="selectedNode.config.transform_param" placeholder="e.g. 971" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">These characters are removed from the beginning of the value. E.g. input <code>971501234567</code> → strip <code>971</code> → result <code>501234567</code>.</small>
              </div>

              <!-- format_date params -->
              <div v-if="selectedNode.config.transform === 'format_date'" class="property-group">
                <label class="property-label">Output format</label>
                <select v-model="selectedNode.config.transform_format" class="property-input" @change="onNodePropertyChange">
                  <option value="g:i A">9:15 AM</option>
                  <option value="H:i">09:15</option>
                  <option value="h:i A">09:15 AM</option>
                  <option value="M j · g:i A">Apr 21 · 9:15 AM</option>
                  <option value="D, M j · g:i A">Tue, Apr 21 · 9:15 AM</option>
                  <option value="l, F j">Tuesday, April 21</option>
                  <option value="M j, Y">Apr 21, 2026</option>
                  <option value="d/m/Y">21/04/2026</option>
                  <option value="m/d/Y">04/21/2026</option>
                </select>
                <small class="property-hint">Input example: <code>2026-04-21T09:15:00</code> → output with selected format.</small>
              </div>

              <!-- array_map_date params -->
              <template v-if="selectedNode.config.transform === 'array_map_date'">
                <div class="property-group">
                  <label class="property-label">Field to format <span style="color:#e53e3e">*</span></label>
                  <input v-model="selectedNode.config.transform_param" class="property-input" placeholder="e.g. myDate" @input="onNodePropertyChange" />
                  <small class="property-hint">The key inside each array item that contains the ISO date string.</small>
                </div>
                <div class="property-group">
                  <label class="property-label">Output format</label>
                  <select v-model="selectedNode.config.transform_format" class="property-input" @change="onNodePropertyChange">
                    <option value="g:i A">9:15 AM</option>
                    <option value="H:i">09:15</option>
                    <option value="h:i A">09:15 AM</option>
                    <option value="M j · g:i A">Apr 21 · 9:15 AM</option>
                    <option value="D, M j · g:i A">Tue, Apr 21 · 9:15 AM</option>
                    <option value="l, F j">Tuesday, April 21</option>
                    <option value="M j, Y">Apr 21, 2026</option>
                    <option value="d/m/Y">21/04/2026</option>
                    <option value="m/d/Y">04/21/2026</option>
                  </select>
                </div>
                <div class="property-group">
                  <label class="property-label">Save formatted label as field <span style="color:#e53e3e">*</span></label>
                  <input v-model="selectedNode.config.transform_param2" class="property-input" placeholder="e.g. display_time" @input="onNodePropertyChange" />
                  <small class="property-hint">A new key added to every item in the array. Use this as the <strong>Button text field</strong> in your Text+Buttons node. Leave blank to overwrite the original field.</small>
                </div>
              </template>

              <!-- STEP 3 — OUTPUT -->
              <div class="setvar-section-label" style="margin-top:14px">
                <span class="setvar-step">③ OUTPUT</span>
                <span class="setvar-step-desc">Where should the result be saved?</span>
              </div>
              <div class="property-group">
                <label class="property-label">Save result as <span style="color:#e53e3e">*</span></label>
                <input v-model="selectedNode.config.variable_name" class="property-input" placeholder="e.g. customer_phone_local" @input="onNodePropertyChange" />
                <small class="property-hint">Use <code>{{ selectedNode.config.variable_name || 'your_variable_name' }}</code> in any node that comes after this one in the flow.</small>
              </div>

            </div>

            <!-- API Call Node Editor -->
            <div v-if="selectedNode.type === 'api_call'" class="node-editor">
              <div class="property-group">
                <label class="property-label">API Node Name</label>
                <input
                  v-model="ensureApiCallConfig(selectedNode).api_node_name"
                  class="property-input"
                  placeholder="e.g., GetCategories, FetchUserData"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">Give this API call a unique name to identify response variables</small>
              </div>

              <div class="property-group">
                <label class="property-label">API URL</label>
                <VarTextEditor v-model="ensureApiCallConfig(selectedNode).api_url" placeholder="https://api.example.com/endpoint" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>

              <div class="property-group">
                <label class="property-label">HTTP Method</label>
                <select v-model="ensureApiCallConfig(selectedNode).http_method" class="property-input" @change="onNodePropertyChange">
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="PATCH">PATCH</option>
                  <option value="DELETE">DELETE</option>
                </select>
              </div>

              <div class="property-group" v-if="ensureApiCallConfig(selectedNode).http_method === 'GET'">
                <label class="property-label">Query Parameters</label>
                <div v-for="(value, key, index) in ensureApiCallConfig(selectedNode).query_params" :key="'param-' + index" class="kv-pair">
                  <input
                    :value="key"
                    class="property-input kv-key"
                    placeholder="Parameter name"
                    @blur="updateQueryParam(selectedNode, key, $event.target.value, value)"
                  />
                  <VarTextEditor
                    :modelValue="ensureApiCallConfig(selectedNode).query_params[key]"
                    :single-line="true"
                    placeholder="Value or {{variable}}"
                    class="kv-vte"
                    @focus="activeVarEditor = $event"
                    @update:modelValue="val => { ensureApiCallConfig(selectedNode).query_params[key] = val; onNodePropertyChange() }"
                  />
                  <button @click="removeQueryParam(selectedNode, key)" class="btn-remove">×</button>
                </div>
                <button @click="addQueryParam(selectedNode)" class="btn-add">+ Add Query Parameter</button>
              </div>

              <div class="property-group">
                <label class="property-label">Headers</label>
                <div v-for="(value, key, index) in ensureApiCallConfig(selectedNode).headers" :key="'header-' + index" class="kv-pair header-pair">
                  <select
                    :value="key"
                    class="property-input header-type-select"
                    @change="updateHeaderKey(selectedNode, key, $event.target.value)"
                  >
                    <option value="">Select Type...</option>
                    <option value="Authorization">Authorization</option>
                    <option value="Content-Type">Content-Type</option>
                    <option value="Accept">Accept</option>
                    <option value="X-API-Key">X-API-Key</option>
                    <option value="X-Auth-Token">X-Auth-Token</option>
                    <option value="Cookie">Cookie</option>
                    <option value="User-Agent">User-Agent</option>
                    <option value="Referer">Referer</option>
                    <option value="Origin">Origin</option>
                    <option :value="key">{{ key }}</option>
                  </select>
                  <VarTextEditor
                    :modelValue="ensureApiCallConfig(selectedNode).headers[key]"
                    :single-line="true"
                    placeholder="e.g., Bearer {{token}}"
                    class="kv-vte"
                    @focus="activeVarEditor = $event"
                    @update:modelValue="val => { ensureApiCallConfig(selectedNode).headers[key] = val; onNodePropertyChange() }"
                  />
                  <button @click="removeHeader(selectedNode, key)" class="btn-remove">×</button>
                </div>
                <button @click="addHeader(selectedNode)" class="btn-add">+ Add Header</button>
              </div>

              <div class="property-group" v-if="['POST', 'PUT', 'PATCH'].includes(ensureApiCallConfig(selectedNode).http_method)">
                <label class="property-label">Body Format</label>
                <select v-model="ensureApiCallConfig(selectedNode).body_format" class="property-input" @change="onNodePropertyChange">
                  <option value="json">JSON (application/json)</option>
                  <option value="form">Form URL-Encoded (application/x-www-form-urlencoded)</option>
                  <option value="form-data">Form Data (multipart/form-data)</option>
                </select>
              </div>

              <!-- JSON body -->
              <div class="property-group" v-if="['POST', 'PUT', 'PATCH'].includes(ensureApiCallConfig(selectedNode).http_method) && ensureApiCallConfig(selectedNode).body_format === 'json'">
                <label class="property-label">Request Body <span class="field-optional">JSON</span></label>
                <VarTextEditor v-model="ensureApiCallConfig(selectedNode).request_body_json" placeholder='{"key": "value", "token": "{{my_token}}"}' :rows="5" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Type valid JSON — click a variable below to insert it</small>
              </div>

              <!-- Form params (form-urlencoded / form-data) -->
              <div class="property-group" v-if="['POST', 'PUT', 'PATCH'].includes(ensureApiCallConfig(selectedNode).http_method) && ['form', 'form-data'].includes(ensureApiCallConfig(selectedNode).body_format)">
                <label class="property-label">Form Fields</label>
                <div v-for="(value, key, index) in ensureApiCallConfig(selectedNode).form_params" :key="'fp-' + index" class="kv-pair">
                  <input
                    :value="key"
                    class="property-input kv-key"
                    placeholder="Field name"
                    @blur="updateFormParam(selectedNode, key, $event.target.value, value)"
                  />
                  <VarTextEditor
                    :modelValue="ensureApiCallConfig(selectedNode).form_params[key]"
                    :single-line="true"
                    placeholder="Value or {{variable}}"
                    class="kv-vte"
                    @focus="activeVarEditor = $event"
                    @update:modelValue="val => { ensureApiCallConfig(selectedNode).form_params[key] = val; onNodePropertyChange() }"
                  />
                  <button @click="removeFormParam(selectedNode, key)" class="btn-remove">×</button>
                </div>
                <button @click="addFormParam(selectedNode)" class="btn-add">+ Add Field</button>
              </div>

              <!-- Request ID (idmsg) -->
              <div class="property-group">
                <label class="property-label">Request ID (idmsg) <span class="field-optional">optional</span></label>
                <select v-model="ensureApiCallConfig(selectedNode).idmsg_mode" class="property-input" @change="onNodePropertyChange">
                  <option value="off">Disabled</option>
                  <option value="auto">Auto — generate UUID</option>
                  <option value="custom">Custom value</option>
                </select>
                <small v-if="ensureApiCallConfig(selectedNode).idmsg_mode !== 'off'" class="property-hint">
                  Use <code style="background:rgba(99,102,241,.12);padding:1px 5px;border-radius:4px;">{{idmsg}}</code> anywhere in this request (URL, headers, body)
                </small>
              </div>
              <div class="property-group" v-if="ensureApiCallConfig(selectedNode).idmsg_mode === 'custom'">
                <label class="property-label">Custom idmsg value</label>
                <VarTextEditor v-model="ensureApiCallConfig(selectedNode).idmsg_custom_value" placeholder="e.g. {{session_id}} or a static string" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>

              <!-- SSL verification -->
              <div class="property-group">
                <label class="property-checkbox">
                  <input type="checkbox" v-model="ensureApiCallConfig(selectedNode).skip_ssl_verify" @change="onNodePropertyChange" />
                  <span>Skip SSL certificate verification <span class="field-optional">( -k / --insecure )</span></span>
                </label>
                <small class="property-hint" style="color:#f59e0b;" v-if="ensureApiCallConfig(selectedNode).skip_ssl_verify">
                  ⚠️ Only use this for trusted internal/test APIs. Disabling SSL verification is a security risk on production endpoints.
                </small>
              </div>

              <!-- Variable picker — same as all other nodes -->
              <!-- Smart variable picker -->
              <div v-if="Object.keys(flowVariables).length > 0" class="api-var-picker">
                <div class="api-var-picker-header">
                  <span class="var-pick-label">Insert variable</span>
                  <span class="api-var-count">{{ Object.keys(filteredApiVars).length }}<template v-if="apiVarSearch"> of {{ Object.keys(flowVariables).length }}</template></span>
                </div>
                <input
                  v-model="apiVarSearch"
                  class="api-var-search"
                  placeholder="Search by name or description..."
                  @keydown.esc="apiVarSearch = ''"
                />
                <div class="api-var-chips">
                  <span
                    v-for="(varInfo, varName) in filteredApiVars"
                    :key="varName"
                    class="var-pick-chip"
                    :class="'chip-src-' + (varInfo.source || 'custom')"
                    :title="varInfo.description"
                    @mousedown.prevent="insertVariableAtCursor(varName)"
                  >{{ varName }}</span>
                  <span v-if="apiVarSearch && !Object.keys(filteredApiVars).length" class="api-var-empty">No variables match "{{ apiVarSearch }}"</span>
                </div>
              </div>

              <div class="property-group">
                <label class="property-label">Response Variable Mapping</label>
                <small class="property-hint">Map JSON response keys to flow variables</small>
                <div v-for="(varName, jsonPath, index) in ensureApiCallConfig(selectedNode).response_mapping" :key="'mapping-' + index" class="kv-pair">
                  <input
                    :value="jsonPath"
                    class="property-input kv-key"
                    placeholder="JSON path (e.g., data.token)"
                    @blur="updateResponseMapping(selectedNode, jsonPath, $event.target.value, varName)"
                  />
                  <input
                    v-model="ensureApiCallConfig(selectedNode).response_mapping[jsonPath]"
                    class="property-input kv-value"
                    placeholder="Variable name (e.g., my_website_token)"
                    @input="onNodePropertyChange"
                  />
                  <button @click="removeResponseMapping(selectedNode, jsonPath)" class="btn-remove">×</button>
                </div>
                <button @click="addResponseMapping(selectedNode)" class="btn-add">+ Add Mapping</button>
              </div>

              <div class="property-group">
                <button @click="prepareApiTest(selectedNode)" class="btn-primary" :disabled="testingApi">
                  {{ testingApi ? 'Testing...' : '🧪 Test API Call' }}
                </button>
              </div>

              <!-- Sample values for variables detected in URL / headers / body -->
              <div v-if="apiTestVarsDetected.length > 0" class="api-test-samples">
                <div class="api-test-samples-header">
                  <span>📝 Variable sample values</span>
                  <small>These replace <code>{{variable}}</code> placeholders during the test only</small>
                </div>
                <div
                  v-for="varName in apiTestVarsDetected"
                  :key="varName"
                  class="api-test-sample-row"
                >
                  <span class="api-test-sample-tag chip-src-auto">{{ varName }}</span>
                  <input
                    v-model="testSampleValues[varName]"
                    class="property-input api-test-sample-input"
                    :placeholder="`sample value for ${varName}...`"
                  />
                </div>
                <button
                  @click="runApiTest(selectedNode)"
                  class="btn-primary"
                  :disabled="testingApi"
                  style="margin-top: 8px; width: 100%;"
                >
                  {{ testingApi ? 'Testing...' : '▶ Run Test' }}
                </button>
              </div>

              <div v-if="apiTestResponse && apiTestNodeId === selectedNode.id" class="property-group api-test-result">
                <label class="property-label">Test Response (Raw JSON)</label>
                <pre class="api-response-preview">{{ JSON.stringify(apiTestResponse, null, 2) }}</pre>
              </div>

              <div v-if="detectedResponseKeys.length > 0 && apiTestNodeId === selectedNode.id" class="property-group">
                <label class="property-label">Detected Response Keys</label>
                <small class="property-hint success">✓ Auto-detected {{ detectedResponseKeys.length }} keys. Customize variable names below:</small>
                <div v-for="(item, index) in detectedResponseKeys" :key="'detected-' + index" class="detected-key-row">
                  <div class="detected-key-info">
                    <span class="detected-key-name">{{ item.key }}</span>
                    <span class="detected-key-type">{{ item.type }}</span>
                  </div>
                  <input
                    v-model="item.varName"
                    class="property-input detected-var-input"
                    :placeholder="item.suggestedName"
                    @input="onNodePropertyChange"
                  />
                  <button
                    v-if="item.varName && flowDefinition.variables && flowDefinition.variables[item.varName] !== undefined"
                    @click="unsaveDetectedVariable(item)"
                    class="btn-save-var btn-unsave-var"
                  >Unsave</button>
                  <button
                    v-else
                    @click="saveDetectedVariable(selectedNode, item)"
                    class="btn-save-var"
                    :disabled="!item.varName"
                  >{{ item.saved ? '✓' : 'Save' }}</button>
                </div>
                <button @click="saveAllDetectedVariables(selectedNode)" class="btn-primary" style="margin-top: 10px;">
                  💾 Save All Variables
                </button>
              </div>

              <div v-if="apiTestError" class="property-group api-test-error">
                <label class="property-label">Test Error</label>
                <pre class="api-error-preview">{{ apiTestError }}</pre>
              </div>
            </div>

            <!-- ════════════════════════════════════════════════════ -->
            <!-- AI Node Editor (OpenAI / Claude / Gemini unified)  -->
            <!-- ════════════════════════════════════════════════════ -->
            <div v-if="['openai_call','claude_call','gemini_call'].includes(selectedNode.type)" class="node-editor node-editor-ai">

              <!-- Provider banner -->
              <div class="ai-node-banner" :class="getAiBannerClass(selectedNode.type)">
                <img :src="getAiNodeIconSrc(selectedNode.type)" class="ai-node-logo-img" :alt="getAiProviderName(selectedNode.type)" />
                <div>
                  <strong>{{ getAiProviderName(selectedNode.type) }}</strong>
                  <small>Smart AI agent — auto-context &amp; session memory</small>
                </div>
              </div>

              <!-- API Key -->
              <div class="property-group">
                <label class="property-label">API Key</label>
                <input v-model="ensureAiConfig(selectedNode).api_key" class="property-input" :placeholder="getAiKeyPlaceholder(selectedNode.type)" @input="onNodePropertyChange" />
                <small class="property-hint">Paste directly or reference a preset variable like <code>{{openai_key}}</code></small>
              </div>

              <!-- Model -->
              <div class="property-group">
                <label class="property-label">Model</label>
                <select v-model="ensureAiConfig(selectedNode).model" class="property-input" @change="onNodePropertyChange">
                  <option v-for="m in getAiModelOptions(selectedNode.type)" :key="m.value" :value="m.value">{{ m.label }}</option>
                </select>
              </div>

              <!-- Mode selector -->
              <div class="property-group">
                <label class="property-label">Mode</label>
                <div class="ai-mode-pills">
                  <button
                    v-for="modeOpt in [{value:'generate',icon:'✍️',label:'Generate'},{value:'classify',icon:'🔀',label:'Classify'},{value:'extract',icon:'📋',label:'Extract'}]"
                    :key="modeOpt.value"
                    type="button"
                    class="ai-mode-pill"
                    :class="{'ai-mode-pill--active': ensureAiConfig(selectedNode).mode === modeOpt.value}"
                    @click="ensureAiConfig(selectedNode).mode = modeOpt.value; onNodePropertyChange()"
                  >{{ modeOpt.icon }} {{ modeOpt.label }}</button>
                </div>
                <small v-if="ensureAiConfig(selectedNode).mode === 'generate'" class="property-hint">AI generates text or JSON saved to a variable. Optionally sends to customer.</small>
                <small v-else-if="ensureAiConfig(selectedNode).mode === 'classify'" class="property-hint">AI reads context and picks one intent → the flow routes to that output port.</small>
                <small v-else class="property-hint">AI extracts structured fields from context → each field saved to a session variable.</small>
              </div>

              <!-- Auto-context injection -->
              <div class="property-group">
                <label class="property-label">Auto-Context <span class="field-optional">injected automatically before your prompt</span></label>
                <label class="property-checkbox">
                  <input type="checkbox" v-model="ensureAiConfig(selectedNode).inject_session" @change="onNodePropertyChange" />
                  <span>Inject all session variables</span>
                </label>
                <label class="property-checkbox" style="margin-top:4px;">
                  <input type="checkbox" v-model="ensureAiConfig(selectedNode).inject_history" @change="onNodePropertyChange" />
                  <span>Inject conversation history</span>
                </label>
                <div v-if="ensureAiConfig(selectedNode).inject_history" class="history-limit-row">
                  <span>Last</span>
                  <input type="number" v-model.number="ensureAiConfig(selectedNode).history_limit" class="property-input history-limit-input" min="1" max="20" @input="onNodePropertyChange" />
                  <span>messages</span>
                </div>
              </div>

              <!-- System prompt -->
              <div class="property-group">
                <label class="property-label">System Prompt <span class="field-optional">optional — context is appended below it</span></label>
                <VarTextEditor v-model="ensureAiConfig(selectedNode).system_prompt" placeholder="You are a helpful assistant for {{company_name}}..." :rows="3" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>

              <!-- User prompt (generate + classify modes) -->
              <div v-if="ensureAiConfig(selectedNode).mode !== 'extract'" class="property-group">
                <label class="property-label">{{ ensureAiConfig(selectedNode).mode === 'classify' ? 'What to classify' : 'User Prompt' }}</label>
                <VarTextEditor
                  v-model="ensureAiConfig(selectedNode).user_prompt"
                  :placeholder="ensureAiConfig(selectedNode).mode === 'classify' ? 'The customer said: {{customer_input}}' : 'Summarise the following: {{customer_input}}'"
                  :rows="4"
                  @focus="activeVarEditor = $event"
                  @update:modelValue="onNodePropertyChange"
                />
              </div>

              <!-- IMAGE / VISION INPUT -->
              <div class="property-group">
                <label class="property-label">Image Input <span class="field-optional">vision — OpenAI, Claude &amp; Gemini all support this</span></label>
                <label class="property-checkbox">
                  <input type="checkbox" v-model="ensureAiConfig(selectedNode).include_image" @change="onNodePropertyChange" />
                  <span>Send an image to the AI with this request</span>
                </label>
                <div v-if="ensureAiConfig(selectedNode).include_image" style="margin-top:8px;">
                  <VarTextEditor
                    v-model="ensureAiConfig(selectedNode).image_url_input"
                    placeholder="{{customer_image_url}}"
                    :single-line="true"
                    @focus="activeVarEditor = $event"
                    @update:modelValue="onNodePropertyChange"
                  />
                  <small class="property-hint">Use a variable from an <strong>Image Input</strong> node upstream, e.g. <code>{{customer_image}}</code>. Gemini downloads and encodes the image server-side.</small>
                </div>
              </div>

              <!-- CLASSIFY: intents list -->
              <div v-if="ensureAiConfig(selectedNode).mode === 'classify'" class="property-group">
                <label class="property-label">Intents <span class="field-optional">each becomes a flow output port</span></label>
                <div v-for="(intent, idx) in (ensureAiConfig(selectedNode).intents || [])" :key="'intent-'+idx" class="ai-field-row">
                  <input v-model="intent.name" class="property-input ai-field-name" placeholder="intent_name" @input="onNodePropertyChange" />
                  <input v-model="intent.description" class="property-input ai-field-desc" placeholder="What this intent means..." @input="onNodePropertyChange" />
                  <button class="btn-remove-field" type="button" @click="removeAiIntent(selectedNode, idx)" title="Remove">×</button>
                </div>
                <button class="btn-add-field" type="button" @click="addAiIntent(selectedNode)">+ Add Intent</button>
                <small class="property-hint">A "fallback" port is always available for unrecognised intents</small>
              </div>

              <!-- EXTRACT: what to extract + fields list -->
              <div v-if="ensureAiConfig(selectedNode).mode === 'extract'" class="property-group">
                <label class="property-label">What to extract</label>
                <VarTextEditor v-model="ensureAiConfig(selectedNode).user_prompt" placeholder="From the customer's messages, extract their name, email, and budget." :rows="3" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div v-if="ensureAiConfig(selectedNode).mode === 'extract'" class="property-group">
                <label class="property-label">Save to variables <span class="field-optional">name + description</span></label>
                <div v-for="(field, idx) in (ensureAiConfig(selectedNode).extract_fields || [])" :key="'field-'+idx" class="ai-field-row">
                  <input v-model="field.var_name" class="property-input ai-field-name" placeholder="var_name" @input="onNodePropertyChange" />
                  <input v-model="field.description" class="property-input ai-field-desc" placeholder="e.g. customer's full name" @input="onNodePropertyChange" />
                  <button class="btn-remove-field" type="button" @click="removeAiExtractField(selectedNode, idx)" title="Remove">×</button>
                </div>
                <button class="btn-add-field" type="button" @click="addAiExtractField(selectedNode)">+ Add Field</button>
              </div>

              <!-- GENERATE: result variable + send to customer + json mode -->
              <div v-if="ensureAiConfig(selectedNode).mode === 'generate'" class="property-group">
                <label class="property-label">Save response as</label>
                <input v-model="ensureAiConfig(selectedNode).result_variable" class="property-input" placeholder="ai_response" @input="onNodePropertyChange" />
              </div>
              <div v-if="ensureAiConfig(selectedNode).mode === 'generate'" class="property-group">
                <label class="property-checkbox">
                  <input type="checkbox" v-model="ensureAiConfig(selectedNode).send_to_customer" @change="onNodePropertyChange" />
                  <span>Auto-send response to customer <span class="field-optional">skip Text Message node</span></span>
                </label>
                <small v-if="ensureAiConfig(selectedNode).send_to_customer" class="property-hint property-hint--warn">
                  ⚠ Sends immediately — do NOT also connect a Text Message node using variable <code>{{ ensureAiConfig(selectedNode).result_variable || 'ai_response' }}</code> or the customer receives it twice.
                </small>
                <small v-else class="property-hint">
                  Leave off if you are routing the response through a Text Message node downstream using variable <code>{{ ensureAiConfig(selectedNode).result_variable || 'ai_response' }}</code>.
                </small>
                <label class="property-checkbox" style="margin-top:8px;">
                  <input type="checkbox" v-model="ensureAiConfig(selectedNode).json_mode" @change="onNodePropertyChange" />
                  <span>JSON mode — parse response and save each top-level key as a variable</span>
                </label>
              </div>

              <!-- CLASSIFY: save detected intent -->
              <div v-if="ensureAiConfig(selectedNode).mode === 'classify'" class="property-group">
                <label class="property-label">Save detected intent as <span class="field-optional">optional</span></label>
                <input v-model="ensureAiConfig(selectedNode).result_variable" class="property-input" placeholder="detected_intent" @input="onNodePropertyChange" />
              </div>

              <!-- TOOLS -->
              <div class="property-group">
                <label class="property-label">Function Tools <span class="field-optional">nodes the AI can call autonomously</span></label>
                <!-- Empty state -->
                <div v-if="!ensureAiConfig(selectedNode).tools.length" class="ai-tools-empty">
                  Drag from the <strong>⊕ port at the bottom</strong> of this node to any API Call, Google Sheets, CRM, or action node to add it as a callable tool.
                </div>
                <!-- Connected tools -->
                <div class="ai-tools-list">
                  <div v-for="(tool, tidx) in ensureAiConfig(selectedNode).tools" :key="tidx" class="ai-tool-row">
                    <div class="ai-tool-row-header">
                      <span class="ai-tool-type-badge">{{ getToolNodeTypeName(selectedNode, tool.node_id) }}</span>
                      <button class="btn-remove-field" type="button" @click="removeAiTool(selectedNode, tidx)" title="Remove tool">×</button>
                    </div>
                    <input
                      class="property-input ai-tool-name-input"
                      placeholder="function_name (no spaces)"
                      :value="tool.name"
                      @input="updateAiToolField(selectedNode, tool.node_id, 'name', $event.target.value)"
                    />
                    <input
                      class="property-input ai-tool-desc-input"
                      placeholder="Describe when the AI should use this tool..."
                      :value="tool.description"
                      @input="updateAiToolField(selectedNode, tool.node_id, 'description', $event.target.value)"
                    />
                  </div>
                </div>
                <small v-if="ensureAiConfig(selectedNode).tools.length" class="property-hint">The AI autonomously decides which tool to call and what data to pass. Results are saved as session variables.</small>
              </div>

              <!-- Advanced -->
              <div class="property-group ai-advanced">
                <label class="property-label">Max Tokens</label>
                <input type="number" v-model.number="ensureAiConfig(selectedNode).max_tokens" class="property-input" min="1" max="8192" @input="onNodePropertyChange" />
              </div>
              <div class="property-group ai-advanced">
                <label class="property-label">Temperature <span class="field-optional">0 = focused · 2 = creative</span></label>
                <input type="number" v-model.number="ensureAiConfig(selectedNode).temperature" class="property-input" min="0" max="2" step="0.1" @input="onNodePropertyChange" />
              </div>

              <!-- Variable picker -->
              <div v-if="Object.keys(flowVariables).length > 0" class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" :title="varInfo.description" @mousedown.prevent="insertVariableAtCursor(varName)">{{ varName }}</span>
              </div>
            </div>

            <!-- Text with Buttons Node Editor -->
            <div v-if="selectedNode.type === 'text_with_buttons'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Header <span class="field-optional">optional · max 60 chars</span></label>
                <VarTextEditor v-model="ensureHeader(selectedNode).en" placeholder="Bold title above the message..." :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Message Text</label>
                <VarTextEditor v-model="selectedNode.config.text.en" placeholder="Enter your message..." :rows="3" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Footer <span class="field-optional">optional · max 60 chars</span></label>
                <VarTextEditor v-model="ensureFooter(selectedNode).en" placeholder="Small grey note below the buttons..." :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="var-pick-row">
                <span class="var-pick-label">Insert:</span>
                <span
                  v-for="(varInfo, varName) in flowVariables"
                  :key="varName"
                  class="var-pick-chip"
                  :class="'chip-src-' + (varInfo.source || 'custom')"
                  :title="varInfo.description"
                  @mousedown.prevent="insertVariableAtCursor(varName)"
                >{{ varName }}</span>
              </div>

              <!-- Dynamic Buttons Toggle (hidden on Welcome Message — no session data exists yet) -->
              <div v-if="!selectedNode.isWelcomeMessage" class="property-group">
                <label class="property-checkbox dynamic-toggle">
                  <input
                    type="checkbox"
                    v-model="selectedNode.config.use_dynamic_buttons"
                    @change="onNodePropertyChange"
                  />
                  <span>🔄 Generate buttons dynamically from array</span>
                </label>
                <small class="property-hint">When enabled, buttons are created from an API response array</small>
              </div>

              <!-- Dynamic Button Configuration -->
              <div v-if="!selectedNode.isWelcomeMessage && selectedNode.config.use_dynamic_buttons" class="dynamic-config-section">
                <div class="property-group">
                  <label class="property-label">Array Variable</label>
                  <select v-model="selectedNode.config.dynamic_array_variable" class="property-input" @change="onNodePropertyChange">
                    <option value="">Select array variable...</option>
                    <option v-for="(varData, varName) in getArrayVariables()" :key="varName" :value="varName">
                      {{ varName }} - {{ varData.arrayItemType }}[{{ varData.arrayLength }}]
                    </option>
                  </select>
                  <small class="property-hint">Choose which array variable to use for generating buttons</small>
                </div>

                <!-- Field Mapping for Object Arrays -->
                <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayItemType === 'object'" class="property-group">
                  <label class="property-label">Button Text Field</label>
                  <select v-model="selectedNode.config.button_text_field" class="property-input" @change="onNodePropertyChange">
                    <option value="">Select field for button text...</option>
                    <option v-for="field in getSelectedArrayMetadata().objectFields" :key="field" :value="field">
                      {{ field }}
                    </option>
                  </select>
                  <small class="property-hint">Which field to use as button text</small>
                </div>

                <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayItemType === 'object' && selectedNode.config.button_text_field" class="property-group">
                  <label class="property-label">Format button text as date/time <span class="field-optional">optional</span></label>
                  <select v-model="selectedNode.config.button_text_date_format" class="property-input" @change="onNodePropertyChange">
                    <option value="">No — use value as-is</option>
                    <option value="g:i A">9:15 AM</option>
                    <option value="H:i">09:15</option>
                    <option value="h:i A">09:15 AM</option>
                    <option value="M j · g:i A">Apr 21 · 9:15 AM</option>
                    <option value="D, M j · g:i A">Tue, Apr 21 · 9:15 AM</option>
                    <option value="l, F j">Tuesday, April 21</option>
                    <option value="M j, Y">Apr 21, 2026</option>
                    <option value="d/m/Y">21/04/2026</option>
                    <option value="m/d/Y">04/21/2026</option>
                  </select>
                  <small class="property-hint">If the field contains an ISO date like <code>2026-04-21T09:15:00</code>, pick a format to display it readably.</small>
                </div>

                <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayItemType === 'object'" class="property-group">
                  <label class="property-label">Button ID Field</label>
                  <select v-model="selectedNode.config.button_id_field" class="property-input" @change="onNodePropertyChange">
                    <option value="">Select field for button ID...</option>
                    <option v-for="field in getSelectedArrayMetadata().objectFields" :key="field" :value="field">
                      {{ field }}
                    </option>
                  </select>
                  <small class="property-hint">Which field to use as button value/identifier</small>
                </div>

                <!-- Routing Mode Selection -->
                <div class="property-group" style="background: rgba(249, 115, 22, 0.08); padding: 12px; border-radius: 8px; border: 1.5px solid rgba(249, 115, 22, 0.2);">
                  <label class="property-label" style="color: #f97316; font-weight: 600;">🔀 After User Selects, What Happens?</label>
                  <small class="property-hint" style="margin-bottom: 12px; display: block;">Choose how to route users after they click a dynamic button</small>

                  <div style="display: flex; gap: 12px;">
                    <label class="routing-option" :class="{ active: selectedNode.config.dynamic_routing_mode === 'end' }">
                      <input
                        type="radio"
                        value="end"
                        v-model="selectedNode.config.dynamic_routing_mode"
                        @change="onNodePropertyChange"
                      />
                      <div class="routing-option-content">
                        <div class="routing-option-title">🏁 End Here</div>
                        <small>Connect to a final message/action node</small>
                      </div>
                    </label>

                    <label class="routing-option" :class="{ active: selectedNode.config.dynamic_routing_mode === 'conditional' }">
                      <input
                        type="radio"
                        value="conditional"
                        v-model="selectedNode.config.dynamic_routing_mode"
                        @change="onNodePropertyChange"
                      />
                      <div class="routing-option-content">
                        <div class="routing-option-title">⚡ Use Conditional Router</div>
                        <small>Route based on what was selected</small>
                      </div>
                    </label>
                  </div>
                </div>

                <!-- Auto-Generated Variable Info (always shown for dynamic buttons) -->
                <div v-if="selectedNode.config.dynamic_array_variable" class="property-group" style="background: rgba(16, 185, 129, 0.08); padding: 12px; border-radius: 8px; border: 1.5px solid rgba(16, 185, 129, 0.2);">
                  <label class="property-label" style="color: #10b981; font-weight: 600;">💾 Auto-Stored Variables After Selection</label>
                  <small class="property-hint" style="margin-bottom: 12px; display: block;">When the user picks a button, ALL fields of the selected item are saved automatically</small>

                  <div style="display: flex; flex-direction: column; gap: 8px;">
                    <div style="display: flex; align-items: center; gap: 8px; padding: 8px; background: rgba(16, 185, 129, 0.1); border-radius: 6px;">
                      <code style="font-size: 12px; color: #10b981; font-weight: 600;">{{ getAutoVarName(selectedNode, '_label') }}</code>
                      <span style="font-size: 11px; color: var(--text-secondary);">= Button text the user selected</span>
                    </div>
                    <div v-if="selectedNode.config.dynamic_routing_mode === 'conditional'" style="display: flex; align-items: center; gap: 8px; padding: 8px; background: rgba(16, 185, 129, 0.1); border-radius: 6px;">
                      <code style="font-size: 12px; color: #10b981; font-weight: 600;">{{ getAutoVarName(selectedNode, '_id') }}</code>
                      <span style="font-size: 11px; color: var(--text-secondary);">= Button ID value</span>
                    </div>
                    <div v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().objectFields" style="display: flex; flex-direction: column; gap: 4px; padding: 8px; background: rgba(16, 185, 129, 0.1); border-radius: 6px;">
                      <span style="font-size: 11px; color: #10b981; font-weight: 600;">All item fields:</span>
                      <div v-for="field in getSelectedArrayMetadata().objectFields" :key="field" style="font-size: 11px; color: var(--text-secondary); padding-left: 8px;">
                        <code style="color: #10b981;">{{ getAutoVarName(selectedNode, '_' + field) }}</code>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Fallback Message -->
                <div class="property-group">
                  <label class="property-label">Fallback Message (if array is empty)</label>
                  <input
                    v-model="selectedNode.config.empty_array_fallback.en"
                    class="property-input"
                    placeholder="No options available"
                    @input="onNodePropertyChange"
                  />
                </div>
              </div>

              <!-- Static Buttons (Only show when NOT using dynamic) -->
              <div v-if="!selectedNode.config.use_dynamic_buttons" class="property-group">
                <label class="property-label">Buttons</label>
                <small class="property-hint" style="color: #3b82f6;">
                  💡 1–3 buttons = Reply Buttons, 4+ = List Menu (auto-switches)
                </small>
                <div
                  v-for="(button, index) in selectedNode.config.buttons"
                  :key="button.id"
                  class="button-item"
                  draggable="true"
                  @dragstart="onButtonDragStart(index)"
                  @dragover.prevent="onButtonDragOver(index)"
                  @dragend="onButtonDragEnd"
                >
                  <span class="drag-handle">⠿</span>
                  <div class="button-item-fields">
                    <div class="btn-text-wrap">
                      <input
                        v-model="button.text.en"
                        :class="['property-input', { 'input-over-limit': button.text.en && button.text.en.length > (selectedNode.config.buttons.length > 3 ? 24 : 20) }]"
                        :placeholder="selectedNode.config.buttons.length > 3 ? 'Row title (max 24)' : 'Button text (max 20)'"
                        :maxlength="selectedNode.config.buttons.length > 3 ? 24 : 20"
                        @input="onNodePropertyChange"
                      />
                      <span
                        v-if="button.text.en && button.text.en.length > (selectedNode.config.buttons.length > 3 ? 24 : 20)"
                        class="btn-char-error"
                      >{{ button.text.en.length }}/{{ selectedNode.config.buttons.length > 3 ? 24 : 20 }}</span>
                    </div>
                    <input
                      v-if="selectedNode.config.buttons.length > 3"
                      v-model="button.description.en"
                      class="property-input"
                      placeholder="Description (optional, max 72)"
                      maxlength="72"
                      @input="onNodePropertyChange"
                    />
                  </div>
                  <button @click="removeButton(index)" class="btn-remove">×</button>
                </div>
                <button @click="addButton" class="btn-add">
                  + Add Button
                  <span v-if="selectedNode.config.buttons.length >= 3" style="font-size: 11px; opacity: 0.7;"> (will convert to list)</span>
                </button>
              </div>

              <!-- Auto Variable Hint -->
              <div v-if="selectedNode.name" class="property-group">
                <label class="property-label">Auto Variable <span class="field-optional">set when customer taps a button</span></label>
                <span
                  class="var-pick-chip chip-src-auto"
                  style="cursor:pointer;"
                  title="Click to copy"
                  @mousedown.prevent="copyVariableToClipboard(selectedNode.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') + '_label')"
                >{{ selectedNode.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') }}_label</span>
                <small class="property-hint" style="margin-top:4px;">Automatically holds the label of whichever button the customer tapped. No setup needed.</small>
              </div>

              <!-- Back Button Toggle -->
              <div v-if="!selectedNode.isWelcomeMessage" class="property-group">
                <label class="property-checkbox">
                  <input
                    type="checkbox"
                    v-model="selectedNode.config.show_back_button"
                    @change="onNodePropertyChange"
                  />
                  <span>⬅️ Show "Back" button</span>
                </label>
                <small class="property-hint">Adds a button to return to the previous node</small>
              </div>
            </div>

            <!-- List Selection Node Editor -->
            <div v-if="selectedNode.type === 'list_selection'" class="node-editor">
              <div class="property-group">
                <label class="property-label">Header <span class="field-optional">optional</span></label>
                <input
                  v-model="selectedNode.config.header.en"
                  class="property-input"
                  placeholder="Choose an option"
                  @input="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">Body Text</label>
                <textarea
                  v-model="selectedNode.config.body.en"
                  class="property-textarea"
                  placeholder="Please select from the list below..."
                  rows="2"
                  @input="onNodePropertyChange"
                ></textarea>
              </div>
              <div class="property-group">
                <label class="property-label">Button Text</label>
                <input
                  v-model="selectedNode.config.button_text.en"
                  class="property-input"
                  placeholder="Select"
                  @input="onNodePropertyChange"
                />
              </div>
              <div class="property-group">
                <label class="property-label">List Items {{ selectedNode.isWelcomeMessage ? '(unlimited with pagination)' : '(max 10)' }}</label>
                <small v-if="selectedNode.isWelcomeMessage" class="property-hint" style="color: #3b82f6;">
                  💡 Welcome Message: Auto pagination at 10 items. Users can tap "Show More" to load next 10.
                </small>
                <div v-for="(row, index) in selectedNode.config.rows" :key="index" class="list-row-item">
                  <div class="list-row-fields">
                    <input
                      v-model="row.title.en"
                      class="property-input"
                      placeholder="Title (max 24)"
                      maxlength="24"
                      @input="onNodePropertyChange"
                    />
                    <input
                      v-model="row.description.en"
                      class="property-input"
                      placeholder="Description (optional, max 72)"
                      maxlength="72"
                      @input="onNodePropertyChange"
                    />
                  </div>
                  <button @click="removeListRow(index)" class="btn-remove">×</button>
                </div>
                <button v-if="selectedNode.isWelcomeMessage || selectedNode.config.rows.length < 10" @click="addListRow" class="btn-add">
                  + Add List Item
                  <span v-if="selectedNode.isWelcomeMessage && selectedNode.config.rows.length >= 10" style="font-size: 11px; opacity: 0.7;"> (page {{ Math.floor(selectedNode.config.rows.length / 10) + 1 }})</span>
                </button>
              </div>
            </div>

            <!-- Condition Node Editor -->
            <div v-if="selectedNode.type === 'condition'" class="node-editor">
              <!-- Available Variables -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">📦 Available Variables</label>
                <div class="variables-list">
                  <span
                    v-for="(varInfo, varName) in flowVariables"
                    :key="varName"
                    class="variable-chip"
                    :class="'chip-src-' + (varInfo.source || 'custom')"
                    @click="copyVariableToClipboard(varName)"
                    :title="(varInfo.description ? varInfo.description + '\n' : '') + 'Click to copy'"
                  >
                    &#123;&#123;{{ varName }}&#125;&#125;
                  </span>
                </div>
                <small class="property-hint">Click a variable to copy, then paste into the variable field below</small>
              </div>

              <div class="property-group">
                <label class="property-label" style="margin-bottom: 12px; display: block;">Conditions <small style="font-weight: 400; opacity: 0.7;">(evaluated top-to-bottom, first match wins)</small></label>

                <div
                  v-for="(cond, index) in selectedNode.config.conditions"
                  :key="cond.id"
                  class="condition-rule-row"
                >
                  <div class="condition-rule-header">
                    <span class="condition-rule-badge">IF {{ index + 1 }}</span>
                    <button
                      v-if="selectedNode.config.conditions.length > 1"
                      @click="removeCondition(index)"
                      class="btn-remove-condition"
                      title="Remove condition"
                    >×</button>
                  </div>
                  <div class="condition-rule-fields">
                    <select
                      v-model="cond.variable"
                      class="property-input"
                      @change="onNodePropertyChange"
                    >
                      <option value="">— select variable —</option>
                      <template v-for="(group, srcKey) in groupedFlowVariables" :key="srcKey">
                        <optgroup v-if="Object.keys(group.vars).length > 0" :label="group.icon + ' ' + group.label">
                          <option v-for="(varInfo, varName) in group.vars" :key="varName" :value="varName">
                            {{ varName }}{{ varInfo.type ? ' (' + varInfo.type + ')' : '' }}
                          </option>
                        </optgroup>
                      </template>
                    </select>
                    <select
                      v-model="cond.operator"
                      class="property-input condition-operator-select"
                      @change="onNodePropertyChange"
                    >
                      <option value="==">== equals</option>
                      <option value="!=">!= not equals</option>
                      <option value=">">&gt; greater than</option>
                      <option value="<">&lt; less than</option>
                      <option value=">=">&gt;= greater or equal</option>
                      <option value="<=">&lt;= less or equal</option>
                      <option value="contains">contains</option>
                      <option value="not_contains">not contains</option>
                      <option value="starts_with">starts with</option>
                      <option value="ends_with">ends with</option>
                      <option value="is_empty">is empty</option>
                      <option value="is_not_empty">is not empty</option>
                    </select>
                    <input
                      v-if="!['is_empty', 'is_not_empty'].includes(cond.operator)"
                      v-model="cond.value"
                      class="property-input"
                      placeholder="value"
                      @input="onNodePropertyChange"
                    />
                  </div>
                  <div class="condition-port-hint">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity: 0.6;">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    Drag the <strong>IF {{ index + 1 }}</strong> port on the node to connect to target
                  </div>
                </div>

                <!-- Else row -->
                <div class="condition-rule-row condition-else-row">
                  <div class="condition-rule-header">
                    <span class="condition-rule-badge else-badge">ELSE</span>
                  </div>
                  <div class="condition-port-hint">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity: 0.6;">
                      <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
                    </svg>
                    Drag the <strong>Else</strong> port to the fallback node (when no conditions match)
                  </div>
                </div>

                <button @click="addCondition" class="btn-add" style="margin-top: 8px;">
                  + Add Condition
                </button>
              </div>
            </div>

            <!-- ══════════════════════════════════════════════ -->
            <!-- Google: No Account Connected Banner          -->
            <!-- ══════════════════════════════════════════════ -->
            <div
              v-if="isGoogleNodeType(selectedNode.type) && googleConnections.length === 0"
              class="node-editor google-not-connected-panel"
            >
              <div class="gnc-icon">
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <line x1="2" y1="12" x2="22" y2="12"/>
                  <path d="M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10 15.3 15.3 0 014-10z"/>
                </svg>
              </div>
              <h3>No Google Account Connected</h3>
              <p>Connect a Google account first, then come back to configure this node.</p>
              <router-link to="/settings/google" class="btn-go-connect" target="_blank">
                Open Google Integrations
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </router-link>
            </div>

            <!-- ═══════════════════════════════════════ -->
            <!-- Google Sheets: Read Rows Node Editor -->
            <!-- ═══════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'google_sheets_read' && googleConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner sheets-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
                <span>Google Sheets — Read Rows</span>
              </div>
              <div class="property-group">
                <label class="property-label">Google Account</label>
                <select v-model="selectedNode.config.google_connection_id" class="property-input" @change="onGsConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Spreadsheet</label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.spreadsheet_id" class="property-input" @change="onGsSpreadsheetChange" :disabled="!selectedNode.config.google_connection_id || gsLoadingSpreadsheets">
                    <option value="">{{ gsLoadingSpreadsheets ? 'Loading...' : (gsSpreadsheets.length ? '— select spreadsheet —' : 'Select account first') }}</option>
                    <option v-for="ss in gsSpreadsheets" :key="ss.id" :value="ss.id">{{ ss.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.google_connection_id || gsLoadingSpreadsheets" @click="loadGsSpreadsheets(selectedNode.config.google_connection_id)" title="Refresh list">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <a v-if="selectedNode.config.spreadsheet_id" :href="'https://docs.google.com/spreadsheets/d/' + selectedNode.config.spreadsheet_id + '/edit'" target="_blank" rel="noopener" class="link-open-sheet">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open in Google Sheets
                </a>
              </div>
              <div class="property-group">
                <label class="property-label">Sheet Tab</label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.sheet_name" class="property-input" @change="onGsSheetChange" :disabled="!selectedNode.config.spreadsheet_id || gsLoadingSheets">
                    <option value="">{{ gsLoadingSheets ? 'Loading...' : (gsSheetsInSpread.length ? '— select tab —' : 'Select spreadsheet first') }}</option>
                    <option v-for="s in gsSheetsInSpread" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.spreadsheet_id || gsLoadingSheets" @click="loadGsSheetsInSpread(selectedNode.config.google_connection_id, selectedNode.config.spreadsheet_id)" title="Refresh tabs">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Filter (where) <span class="field-optional">optional</span></label>
                <div class="filter-row">
                  <select v-model="selectedNode.config.filter_column" class="property-input" style="flex:1" @change="onNodePropertyChange" :disabled="!selectedNode.config.sheet_name || gsLoadingHeaders">
                    <option value="">{{ gsLoadingHeaders ? 'Loading...' : (gsColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                    <option v-for="h in gsColumnHeaders" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <select v-model="selectedNode.config.filter_operator" class="property-input" style="width:118px" @change="onNodePropertyChange">
                    <option value="==">==(equals)</option>
                    <option value="contains">contains</option>
                    <option value="starts_with">starts with</option>
                    <option value="!=">!=(not equals)</option>
                  </select>
                  <VarTextEditor v-model="selectedNode.config.filter_value" placeholder="value or {{variable}}" :single-line="true" style="flex:1" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Limit rows</label>
                <input v-model.number="selectedNode.config.limit" class="property-input" type="number" placeholder="10" min="1" max="1000" @input="onNodePropertyChange"/>
              </div>
              <div class="property-group">
                <label class="property-label">Save result as variable</label>
                <input v-model="selectedNode.config.result_variable" class="property-input" placeholder="sheet_rows" @input="onNodePropertyChange"/>
                <small class="property-hint">
                  Saves the full array as <code>{{sheet_rows}}</code> and count as <code>{{sheet_rows_count}}</code>.<br>
                  Also saves each column of the <strong>first matched row</strong> as individual variables (see below).
                </small>
              </div>
              <!-- First-row column variables preview -->
              <div v-if="selectedNode.config.result_variable && (gsColumnHeaders.length || (selectedNode.config.sheet_columns || []).length)" class="property-group">
                <div class="property-label-row">
                  <label class="property-label" style="margin:0">First row variables</label>
                  <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNode.config.sheet_name || gsLoadingHeaders" @click="loadGsColumnHeaders(selectedNode.config.google_connection_id, selectedNode.config.spreadsheet_id, selectedNode.config.sheet_name)" title="Refresh columns">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <div class="variables-list" style="margin-top:6px">
                  <span
                    v-for="col in (gsColumnHeaders.length ? gsColumnHeaders : (selectedNode.config.sheet_columns || []))"
                    :key="col"
                    class="variable-chip chip-src-google"
                    @click="copyVariableToClipboard(selectedNode.config.result_variable + '_' + sanitizeColName(col))"
                    :title="col + ' from first matched row\nClick to copy'"
                  >&#123;&#123;{{ selectedNode.config.result_variable }}_{{ sanitizeColName(col) }}&#125;&#125;</span>
                </div>
                <small class="property-hint">Click a variable to copy it. Each column of the first matched row is saved automatically.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Output Ports</label>
                <div class="condition-port-hint" style="margin-bottom:6px">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity:0.6"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Drag the <strong>✓ Found</strong> port → runs when 1 or more rows match
                </div>
                <div class="condition-port-hint">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" style="opacity:0.6"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                  Drag the <strong>✗ Not Found</strong> port → runs when no rows match
                </div>
              </div>
              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ════════════════════════════════════════ -->
            <!-- Google Sheets: Append Row Node Editor   -->
            <!-- ════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'google_sheets_append' && googleConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner sheets-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
                <span>Google Sheets — Add Row</span>
              </div>
              <div class="property-group">
                <label class="property-label">Google Account</label>
                <select v-model="selectedNode.config.google_connection_id" class="property-input" @change="onGsConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Spreadsheet</label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.spreadsheet_id" class="property-input" @change="onGsSpreadsheetChange" :disabled="!selectedNode.config.google_connection_id || gsLoadingSpreadsheets">
                    <option value="">{{ gsLoadingSpreadsheets ? 'Loading...' : (gsSpreadsheets.length ? '— select spreadsheet —' : 'Select account first') }}</option>
                    <option v-for="ss in gsSpreadsheets" :key="ss.id" :value="ss.id">{{ ss.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.google_connection_id || gsLoadingSpreadsheets" @click="loadGsSpreadsheets(selectedNode.config.google_connection_id)" title="Refresh">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <a v-if="selectedNode.config.spreadsheet_id" :href="'https://docs.google.com/spreadsheets/d/' + selectedNode.config.spreadsheet_id + '/edit'" target="_blank" rel="noopener" class="link-open-sheet">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open in Google Sheets
                </a>
              </div>
              <div class="property-group">
                <label class="property-label">Sheet Tab</label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.sheet_name" class="property-input" @change="onGsSheetChange" :disabled="!selectedNode.config.spreadsheet_id || gsLoadingSheets">
                    <option value="">{{ gsLoadingSheets ? 'Loading...' : (gsSheetsInSpread.length ? '— select tab —' : 'Select spreadsheet first') }}</option>
                    <option v-for="s in gsSheetsInSpread" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.spreadsheet_id || gsLoadingSheets" @click="loadGsSheetsInSpread(selectedNode.config.google_connection_id, selectedNode.config.spreadsheet_id)" title="Refresh">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
              </div>
              <div class="property-group">
                <div class="property-label-row">
                  <label class="property-label" style="margin:0">Row Data</label>
                  <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNode.config.sheet_name || gsLoadingHeaders" @click="loadGsColumnHeaders(selectedNode.config.google_connection_id, selectedNode.config.spreadsheet_id, selectedNode.config.sheet_name)" title="Refresh columns">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <div class="row-builder">
                  <div v-for="(item, i) in rowDataItems" :key="i" class="row-builder-item">
                    <select v-model="item.col" class="property-input row-builder-col" @change="onRowDataItemChange">
                      <option value="">{{ gsColumnHeaders.length ? '— column —' : 'column name' }}</option>
                      <option v-for="h in gsColumnHeaders" :key="h" :value="h">{{ h }}</option>
                    </select>
                    <input v-if="!gsColumnHeaders.length" v-model="item.col" class="property-input row-builder-col" placeholder="Column header" @input="onRowDataItemChange" style="display:none"/>
                    <VarTextEditor v-model="item.val" placeholder="value or {{variable}}" :single-line="true" class="row-builder-val" @focus="activeVarEditor = $event" @update:modelValue="onRowDataItemChange" />
                    <button class="btn-row-remove" @click="removeRowDataItem(i)" title="Remove">×</button>
                  </div>
                  <button class="btn-row-add" @click="addRowDataItem">+ Add Column</button>
                </div>
                <small v-if="!gsColumnHeaders.length && selectedNode.config.sheet_name" class="property-hint">No columns loaded — click ↻ to refresh.</small>
                <small v-else-if="!selectedNode.config.sheet_name" class="property-hint">Select account → spreadsheet → tab to auto-load columns.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save inserted row number as <span class="field-optional">optional</span></label>
                <input v-model="selectedNode.config.result_variable" class="property-input" placeholder="new_row_number" @input="onNodePropertyChange"/>
              </div>
              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ════════════════════════════════════════ -->
            <!-- Google Sheets: Update Row Node Editor   -->
            <!-- ════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'google_sheets_update' && googleConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner sheets-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/><line x1="3" y1="15" x2="21" y2="15"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
                <span>Google Sheets — Update Row</span>
              </div>
              <div class="property-group">
                <label class="property-label">Google Account</label>
                <select v-model="selectedNode.config.google_connection_id" class="property-input" @change="onGsConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Spreadsheet</label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.spreadsheet_id" class="property-input" @change="onGsSpreadsheetChange" :disabled="!selectedNode.config.google_connection_id || gsLoadingSpreadsheets">
                    <option value="">{{ gsLoadingSpreadsheets ? 'Loading...' : (gsSpreadsheets.length ? '— select spreadsheet —' : 'Select account first') }}</option>
                    <option v-for="ss in gsSpreadsheets" :key="ss.id" :value="ss.id">{{ ss.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.google_connection_id || gsLoadingSpreadsheets" @click="loadGsSpreadsheets(selectedNode.config.google_connection_id)" title="Refresh">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <a v-if="selectedNode.config.spreadsheet_id" :href="'https://docs.google.com/spreadsheets/d/' + selectedNode.config.spreadsheet_id + '/edit'" target="_blank" rel="noopener" class="link-open-sheet">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                  Open in Google Sheets
                </a>
              </div>
              <div class="property-group">
                <label class="property-label">Sheet Tab</label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.sheet_name" class="property-input" @change="onGsSheetChange" :disabled="!selectedNode.config.spreadsheet_id || gsLoadingSheets">
                    <option value="">{{ gsLoadingSheets ? 'Loading...' : (gsSheetsInSpread.length ? '— select tab —' : 'Select spreadsheet first') }}</option>
                    <option v-for="s in gsSheetsInSpread" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.spreadsheet_id || gsLoadingSheets" @click="loadGsSheetsInSpread(selectedNode.config.google_connection_id, selectedNode.config.spreadsheet_id)" title="Refresh">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Match Row Where</label>
                <div class="filter-row">
                  <select v-model="selectedNode.config.match_column" class="property-input" style="flex:1" @change="onNodePropertyChange" :disabled="!selectedNode.config.sheet_name || gsLoadingHeaders">
                    <option value="">{{ gsLoadingHeaders ? 'Loading...' : (gsColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                    <option v-for="h in gsColumnHeaders" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <span style="padding: 0 6px; line-height: 36px; opacity:.6; font-size:13px">==</span>
                  <VarTextEditor v-model="selectedNode.config.match_value" placeholder="{{variable}} or value" :single-line="true" style="flex:1" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                </div>
                <small class="property-hint">Updates the first row where the selected column matches the value</small>
              </div>
              <div class="property-group">
                <div class="property-label-row">
                  <label class="property-label" style="margin:0">Set Columns</label>
                  <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNode.config.sheet_name || gsLoadingHeaders" @click="loadGsColumnHeaders(selectedNode.config.google_connection_id, selectedNode.config.spreadsheet_id, selectedNode.config.sheet_name)" title="Refresh columns">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <div class="row-builder">
                  <div v-for="(item, i) in setDataItems" :key="i" class="row-builder-item">
                    <select v-model="item.col" class="property-input row-builder-col" @change="onSetDataItemChange">
                      <option value="">{{ gsColumnHeaders.length ? '— column —' : 'column name' }}</option>
                      <option v-for="h in gsColumnHeaders" :key="h" :value="h">{{ h }}</option>
                    </select>
                    <VarTextEditor v-model="item.val" placeholder="new value or {{variable}}" :single-line="true" class="row-builder-val" @focus="activeVarEditor = $event" @update:modelValue="onSetDataItemChange" />
                    <button class="btn-row-remove" @click="removeSetDataItem(i)" title="Remove">×</button>
                  </div>
                  <button class="btn-row-add" @click="addSetDataItem">+ Add Column</button>
                </div>
              </div>
              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ════════════════════════════════════════ -->
            <!-- Google Drive: Upload File Node Editor   -->
            <!-- ════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'google_drive_upload' && googleConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner drive-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                <span>Google Drive — Upload File</span>
              </div>
              <div class="property-group">
                <label class="property-label">Google Account</label>
                <select v-model="selectedNode.config.google_connection_id" class="property-input" @change="onDriveConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">File URL</label>
                <VarTextEditor v-model="selectedNode.config.file_url" placeholder="https://... or {{document_url}}" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Public URL or a flow variable containing a URL. The file will be fetched and uploaded to Drive.</small>
              </div>
              <div class="property-group">
                <label class="property-label">File Name <span class="field-optional">optional</span></label>
                <VarTextEditor v-model="selectedNode.config.file_name" placeholder="Auto-detected from URL" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Leave empty to use the filename from the URL. Override with e.g. <code>{{customer_name}}_report.pdf</code>.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Upload to Folder <span class="field-optional">optional</span></label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.folder_id" class="property-input" @change="onNodePropertyChange" :disabled="!selectedNode.config.google_connection_id || gdLoadingFolders">
                    <option value="">{{ gdLoadingFolders ? 'Loading...' : '— root of Drive —' }}</option>
                    <option v-for="f in gdFolders" :key="f.id" :value="f.id">📁 {{ f.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.google_connection_id || gdLoadingFolders" @click="loadGdFolders(selectedNode.config.google_connection_id)" title="Refresh folders">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <VarTextEditor v-model="selectedNode.config.folder_id" placeholder="or paste {{folder_id}} / folder ID" :single-line="true" style="margin-top:6px" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Pick a folder above or use a <code>{{variable}}</code> from a Create Folder node.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save result as variable <span class="field-optional">optional</span></label>
                <input v-model="selectedNode.config.result_variable" class="property-input" placeholder="uploaded_file" @input="onNodePropertyChange"/>
                <small class="property-hint">
                  Saves two variables automatically:<br>
                  <code>{{uploaded_file}}</code> — the Drive file ID<br>
                  <code>{{uploaded_file_url}}</code> — shareable link (anyone with the link can view)
                </small>
              </div>
              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════ -->
            <!-- Google Drive: Create Folder Node Editor    -->
            <!-- ═══════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'google_drive_create_folder' && googleConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner drive-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z"/></svg>
                <span>Google Drive — Create Folder</span>
              </div>
              <div class="property-group">
                <label class="property-label">Google Account</label>
                <select v-model="selectedNode.config.google_connection_id" class="property-input" @change="onDriveConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Folder Name</label>
                <VarTextEditor v-model="selectedNode.config.folder_name" placeholder="{{customer_name}} Documents" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Supports <code>{{variables}}</code> — e.g. one folder per customer.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Create inside folder <span class="field-optional">optional</span></label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.parent_folder_id" class="property-input" @change="onNodePropertyChange" :disabled="!selectedNode.config.google_connection_id || gdLoadingFolders">
                    <option value="">{{ gdLoadingFolders ? 'Loading...' : '— root of Drive —' }}</option>
                    <option v-for="f in gdFolders" :key="f.id" :value="f.id">📁 {{ f.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.google_connection_id || gdLoadingFolders" @click="loadGdFolders(selectedNode.config.google_connection_id)" title="Refresh folders">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <VarTextEditor v-model="selectedNode.config.parent_folder_id" placeholder="or paste {{parent_folder_id}} / folder ID" :single-line="true" style="margin-top:6px" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Leave empty to create in the root of Drive. Use <code>{{variable}}</code> to nest dynamically.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save new folder ID as variable <span class="field-optional">optional</span></label>
                <input v-model="selectedNode.config.result_variable" class="property-input" placeholder="new_folder_id" @input="onNodePropertyChange"/>
                <small class="property-hint">The created folder's Drive ID is stored here. Use <code>{{new_folder_id}}</code> in the next Upload File or Create Doc node to save files directly into this folder.</small>
              </div>
              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ═══════════════════════════════════════ -->
            <!-- Google Docs: Create Doc Node Editor    -->
            <!-- ═══════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'google_docs_create' && googleConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner docs-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                <span>Google Docs — Create Document</span>
              </div>
              <div class="property-group">
                <label class="property-label">Google Account</label>
                <select v-model="selectedNode.config.google_connection_id" class="property-input" @change="onDriveConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Document Title</label>
                <VarTextEditor v-model="selectedNode.config.doc_title" placeholder="{{customer_name}} — Report {{current_date}}" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Initial Content <span class="field-optional">optional</span></label>
                <VarTextEditor v-model="selectedNode.config.initial_content" placeholder="Dear {{customer_name}}, Here is your report..." :rows="5" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Plain text. Use <code>{{variable}}</code> for dynamic content.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save to folder <span class="field-optional">optional</span></label>
                <div class="gs-picker-row">
                  <select v-model="selectedNode.config.folder_id" class="property-input" @change="onNodePropertyChange" :disabled="!selectedNode.config.google_connection_id || gdLoadingFolders">
                    <option value="">{{ gdLoadingFolders ? 'Loading...' : '— root of Drive —' }}</option>
                    <option v-for="f in gdFolders" :key="f.id" :value="f.id">📁 {{ f.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.google_connection_id || gdLoadingFolders" @click="loadGdFolders(selectedNode.config.google_connection_id)" title="Refresh folders">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                  </button>
                </div>
                <VarTextEditor v-model="selectedNode.config.folder_id" placeholder="or paste {{new_folder_id}} / folder ID" :single-line="true" style="margin-top:6px" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Select a folder or use <code>{{new_folder_id}}</code> from a Create Folder node.</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save created doc ID as variable <span class="field-optional">optional</span></label>
                <input v-model="selectedNode.config.result_variable" class="property-input" placeholder="created_doc_id" @input="onNodePropertyChange"/>
                <small class="property-hint">Use <code>{{created_doc_id}}</code> in an Append Text node to add more content to this doc.</small>
              </div>
              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ════════════════════════════════════════ -->
            <!-- Google Docs: Append Text Node Editor    -->
            <!-- ════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'google_docs_append' && googleConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner docs-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                <span>Google Docs — Append Text</span>
              </div>
              <div class="property-group">
                <label class="property-label">Google Account</label>
                <select v-model="selectedNode.config.google_connection_id" class="property-input" @change="onNodePropertyChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in googleConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Document ID</label>
                <VarTextEditor v-model="selectedNode.config.doc_id" placeholder="{{created_doc_id}} or paste ID" :single-line="true" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Found in the Docs URL: /document/d/<strong>[ID]</strong>/edit</small>
              </div>
              <div class="property-group">
                <label class="property-label">Text to Append</label>
                <VarTextEditor v-model="selectedNode.config.append_text" placeholder="New section: {{customer_name}} — {{appointment_date}}" :rows="5" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Plain text. Use <code>{{variable}}</code> for dynamic content.</small>
              </div>
              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ══════════════════════════════════════════════════ -->
            <!-- Microsoft: No Account Connected Banner           -->
            <!-- ══════════════════════════════════════════════════ -->
            <div
              v-if="isMicrosoftNodeType(selectedNode.type) && microsoftConnections.length === 0"
              class="node-editor google-not-connected-panel"
            >
              <div class="gnc-icon">
                <img src="@/assets/microsoft-color.svg" style="width:36px;height:36px;opacity:.7" alt="Microsoft" />
              </div>
              <h3>No Microsoft Account Connected</h3>
              <p>Connect a Microsoft account first, then come back to configure this node.</p>
              <router-link to="/settings/microsoft" class="btn-go-connect" target="_blank">
                Open Microsoft Integrations
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
              </router-link>
            </div>

            <!-- ═══════════════════════════════════════════ -->
            <!-- Excel: Read Rows Node Editor               -->
            <!-- ═══════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'excel_read' && microsoftConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner sheets-banner">
                <img src="@/assets/excel.svg" style="width:18px;height:18px" alt="Excel" />
                <span>Excel Online — Read Rows</span>
              </div>
              <div class="property-group">
                <label class="property-label">Microsoft Account</label>
                <select v-model="selectedNode.config.microsoft_connection_id" class="property-input" @change="onMsConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Workbook (Excel file)</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.workbook_id" class="property-input" @change="onMsWorkbookChange" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingWorkbooks">
                    <option value="">{{ msLoadingWorkbooks ? 'Loading...' : (msWorkbooks.length ? '— select workbook —' : 'Select account first') }}</option>
                    <option v-for="wb in msWorkbooks" :key="wb.id" :value="wb.id">{{ wb.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingWorkbooks" @click="loadMsWorkbooks(selectedNode.config.microsoft_connection_id)" title="Refresh">↻</button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Worksheet Tab</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.worksheet_name" class="property-input" @change="onMsWorksheetChange" :disabled="!selectedNode.config.workbook_id || msLoadingWorksheets">
                    <option value="">{{ msLoadingWorksheets ? 'Loading...' : (msWorksheetsInBook.length ? '— select tab —' : 'Select workbook first') }}</option>
                    <option v-for="s in msWorksheetsInBook" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.workbook_id || msLoadingWorksheets" @click="loadMsWorksheetsInBook(selectedNode.config.microsoft_connection_id, selectedNode.config.workbook_id)" title="Refresh">↻</button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Filter <small style="opacity:.6">(optional)</small></label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.filter_column" class="property-input" style="flex:1" @change="onNodePropertyChange" :disabled="!selectedNode.config.worksheet_name || msLoadingHeaders">
                    <option value="">{{ msLoadingHeaders ? 'Loading...' : (msColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                    <option v-for="h in msColumnHeaders" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <select v-model="selectedNode.config.filter_operator" class="property-input" style="width:130px" @change="onNodePropertyChange">
                    <option value="==">equals</option>
                    <option value="contains">contains</option>
                    <option value="starts_with">starts with</option>
                    <option value="!=">not equals</option>
                  </select>
                </div>
                <VarTextEditor v-if="selectedNode.config.filter_column" v-model="selectedNode.config.filter_value" placeholder="value or {{variable}}" :rows="1" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" style="margin-top:6px" />
              </div>
              <div class="property-group">
                <label class="property-label">Limit rows</label>
                <input type="number" v-model.number="selectedNode.config.limit" class="property-input" min="1" max="1000" @change="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Save result as variable</label>
                <input type="text" v-model="selectedNode.config.result_variable" class="property-input" placeholder="excel_rows" @input="onNodePropertyChange" />
              </div>
              <div v-if="selectedNode.config.result_variable" class="property-group">
                <label class="property-label">Output Ports</label>
                <div class="gs-ports-info">
                  <span class="port-pill port-found">✓ Found — runs when 1+ rows match</span>
                  <span class="port-pill port-notfound">✗ Not Found — runs when no rows match</span>
                </div>
              </div>
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════ -->
            <!-- Excel: Append Row Node Editor              -->
            <!-- ═══════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'excel_append' && microsoftConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner sheets-banner">
                <img src="@/assets/excel.svg" style="width:18px;height:18px" alt="Excel" />
                <span>Excel Online — Add Row</span>
              </div>
              <div class="property-group">
                <label class="property-label">Microsoft Account</label>
                <select v-model="selectedNode.config.microsoft_connection_id" class="property-input" @change="onMsConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Workbook</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.workbook_id" class="property-input" @change="onMsWorkbookChange" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingWorkbooks">
                    <option value="">{{ msLoadingWorkbooks ? 'Loading...' : (msWorkbooks.length ? '— select workbook —' : 'Select account first') }}</option>
                    <option v-for="wb in msWorkbooks" :key="wb.id" :value="wb.id">{{ wb.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingWorkbooks" @click="loadMsWorkbooks(selectedNode.config.microsoft_connection_id)" title="Refresh">↻</button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Worksheet Tab</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.worksheet_name" class="property-input" @change="onMsWorksheetChange" :disabled="!selectedNode.config.workbook_id || msLoadingWorksheets">
                    <option value="">{{ msLoadingWorksheets ? 'Loading...' : (msWorksheetsInBook.length ? '— select tab —' : 'Select workbook first') }}</option>
                    <option v-for="s in msWorksheetsInBook" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.workbook_id || msLoadingWorksheets" @click="loadMsWorksheetsInBook(selectedNode.config.microsoft_connection_id, selectedNode.config.workbook_id)" title="Refresh">↻</button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Row Data</label>
                <div style="display:flex;gap:4px;align-items:center;margin-bottom:4px">
                  <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNode.config.worksheet_name || msLoadingHeaders" @click="loadMsColumnHeaders(selectedNode.config.microsoft_connection_id, selectedNode.config.workbook_id, selectedNode.config.worksheet_name)" title="Refresh columns">↻</button>
                  <small style="opacity:.6">Refresh to load column names from Excel</small>
                </div>
                <div v-for="(item, idx) in msRowDataItems" :key="idx" style="display:flex;gap:6px;margin-bottom:6px;align-items:center">
                  <select v-model="item.col" class="property-input" style="flex:1" @change="syncMsRowData('append')">
                    <option value="">{{ msColumnHeaders.length ? '— column —' : 'column name' }}</option>
                    <option v-for="h in msColumnHeaders" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <VarTextEditor v-model="item.val" placeholder="value or {{variable}}" :rows="1" @focus="activeVarEditor = $event" @update:modelValue="syncMsRowData('append')" style="flex:2" />
                  <button class="btn-remove-row" @click="msRowDataItems.splice(idx, 1); syncMsRowData('append')">✕</button>
                </div>
                <button class="btn-add-row" @click="msRowDataItems.push({col:'',val:''}); syncMsRowData('append')">+ Add Column</button>
              </div>
              <div class="property-group">
                <label class="property-label">Save inserted row number as <small style="opacity:.6">(optional)</small></label>
                <input type="text" v-model="selectedNode.config.result_variable" class="property-input" placeholder="e.g. new_row_num" @input="onNodePropertyChange" />
              </div>
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════ -->
            <!-- Excel: Update Row Node Editor              -->
            <!-- ═══════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'excel_update' && microsoftConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner sheets-banner">
                <img src="@/assets/excel.svg" style="width:18px;height:18px" alt="Excel" />
                <span>Excel Online — Update Row</span>
              </div>
              <div class="property-group">
                <label class="property-label">Microsoft Account</label>
                <select v-model="selectedNode.config.microsoft_connection_id" class="property-input" @change="onMsConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Workbook</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.workbook_id" class="property-input" @change="onMsWorkbookChange" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingWorkbooks">
                    <option value="">{{ msLoadingWorkbooks ? 'Loading...' : (msWorkbooks.length ? '— select workbook —' : 'Select account first') }}</option>
                    <option v-for="wb in msWorkbooks" :key="wb.id" :value="wb.id">{{ wb.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingWorkbooks" @click="loadMsWorkbooks(selectedNode.config.microsoft_connection_id)" title="Refresh">↻</button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Worksheet Tab</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.worksheet_name" class="property-input" @change="onMsWorksheetChange" :disabled="!selectedNode.config.workbook_id || msLoadingWorksheets">
                    <option value="">{{ msLoadingWorksheets ? 'Loading...' : (msWorksheetsInBook.length ? '— select tab —' : 'Select workbook first') }}</option>
                    <option v-for="s in msWorksheetsInBook" :key="s" :value="s">{{ s }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.workbook_id || msLoadingWorksheets" @click="loadMsWorksheetsInBook(selectedNode.config.microsoft_connection_id, selectedNode.config.workbook_id)" title="Refresh">↻</button>
                </div>
              </div>
              <div class="property-group">
                <label class="property-label">Match Row Where</label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.match_column" class="property-input" style="flex:1" @change="onNodePropertyChange" :disabled="!selectedNode.config.worksheet_name || msLoadingHeaders">
                    <option value="">{{ msLoadingHeaders ? 'Loading...' : (msColumnHeaders.length ? '— column —' : 'Select tab first') }}</option>
                    <option v-for="h in msColumnHeaders" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <span style="white-space:nowrap;opacity:.6;font-size:12px">==</span>
                  <VarTextEditor v-model="selectedNode.config.match_value" placeholder="value or {{variable}}" :rows="1" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" style="flex:2" />
                </div>
                <small class="property-hint">Updates the first row where the column matches the value</small>
              </div>
              <div class="property-group">
                <label class="property-label">Set Columns</label>
                <div style="display:flex;gap:4px;align-items:center;margin-bottom:4px">
                  <button class="btn-gs-refresh" style="width:26px;height:26px" :disabled="!selectedNode.config.worksheet_name || msLoadingHeaders" @click="loadMsColumnHeaders(selectedNode.config.microsoft_connection_id, selectedNode.config.workbook_id, selectedNode.config.worksheet_name)" title="Refresh columns">↻</button>
                  <small style="opacity:.6">Refresh to load column names</small>
                </div>
                <div v-for="(item, idx) in msSetDataItems" :key="idx" style="display:flex;gap:6px;margin-bottom:6px;align-items:center">
                  <select v-model="item.col" class="property-input" style="flex:1" @change="syncMsRowData('update')">
                    <option value="">{{ msColumnHeaders.length ? '— column —' : 'column name' }}</option>
                    <option v-for="h in msColumnHeaders" :key="h" :value="h">{{ h }}</option>
                  </select>
                  <VarTextEditor v-model="item.val" placeholder="new value or {{variable}}" :rows="1" @focus="activeVarEditor = $event" @update:modelValue="syncMsRowData('update')" style="flex:2" />
                  <button class="btn-remove-row" @click="msSetDataItems.splice(idx, 1); syncMsRowData('update')">✕</button>
                </div>
                <button class="btn-add-row" @click="msSetDataItems.push({col:'',val:''}); syncMsRowData('update')">+ Add Column</button>
              </div>
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ═══════════════════════════════════════════ -->
            <!-- OneDrive: Upload File Node Editor          -->
            <!-- ═══════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'onedrive_upload' && microsoftConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner drive-banner">
                <img src="@/assets/onedrive.svg" style="width:18px;height:18px" alt="OneDrive" />
                <span>OneDrive — Upload File</span>
              </div>
              <div class="property-group">
                <label class="property-label">Microsoft Account</label>
                <select v-model="selectedNode.config.microsoft_connection_id" class="property-input" @change="onMsDriveConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">File URL</label>
                <VarTextEditor v-model="selectedNode.config.file_url" placeholder="https://... or {{file_url_variable}}" :rows="1" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Filename Prefix <small style="opacity:.6">(optional)</small></label>
                <VarTextEditor v-model="selectedNode.config.file_name" placeholder="e.g. {{customer_name}}_" :rows="1" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
                <small class="property-hint">Prepended to the filename from the URL. E.g. <code>invoice_</code> + <code>doc.pdf</code> → <code>invoice_doc.pdf</code></small>
              </div>
              <div class="property-group">
                <label class="property-label">Folder ID <small style="opacity:.6">(optional)</small></label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.folder_id" class="property-input" @change="onNodePropertyChange" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingDriveFolders">
                    <option value="">{{ msLoadingDriveFolders ? 'Loading...' : '— root / select folder —' }}</option>
                    <option v-for="f in msDriveFolders" :key="f.id" :value="f.id">{{ f.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingDriveFolders" @click="loadMsDriveFolders(selectedNode.config.microsoft_connection_id)" title="Refresh">↻</button>
                </div>
                <small class="property-hint">Leave blank to upload to OneDrive root</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save result as variable</label>
                <input type="text" v-model="selectedNode.config.result_variable" class="property-input" placeholder="uploaded_file_id" @input="onNodePropertyChange" />
                <small class="property-hint">Exposes <code>&#123;&#123;{{ selectedNode.config.result_variable || 'uploaded_file_id' }}&#125;&#125;</code> (file ID) and <code>&#123;&#123;{{ selectedNode.config.result_variable || 'uploaded_file_id' }}_url&#125;&#125;</code> (share link)</small>
              </div>
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ══════════════════════════════════════════════ -->
            <!-- OneDrive: Create Folder Node Editor           -->
            <!-- ══════════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'onedrive_create_folder' && microsoftConnections.length > 0" class="node-editor node-editor-google">
              <div class="google-node-banner drive-banner">
                <img src="@/assets/onedrive.svg" style="width:18px;height:18px" alt="OneDrive" />
                <span>OneDrive — Create Folder</span>
              </div>
              <div class="property-group">
                <label class="property-label">Microsoft Account</label>
                <select v-model="selectedNode.config.microsoft_connection_id" class="property-input" @change="onMsDriveConnectionChange">
                  <option value="">— select connected account —</option>
                  <option v-for="conn in microsoftConnections" :key="conn.id" :value="conn.id">{{ conn.email }}</option>
                </select>
              </div>
              <div class="property-group">
                <label class="property-label">Folder Name</label>
                <VarTextEditor v-model="selectedNode.config.folder_name" placeholder="Reports — {{month}}" :rows="1" @focus="activeVarEditor = $event" @update:modelValue="onNodePropertyChange" />
              </div>
              <div class="property-group">
                <label class="property-label">Parent Folder ID <small style="opacity:.6">(optional)</small></label>
                <div style="display:flex;gap:6px;align-items:center">
                  <select v-model="selectedNode.config.parent_folder_id" class="property-input" @change="onNodePropertyChange" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingDriveFolders">
                    <option value="">{{ msLoadingDriveFolders ? 'Loading...' : '— root / select folder —' }}</option>
                    <option v-for="f in msDriveFolders" :key="f.id" :value="f.id">{{ f.name }}</option>
                  </select>
                  <button class="btn-gs-refresh" :disabled="!selectedNode.config.microsoft_connection_id || msLoadingDriveFolders" @click="loadMsDriveFolders(selectedNode.config.microsoft_connection_id)" title="Refresh">↻</button>
                </div>
                <small class="property-hint">Leave blank to create in OneDrive root</small>
              </div>
              <div class="property-group">
                <label class="property-label">Save result as variable</label>
                <input type="text" v-model="selectedNode.config.result_variable" class="property-input" placeholder="new_folder_id" @input="onNodePropertyChange" />
              </div>
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- ════════════════════════════════════════ -->
            <!-- Advertiser Node Editor                 -->
            <!-- ════════════════════════════════════════ -->
            <div v-if="selectedNode.type === 'advertiser'" class="node-editor node-editor-advertiser">
              <div class="advertiser-node-banner">
                <span class="advertiser-banner-icon">📢</span>
                <div>
                  <strong>Advertiser</strong>
                  <small>Pick one connected message to send per visit</small>
                </div>
              </div>

              <!-- Mode selector -->
              <div class="property-group">
                <label class="property-label">Selection Mode</label>
                <div class="adv-mode-pills">
                  <button
                    v-for="modeOpt in [{value:'random',icon:'🎲',label:'Random'},{value:'sequential',icon:'🔁',label:'Sequential'},{value:'weighted',icon:'⚖️',label:'Weighted'}]"
                    :key="modeOpt.value"
                    type="button"
                    class="adv-mode-pill"
                    :class="{'adv-mode-pill--active': (selectedNode.config.mode || 'random') === modeOpt.value}"
                    @click="selectedNode.config.mode = modeOpt.value; onNodePropertyChange()"
                  >{{ modeOpt.icon }} {{ modeOpt.label }}</button>
                </div>
                <small v-if="(selectedNode.config.mode || 'random') === 'random'" class="property-hint">Picks a random connected message node each time.</small>
                <small v-else-if="selectedNode.config.mode === 'sequential'" class="property-hint">Cycles through connected nodes in order (1 → 2 → 3 → …).</small>
                <small v-else class="property-hint">Sends each node proportionally to its weight.</small>
              </div>

              <!-- Slot count -->
              <div class="property-group">
                <label class="property-label">Number of Message Slots</label>
                <div class="adv-count-row">
                  <button
                    v-for="n in [2,3,4,5,6,7,8]" :key="n"
                    type="button"
                    class="adv-count-btn"
                    :class="{'adv-count-btn--active': (selectedNode.config.count || 2) === n}"
                    @click="setAdvSlotCount(selectedNode, n)"
                  >{{ n }}</button>
                </div>
                <small class="property-hint">Drag from the bottom ports to any message node on the canvas.</small>
              </div>

              <!-- Slot cards -->
              <div class="property-group">
                <label class="property-label">Slots</label>
                <div class="adv-slots-list">
                  <div
                    v-for="i in (selectedNode.config.count || 2)"
                    :key="`slot-${i}`"
                    class="adv-slot-card"
                    :class="{ 'adv-slot-connected': !!(selectedNode.config.message_nodes || [])[i-1] }"
                  >
                    <!-- Slot header row -->
                    <div class="adv-slot-header">
                      <span class="adv-slot-num">{{ i }}</span>
                      <span class="adv-slot-name">{{ getAdvSlotLabel(selectedNode, i-1) }}</span>

                      <!-- Weight (weighted mode only) -->
                      <template v-if="selectedNode.config.mode === 'weighted'">
                        <input
                          type="number" min="1" max="999"
                          class="adv-weight-input"
                          :value="(selectedNode.config.weights || [])[i-1] || 1"
                          @input="setAdvWeight(selectedNode, i-1, $event.target.value)"
                          title="Weight"
                        />
                        <span class="adv-weight-pct">{{ getAdvWeightPct(selectedNode, i-1) }}%</span>
                      </template>

                      <!-- Active targeting badges -->
                      <span v-if="getAdvSlotBadges(selectedNode, i-1)" class="adv-targeting-badges">{{ getAdvSlotBadges(selectedNode, i-1) }}</span>

                      <button
                        type="button"
                        class="adv-target-btn"
                        :class="{ 'adv-target-btn--active': expandedAdvSlots[selectedNode.id + '_' + (i-1)] }"
                        @click="expandedAdvSlots = { ...expandedAdvSlots, [selectedNode.id + '_' + (i-1)]: !expandedAdvSlots[selectedNode.id + '_' + (i-1)] }"
                        title="Targeting settings"
                      >🎯</button>
                      <button
                        v-if="!!(selectedNode.config.message_nodes || [])[i-1]"
                        type="button" class="adv-slot-clear"
                        @click="clearAdvSlot(selectedNode, i-1)" title="Disconnect"
                      >✕</button>
                    </div>

                    <!-- Targeting settings (expandable) -->
                    <div v-show="expandedAdvSlots[selectedNode.id + '_' + (i-1)]" class="adv-slot-targeting">

                      <!-- Country targeting -->
                      <div class="adv-target-section">
                        <div class="adv-target-label">🌍 Show only to</div>
                        <div class="adv-country-grid">
                          <button
                            v-for="c in advCountries" :key="c.code"
                            type="button"
                            class="adv-country-chip"
                            :class="{ 'adv-country-chip--on': (ensureAdvSlot(selectedNode, i-1).countries || []).includes(c.code) }"
                            @click="toggleAdvCountry(selectedNode, i-1, c.code)"
                          >{{ c.flag }} {{ c.name }}</button>
                        </div>
                        <small class="adv-target-hint">{{ (ensureAdvSlot(selectedNode, i-1).countries || []).length === 0 ? 'All countries' : (ensureAdvSlot(selectedNode, i-1).countries || []).length + ' countr' + ((ensureAdvSlot(selectedNode, i-1).countries || []).length === 1 ? 'y' : 'ies') + ' selected' }}</small>
                      </div>

                      <!-- Time range -->
                      <div class="adv-target-section">
                        <div class="adv-target-label">⏰ Active hours</div>
                        <div class="adv-time-row">
                          <span class="adv-time-label">From</span>
                          <input
                            type="time"
                            class="adv-time-input"
                            :value="ensureAdvSlot(selectedNode, i-1).time_from || ''"
                            @change="ensureAdvSlot(selectedNode, i-1).time_from = $event.target.value; onNodePropertyChange()"
                          />
                          <span class="adv-time-label">To</span>
                          <input
                            type="time"
                            class="adv-time-input"
                            :value="ensureAdvSlot(selectedNode, i-1).time_to || ''"
                            @change="ensureAdvSlot(selectedNode, i-1).time_to = $event.target.value; onNodePropertyChange()"
                          />
                          <button
                            v-if="ensureAdvSlot(selectedNode, i-1).time_from || ensureAdvSlot(selectedNode, i-1).time_to"
                            type="button" class="adv-clear-time"
                            @click="ensureAdvSlot(selectedNode, i-1).time_from = ''; ensureAdvSlot(selectedNode, i-1).time_to = ''; onNodePropertyChange()"
                          >✕</button>
                        </div>
                        <small class="adv-target-hint">Leave empty to show at any time. Overnight ranges work (e.g. 22:00 → 06:00).</small>
                      </div>

                      <!-- Frequency cap -->
                      <div class="adv-target-section">
                        <div class="adv-target-label">🔁 Frequency cap</div>
                        <div class="adv-freq-row">
                          <span class="adv-time-label">Max</span>
                          <input
                            type="number" min="0" max="9999"
                            class="adv-freq-input"
                            :value="ensureAdvSlot(selectedNode, i-1).freq_cap || 0"
                            @input="ensureAdvSlot(selectedNode, i-1).freq_cap = parseInt($event.target.value) || 0; onNodePropertyChange()"
                          />
                          <span class="adv-time-label">times per</span>
                          <select
                            class="adv-freq-select"
                            :value="ensureAdvSlot(selectedNode, i-1).freq_period || 'lifetime'"
                            @change="ensureAdvSlot(selectedNode, i-1).freq_period = $event.target.value; onNodePropertyChange()"
                          >
                            <option value="day">Day</option>
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                            <option value="lifetime">Lifetime</option>
                          </select>
                        </div>
                        <small class="adv-target-hint">{{ (ensureAdvSlot(selectedNode, i-1).freq_cap || 0) === 0 ? 'Unlimited — no cap applied.' : 'Customer sees this ad max ' + ensureAdvSlot(selectedNode, i-1).freq_cap + 'x per ' + (ensureAdvSlot(selectedNode, i-1).freq_period || 'lifetime') + '.' }}</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Result variable -->
              <div class="property-group">
                <label class="property-label">Result Variable <span class="field-optional">(optional)</span></label>
                <input
                  v-model="selectedNode.config.result_variable"
                  class="property-input"
                  placeholder="e.g. chosen_ad_slot"
                  @input="onNodePropertyChange"
                />
                <small class="property-hint">Saves the chosen slot number (1-based) to this variable.</small>
              </div>

              <!-- Variables helper -->
              <div v-if="Object.keys(flowVariables).length > 0" class="property-group variables-helper">
                <label class="property-label">Available Variables</label>
                <div class="variables-list">
                  <span v-for="(varInfo, varName) in flowVariables" :key="varName" class="var-pick-chip" :class="'chip-src-' + (varInfo.source || 'custom')" @mousedown.prevent="insertVariableAtCursor(varName)" :title="varInfo.description">{{ varName }}</span>
                </div>
              </div>
            </div>

            <!-- WhatsApp Preview -->
            <div v-if="selectedNode && !['api_call', 'image_input', 'google_sheets_read', 'google_sheets_append', 'google_sheets_update', 'google_drive_upload', 'google_drive_create_folder', 'google_docs_create', 'google_docs_append', 'excel_read', 'excel_append', 'excel_update', 'onedrive_upload', 'onedrive_create_folder', 'advertiser', 'qr_generator', 'calculator'].includes(selectedNode.type)" class="whatsapp-preview-section">
              <div class="preview-header">
                <h4>WhatsApp Preview</h4>
              </div>
              <div class="whatsapp-mockup">
                <div class="whatsapp-message">
                  <!-- Message Node Preview -->
                  <div v-if="selectedNode.type === 'message'" class="wa-bubble">
                    <p v-if="selectedNode.config.header?.en || selectedNode.config.header?.ar" class="wa-header">
                      {{ selectedNode.config.header.en || selectedNode.config.header.ar }}
                    </p>
                    <p>{{ selectedNode.config.text.en || selectedNode.config.text.ar || 'Empty message' }}</p>
                    <p v-if="selectedNode.config.footer?.en || selectedNode.config.footer?.ar" class="wa-footer">
                      {{ selectedNode.config.footer.en || selectedNode.config.footer.ar }}
                    </p>
                  </div>

                  <!-- List Message Node Preview -->
                  <div v-if="selectedNode.type === 'list_message'" class="wa-bubble">
                    <p v-if="selectedNode.config.header?.en" class="wa-header">{{ selectedNode.config.header.en }}</p>
                    <div v-if="selectedNode.config.item_template?.en" class="list-msg-preview-lines">
                      <div class="list-preview-item">{{ selectedNode.config.item_template.en }}</div>
                      <div class="list-preview-item list-preview-item--dim">{{ selectedNode.config.item_template.en }}</div>
                      <div class="list-preview-item list-preview-item--dimmer">{{ selectedNode.config.item_template.en }}</div>
                      <div class="list-preview-more">⋯ repeated for each {{ selectedNode.config.array_variable || 'array' }} item</div>
                    </div>
                    <p v-else class="preview-placeholder">Set item template above to preview</p>
                    <p v-if="selectedNode.config.footer?.en" class="wa-footer">{{ selectedNode.config.footer.en }}</p>
                  </div>

                  <!-- Image Message Preview -->
                  <div v-if="selectedNode.type === 'image_message'" class="wa-media-bubble">
                    <div class="wa-image-container">
                      <img
                        v-if="selectedNode.config.image_url"
                        :src="selectedNode.config.image_url"
                        class="wa-image"
                        @error="onImageError"
                        alt="Preview"
                      />
                      <div v-else class="wa-image-placeholder">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke-width="2"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                          <polyline points="21 15 16 10 5 21" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <p>Enter image URL</p>
                      </div>
                      <div v-if="selectedNode.config.caption?.en || selectedNode.config.caption?.ar" class="wa-media-caption">
                        <p>{{ selectedNode.config.caption.en || selectedNode.config.caption.ar }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Document Message Preview -->
                  <div v-if="selectedNode.type === 'document_message'" class="wa-media-bubble">
                    <div class="wa-document-container">
                      <div class="wa-document-row">
                        <div class="wa-document-icon">
                          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                          </svg>
                        </div>
                        <div class="wa-document-info">
                          <p v-if="selectedNode.config.document_url" class="wa-document-name">
                            {{ selectedNode.config.document_url.split('/').pop() || 'document.pdf' }}
                          </p>
                          <p v-else class="wa-document-placeholder">No document URL set</p>
                        </div>
                      </div>
                      <div v-if="selectedNode.config.caption?.en || selectedNode.config.caption?.ar" class="wa-media-caption">
                        <p>{{ selectedNode.config.caption.en || selectedNode.config.caption.ar }}</p>
                      </div>
                    </div>
                  </div>

                  <!-- Text with Buttons Preview -->
                  <div v-if="selectedNode.type === 'text_with_buttons'">
                    <div class="wa-bubble">
                      <p v-if="selectedNode.config.header?.en || selectedNode.config.header?.ar" class="wa-header">
                        {{ selectedNode.config.header.en || selectedNode.config.header.ar }}
                      </p>
                      <p>{{ selectedNode.config.text.en || selectedNode.config.text.ar || 'Your message here...' }}</p>
                      <p v-if="selectedNode.config.footer?.en || selectedNode.config.footer?.ar" class="wa-footer">
                        {{ selectedNode.config.footer.en || selectedNode.config.footer.ar }}
                      </p>
                    </div>
                    <!-- Static Buttons -->
                    <div v-if="!selectedNode.config.use_dynamic_buttons && selectedNode.config.buttons.length > 0" class="wa-buttons">
                      <button v-for="(btn, idx) in selectedNode.config.buttons" :key="idx" class="wa-button">
                        {{ btn.text.en || btn.text.ar || `Button ${idx + 1}` }}
                      </button>
                    </div>
                    <!-- Dynamic Buttons Preview -->
                    <div v-if="selectedNode.config.use_dynamic_buttons" class="wa-buttons">
                      <div v-if="selectedNode.config.dynamic_array_variable" class="dynamic-buttons-preview">
                        <small style="color: #00a884; font-size: 10px; display: block; margin-bottom: 4px;">
                          🔄 Dynamic from: {{ selectedNode.config.dynamic_array_variable }}
                        </small>
                        <!-- Show actual button values if available -->
                        <template v-if="getDynamicButtonsPreview().length > 0">
                          <button v-for="(btn, idx) in getDynamicButtonsPreview()" :key="idx" class="wa-button">
                            {{ btn.text }}
                          </button>
                          <small v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayLength > 3" style="color: #8696a0; font-size: 10px;">
                            ... and {{ getSelectedArrayMetadata().arrayLength - 3 }} more
                          </small>
                        </template>
                        <!-- Fallback to placeholder if no data yet -->
                        <template v-else>
                          <button v-for="idx in 3" :key="idx" class="wa-button" style="opacity: 0.5;">
                            {{ selectedNode.config.button_text_field ? '{item.' + selectedNode.config.button_text_field + '}' : 'Item ' + idx }}
                          </button>
                          <small style="color: #f59e0b; font-size: 10px;">⚠️ Save array variable to see preview</small>
                        </template>
                      </div>
                      <div v-else class="wa-preview-hint">
                        <small style="color: #f59e0b;">⚠️ Select an array variable</small>
                      </div>
                    </div>
                    <!-- Back Button -->
                    <div v-if="selectedNode.config.show_back_button && !selectedNode.isWelcomeMessage" class="wa-buttons" style="margin-top: 8px;">
                      <button class="wa-button" style="opacity: 0.6;">⬅️ Back</button>
                    </div>
                  </div>

                  <!-- Button Selection Preview -->
                  <div v-if="selectedNode.type === 'button_selection'">
                    <div class="wa-bubble">
                      <p>{{ selectedNode.config.body.en || selectedNode.config.body.ar || 'Choose an option' }}</p>
                    </div>
                    <!-- Static Buttons -->
                    <div v-if="!selectedNode.config.use_dynamic_buttons && selectedNode.config.buttons.length > 0" class="wa-buttons">
                      <button v-for="(btn, idx) in selectedNode.config.buttons" :key="idx" class="wa-button">
                        {{ btn.text.en || btn.text.ar || `Button ${idx + 1}` }}
                      </button>
                    </div>
                    <!-- Dynamic Buttons Preview -->
                    <div v-if="selectedNode.config.use_dynamic_buttons" class="wa-buttons">
                      <div v-if="selectedNode.config.dynamic_array_variable" class="dynamic-buttons-preview">
                        <small style="color: #00a884; font-size: 10px; display: block; margin-bottom: 4px;">
                          🔄 Dynamic from: {{ selectedNode.config.dynamic_array_variable }}
                        </small>
                        <!-- Show actual button values if available -->
                        <template v-if="getDynamicButtonsPreview().length > 0">
                          <button v-for="(btn, idx) in getDynamicButtonsPreview()" :key="idx" class="wa-button">
                            {{ btn.text }}
                          </button>
                          <small v-if="getSelectedArrayMetadata() && getSelectedArrayMetadata().arrayLength > 3" style="color: #8696a0; font-size: 10px;">
                            ... and {{ getSelectedArrayMetadata().arrayLength - 3 }} more
                          </small>
                        </template>
                        <!-- Fallback to placeholder if no data yet -->
                        <template v-else>
                          <button v-for="idx in 3" :key="idx" class="wa-button" style="opacity: 0.5;">
                            {{ selectedNode.config.button_text_field ? '{item.' + selectedNode.config.button_text_field + '}' : 'Item ' + idx }}
                          </button>
                          <small style="color: #f59e0b; font-size: 10px;">⚠️ Save array variable to see preview</small>
                        </template>
                      </div>
                      <div v-else class="wa-preview-hint">
                        <small style="color: #f59e0b;">⚠️ Select an array variable</small>
                      </div>
                    </div>
                    <!-- Back Button -->
                    <div v-if="selectedNode.config.show_back_button && !selectedNode.isWelcomeMessage" class="wa-buttons" style="margin-top: 8px;">
                      <button class="wa-button" style="opacity: 0.6;">⬅️ Back</button>
                    </div>
                  </div>

                  <!-- List Selection Preview -->
                  <div v-if="selectedNode.type === 'list_selection'">
                    <div class="wa-bubble">
                      <p v-if="selectedNode.config.header.en || selectedNode.config.header.ar" class="wa-header">
                        {{ selectedNode.config.header.en || selectedNode.config.header.ar }}
                      </p>
                      <p>{{ selectedNode.config.body.en || selectedNode.config.body.ar || 'Select from list...' }}</p>
                    </div>
                    <div class="wa-list-button">
                      <button class="wa-button wa-list-btn">
                        📋 {{ selectedNode.config.button_text.en || selectedNode.config.button_text.ar || 'Select' }}
                      </button>
                    </div>
                    <div v-if="selectedNode.config.rows.length > 0" class="wa-list-preview">
                      <div v-for="(row, idx) in selectedNode.config.rows" :key="idx" class="wa-list-item">
                        <div class="wa-list-title">{{ row.title.en || row.title.ar || `Item ${idx + 1}` }}</div>
                        <div v-if="row.description.en || row.description.ar" class="wa-list-desc">
                          {{ row.description.en || row.description.ar }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Text Input Preview - Shows as simple message since WhatsApp doesn't support input fields -->
                  <div v-if="selectedNode.type === 'text_input'" class="wa-bubble">
                    <p>{{ selectedNode.config.prompt.en || selectedNode.config.prompt.ar || 'Enter your response...' }}</p>
                    <small style="color: #00a884;">💬 User can reply with text</small>
                  </div>

                  <!-- Date Picker Preview -->
                  <div v-if="selectedNode.type === 'date_selection'" class="wa-bubble">
                    <p>{{ selectedNode.config.prompt?.en || selectedNode.config.prompt?.ar || 'Please choose an available date...' }}</p>
                    <small style="color: #00a884;">📅 Shows list of dates ({{ selectedNode.config.max_past_days || 0 }} past · {{ selectedNode.config.max_future_days || 7 }} future days)</small>
                  </div>

                  <!-- Time Picker Preview -->
                  <div v-if="selectedNode.type === 'time_selection'" class="wa-bubble">
                    <p>{{ selectedNode.config.prompt?.en || selectedNode.config.prompt?.ar || 'Please enter a time...' }}</p>
                    <small style="color: #00a884;">⏰ User replies with a time ({{ selectedNode.config.time_format === '24h' ? '14:30' : '2:30 PM' }})</small>
                  </div>

                  <!-- Location Input Preview -->
                  <div v-if="selectedNode.type === 'location_input'" class="wa-bubble">
                    <p>{{ selectedNode.config.prompt?.en || selectedNode.config.prompt?.ar || '📍 Please share your location' }}</p>
                    <small style="color: #00a884;">📍 Customer shares their live location</small>
                  </div>

                  <!-- Action Node Preview -->
                  <div v-if="selectedNode.type === 'action'" class="wa-bubble">
                    <p>{{ selectedNode.config.success_message.en || selectedNode.config.success_message.ar || 'Thank you!' }}</p>
                  </div>

                  <!-- Generic Preview for other types -->
                  <div v-if="!['message', 'image_message', 'text_with_buttons', 'button_selection', 'list_selection', 'text_input', 'date_selection', 'time_selection', 'location_input', 'image_input', 'action', 'restart_flow', 'google_sheets_read', 'google_sheets_append', 'google_sheets_update', 'google_drive_upload', 'google_drive_create_folder', 'google_docs_create', 'google_docs_append', 'excel_read', 'excel_append', 'excel_update', 'onedrive_upload', 'onedrive_create_folder'].includes(selectedNode.type)" class="wa-bubble wa-generic">
                    <p>{{ getNodeTypeName(selectedNode.type) }}</p>
                    <small>{{ getNodePreview(selectedNode) }}</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add/Edit Variable Modal -->
    <div v-if="showVariableModal" class="modal-overlay variable-modal-overlay" @click="closeVariableModal" style="background: rgba(0, 0, 0, 0.7) !important; backdrop-filter: blur(2px);">
      <div class="modal-content variable-modal" @click.stop style="background: var(--card-background, #1f2937) !important;">
        <div class="modal-header" style="background: var(--card-background, #1f2937);">
          <h2>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 8px;">
              <path d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ editingVariableName ? 'Edit Variable' : 'Add Custom Variable' }}
          </h2>
          <button @click="closeVariableModal" class="btn-close">×</button>
        </div>
        <div class="modal-body" style="background: var(--card-background, #1f2937);">
          <div class="form-group">
            <label for="varName">Variable Name</label>
            <input
              id="varName"
              v-model="newVariableName"
              type="text"
              placeholder="e.g., api_key, user_name, customer_id"
              class="form-input"
              @keyup.enter="addCustomVariable"
              autofocus
            />
            <small class="form-hint">Must start with a letter or underscore, followed by letters, numbers, or underscores.</small>
          </div>

          <div class="form-group">
            <label for="varValue">Variable Value</label>
            <input
              id="varValue"
              v-model="newVariableValue"
              :type="newVariableSecret ? 'password' : 'text'"
              :placeholder="editingVariableName && newVariableSecret ? 'Leave blank to keep current encrypted value' : 'Enter the value for this variable'"
              class="form-input"
              @keyup.enter="addCustomVariable"
            />
            <small v-if="!newVariableSecret" class="form-hint">This value will be used in API requests and message templates.</small>
            <small v-else class="form-hint" style="color:#f59e0b;">
              🔒 The value will be <strong>encrypted</strong> on the server before being stored. It will never be shown again after saving.
            </small>
          </div>

          <!-- Secret toggle -->
          <div class="form-group secret-toggle-group">
            <label class="secret-toggle-label">
              <input
                type="checkbox"
                v-model="newVariableSecret"
                class="secret-checkbox"
                @change="onSecretToggle"
              />
              <span class="secret-toggle-text">
                <strong>🔒 Secret / Encrypted variable</strong>
                <small>Use this for API keys, tokens, and passwords. The value is encrypted on the server — it cannot be read back after saving.</small>
              </span>
            </label>
          </div>
        </div>
        <div class="modal-footer" style="background: var(--bg-secondary, #374151);">
          <button @click="closeVariableModal" class="btn-secondary">Cancel</button>
          <button @click="addCustomVariable" class="btn-primary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display: inline-block; vertical-align: middle; margin-right: 6px;">
              <polyline points="20 6 9 17 4 12" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ editingVariableName ? 'Update Variable' : 'Add Variable' }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Duplicate menu — teleported to body to escape overflow:hidden node-header -->
  <Teleport to="body">
    <div
      v-if="duplicateMenuNodeId"
      class="duplicate-dropdown"
      :style="{ left: duplicateMenuPos.x + 'px', top: duplicateMenuPos.y + 'px' }"
      @click.stop
    >
      <button @click="duplicateNode(duplicateMenuNodeId); duplicateMenuNodeId = null">This Node</button>
      <button @click="duplicateNodeTree(duplicateMenuNodeId); duplicateMenuNodeId = null">Node Tree</button>
    </div>
  </Teleport>

  <!-- Delete Node Confirmation Modal -->
  <div v-if="deleteNodeDialog.show" class="modal-overlay delete-node-overlay" @click="deleteNodeDialog.show = false">
    <div class="modal-content delete-node-modal" @click.stop>
      <div class="modal-header delete-node-header">
        <h2>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display:inline-block;vertical-align:middle;margin-right:8px;color:#ef4444;">
            <polyline points="3 6 5 6 21 6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Delete Node
        </h2>
        <button @click="deleteNodeDialog.show = false" class="btn-close">×</button>
      </div>

      <div class="modal-body delete-node-body">
        <p class="delete-node-name">
          Are you sure you want to delete <strong>"{{ deleteNodeDialog.nodeName }}"</strong>?
        </p>

        <!-- No affected variables — simple confirm -->
        <p v-if="deleteNodeDialog.affectedVars.length === 0" class="delete-node-safe">
          No other nodes reference variables from this node.
        </p>

        <!-- Affected variables warning -->
        <div v-else class="delete-node-warning">
          <div class="delete-warning-header">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="flex-shrink:0;color:#f59e0b;">
              <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <line x1="12" y1="9" x2="12" y2="13" stroke-width="2" stroke-linecap="round"/>
              <line x1="12" y1="17" x2="12.01" y2="17" stroke-width="2" stroke-linecap="round"/>
            </svg>
            <span>{{ deleteNodeDialog.affectedVars.length }} variable reference{{ deleteNodeDialog.affectedVars.length > 1 ? 's' : '' }} will break</span>
          </div>
          <div class="delete-affected-list">
            <div v-for="(ref, i) in deleteNodeDialog.affectedVars" :key="i" class="delete-affected-row">
              <code class="delete-var-chip">{{ varRef(ref.varName) }}</code>
              <span class="delete-affected-arrow">→</span>
              <span class="delete-affected-node">{{ ref.nodeName }}</span>
            </div>
          </div>
          <p class="delete-warning-note">These references will become <code>[varName]</code> placeholders at runtime.</p>
        </div>
      </div>

      <div class="delete-node-footer">
        <button @click="deleteNodeDialog.show = false" class="btn-cancel-delete">Cancel</button>
        <button @click="confirmDeleteNode" class="btn-confirm-delete">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" style="display:inline-block;vertical-align:middle;margin-right:5px;">
            <polyline points="3 6 5 6 21 6" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Delete
        </button>
      </div>
    </div>
  </div>

  <!-- Flow Simulator Modal -->
  <FlowSimulatorModal
    :visible="showSimulator"
    :flow-id="flowId"
    :flow-name="flowName"
    :flow-definition="flowDefinition"
    @close="showSimulator = false"
  />

  <!-- ── AI Flow Builder Panel ─────────────────────────────────────── -->
  <transition name="ai-panel-slide">
    <div v-if="aiPanelOpen" class="ai-panel">
      <div class="ai-panel-header">
        <div class="ai-panel-title">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 2a10 10 0 0110 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
          AI Flow Builder
          <span class="ai-model-badge">{{ aiSettings.model.replace('claude-','').replace('-',' ') }}</span>
        </div>
        <div class="ai-panel-header-actions">
          <button v-if="aiUndoStack.length" @click="revertLastAiChange" class="ai-revert-btn" title="Revert last AI change">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M3 7v6h6"/><path d="M21 17a9 9 0 00-9-9 9 9 0 00-6 2.3L3 13"/>
            </svg>
            Revert
          </button>
          <button v-if="aiDisplayMessages.length" @click="clearAiChat" class="ai-icon-btn" title="Clear chat history" style="color:rgba(255,255,255,0.4)">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
            </svg>
          </button>
          <button @click="aiSettingsOpen = true" class="ai-icon-btn" title="AI Settings">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
            </svg>
          </button>
          <button @click="aiPanelOpen = false" class="ai-icon-btn" title="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- ── Plan Checklist ──────────────────────────────────────────── -->
      <div v-if="aiPlan.length" class="ai-plan-section">
        <div class="ai-plan-header" @click="aiPlanVisible = !aiPlanVisible">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.6">
            <polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
          </svg>
          <span class="ai-plan-title">Plan</span>
          <span class="ai-plan-progress">{{ aiPlan.filter(s=>s.status==='done'||s.status==='skipped').length }}/{{ aiPlan.length }}</span>
          <svg :style="{ transform: aiPlanVisible ? 'rotate(0deg)' : 'rotate(-90deg)', transition:'transform 0.2s', marginLeft:'auto', opacity:0.5 }" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="6 9 12 15 18 9"/></svg>
        </div>
        <div v-if="aiPlanVisible" class="ai-plan-list">
          <div v-for="step in aiPlan" :key="step.id" :class="['ai-plan-step', 'ai-plan-step--' + step.status]">
            <span class="ai-plan-step-icon">
              <svg v-if="step.status==='done'" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              <svg v-else-if="step.status==='in_progress'" width="11" height="11" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="5"/></svg>
              <svg v-else-if="step.status==='skipped'" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="5" y1="12" x2="19" y2="12"/></svg>
              <svg v-else width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="9"/></svg>
            </span>
            <span class="ai-plan-step-desc">{{ step.description }}<span v-if="step.detail" class="ai-plan-step-detail"> — {{ step.detail }}</span></span>
          </div>
        </div>
      </div>

      <div class="ai-chat-messages" ref="aiChatMessages">
        <div v-if="aiDisplayMessages.length === 0" class="ai-empty-state">
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" style="opacity:0.3;margin-bottom:10px">
            <path d="M12 2a10 10 0 0110 10c0 5.52-4.48 10-10 10S2 17.52 2 12 6.48 2 12 2z"/>
            <path d="M8 14s1.5 2 4 2 4-2 4-2"/>
            <line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/>
          </svg>
          <template v-if="!aiSettings.apiKeyHint">
            <p style="color:#f87171;font-weight:600;">No API key configured</p>
            <p class="ai-hint-text">Click the gear icon above to add your Anthropic API key and select a model.</p>
            <button @click="aiSettingsOpen = true" class="ai-setup-key-btn">Configure API Key</button>
          </template>
          <template v-else>
            <p>Ask me to build, modify, or explain your flow.</p>
            <p class="ai-hint-text">Try: "Add a welcome message that asks for the customer's name, then routes to a thank you message"</p>
          </template>
        </div>
        <div
          v-for="(msg, idx) in aiDisplayMessages"
          :key="idx"
          :class="['ai-msg', `ai-msg--${msg.role}`, { 'ai-msg--error': msg.isError, 'ai-msg--system': msg.isSystem, 'ai-msg--question': msg.isQuestion }]"
        >
          <div class="ai-msg-bubble">
            <div v-if="msg.isQuestion" class="ai-question-label">❓ Needs your input</div>
            <span v-html="aiFormatMessage(msg.text)"></span>
          </div>
          <div v-if="msg.isQuestion && msg.options && msg.options.length && !msg.answered" class="ai-question-options">
            <button
              v-for="opt in msg.options"
              :key="opt"
              class="ai-option-btn"
              @click="selectAiOption(opt, idx)"
            >{{ opt }}</button>
            <button class="ai-option-btn ai-option-btn--other" @click="selectAiOption(null, idx)">Something else…</button>
          </div>
          <div v-if="msg.isQuestion && msg.answered" class="ai-question-answered">
            ✓ {{ msg.selectedOption || 'Answered' }}
          </div>
        </div>
        <div v-if="aiLoading" class="ai-msg ai-msg--assistant">
          <div class="ai-msg-bubble ai-typing">
            <span></span><span></span><span></span>
          </div>
        </div>
      </div>

      <div v-if="aiInput.length > 1500" class="ai-length-warning">
        Long message (~{{ Math.round(aiInput.length / 4) }} tokens). Consider breaking it into smaller steps.
      </div>
      <div class="ai-chat-input-area">
        <textarea
          v-model="aiInput"
          @keydown.enter.exact.prevent="sendAiMessage"
          @keydown.enter.shift.exact="null"
          class="ai-chat-textarea"
          rows="3"
          :placeholder="aiSettings.apiKeyHint ? 'Describe what you want to build or change… (Enter to send, Shift+Enter for new line)' : 'Configure your API key first…'"
          :disabled="aiLoading || !aiSettings.apiKeyHint"
        ></textarea>
        <button @click="sendAiMessage" :disabled="!aiInput.trim() || aiLoading || !aiSettings.apiKeyHint" class="ai-send-btn">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="22" y1="2" x2="11" y2="13"/>
            <polygon points="22 2 15 22 11 13 2 9 22 2"/>
          </svg>
        </button>
      </div>
    </div>
  </transition>

  <!-- ── AI Settings Modal ─────────────────────────────────────────── -->
  <div v-if="aiSettingsOpen" class="modal-overlay" style="background:rgba(0,0,0,0.7);backdrop-filter:blur(2px);" @click.self="aiSettingsOpen = false">
    <div class="modal-content ai-settings-modal" @click.stop>
      <div class="modal-header" style="background:var(--card-background,#1f2937);">
        <h2>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:inline-block;vertical-align:middle;margin-right:8px;">
            <circle cx="12" cy="12" r="3"/>
            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/>
          </svg>
          AI Settings
        </h2>
        <button @click="aiSettingsOpen = false" class="btn-close">×</button>
      </div>
      <div class="modal-body" style="background:var(--card-background,#1f2937);padding:20px;display:flex;flex-direction:column;gap:16px;">
        <div>
          <label class="form-label" style="display:block;margin-bottom:6px;font-size:0.85rem;color:rgba(255,255,255,0.7);">Model</label>
          <select v-model="aiSettingsForm.model" class="form-input">
            <option value="claude-haiku-4-5">Claude Haiku 4.5 — Fast &amp; cheap</option>
            <option value="claude-sonnet-4-6">Claude Sonnet 4.6 — Balanced</option>
            <option value="claude-opus-4-6">Claude Opus 4.6 — Most capable</option>
          </select>
        </div>
        <div>
          <label class="form-label" style="display:block;margin-bottom:6px;font-size:0.85rem;color:rgba(255,255,255,0.7);">
            Anthropic API Key
            <span v-if="aiSettings.apiKeyHint" style="color:rgba(255,255,255,0.4);margin-left:6px;">(current: {{ aiSettings.apiKeyHint }})</span>
          </label>
          <input
            v-model="aiSettingsForm.apiKey"
            type="password"
            class="form-input"
            placeholder="sk-ant-… (leave blank to keep current key)"
          />
          <p style="font-size:0.75rem;color:rgba(255,255,255,0.4);margin-top:4px;">Your key is stored encrypted. Never shared.</p>
        </div>
      </div>
      <div class="modal-footer" style="background:var(--card-background,#1f2937);display:flex;justify-content:flex-end;gap:8px;padding:12px 20px;border-top:1px solid var(--border-primary,#374151);">
        <button @click="aiSettingsOpen = false" class="hdr-btn">Cancel</button>
        <button @click="saveAiSettings" class="hdr-btn hdr-btn-save">Save</button>
      </div>
    </div>
  </div>
</template>

<script>
import DashboardService from '../services/dashboard'
import VarTextEditor from '../components/VarTextEditor.vue'
import FlowSimulatorModal from '../components/FlowSimulatorModal.vue'

export default {
  name: 'FlowBuilderView',

  components: { VarTextEditor, FlowSimulatorModal },

  data () {
    return {
      flowId: null,
      flowName: 'Untitled Flow',
      flowEnabled: false,
      targetOwnerId: null,
      targetOwnerName: '',
      targetOwnerCompany: '',
      saving: false,
      publishing: false,
      showSimulator: false,
      autosaveEnabled: true,
      lastSaved: null,
      autosaveTimer: null,
      autosaveFeedback: false,
      showRenameModal: false,
      renameFlowName: '',

      // Flow definition
      flowDefinition: {
        version: '1.0',
        start_node: null,
        nodes: [],
        variables: {},
        variable_meta: {},
        settings: {
          timeout_value: 24,
          timeout_unit: 'hours',
          never_expire: false,
          department_handoff_mode: false,
          max_agent_handle_value: 4,
          max_agent_handle_unit: 'hours',
          resolve_message_enabled: false,
          resolve_message: '',
          handoff_late_threshold_minutes: 30,
          handoff_warn_before_24h_minutes: 120,
          handoff_extend_message: '',
          handoff_extend_button_text: ''
        }
      },

      // Canvas state
      zoomLevel: 1,
      canvasPan: { x: 0, y: 0 },

      // Node selection
      selectedNodeId: null,
      selectedEdge: null,

      // Multi-select
      multiSelectMode: false,
      selectedNodeIds: [],
      multiDragOffsets: null,

      // Properties panel resize
      propertiesPanelWidth: 380,
      isResizingPanel: false,

      // Drag state
      draggingNode: null,
      dragOffset: { x: 0, y: 0 },
      connectingFrom: null,
      tempConnection: null,
      isPanning: false,
      panStart: { x: 0, y: 0 },

      // UI state
      nodeSearchQuery: '',
      expandedCategories: {
        messages: true,
        inputs: true,
        logic: false,
        actions: false,
        crm: false,

        leads: false,
        appointments: false,
        google_sheets: false,
        google_drive: false,
        google_docs: false,
        ai: false,
        advertiser: false
      },
      activeSidebarTab: 'nodes',
      deleteNodeDialog: {
        show: false,
        nodeId: null,
        nodeName: '',
        producedVars: [],
        affectedVars: [] // [{ varName, nodeName, nodeType }]
      },
      activeVarEditor: null,
      activeCalcInput: null, // { idx, field: 'a'|'b' }
      dragButtonFromIndex: null,
      expandedAdvSlots: {}, // { [nodeId_slotIdx]: bool }
      advCountries: [
        { code: '20', flag: '🇪🇬', name: 'Egypt' },
        { code: '966', flag: '🇸🇦', name: 'Saudi Arabia' },
        { code: '971', flag: '🇦🇪', name: 'UAE' },
        { code: '974', flag: '🇶🇦', name: 'Qatar' },
        { code: '965', flag: '🇰🇼', name: 'Kuwait' },
        { code: '968', flag: '🇴🇲', name: 'Oman' },
        { code: '973', flag: '🇧🇭', name: 'Bahrain' },
        { code: '962', flag: '🇯🇴', name: 'Jordan' },
        { code: '961', flag: '🇱🇧', name: 'Lebanon' },
        { code: '963', flag: '🇸🇾', name: 'Syria' },
        { code: '964', flag: '🇮🇶', name: 'Iraq' },
        { code: '970', flag: '🇵🇸', name: 'Palestine' },
        { code: '967', flag: '🇾🇪', name: 'Yemen' },
        { code: '212', flag: '🇲🇦', name: 'Morocco' },
        { code: '213', flag: '🇩🇿', name: 'Algeria' },
        { code: '216', flag: '🇹🇳', name: 'Tunisia' },
        { code: '218', flag: '🇱🇾', name: 'Libya' },
        { code: '249', flag: '🇸🇩', name: 'Sudan' },
        { code: '90', flag: '🇹🇷', name: 'Turkey' },
        { code: '98', flag: '🇮🇷', name: 'Iran' },
        { code: '92', flag: '🇵🇰', name: 'Pakistan' },
        { code: '91', flag: '🇮🇳', name: 'India' },
        { code: '1', flag: '🇺🇸', name: 'USA / Canada' },
        { code: '44', flag: '🇬🇧', name: 'UK' },
        { code: '33', flag: '🇫🇷', name: 'France' },
        { code: '49', flag: '🇩🇪', name: 'Germany' },
        { code: '55', flag: '🇧🇷', name: 'Brazil' },
        { code: '234', flag: '🇳🇬', name: 'Nigeria' },
        { code: '254', flag: '🇰🇪', name: 'Kenya' },
        { code: '27', flag: '🇿🇦', name: 'South Africa' },
        { code: '62', flag: '🇮🇩', name: 'Indonesia' },
        { code: '60', flag: '🇲🇾', name: 'Malaysia' },
        { code: '65', flag: '🇸🇬', name: 'Singapore' }
      ],
      duplicateMenuNodeId: null,
      duplicateMenuPos: { x: 0, y: 0 },

      // API Testing
      testingApi: false,
      apiTestResponse: null,
      apiTestError: null,
      apiTestNodeId: null,
      detectedResponseKeys: [],
      apiTestVarsDetected: [],
      testSampleValues: {},
      apiVarSearch: '',

      // Departments
      departments: [],
      availableTags: [],
      currencyList: [
        { code: 'USD', name: 'US Dollar' }, { code: 'EUR', name: 'Euro' },
        { code: 'GBP', name: 'British Pound' }, { code: 'EGP', name: 'Egyptian Pound' },
        { code: 'AED', name: 'UAE Dirham' }, { code: 'SAR', name: 'Saudi Riyal' },
        { code: 'QAR', name: 'Qatari Riyal' }, { code: 'KWD', name: 'Kuwaiti Dinar' },
        { code: 'BHD', name: 'Bahraini Dinar' }, { code: 'OMR', name: 'Omani Rial' },
        { code: 'JOD', name: 'Jordanian Dinar' }, { code: 'MAD', name: 'Moroccan Dirham' },
        { code: 'TND', name: 'Tunisian Dinar' }, { code: 'DZD', name: 'Algerian Dinar' },
        { code: 'LYD', name: 'Libyan Dinar' }, { code: 'JPY', name: 'Japanese Yen' },
        { code: 'CNY', name: 'Chinese Yuan' }, { code: 'INR', name: 'Indian Rupee' },
        { code: 'CAD', name: 'Canadian Dollar' }, { code: 'AUD', name: 'Australian Dollar' },
        { code: 'CHF', name: 'Swiss Franc' }, { code: 'SEK', name: 'Swedish Krona' },
        { code: 'NOK', name: 'Norwegian Krone' }, { code: 'DKK', name: 'Danish Krone' },
        { code: 'TRY', name: 'Turkish Lira' }, { code: 'RUB', name: 'Russian Ruble' },
        { code: 'BRL', name: 'Brazilian Real' }, { code: 'MXN', name: 'Mexican Peso' },
        { code: 'ZAR', name: 'South African Rand' }, { code: 'NGN', name: 'Nigerian Naira' },
        { code: 'KES', name: 'Kenyan Shilling' }, { code: 'PKR', name: 'Pakistani Rupee' },
        { code: 'BDT', name: 'Bangladeshi Taka' }, { code: 'THB', name: 'Thai Baht' },
        { code: 'SGD', name: 'Singapore Dollar' }, { code: 'MYR', name: 'Malaysian Ringgit' },
        { code: 'IDR', name: 'Indonesian Rupiah' }, { code: 'PHP', name: 'Philippine Peso' },
        { code: 'VND', name: 'Vietnamese Dong' }, { code: 'HKD', name: 'Hong Kong Dollar' }
      ],

      // CRM
      customerCategories: [],
      teamAgents: [],

      // Google Integrations
      googleConnections: [],

      // WhatsApp Templates (for send_template node)
      approvedTemplates: [],

      // Leads
      allLeadFields: [],

      // Appointments
      bookingServices: [],

      // Google Sheets dynamic data (for current node)
      gsSpreadsheets: [],
      gsSheetsInSpread: [],
      gsColumnHeaders: [],
      gsLoadingSpreadsheets: false,
      gsLoadingSheets: false,
      gsLoadingHeaders: false,
      rowDataItems: [], // [{col:'', val:''}] for Append Row
      setDataItems: [], // [{col:'', val:''}] for Update Row
      // Google Drive dynamic data
      gdFolders: [],
      gdLoadingFolders: false,

      // Microsoft Integrations
      microsoftConnections: [],

      // Microsoft Excel dynamic data (for current node)
      msWorkbooks: [],
      msWorksheetsInBook: [],
      msColumnHeaders: [],
      msLoadingWorkbooks: false,
      msLoadingWorksheets: false,
      msLoadingHeaders: false,
      msRowDataItems: [],
      msSetDataItems: [],
      // Microsoft OneDrive dynamic data
      msDriveFolders: [],
      msLoadingDriveFolders: false,

      // Custom Variables
      showVariableModal: false,
      newVariableName: '',
      newVariableValue: '',
      newVariableSecret: false,
      editingVariableName: null,

      // Undo/Redo
      history: [],
      historyIndex: -1,

      // Node type definitions
      nodeTypes: [
        // Messages
        { type: 'message', name: 'Text Message', icon: '💬', category: 'messages', description: 'Send a text message' },
        { type: 'list_message', name: 'List Message', icon: '📋', category: 'messages', description: 'Send a formatted list from an array variable' },
        { type: 'image_message', name: 'Image Message', icon: '🖼️', category: 'messages', description: 'Send an image' },
        { type: 'document_message', name: 'Document', icon: '📄', category: 'messages', description: 'Send a PDF or file' },
        { type: 'video_message', name: 'Video Message', icon: '🎥', category: 'messages', description: 'Send a video' },

        // Inputs
        { type: 'text_with_buttons', name: 'Text + Buttons', icon: '💬🔘', category: 'inputs', description: 'Message with buttons' },
        { type: 'button_selection', name: 'Buttons Only', icon: '🔘', category: 'inputs', description: 'Button choices — 4+ auto-converts to list' },
        { type: 'list_selection', name: 'List Menu', icon: '📋', category: 'inputs', description: 'Scrollable list menu (up to 10)' },
        { type: 'text_input', name: 'Text Input', icon: '✏️', category: 'inputs', description: 'Collect text from user' },
        { type: 'number_input', name: 'Number Input', icon: '🔢', category: 'inputs', description: 'Collect numbers' },
        { type: 'email_input', name: 'Email Input', icon: '📧', category: 'inputs', description: 'Collect email address' },
        { type: 'date_selection', name: 'Date Picker', icon: '📅', category: 'inputs', description: 'Select a date' },
        { type: 'time_selection', name: 'Time Picker', icon: '⏰', category: 'inputs', description: 'Select a time' },
        { type: 'location_input', name: 'Location', icon: '📍', category: 'inputs', description: 'Request customer location' },
        { type: 'image_input', name: 'Image Input', icon: '🖼', category: 'inputs', description: 'Request an image from the customer' },

        // Logic
        { type: 'condition', name: 'Condition', icon: '🔀', category: 'logic', description: 'Branch based on logic' },
        { type: 'calculator', name: 'Calculator', icon: '🧮', category: 'logic', description: 'Evaluate math formulas and store results in variables' },
        { type: 'delay', name: 'Delay', icon: '⏱️', category: 'logic', description: 'Wait before next step' },
        { type: 'current_datetime', name: 'Current Date / Time', icon: '🕐', category: 'logic', description: 'Get current date and time in your timezone' },
        { type: 'check_24h_window', name: 'Check 24h Window', icon: '⏰', category: 'logic', description: 'Branch based on whether the customer messaged within the last 24 hours' },
        { type: 'check_working_hours', name: 'Check Working Hours', icon: '🏢', category: 'logic', description: 'Route based on whether the current time is within your company working hours and days' },

        // Actions
        { type: 'send_template', name: 'Send WhatsApp Template', icon: '📋', category: 'actions', description: 'Send an approved WhatsApp message template with dynamic parameters' },
        { type: 'qr_generator', name: 'QR Code', icon: '🔲', category: 'actions', description: 'Generate a QR code from any text or URL and send it to the customer' },
        { type: 'api_call', name: 'API Call', icon: '🌐', category: 'actions', description: 'Call REST API' },
        { type: 'send_email', name: 'Send Email', icon: '📧', category: 'actions', description: 'Send an email via the Quali-D SMTP server' },
        { type: 'currency_exchange', name: 'Currency Exchange', icon: '💱', category: 'actions', description: 'Convert an amount between currencies using live ECB rates (frankfurter.app)' },
        { type: 'action', name: 'Save & Finish', icon: '✅', category: 'actions', description: 'Complete the flow' },
        { type: 'restart_flow', name: 'Restart Flow', icon: '🔄', category: 'actions', description: 'Restart the conversation from the beginning' },
        { type: 'set_session_timeout', name: 'Override Session Timeout', icon: '⏱', category: 'actions', description: 'Change how long the session stays active from this point forward' },
        { type: 'assign_department', name: 'Assign to Department', icon: '🏢', category: 'actions', description: 'Route customer to a department for agent handling' },
        { type: 'csat_rating', name: 'CSAT Rating', icon: '⭐', category: 'actions', description: 'Ask customers to rate their experience (1–5 or 1–10 stars) and save the score to a variable' },
        { type: 'get_departments', name: 'Get Departments', icon: '🏢', category: 'actions', description: 'Send a list of your departments and let the customer pick one' },
        { type: 'set_variable', name: 'Set Variable', icon: '📦', category: 'actions', description: 'Set a variable value' },
        { type: 'store_locator', name: 'Store Locator', icon: '🗺️', category: 'actions', description: 'Find nearest store based on customer location' },

        // CRM
        { type: 'crm_add_note', name: 'Add Customer Note', icon: '📝', category: 'crm', description: 'Add a note to the customer profile' },
        { type: 'crm_update_category', name: 'Update Category', icon: '🏷️', category: 'crm', description: 'Move customer to a different category' },
        { type: 'crm_update_customer', name: 'Update Customer', icon: '👤', category: 'crm', description: 'Update customer fields (name, address, VIP…)' },
        { type: 'assign_tag', name: 'Assign Tag', icon: '🏷️', category: 'crm', description: 'Assign a tag to the customer' },
        { type: 'remove_tag', name: 'Remove Tag', icon: '🗑️', category: 'crm', description: 'Remove a tag from the customer' },
        { type: 'check_tag', name: 'Check Tag', icon: '🔍', category: 'logic', description: 'Branch based on whether customer has a specific tag' },
        { type: 'notify_team', name: 'Notify Team Member', icon: '🔔', category: 'actions', description: 'Send an internal message to an agent or the whole team' },

        { type: 'todo_create', name: 'Create Todo', icon: '✅', category: 'actions', description: 'Create a task linked to this customer' },

        // Leads
        { type: 'save_lead_field', name: 'Save Lead Field', icon: '💾', category: 'leads', description: 'Save a value into a lead field for this customer' },
        { type: 'check_lead_complete', name: 'Check Lead Complete', icon: '📋', category: 'leads', description: 'Branch based on whether all required lead fields are filled' },
        { type: 'get_lead_data', name: 'Get Lead Data', icon: '📥', category: 'leads', description: 'Load customer\'s lead field values into flow variables' },

        // Appointments
        { type: 'get_services', name: 'Get My Services', icon: '🛎️', category: 'actions', description: 'Send a list of your services and let the customer choose one — feeds into Book Appointment' },
        { type: 'check_available_dates', name: 'Check Available Dates', icon: '📅', category: 'appointments', description: 'Find bookable dates and send them as a list for the customer to choose from' },
        { type: 'check_available_slots', name: 'Check Available Slots', icon: '🗓️', category: 'appointments', description: 'Get available booking slots for a date' },
        { type: 'book_appointment', name: 'Book Appointment', icon: '✅', category: 'appointments', description: 'Book an appointment for the current customer' },
        { type: 'cancel_appointment', name: 'Cancel Appointment', icon: '❌', category: 'appointments', description: "Cancel the customer's next upcoming appointment" },
        { type: 'get_customer_appointments', name: 'Get My Appointments', icon: '📋', category: 'appointments', description: "Load the customer's upcoming appointments into variables" },
        { type: 'submit_rating', name: 'Submit Rating', icon: '⭐', category: 'appointments', description: 'Save a star rating for the last completed appointment' },

        // Google Sheets
        { type: 'google_sheets_read', name: 'Read Rows', icon: '📊', category: 'google_sheets', description: 'Read rows from a Google Sheet' },
        { type: 'google_sheets_append', name: 'Add Row', icon: '📊', category: 'google_sheets', description: 'Add a new row to a Google Sheet' },
        { type: 'google_sheets_update', name: 'Update Row', icon: '📊', category: 'google_sheets', description: 'Update rows in a Google Sheet' },

        // Google Drive
        { type: 'google_drive_upload', name: 'Upload File', icon: '📁', category: 'google_drive', description: 'Upload a file to Google Drive' },
        { type: 'google_drive_create_folder', name: 'Create Folder', icon: '📁', category: 'google_drive', description: 'Create a folder in Google Drive' },

        // Google Docs
        { type: 'google_docs_create', name: 'Create Doc', icon: '📝', category: 'google_docs', description: 'Create a new Google Docs document' },
        { type: 'google_docs_append', name: 'Append to Doc', icon: '📝', category: 'google_docs', description: 'Append text to a Google Docs document' },

        // Microsoft Excel
        { type: 'excel_read', name: 'Read Rows', icon: '📊', category: 'excel', description: 'Read rows from an Excel workbook' },
        { type: 'excel_append', name: 'Add Row', icon: '📊', category: 'excel', description: 'Add a new row to an Excel workbook' },
        { type: 'excel_update', name: 'Update Row', icon: '📊', category: 'excel', description: 'Update rows in an Excel workbook' },

        // Microsoft OneDrive
        { type: 'onedrive_upload', name: 'Upload File', icon: '📁', category: 'onedrive', description: 'Upload a file to OneDrive' },
        { type: 'onedrive_create_folder', name: 'Create Folder', icon: '📁', category: 'onedrive', description: 'Create a folder in OneDrive' },

        // AI
        { type: 'openai_call', name: 'OpenAI', icon: '🤖', category: 'ai', description: 'Generate text with ChatGPT' },
        { type: 'claude_call', name: 'Claude', icon: '🧠', category: 'ai', description: 'Generate text with Anthropic Claude' },
        { type: 'gemini_call', name: 'Gemini', icon: '✨', category: 'ai', description: 'Generate text with Google Gemini' },

        // Advertiser
        { type: 'advertiser', name: 'Advertiser', icon: '📢', category: 'advertiser', description: 'Randomly or sequentially send one of several connected messages' }
      ],

      // ── AI Flow Builder ─────────────────────────────────────────────
      aiPanelOpen: false,
      aiMessages: [], // Anthropic API message history
      aiDisplayMessages: [], // UI display messages { role, text, isError, isSystem }
      aiLoading: false,
      aiInput: '',
      aiUndoStack: [], // snapshots of flowDefinition before AI changes
      aiPlan: [], // [{id, description, status:'pending'|'in_progress'|'done'|'skipped', detail}]
      aiPlanVisible: true,
      aiSettingsOpen: false,
      aiSettings: {
        model: 'claude-haiku-4-5',
        apiKeyHint: ''
      },
      aiSettingsForm: {
        model: 'claude-haiku-4-5',
        apiKey: ''
      }
    }
  },

  computed: {
    selectedNode () {
      return this.flowDefinition.nodes.find(n => n.id === this.selectedNodeId)
    },

    edges () {
      const edges = []
      const nodeIds = new Set(this.flowDefinition.nodes.map(n => n.id))
      this.flowDefinition.nodes.forEach(node => {
        if (node.next && nodeIds.has(node.next)) {
          edges.push({ from: node.id, to: node.next, portId: null })
        }
        if (node.routes) {
          Object.entries(node.routes).forEach(([portId, targetId]) => {
            if (targetId && nodeIds.has(targetId)) {
              edges.push({ from: node.id, to: targetId, portId })
            }
          })
        }
        // AI tool connections (bottom ports → tool nodes)
        if (['openai_call', 'claude_call', 'gemini_call'].includes(node.type)) {
          ;(node.config?.tools || []).forEach((tool, idx) => {
            if (tool.node_id && nodeIds.has(tool.node_id)) {
              edges.push({ from: node.id, to: tool.node_id, portId: `tool__${idx}`, isTool: true })
            }
          })
        }
        // Advertiser message slot connections (bottom ports → message nodes)
        if (node.type === 'advertiser') {
          ;(node.config?.message_nodes || []).forEach((msgNodeId, idx) => {
            if (msgNodeId && nodeIds.has(msgNodeId)) {
              edges.push({ from: node.id, to: msgNodeId, portId: `msg__${idx}`, isTool: true })
            }
          })
        }
        // Condition node: routing stored directly in config
        if (node.type === 'condition' && node.config?.conditions) {
          node.config.conditions.forEach(cond => {
            if (cond.next_node && nodeIds.has(cond.next_node)) {
              edges.push({ from: node.id, to: cond.next_node, portId: cond.id })
            }
          })
          if (node.config.else_next && nodeIds.has(node.config.else_next)) {
            edges.push({ from: node.id, to: node.config.else_next, portId: 'else' })
          }
        }
        // assign_department post-resolve chain connection
        if (
          node.type === 'assign_department' &&
          node.config?.post_resolve_enabled &&
          node.config?.post_resolve_first_node_id &&
          nodeIds.has(node.config.post_resolve_first_node_id)
        ) {
          edges.push({ from: node.id, to: node.config.post_resolve_first_node_id, portId: 'post_resolve' })
        }
      })
      return edges
    },

    flowVariables () {
      // Auto-detect variables from nodes
      const variables = {}

      // Always-available session variables
      variables.customer_phone = {
        type: 'text',
        description: "Customer's WhatsApp phone number",
        source: 'session'
      }
      variables.customer_name = {
        type: 'text',
        description: "Customer's name (if known)",
        source: 'session'
      }

      // When a node is selected, only expose variables from its upstream (connected ancestor) nodes.
      // When no node is selected (e.g. global variables panel), expose everything.
      const upstream = this.selectedNodeId ? this.getUpstreamNodeIds(this.selectedNodeId) : null
      const visibleNodes = upstream
        ? this.flowDefinition.nodes.filter(n => upstream.has(n.id))
        : this.flowDefinition.nodes

      // Collect API call node name prefixes so we can tag their output variables
      const apiNodePrefixes = []
      visibleNodes.forEach(node => {
        if (node.type === 'api_call' && node.config?.api_node_name) {
          apiNodePrefixes.push({ prefix: node.config.api_node_name + '_', nodeName: node.config.api_node_name })
        }
      })

      visibleNodes.forEach(node => {
        if (node.config && node.config.variable_name) {
          const varBase = node.config.variable_name
          variables[varBase] = {
            type: this.getVariableType(node.type),
            description: `Captured from "${node.name || node.type}" step`,
            source: 'node'
          }

          // Button/list selection exposes _label (display text of what customer tapped)
          if (node.type === 'button_selection' || node.type === 'text_with_buttons' || node.type === 'list_selection') {
            variables[`${varBase}_label`] = { type: 'text', description: `Display label of what the customer tapped ("${node.name || node.type}")`, source: 'node' }
          }

          // Image input exposes URL and caption sub-variables
          if (node.type === 'image_input') {
            variables[`${varBase}_caption`] = { type: 'text', description: 'Caption sent with the image', source: 'node' }
          }

          // Location input exposes multiple sub-variables
          if (node.type === 'location_input') {
            const shortBase = varBase.replace(/_location$/, '')
            variables[`raw_${varBase}`] = { type: 'text', description: 'Full location JSON string', source: 'node' }
            variables[`${shortBase}_latitude`] = { type: 'text', description: 'Latitude coordinate', source: 'node' }
            variables[`${shortBase}_longitude`] = { type: 'text', description: 'Longitude coordinate', source: 'node' }
            variables[`${varBase}_maps_url`] = { type: 'text', description: 'Google Maps URL', source: 'node' }
            variables[`${varBase}_address`] = { type: 'text', description: 'Location address', source: 'node' }
            variables[`${varBase}_name`] = { type: 'text', description: 'Location place name', source: 'node' }
          }
        }

        // Expose result_variable for Google nodes
        const googleResultTypes = {
          google_sheets_read: 'array',
          google_sheets_append: 'text',
          google_sheets_update: 'text',
          google_drive_upload: 'text',
          google_drive_create_folder: 'text',
          google_docs_create: 'text'
        }
        if (googleResultTypes[node.type] && node.config?.result_variable) {
          const rv = node.config.result_variable
          const nodeLabel = node.name || node.type
          if (node.type === 'google_sheets_read') {
            variables[rv] = { type: 'array', description: `All matched rows from "${nodeLabel}"`, source: 'google' }
            variables[`${rv}_count`] = { type: 'text', description: `Row count from "${nodeLabel}"`, source: 'google' }
            // Expose first-row column values using persisted sheet_columns
            const cols = node.config?.sheet_columns || []
            cols.forEach(col => {
              const safe = col.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
              if (safe) {
                variables[`${rv}_${safe}`] = { type: 'text', description: `"${col}" from first matched row (${nodeLabel})`, source: 'google' }
              }
            })
          } else if (node.type === 'google_drive_upload') {
            variables[rv] = { type: 'text', description: `Uploaded file ID from "${nodeLabel}"`, source: 'google' }
            variables[`${rv}_url`] = { type: 'text', description: `Shareable Drive URL from "${nodeLabel}"`, source: 'google' }
          } else {
            variables[rv] = { type: 'text', description: `Result ID from "${nodeLabel}"`, source: 'google' }
          }
        }

        // Expose result_variable for Microsoft nodes
        const msResultTypes = {
          excel_read: 'array',
          excel_append: 'text',
          excel_update: 'text',
          onedrive_upload: 'text',
          onedrive_create_folder: 'text'
        }
        if (msResultTypes[node.type] && node.config?.result_variable) {
          const rv = node.config.result_variable
          const nodeLabel = node.name || node.type
          if (node.type === 'excel_read') {
            variables[rv] = { type: 'array', description: `All matched rows from "${nodeLabel}"`, source: 'microsoft' }
            variables[`${rv}_count`] = { type: 'text', description: `Row count from "${nodeLabel}"`, source: 'microsoft' }
            const cols = node.config?.sheet_columns || []
            cols.forEach(col => {
              const safe = col.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
              if (safe) {
                variables[`${rv}_${safe}`] = { type: 'text', description: `"${col}" from first matched row (${nodeLabel})`, source: 'microsoft' }
              }
            })
          } else if (node.type === 'onedrive_upload') {
            variables[rv] = { type: 'text', description: `Uploaded file ID from "${nodeLabel}"`, source: 'microsoft' }
            variables[`${rv}_url`] = { type: 'text', description: `Shareable OneDrive URL from "${nodeLabel}"`, source: 'microsoft' }
          } else {
            variables[rv] = { type: 'text', description: `Result from "${nodeLabel}"`, source: 'microsoft' }
          }
        }

        // Expose variables from store_locator nodes
        if (node.type === 'store_locator' && node.config?.result_variable) {
          const rv = node.config.result_variable
          const nl = node.name || 'Store Locator'
          variables[`${rv}_name`] = { type: 'text', description: `Nearest store name (${nl})`, source: 'node' }
          variables[`${rv}_address`] = { type: 'text', description: `Nearest store address (${nl})`, source: 'node' }
          variables[`${rv}_lat`] = { type: 'text', description: `Nearest store latitude (${nl})`, source: 'node' }
          variables[`${rv}_lng`] = { type: 'text', description: `Nearest store longitude (${nl})`, source: 'node' }
          variables[`${rv}_distance_km`] = { type: 'text', description: `Distance to nearest store in km (${nl})`, source: 'node' }
          variables[`${rv}_maps_url`] = { type: 'text', description: `Google Maps pin URL for nearest store (${nl})`, source: 'node' }
          variables[`${rv}_directions_url`] = { type: 'text', description: `Google Maps directions from customer to store (${nl})`, source: 'node' }
          // Expose custom field keys defined across all manual stores
          if (node.config.store_source === 'manual') {
            const seenKeys = new Set()
            ;(node.config.stores || []).forEach(store => {
              (store.custom_fields || []).forEach(cf => {
                const key = (cf.key || '').trim().replace(/[^a-zA-Z0-9_]/g, '_')
                if (key && !seenKeys.has(key)) {
                  seenKeys.add(key)
                  variables[`${rv}_${key}`] = { type: 'text', description: `Custom field "${cf.key}" from nearest store (${nl})`, source: 'node' }
                }
              })
            })
          }
        }

        // Expose variables from currency_exchange nodes
        if (node.type === 'currency_exchange' && node.config?.result_variable) {
          const rv = node.config.result_variable
          const nl = node.name || 'Currency Exchange'
          variables[rv] = { type: 'text', description: `Converted amount from "${nl}"`, source: 'node' }
          variables[`${rv}_rate`] = { type: 'text', description: `Exchange rate used in "${nl}"`, source: 'node' }
          variables[`${rv}_date`] = { type: 'text', description: `Rate date from "${nl}"`, source: 'node' }
        }

        // Expose current time variable from check_working_hours nodes
        if (node.type === 'check_working_hours' && node.config?.result_variable) {
          const rv = node.config.result_variable
          const nl = node.name || 'Check Working Hours'
          variables[rv] = { type: 'text', description: `Current day & time seen by "${nl}" (e.g. Wednesday 14:35)`, source: 'node' }
        }

        // Expose variables from AI nodes (mode-aware)
        if (['openai_call', 'claude_call', 'gemini_call'].includes(node.type)) {
          const label = { openai_call: 'OpenAI', claude_call: 'Claude', gemini_call: 'Gemini' }[node.type]
          const mode = node.config?.mode || 'generate'
          const nodeLabel = node.name || node.type
          if (mode === 'extract') {
            // Each extract field becomes a session variable
            const fields = node.config?.extract_fields || []
            fields.forEach(f => {
              if (f.var_name) {
                variables[f.var_name] = {
                  type: 'text',
                  description: `${label} extracted "${f.description || f.var_name}" from "${nodeLabel}"`,
                  source: 'ai'
                }
              }
            })
          } else if (node.config?.result_variable) {
            // generate: AI response; classify: detected intent name
            const modeLabel = mode === 'classify' ? 'detected intent' : 'response'
            variables[node.config.result_variable] = {
              type: 'text',
              description: `${label} ${modeLabel} from "${nodeLabel}"`,
              source: 'ai'
            }
          }
        }

        // Expose result variable from set_variable nodes
        if (node.type === 'set_variable' && node.config?.variable_name) {
          variables[node.config.variable_name] = {
            type: 'text',
            description: `Set by "${node.name || 'Set Variable'}" node`,
            source: 'node'
          }
        }

        // Expose result variables from calculator node
        if (node.type === 'calculator' && node.config?.calculations) {
          const label = node.name || 'Calculator'
          node.config.calculations.forEach(calc => {
            if (calc.result_variable) {
              variables[calc.result_variable] = {
                type: 'number',
                description: `Result of "${calc.formula || '…'}" from "${label}"`,
                source: 'node'
              }
            }
          })
        }

        // Expose idmsg variable from api_call nodes when enabled
        if (node.type === 'api_call' && node.config?.idmsg_mode && node.config.idmsg_mode !== 'off') {
          variables.idmsg = {
            type: 'text',
            description: `Request ID (UUID) generated by "${node.name || 'API Call'}"`,
            source: 'node'
          }
        }

        // Expose QR code URL variable
        if (node.type === 'qr_generator' && node.config?.result_variable) {
          variables[node.config.result_variable] = {
            type: 'text',
            description: `QR code image URL from "${node.name || 'QR Code'}"`,
            source: 'node'
          }
        }

        // Expose result_variable from advertiser node
        if (node.type === 'advertiser' && node.config?.result_variable) {
          variables[node.config.result_variable] = {
            type: 'text',
            description: `Chosen slot number (1-based) from "${node.name || 'Advertiser'}"`,
            source: 'node'
          }
        }

        // Expose variables from get_lead_data mappings
        if (node.type === 'get_lead_data' && node.config?.mappings) {
          node.config.mappings.forEach(m => {
            if (m.variable_name) {
              variables[m.variable_name] = { type: 'text', description: `Lead field value from "${node.name || 'Get Lead Data'}"`, source: 'node' }
            }
          })
        }

        // Expose variables from current_datetime node
        if (node.type === 'current_datetime' && node.config?.variable_prefix) {
          const p = node.config.variable_prefix
          const label = node.name || 'Current Date/Time'
          variables[`${p}_date`] = { type: 'text', description: `Date from "${label}"`, source: 'node' }
          variables[`${p}_time`] = { type: 'text', description: `Time from "${label}"`, source: 'node' }
          variables[`${p}_datetime`] = { type: 'text', description: `Full datetime from "${label}"`, source: 'node' }
          variables[`${p}_day`] = { type: 'text', description: `Day of week name from "${label}"`, source: 'node' }
          variables[`${p}_day_number`] = { type: 'text', description: `Day of week number (1=Mon, 7=Sun) from "${label}"`, source: 'node' }
          variables[`${p}_hour`] = { type: 'text', description: `Current hour (0-23) from "${label}"`, source: 'node' }
          variables[`${p}_minute`] = { type: 'text', description: `Current minute (0-59) from "${label}"`, source: 'node' }
        }

        // Expose result_variable from check_24h_window
        if (node.type === 'check_24h_window' && node.config?.result_variable) {
          variables[node.config.result_variable] = { type: 'text', description: `Hours since last message from "${node.name || 'Check 24h Window'}"`, source: 'node' }
        }

        // Expose missing_fields variable from check_lead_complete
        if (node.type === 'check_lead_complete' && node.config?.missing_fields_var) {
          variables[node.config.missing_fields_var] = { type: 'text', description: `Missing lead field names from "${node.name || 'Check Lead Complete'}"`, source: 'node' }
        }

        // Expose appointment node result variables
        const apptLabel = node.name || node.type
        const rv = (node.config?.result_variable || '').trim()
        if (rv) {
          if (node.type === 'get_services') {
            variables[rv] = { type: 'text', description: `Selected service ID from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_name`] = { type: 'text', description: `Selected service name from "${apptLabel}"`, source: 'node' }
          }
          if (node.type === 'get_departments') {
            variables[rv] = { type: 'text', description: `Selected department ID from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_name`] = { type: 'text', description: `Selected department name from "${apptLabel}"`, source: 'node' }
          }
          if (node.type === 'check_available_dates') {
            variables[rv] = { type: 'text', description: `Selected date (YYYY-MM-DD) from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_formatted`] = { type: 'text', description: `Selected date formatted (e.g. "Monday, Mar 10") from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_day`] = { type: 'text', description: `Day name (e.g. "Monday") from "${apptLabel}"`, source: 'node' }
          }
          if (node.type === 'check_available_slots') {
            variables[rv] = { type: 'text', description: `Selected time slot from "${apptLabel}" — e.g. "9:00 AM"`, source: 'node' }
          }
          if (node.type === 'book_appointment') {
            variables[`${rv}_id`] = { type: 'text', description: `Appointment ID from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_date`] = { type: 'text', description: `Appointment date from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_time`] = { type: 'text', description: `Appointment time from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_payment_status`] = { type: 'text', description: `Payment status from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_summary`] = { type: 'text', description: `Booking confirmation summary from "${apptLabel}"`, source: 'node' }
          }
          if (node.type === 'cancel_appointment') {
            variables[`${rv}_date`] = { type: 'text', description: `Cancelled appointment date from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_time`] = { type: 'text', description: `Cancelled appointment time from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_summary`] = { type: 'text', description: `Cancellation summary from "${apptLabel}"`, source: 'node' }
          }
          if (node.type === 'get_customer_appointments') {
            variables[`${rv}_count`] = { type: 'text', description: `Total upcoming appointments from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_next_date`] = { type: 'text', description: `Next appointment date from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_next_time`] = { type: 'text', description: `Next appointment time from "${apptLabel}"`, source: 'node' }
            variables[`${rv}_summary`] = { type: 'text', description: `Formatted appointments list from "${apptLabel}" — ready to send`, source: 'node' }
          }
          if (node.type === 'submit_rating') {
            variables[`${rv}_text`] = { type: 'text', description: `Rating label (Poor/Fair/Good/Very Good/Excellent) from "${apptLabel}"`, source: 'node' }
          }
        }

        // Auto-expose selection variables from dynamic button nodes with conditional routing
        // These match what PHP saves: {node_name}_id and {node_name}_name
        if ((node.type === 'button_selection' || node.type === 'text_with_buttons') &&
            node.config?.use_dynamic_buttons &&
            node.config?.dynamic_routing_mode === 'conditional' &&
            node.name) {
          const baseName = node.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
          variables[`${baseName}_id`] = { type: 'text', description: `"${node.name}" — the ID of what the customer tapped`, source: 'auto' }
          variables[`${baseName}_name`] = { type: 'text', description: `"${node.name}" — the label of what the customer tapped`, source: 'auto' }
        }

        // Auto-expose ALL selected item fields from dynamic button nodes
        // PHP saves {baseName}_{fieldName} for every scalar field of the selected array item
        if ((node.type === 'button_selection' || node.type === 'text_with_buttons') &&
            node.config?.use_dynamic_buttons &&
            node.config?.dynamic_array_variable &&
            node.name) {
          const baseName = node.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
          variables[`${baseName}_label`] = { type: 'text', description: `"${node.name}" — button text the customer selected`, source: 'auto' }
          // Expose each field from the array's object structure
          const arrayMeta = this.getArrayVariables()[node.config.dynamic_array_variable]
          if (arrayMeta?.objectFields?.length) {
            arrayMeta.objectFields.forEach(field => {
              variables[`${baseName}_${field}`] = {
                type: 'text',
                description: `"${node.name}" — selected item's ${field}`,
                source: 'auto'
              }
            })
          }
        }

        // Auto-expose {node_name}_label for all button/list nodes.
        // Set at runtime in processReply to the label of whichever button/item the customer tapped.
        if ((node.type === 'button_selection' || node.type === 'text_with_buttons' || node.type === 'list_selection') && node.name) {
          const baseName = node.name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '')
          if (baseName) {
            variables[`${baseName}_label`] = { type: 'text', description: `Label of the button/item tapped in "${node.name}"`, source: 'auto' }
          }
        }
      })

      // If the currently selected node is an api_call with idmsg enabled, expose idmsg for use
      // within that same node (idmsg is resolved before variable substitution in PHP)
      if (this.selectedNodeId) {
        const selfNode = this.flowDefinition.nodes.find(n => n.id === this.selectedNodeId)
        if (selfNode?.type === 'api_call' && selfNode.config?.idmsg_mode && selfNode.config.idmsg_mode !== 'off') {
          variables.idmsg = {
            type: 'text',
            description: `Request ID generated by this node (${selfNode.config.idmsg_mode === 'auto' ? 'UUID' : 'custom'})`,
            source: 'node'
          }
        }
      }

      // Add custom/stored variables from flowDefinition.variables
      // Tag them as 'api' if their name starts with a known API node prefix
      if (this.flowDefinition.variables) {
        const varMeta = this.flowDefinition.variable_meta || {}
        Object.keys(this.flowDefinition.variables).forEach(varName => {
          if (!variables[varName]) {
            const value = this.flowDefinition.variables[varName]
            const isArray = Array.isArray(value)
            const matchedApi = apiNodePrefixes.find(a => varName.startsWith(a.prefix))
            const isSecret = !!(varMeta[varName] && varMeta[varName].secret)

            variables[varName] = {
              type: isSecret ? 'secret' : (isArray ? 'array' : typeof value),
              description: isSecret
                ? 'Encrypted secret — value is never exposed'
                : (matchedApi
                    ? `From "${matchedApi.nodeName}" API call`
                    : 'Custom preset value'),
              value: isSecret ? '' : value,
              source: matchedApi ? 'api' : 'custom',
              secret: isSecret,
              // Array-specific metadata
              isArray: isArray,
              arrayLength: isArray ? value.length : undefined,
              arrayItemType: isArray && value.length > 0 ? typeof value[0] : undefined,
              sampleItems: isArray ? value.slice(0, 3) : undefined
            }
          }
        })
      }

      // Enrich all variables with source node id/name (reuses getNodeProducedVarNames)
      const varSourceMap = {}
      visibleNodes.forEach(node => {
        this.getNodeProducedVarNames(node).forEach(varName => {
          varSourceMap[varName] = { nodeId: node.id, nodeName: node.name || node.type }
        })
      })
      Object.keys(variables).forEach(varName => {
        if (varSourceMap[varName] && !variables[varName].nodeId) {
          variables[varName].nodeId = varSourceMap[varName].nodeId
          variables[varName].nodeName = varSourceMap[varName].nodeName
        }
      })

      return variables
    },

    sessionTimeoutLabel () {
      const s = this.flowDefinition.settings
      if (!s || s.never_expire) return 'Never expires'
      const v = s.timeout_value ?? 24
      const u = s.timeout_unit || 'hours'
      return `${v} ${u}`
    },

    filteredApiVars () {
      const q = (this.apiVarSearch || '').toLowerCase().trim()
      if (!q) return this.flowVariables
      // Split into individual terms — ALL must match (space = underscore for matching)
      const terms = q.split(/\s+/).filter(t => t)
      const result = {}
      for (const [name, info] of Object.entries(this.flowVariables)) {
        const normalizedName = name.toLowerCase().replace(/_/g, ' ')
        const desc = (info.description || '').toLowerCase()
        if (terms.every(t => normalizedName.includes(t) || name.toLowerCase().includes(t) || desc.includes(t))) {
          result[name] = info
        }
      }
      return result
    },

    groupedFlowVariables () {
      const groups = {
        session: { label: 'Customer Info', icon: '👤', desc: 'Always available — phone number and name', vars: {} },
        auto: { label: 'Customer Selections', icon: '👆', desc: 'Saved when the customer taps a button or list item', vars: {} },
        node: { label: 'Customer Inputs', icon: '⌨️', desc: 'Saved when the customer types a reply', vars: {} },
        api: { label: 'API Data', icon: '🌐', desc: 'Data fetched from your connected services', vars: {} },
        google: { label: 'Google Data', icon: '🔵', desc: 'Data from Google Sheets, Drive, and Docs', vars: {} },
        ai: { label: 'AI Responses', icon: '🤖', desc: 'Text generated by OpenAI, Claude, or Gemini', vars: {} },
        custom: { label: 'Preset Values', icon: '⚙️', desc: 'Fixed values you configured manually', vars: {} }
      }
      Object.entries(this.flowVariables).forEach(([name, info]) => {
        const src = info.source || 'custom'
        const target = groups[src] || groups.custom
        target.vars[name] = info
      })
      return groups
    },

    orphanedVarNames () {
      const allReferenced = new Set()
      this.flowDefinition.nodes.forEach(node => {
        const configStr = JSON.stringify(node.config || {})
        const re = /\{\{([^}]+)\}\}/g
        let m
        while ((m = re.exec(configStr)) !== null) {
          allReferenced.add(m[1].trim())
        }
      })
      const defined = new Set(Object.keys(this.flowVariables))
      const orphaned = new Set()
      for (const v of allReferenced) {
        if (!defined.has(v)) orphaned.add(v)
      }
      return orphaned
    },

    aiToolableNodes () {
      const TOOL_TYPES = new Set([
        'api_call',
        'google_sheets_read', 'google_sheets_append', 'google_sheets_update',
        'google_drive_upload', 'google_drive_create_folder',
        'google_docs_create', 'google_docs_append',
        'crm_add_note', 'crm_update_category', 'crm_update_customer',
        'save_lead_field', 'book_appointment',
        'check_lead_complete', 'get_lead_data'
      ])
      return (this.flowDefinition?.nodes || []).filter(n =>
        TOOL_TYPES.has(n.type) && n.id !== this.selectedNode?.id
      )
    },

    upstreamDateNode () { return this._upstreamNodeOfType('check_available_dates') },
    upstreamSlotsNode () { return this._upstreamNodeOfType('check_available_slots') },
    upstreamServicesNode () { return this._upstreamNodeOfType('get_services') },
    upstreamDatesNodeForBooking () { return this._upstreamNodeOfType('check_available_dates') },

    canUndo () {
      return this.historyIndex > 0
    },

    canRedo () {
      return this.historyIndex < this.history.length - 1
    },

    canvasTransform () {
      return {
        transform: `translate(${this.canvasPan.x}px, ${this.canvasPan.y}px) scale(${this.zoomLevel})`
      }
    },

    flowValidation () {
      const warnings = []
      const errors = []

      const reachable = this.getReachableNodeIds()
      const reachableNodes = this.flowDefinition.nodes.filter(n => reachable.has(n.id))

      const hasText = (textObj) => {
        if (!textObj) return false
        const en = (textObj.en || '').trim()
        const ar = (textObj.ar || '').trim()
        return en.length > 0 || ar.length > 0
      }

      const inputNodeTypes = ['text_input', 'number_input', 'email_input', 'date_selection', 'time_selection', 'location_input', 'phone_input', 'rating_input', 'otp_input']

      reachableNodes.forEach(node => {
        const cfg = node.config || {}

        // ── Required content checks ──────────────────────────────────────

        if (node.type === 'message') {
          if (!hasText(cfg.text)) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'empty_text', message: 'Message text is required' })
          }
        }

        if (node.type === 'list_message') {
          if (!cfg.array_variable) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_array', message: 'List Message requires an array variable' })
          }
          if (!hasText(cfg.item_template)) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'empty_template', message: 'List Message requires an item template' })
          }
        }

        if (node.type === 'text_with_buttons') {
          if (!hasText(cfg.text)) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'empty_text', message: 'Message text is required' })
          }
          if (!cfg.use_dynamic_buttons && (!cfg.buttons || cfg.buttons.length === 0)) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'no_buttons', message: 'Button node has no buttons defined' })
          }
          if (!cfg.use_dynamic_buttons && cfg.buttons) {
            const isList = cfg.buttons.length > 3
            const maxLen = isList ? 24 : 20
            cfg.buttons.forEach((btn, i) => {
              const len = (btn.text?.en || btn.text?.ar || '').length
              if (len > maxLen) {
                warnings.push({ nodeId: node.id, nodeName: node.name, type: 'button_text_too_long', message: `Button ${i + 1} text is ${len} chars (max ${maxLen})` })
              }
            })
          }
        }

        if (node.type === 'button_selection') {
          if (!hasText(cfg.body)) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'empty_text', message: 'Message text is required' })
          }
          if (!cfg.use_dynamic_buttons && (!cfg.buttons || cfg.buttons.length === 0)) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'no_buttons', message: 'Button node has no buttons defined' })
          }
          if (!cfg.use_dynamic_buttons && cfg.buttons) {
            const isList = cfg.buttons.length > 3
            const maxLen = isList ? 24 : 20
            cfg.buttons.forEach((btn, i) => {
              if (!btn.text || (!btn.text.en && !btn.text.ar)) {
                errors.push({ nodeId: node.id, nodeName: node.name, type: 'empty_button_text', message: `Button ${i + 1} has no label text` })
              }
              const len = (btn.text?.en || btn.text?.ar || '').length
              if (len > maxLen) {
                warnings.push({ nodeId: node.id, nodeName: node.name, type: 'button_text_too_long', message: `Button ${i + 1} text is ${len} chars (max ${maxLen})` })
              }
            })
          }
        }

        if (node.type === 'list_selection') {
          if (!cfg.rows || cfg.rows.length === 0) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'no_list_items', message: 'List node has no items defined' })
          }
        }

        if (node.type === 'image_message') {
          const imageUrl = (cfg.image_url || '').trim()
          const imageVar = (cfg.image_url_variable || '').trim()
          if (!imageUrl && !imageVar) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_image', message: 'Image URL is required' })
          }
        }

        if (inputNodeTypes.includes(node.type)) {
          if (!(cfg.variable_name || '').trim()) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_variable', message: 'Variable name is required' })
          }
        }

        if (node.type === 'api_call') {
          const hasUrl = (cfg.api_url || '').trim()
          const hasIntegration = cfg.integration_id
          if (!hasUrl && !hasIntegration) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_api_url', message: 'API URL is required' })
          }
        }

        // ── Dead-end check ───────────────────────────────────────────────

        if (this.canHaveOutput(node)) {
          if (!this.hasConnection(node)) {
            errors.push({ nodeId: node.id, nodeName: node.name, type: 'dead_end', message: 'Node has no connections - users will get stuck here' })
          }
        }

        // ── Warning checks ───────────────────────────────────────────────

        if (node.type === 'text_input' && !cfg.input_type) {
          warnings.push({ nodeId: node.id, nodeName: node.name, type: 'missing_input_type', message: 'Text input has no validation type selected' })
        }

        // ── Google Sheets / Drive / Docs ─────────────────────────────────
        if (node.type === 'google_sheets_read' || node.type === 'google_sheets_append' || node.type === 'google_sheets_update') {
          if (!cfg.google_connection_id) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_connection', message: 'Google account is required' })
          if (!cfg.spreadsheet_id) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_spreadsheet', message: 'Spreadsheet is required' })
        }
        if (node.type === 'google_sheets_append' && !cfg.row_data_json) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_row_data', message: 'Row data is required' })
        }
        if (node.type === 'google_sheets_update' && !cfg.match_value) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_match_value', message: 'Match value is required' })
        }
        if (node.type === 'google_drive_upload' || node.type === 'google_drive_create_folder') {
          if (!cfg.google_connection_id) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_connection', message: 'Google account is required' })
        }
        if (node.type === 'google_drive_upload') {
          if (!cfg.file_url) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_file_url', message: 'File URL is required' })
          if (!cfg.file_name) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_file_name', message: 'File Name is required' })
        }
        if (node.type === 'google_drive_create_folder' && !cfg.folder_name) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_folder_name', message: 'Folder Name is required' })
        }
        if (node.type === 'google_docs_create' || node.type === 'google_docs_append') {
          if (!cfg.google_connection_id) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_connection', message: 'Google account is required' })
        }
        if (node.type === 'google_docs_create' && !cfg.doc_title) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_doc_title', message: 'Document title is required' })
        }
        if (node.type === 'google_docs_append' && !cfg.doc_id) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_doc_id', message: 'Document is required' })
        }

        // ── Microsoft Excel / OneDrive ────────────────────────────────────
        if (node.type === 'excel_read' || node.type === 'excel_append' || node.type === 'excel_update') {
          if (!cfg.microsoft_connection_id) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_connection', message: 'Microsoft account is required' })
          if (!cfg.workbook_id) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_workbook', message: 'Workbook is required' })
          if (!cfg.worksheet_name) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_worksheet', message: 'Worksheet is required' })
        }
        if (node.type === 'excel_append' && !cfg.row_data_json) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_row_data', message: 'Row data is required' })
        }
        if (node.type === 'excel_update' && !cfg.match_value) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_match_value', message: 'Match value is required' })
        }
        if (node.type === 'onedrive_upload' || node.type === 'onedrive_create_folder') {
          if (!cfg.microsoft_connection_id) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_connection', message: 'Microsoft account is required' })
        }
        if (node.type === 'onedrive_upload') {
          if (!cfg.file_url) errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_file_url', message: 'File URL is required' })
        }
        if (node.type === 'onedrive_create_folder' && !cfg.folder_name) {
          errors.push({ nodeId: node.id, nodeName: node.name, type: 'missing_folder_name', message: 'Folder Name is required' })
        }
      })

      // ── Settings-level validation ─────────────────────────────────────────
      const s = this.flowDefinition.settings
      if (s.department_handoff_mode) {
        const threshold = s.handoff_late_threshold_minutes
        if (!threshold || threshold < 1) {
          errors.push({ nodeId: '__settings__', nodeName: 'Flow Settings', type: 'invalid_late_threshold', message: 'LATE threshold must be at least 1 minute' })
        }
        const warnBefore = s.handoff_warn_before_24h_minutes
        if (!warnBefore || warnBefore < 1) {
          errors.push({ nodeId: '__settings__', nodeName: 'Flow Settings', type: 'invalid_warn_before', message: '24h warning window must be at least 1 minute' })
        } else if (warnBefore >= 1440) {
          errors.push({ nodeId: '__settings__', nodeName: 'Flow Settings', type: 'invalid_warn_before_max', message: '24h warning cannot exceed 1440 minutes (24 hours)' })
        }
      }

      return { warnings, errors, isValid: errors.length === 0 }
    }
  },

  watch: {
    selectedNode (newNode, oldNode) {
      if (!newNode) return
      if (oldNode?.id === newNode?.id) return
      if (this.isGoogleSheetsNodeType(newNode.type)) {
        this._reloadGsForNode(newNode)
      } else if (this.isGoogleDriveFolderNodeType(newNode.type)) {
        this._reloadGdFoldersForNode(newNode)
      } else if (this.isMicrosoftExcelNodeType(newNode.type)) {
        this._reloadMsForNode(newNode)
      } else if (this.isMicrosoftDriveNodeType(newNode.type)) {
        this._reloadMsDriveFoldersForNode(newNode)
      } else if (newNode.type === 'assign_department') {
        this.loadDepartments()
      }
    },
    // Sanitize configs whenever AI panel is opened or closed (repairs any broken nodes)
    aiPanelOpen () {
      this._sanitizeAllNodeConfigs()
    },
    // Persist AI chat history to localStorage so it survives page rebuilds
    // deep:true is required because we mutate the arrays with .push() not reassignment
    aiMessages: {
      deep: true,
      handler (val) {
        const key = this.flowId ? `ai_flow_messages_${this.flowId}` : 'ai_flow_messages'
        try { localStorage.setItem(key, JSON.stringify(val)) } catch (e) { /* quota */ }
      }
    },
    aiDisplayMessages: {
      deep: true,
      handler (val) {
        const key = this.flowId ? `ai_flow_display_${this.flowId}` : 'ai_flow_display'
        try { localStorage.setItem(key, JSON.stringify(val)) } catch (e) { /* quota */ }
      }
    }
  },

  mounted () {
    // Check if editing existing flow
    this.flowId = this.$route.params.id || null

    if (this.flowId) {
      this.loadFlow()
    } else {
      // Create default welcome node with button_selection (starts with buttons)
      this.addWelcomeNode(400, 200)
    }

    // Load departments, CRM data, and Google connections for node editors
    this.loadDepartments()
    this.loadCustomerCategories()
    this.loadTeamAgents()
    this.loadGoogleConnections()
    this.loadMicrosoftConnections()
    this.loadApprovedTemplates()
    this.loadAllLeadFields()
    this.loadBookingServices()
    this.loadAiSettings()
    this.loadAvailableTags()
    // Restore AI chat history from localStorage (keyed per flow so each flow has its own history)
    try {
      const fid = this.flowId
      const msgKey = fid ? `ai_flow_messages_${fid}` : 'ai_flow_messages'
      const dispKey = fid ? `ai_flow_display_${fid}` : 'ai_flow_display'
      const msgs = localStorage.getItem(msgKey)
      const disp = localStorage.getItem(dispKey)
      if (msgs) this.aiMessages = JSON.parse(msgs)
      if (disp) this.aiDisplayMessages = JSON.parse(disp)
    } catch (e) { /* corrupted storage — ignore */ }

    // Add event listeners
    document.addEventListener('mousemove', this.onMouseMove)
    document.addEventListener('mouseup', this.onMouseUp)
    document.addEventListener('keydown', this.onKeyDown)
    document.addEventListener('mousemove', this.handlePanelResize)
    document.addEventListener('mouseup', this.stopPanelResize)
  },

  beforeUnmount () {
    document.removeEventListener('mousemove', this.onMouseMove)
    document.removeEventListener('mouseup', this.onMouseUp)
    document.removeEventListener('mousemove', this.handlePanelResize)
    document.removeEventListener('mouseup', this.stopPanelResize)
    document.removeEventListener('keydown', this.onKeyDown)
  },

  methods: {
    // BFS helper — finds nearest ancestor node of a given type (up to 8 hops upstream)
    _upstreamNodeOfType (type) {
      if (!this.selectedNode) return null
      const nodes = this.flowDefinition?.nodes || []
      const visited = new Set()
      let frontier = [this.selectedNode.id]
      for (let hop = 0; hop < 8; hop++) {
        const next = []
        for (const id of frontier) {
          if (visited.has(id)) continue
          visited.add(id)
          for (const n of nodes) {
            if (!n || !n.id) continue
            const routeVals = n.routes && typeof n.routes === 'object' ? Object.values(n.routes) : []
            if (n.next !== id && !routeVals.includes(id)) continue
            if (n.type === type) return n
            if (!visited.has(n.id)) next.push(n.id)
          }
        }
        frontier = next
        if (!frontier.length) break
      }
      return null
    },

    // ==================== FLOW MANAGEMENT ====================

    async loadFlow () {
      try {
        const response = await DashboardService.getCustomFlow(this.flowId)
        console.log('Flow response:', response)

        if (response && response.success && response.flow) {
          this.flowName = response.flow.flow_name || 'Untitled Flow'
          this.flowEnabled = response.flow.enabled
          this.targetOwnerId = response.flow.target_owner_id || null
          this.targetOwnerName = response.flow.target_owner_name || ''
          this.targetOwnerCompany = response.flow.target_owner_company || ''

          // Parse flow_definition if it's a string
          let flowDef = response.flow.flow_definition
          if (typeof flowDef === 'string') {
            flowDef = JSON.parse(flowDef)
          }

          // Convert nodes object to array if needed
          if (flowDef.nodes && !Array.isArray(flowDef.nodes)) {
            flowDef.nodes = Object.values(flowDef.nodes)
          }

          // Normalize routes: any node with routes:[] (array) should be routes:{} (object)
          if (Array.isArray(flowDef.nodes)) {
            flowDef.nodes.forEach(node => {
              if (Array.isArray(node.routes)) node.routes = {}
            })
          }

          this.flowDefinition = flowDef

          // Backward compat: ensure settings object exists on older flows
          if (!this.flowDefinition.settings) {
            this.flowDefinition.settings = {}
          }
          const s = this.flowDefinition.settings
          // Migrate old session_timeout_hours → new value+unit format
          if (s.session_timeout_hours !== undefined && s.timeout_value === undefined) {
            const h = parseFloat(s.session_timeout_hours) || 24
            if (h >= 24 && h % 24 === 0) {
              s.timeout_value = h / 24; s.timeout_unit = 'days'
            } else if (h < 1) {
              s.timeout_value = Math.round(h * 60); s.timeout_unit = 'minutes'
            } else {
              s.timeout_value = h; s.timeout_unit = 'hours'
            }
            s.never_expire = (h === 0)
            delete s.session_timeout_hours
          }
          if (s.timeout_value === undefined) s.timeout_value = 24
          if (!s.timeout_unit) s.timeout_unit = 'hours'
          if (s.never_expire === undefined) s.never_expire = false

          // Sanitize max_agent_handle_unit — days is no longer valid (24h window constraint)
          if (s.max_agent_handle_unit === 'days') {
            s.max_agent_handle_value = Math.min((s.max_agent_handle_value || 1) * 24, 24)
            s.max_agent_handle_unit = 'hours'
          }
          // Clamp to 24h ceiling
          if (s.max_agent_handle_unit === 'hours' && s.max_agent_handle_value > 24) s.max_agent_handle_value = 24
          if (s.max_agent_handle_unit === 'minutes' && s.max_agent_handle_value > 1440) s.max_agent_handle_value = 1440

          // Ensure nodes have positions + fix corrupted configs
          if (Array.isArray(this.flowDefinition.nodes)) {
            this.flowDefinition.nodes.forEach(node => {
              if (!node.position) {
                node.position = { x: 100, y: 100 }
              }
              // Fix condition nodes whose config got stored as [] by PHP
              // (PHP converts empty {} to [] on json_decode — conditions are lost)
              if (node.type === 'condition' && (Array.isArray(node.config) || !node.config || typeof node.config !== 'object')) {
                node.config = { conditions: [], else_next: null }
              }
            })
          }

          // Reload departments and Google connections now that targetOwnerId is known.
          this.loadDepartments()
          this.loadGoogleConnections()
        }
      } catch (error) {
        console.error('Failed to load flow:', error)
        alert('Failed to load flow: ' + error.message)
      }
    },

    async loadAvailableTags () {
      try {
        const token = localStorage.getItem('auth_token') || localStorage.getItem('agent_token')
        const res = await fetch('https://api.quali-d.com/customer-tags', {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        this.availableTags = data.tags || []
      } catch (e) {
        console.error('[FlowBuilder] loadAvailableTags error:', e)
      }
    },

    async loadDepartments () {
      try {
        let response
        if (this.targetOwnerId) {
          response = await DashboardService.adminGetUserDepartments(this.targetOwnerId)
        } else {
          response = await DashboardService.getDepartments()
        }
        console.log('[FlowBuilder] loadDepartments response:', response)
        if (response && Array.isArray(response.departments)) {
          this.departments = response.departments
        }
      } catch (e) {
        console.error('[FlowBuilder] loadDepartments exception:', e)
      }
    },

    async loadCustomerCategories () {
      try {
        const response = await DashboardService.getCustomerCategories()
        if (response && response.categories) {
          this.customerCategories = response.categories
        }
      } catch (e) {
        // Silently ignore
      }
    },

    async loadTeamAgents () {
      try {
        const data = await DashboardService.getAgents()
        if (Array.isArray(data)) {
          this.teamAgents = data
        } else if (data && Array.isArray(data.agents)) {
          this.teamAgents = data.agents
        }
      } catch (e) {
        // Silently ignore
      }
    },

    async loadGoogleConnections () {
      try {
        const response = this.targetOwnerId
          ? await DashboardService.adminGetUserGoogleConnections(this.targetOwnerId)
          : await DashboardService.getGoogleConnections()
        if (response && response.connections) {
          this.googleConnections = response.connections
        }
      } catch (e) {
        // Silently ignore — Google not configured yet
      }
    },

    async loadApprovedTemplates () {
      try {
        const response = await DashboardService.getApprovedTemplates()
        if (response && response.templates) {
          this.approvedTemplates = response.templates
        }
      } catch (e) {
        // Silently ignore
      }
    },

    onSendTemplateSelected (node) {
      const tpl = this.approvedTemplates.find(t => t.name === node.config.template_name)
      if (!tpl) {
        node.config.template_language = ''
        node.config.template_components = []
        node.config.header_type = ''
        node.config.header_params = []
        node.config.header_media_url = ''
        node.config.body_params = []
        node.config.button_params = []
        this.onNodePropertyChange()
        return
      }
      node.config.template_language = tpl.language
      node.config.template_components = tpl.components || []

      // Parse header
      const headerComp = (tpl.components || []).find(c => c.type === 'HEADER')
      if (headerComp) {
        const fmt = headerComp.format || 'TEXT'
        node.config.header_type = fmt
        if (fmt === 'TEXT') {
          const count = (headerComp.text || '').match(/\{\{\d+\}\}/g)?.length || 0
          node.config.header_params = Array.from({ length: count }, () => ({ value: '' }))
        } else {
          node.config.header_params = []
          node.config.header_media_url = ''
        }
      } else {
        node.config.header_type = ''
        node.config.header_params = []
        node.config.header_media_url = ''
      }

      // Parse body
      const bodyComp = (tpl.components || []).find(c => c.type === 'BODY')
      const bodyVarCount = bodyComp ? ((bodyComp.text || '').match(/\{\{\d+\}\}/g)?.length || 0) : 0
      node.config.body_params = Array.from({ length: bodyVarCount }, () => ({ value: '' }))

      // Parse buttons with URL variables
      const buttonsComp = (tpl.components || []).find(c => c.type === 'BUTTONS')
      const btnParams = []
      if (buttonsComp && buttonsComp.buttons) {
        buttonsComp.buttons.forEach((btn, idx) => {
          if (btn.type === 'URL' && (btn.url || '').match(/\{\{\d+\}\}/)) {
            btnParams.push({ button_index: idx, button_text: btn.text, value: '' })
          }
        })
      }
      node.config.button_params = btnParams

      this.onNodePropertyChange()
    },

    templatePreviewText (node) {
      const bodyComp = (node.config.template_components || []).find(c => c.type === 'BODY')
      if (!bodyComp) return ''
      let text = bodyComp.text || ''
      ;(node.config.body_params || []).forEach((p, idx) => {
        text = text.replace(`{{${idx + 1}}}`, p.value ? `[${p.value}]` : `{{${idx + 1}}}`)
      })
      return text
    },

    async loadAllLeadFields () {
      try {
        const [companyRes, customRes] = await Promise.all([
          DashboardService.getCompanyLeads(),
          DashboardService.getCustomLeads()
        ])
        const system = (companyRes.companyLeads || []).map(f => ({ ...f, is_custom: false }))
        const custom = (customRes.leads || []).map(f => ({ ...f, is_custom: true }))
        this.allLeadFields = [...system, ...custom]
      } catch (e) {
        // Silently ignore — leads not configured yet
      }
    },

    varTag (name) {
      return '{{' + name + '}}'
    },

    async loadBookingServices () {
      try {
        const res = await DashboardService.getProductsServices()
        this.bookingServices = (res.data || []).map(item => {
          const d = typeof item.data === 'string' ? JSON.parse(item.data) : (item.data || {})
          return { id: item.id, name: d.name || 'Unnamed Service' }
        })
      } catch (e) {
        // Silently ignore — services not configured yet
      }
    },

    isGoogleNodeType (type) {
      return [
        'google_sheets_read', 'google_sheets_append', 'google_sheets_update',
        'google_drive_upload', 'google_drive_create_folder',
        'google_docs_create', 'google_docs_append'
      ].includes(type)
    },

    isGoogleSheetsNodeType (type) {
      return ['google_sheets_read', 'google_sheets_append', 'google_sheets_update'].includes(type)
    },

    isGoogleDriveFolderNodeType (type) {
      return ['google_drive_upload', 'google_drive_create_folder', 'google_docs_create'].includes(type)
    },

    // ── Google Drive folder loading ────────────────────────────────────

    async _reloadGdFoldersForNode (node) {
      this.gdFolders = []
      const cid = node.config?.google_connection_id
      if (cid) await this.loadGdFolders(cid)
    },

    async loadGdFolders (connectionId) {
      if (!connectionId) return
      this.gdLoadingFolders = true
      try {
        const res = await DashboardService.getGoogleDriveFolders(connectionId, this.targetOwnerId || null)
        this.gdFolders = res.folders || []
      } finally {
        this.gdLoadingFolders = false
      }
    },

    async onDriveConnectionChange () {
      this.gdFolders = []
      this.onNodePropertyChange()
      const cid = this.selectedNode.config.google_connection_id
      if (cid) await this.loadGdFolders(cid)
    },

    // ── Google Sheets dynamic loading ─────────────────────────────────

    async _reloadGsForNode (node) {
      this.gsSpreadsheets = []
      this.gsSheetsInSpread = []
      this.gsColumnHeaders = []
      const cfg = node.config || {}
      if (cfg.google_connection_id) {
        await this.loadGsSpreadsheets(cfg.google_connection_id)
        if (cfg.spreadsheet_id) {
          await this.loadGsSheetsInSpread(cfg.google_connection_id, cfg.spreadsheet_id)
          if (cfg.sheet_name) {
            await this.loadGsColumnHeaders(cfg.google_connection_id, cfg.spreadsheet_id, cfg.sheet_name)
          }
        }
      }
      // Populate row builders
      if (node.type === 'google_sheets_append') {
        this.rowDataItems = this._parseRowJson(cfg.row_data_json || '{}')
      }
      if (node.type === 'google_sheets_update') {
        this.setDataItems = this._parseRowJson(cfg.set_data_json || '{}')
      }
    },

    async loadGsSpreadsheets (connectionId) {
      if (!connectionId) return
      this.gsLoadingSpreadsheets = true
      try {
        const res = await DashboardService.getGoogleSpreadsheets(connectionId, this.targetOwnerId || null)
        this.gsSpreadsheets = res.spreadsheets || []
      } finally {
        this.gsLoadingSpreadsheets = false
      }
    },

    async loadGsSheetsInSpread (connectionId, spreadsheetId) {
      if (!connectionId || !spreadsheetId) return
      this.gsLoadingSheets = true
      try {
        const res = await DashboardService.getGoogleSheets(connectionId, spreadsheetId, this.targetOwnerId || null)
        this.gsSheetsInSpread = res.sheets || []
      } finally {
        this.gsLoadingSheets = false
      }
    },

    async loadGsColumnHeaders (connectionId, spreadsheetId, sheetName) {
      if (!connectionId || !spreadsheetId || !sheetName) return
      this.gsLoadingHeaders = true
      try {
        const res = await DashboardService.getGoogleSheetHeaders(connectionId, spreadsheetId, sheetName, this.targetOwnerId || null)
        this.gsColumnHeaders = res.headers || []
        // Persist columns into node config so flowVariables can expose them as chips
        if (this.selectedNode?.type === 'google_sheets_read' && this.gsColumnHeaders.length) {
          this.selectedNode.config.sheet_columns = [...this.gsColumnHeaders]
          this.onNodePropertyChange()
        }
      } finally {
        this.gsLoadingHeaders = false
      }
    },

    sanitizeColName (col) {
      return col.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '')
    },

    // Returns a Set of node IDs that are upstream (reachable ancestors) of targetNodeId.
    // Uses this.edges (already computed) to build a reverse adjacency map, then BFS backwards.
    getUpstreamNodeIds (targetNodeId) {
      if (!targetNodeId) return null
      // Build reverse map: toId -> [fromId, ...]
      const parents = {}
      this.edges.forEach(({ from, to }) => {
        if (!to) return
        if (!parents[to]) parents[to] = []
        parents[to].push(from)
      })
      // BFS backwards
      const upstream = new Set()
      const queue = [...(parents[targetNodeId] || [])]
      while (queue.length) {
        const id = queue.shift()
        if (!id || upstream.has(id)) continue
        upstream.add(id)
        ;(parents[id] || []).forEach(p => { if (!upstream.has(p)) queue.push(p) })
      }
      return upstream
    },

    async onGsConnectionChange () {
      this.gsSpreadsheets = []
      this.gsSheetsInSpread = []
      this.gsColumnHeaders = []
      this.selectedNode.config.spreadsheet_id = ''
      this.selectedNode.config.sheet_name = ''
      this.onNodePropertyChange()
      const cid = this.selectedNode.config.google_connection_id
      if (cid) await this.loadGsSpreadsheets(cid)
    },

    async onGsSpreadsheetChange () {
      this.gsSheetsInSpread = []
      this.gsColumnHeaders = []
      this.selectedNode.config.sheet_name = ''
      this.onNodePropertyChange()
      const { google_connection_id: cid, spreadsheet_id: sid } = this.selectedNode.config
      if (cid && sid) await this.loadGsSheetsInSpread(cid, sid)
    },

    async onGsSheetChange () {
      this.gsColumnHeaders = []
      this.onNodePropertyChange()
      const { google_connection_id: cid, spreadsheet_id: sid, sheet_name: sn } = this.selectedNode.config
      if (cid && sid && sn) await this.loadGsColumnHeaders(cid, sid, sn)
    },

    // ── Microsoft loading helpers ──────────────────────────────────────

    isMicrosoftNodeType (type) {
      return ['excel_read', 'excel_append', 'excel_update', 'onedrive_upload', 'onedrive_create_folder'].includes(type)
    },

    isMicrosoftExcelNodeType (type) {
      return ['excel_read', 'excel_append', 'excel_update'].includes(type)
    },

    isMicrosoftDriveNodeType (type) {
      return ['onedrive_upload', 'onedrive_create_folder'].includes(type)
    },

    async loadMicrosoftConnections () {
      try {
        const response = await DashboardService.getMicrosoftConnections()
        if (response && response.connections) {
          this.microsoftConnections = response.connections
        }
      } catch (e) {}
    },

    async _reloadMsForNode (node) {
      this.msWorkbooks = []
      this.msWorksheetsInBook = []
      this.msColumnHeaders = []
      const cfg = node.config || {}
      if (cfg.microsoft_connection_id) {
        await this.loadMsWorkbooks(cfg.microsoft_connection_id)
        if (cfg.workbook_id) {
          await this.loadMsWorksheetsInBook(cfg.microsoft_connection_id, cfg.workbook_id)
          if (cfg.worksheet_name) {
            await this.loadMsColumnHeaders(cfg.microsoft_connection_id, cfg.workbook_id, cfg.worksheet_name)
          }
        }
      }
      if (node.type === 'excel_append') {
        this.msRowDataItems = this._parseRowJson(cfg.row_data_json || '{}')
      }
      if (node.type === 'excel_update') {
        this.msSetDataItems = this._parseRowJson(cfg.set_data_json || '{}')
      }
    },

    async _reloadMsDriveFoldersForNode (node) {
      this.msDriveFolders = []
      const cid = node.config?.microsoft_connection_id
      if (cid) await this.loadMsDriveFolders(cid)
    },

    async loadMsWorkbooks (connectionId) {
      if (!connectionId) return
      this.msLoadingWorkbooks = true
      try {
        const res = await DashboardService.getMicrosoftWorkbooks(connectionId)
        this.msWorkbooks = res.workbooks || []
      } finally {
        this.msLoadingWorkbooks = false
      }
    },

    async loadMsWorksheetsInBook (connectionId, workbookId) {
      if (!connectionId || !workbookId) return
      this.msLoadingWorksheets = true
      try {
        const res = await DashboardService.getMicrosoftWorksheets(connectionId, workbookId)
        this.msWorksheetsInBook = res.sheets || []
      } finally {
        this.msLoadingWorksheets = false
      }
    },

    async loadMsColumnHeaders (connectionId, workbookId, sheetName) {
      if (!connectionId || !workbookId || !sheetName) return
      this.msLoadingHeaders = true
      try {
        const res = await DashboardService.getMicrosoftWorksheetHeaders(connectionId, workbookId, sheetName)
        this.msColumnHeaders = res.headers || []
        if (this.selectedNode?.type === 'excel_read' && this.msColumnHeaders.length) {
          this.selectedNode.config.sheet_columns = [...this.msColumnHeaders]
          this.onNodePropertyChange()
        }
      } finally {
        this.msLoadingHeaders = false
      }
    },

    async loadMsDriveFolders (connectionId) {
      if (!connectionId) return
      this.msLoadingDriveFolders = true
      try {
        const res = await DashboardService.getMicrosoftDriveFolders(connectionId)
        this.msDriveFolders = res.folders || []
      } finally {
        this.msLoadingDriveFolders = false
      }
    },

    async onMsConnectionChange () {
      this.msWorkbooks = []
      this.msWorksheetsInBook = []
      this.msColumnHeaders = []
      this.selectedNode.config.workbook_id = ''
      this.selectedNode.config.worksheet_name = ''
      this.onNodePropertyChange()
      const cid = this.selectedNode.config.microsoft_connection_id
      if (cid) await this.loadMsWorkbooks(cid)
    },

    async onMsWorkbookChange () {
      this.msWorksheetsInBook = []
      this.msColumnHeaders = []
      this.selectedNode.config.worksheet_name = ''
      this.onNodePropertyChange()
      const { microsoft_connection_id: cid, workbook_id: wid } = this.selectedNode.config
      if (cid && wid) await this.loadMsWorksheetsInBook(cid, wid)
    },

    async onMsWorksheetChange () {
      this.msColumnHeaders = []
      this.onNodePropertyChange()
      const { microsoft_connection_id: cid, workbook_id: wid, worksheet_name: sn } = this.selectedNode.config
      if (cid && wid && sn) await this.loadMsColumnHeaders(cid, wid, sn)
    },

    async onMsDriveConnectionChange () {
      this.msDriveFolders = []
      this.onNodePropertyChange()
      const cid = this.selectedNode.config.microsoft_connection_id
      if (cid) await this.loadMsDriveFolders(cid)
    },

    syncMsRowData (mode) {
      const items = mode === 'append' ? this.msRowDataItems : this.msSetDataItems
      const obj = {}
      items.forEach(item => { if (item.col) obj[item.col] = item.val })
      if (mode === 'append') {
        this.selectedNode.config.row_data_json = JSON.stringify(obj)
      } else {
        this.selectedNode.config.set_data_json = JSON.stringify(obj)
      }
      this.onNodePropertyChange()
    },

    // ── Row/Set data builder helpers ──────────────────────────────────

    _parseRowJson (jsonStr) {
      try {
        const obj = JSON.parse(jsonStr)
        if (typeof obj !== 'object' || Array.isArray(obj)) return [{ col: '', val: '' }]
        const items = Object.entries(obj).map(([col, val]) => ({ col, val: String(val) }))
        return items.length ? items : [{ col: '', val: '' }]
      } catch {
        return [{ col: '', val: '' }]
      }
    },

    _serializeRowItems (items) {
      const obj = {}
      items.forEach(({ col, val }) => { if (col) obj[col] = val })
      return JSON.stringify(obj)
    },

    onRowDataItemChange () {
      this.selectedNode.config.row_data_json = this._serializeRowItems(this.rowDataItems)
      this.onNodePropertyChange()
    },

    addRowDataItem () {
      this.rowDataItems.push({ col: '', val: '' })
    },

    removeRowDataItem (idx) {
      this.rowDataItems.splice(idx, 1)
      if (this.rowDataItems.length === 0) this.rowDataItems.push({ col: '', val: '' })
      this.onRowDataItemChange()
    },

    onSetDataItemChange () {
      this.selectedNode.config.set_data_json = this._serializeRowItems(this.setDataItems)
      this.onNodePropertyChange()
    },

    addSetDataItem () {
      this.setDataItems.push({ col: '', val: '' })
    },

    removeSetDataItem (idx) {
      this.setDataItems.splice(idx, 1)
      if (this.setDataItems.length === 0) this.setDataItems.push({ col: '', val: '' })
      this.onSetDataItemChange()
    },

    async saveFlow () {
      this.saving = true
      let response = null

      try {
        const flowData = {
          flow_name: this.flowName,
          flow_definition: this.flowDefinition,
          enabled: this.flowEnabled ? 1 : 0
        }

        console.log('Saving flow with data:', flowData)

        if (this.flowId) {
          console.log('Updating existing flow:', this.flowId)
          try {
            response = await DashboardService.updateCustomFlow(this.flowId, flowData)
            console.log('Update response received:', response)
          } catch (updateError) {
            console.error('Update error:', updateError)
            throw updateError
          }
        } else {
          console.log('Creating new flow')
          try {
            response = await DashboardService.createCustomFlow(flowData)
            console.log('Create response received:', response)
            if (response && response.success && response.flow_id) {
              this.flowId = response.flow_id
              this.$router.replace({ params: { id: this.flowId } })
            }
          } catch (createError) {
            console.error('Create error:', createError)
            throw createError
          }
        }

        if (response && response.success) {
          this.$toast.success('Flow saved successfully!')
          this.lastSaved = new Date()
        } else {
          console.error('Save failed with response:', response)
          this.$toast.error((response && response.message) || 'Failed to save flow')
        }
      } catch (error) {
        console.error('Failed to save flow:', error)
        this.$toast.error((error && error.message) || 'Failed to save flow')
      } finally {
        this.saving = false
      }
    },

    saveFlowName () {
      if (this.flowId) {
        this.saveFlow()
      }
    },

    async publishFlow () {
      if (!this.flowId) {
        this.$toast.warning('Please save the flow first')
        return
      }

      this.publishing = true
      try {
        const response = await DashboardService.updateCustomFlow(this.flowId, {
          ...this.getCurrentFlowData(),
          enabled: !this.flowEnabled
        })

        if (response && response.success) {
          this.flowEnabled = !this.flowEnabled
          this.$toast.success(this.flowEnabled ? 'Flow published!' : 'Flow unpublished')
        }
      } catch (error) {
        console.error('Failed to publish flow:', error)
        this.$toast.error('Failed to publish flow')
      } finally {
        this.publishing = false
      }
    },

    getCurrentFlowData () {
      return {
        flow_name: this.flowName,
        flow_definition: this.flowDefinition,
        enabled: this.flowEnabled
      }
    },

    goBack () {
      this.$router.push('/custom-flows')
    },

    // ==================== NODE MANAGEMENT ====================

    getNodesByCategory (category) {
      return this.nodeTypes.filter(node =>
        node.category === category &&
        (this.nodeSearchQuery === '' ||
         node.name.toLowerCase().includes(this.nodeSearchQuery.toLowerCase()))
      )
    },

    toggleCategory (category) {
      this.expandedCategories[category] = !this.expandedCategories[category]
    },

    onNodeDragStart (event, nodeType) {
      event.dataTransfer.effectAllowed = 'copy'
      event.dataTransfer.setData('nodeType', JSON.stringify(nodeType))
    },

    onCanvasDrop (event) {
      const nodeTypeData = event.dataTransfer.getData('nodeType')
      if (!nodeTypeData) return

      const nodeType = JSON.parse(nodeTypeData)
      const rect = this.$refs.canvas.getBoundingClientRect()
      const scale = this.zoomLevel
      const x = (event.clientX - rect.left) / scale
      const y = (event.clientY - rect.top) / scale

      this.addNode(nodeType, x, y)
    },

    addNode (nodeType, x, y) {
      const nodeId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      const newNode = {
        id: nodeId,
        type: nodeType.type,
        name: nodeType.name,
        position: { x, y },
        config: this.getDefaultNodeConfig(nodeType.type),
        next: null
      }

      this.flowDefinition.nodes.push(newNode)

      // Set as start node if it's the first node
      if (this.flowDefinition.nodes.length === 1) {
        this.flowDefinition.start_node = nodeId
      }

      this.saveToHistory()
    },

    addWelcomeNode (x, y) {
      const nodeId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Start with button_selection (up to 3 buttons)
      const newNode = {
        id: nodeId,
        type: 'button_selection',
        name: 'Welcome Message',
        position: { x, y },
        config: { ...this.getDefaultNodeConfig('button_selection'), show_back_button: false },
        next: null,
        isWelcomeMessage: true
      }

      this.flowDefinition.nodes.push(newNode)

      // Set as start node
      this.flowDefinition.start_node = nodeId

      this.saveToHistory()
    },

    openDuplicateMenu (event, nodeId) {
      if (this.duplicateMenuNodeId === nodeId) {
        this.duplicateMenuNodeId = null
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      this.duplicateMenuPos = { x: rect.left, y: rect.bottom + 4 }
      this.duplicateMenuNodeId = nodeId
    },

    duplicateNode (nodeId) {
      const original = this.flowDefinition.nodes.find(n => n.id === nodeId)
      if (!original) return

      const newId = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`

      // Deep-clone the node, strip all connections
      const clone = JSON.parse(JSON.stringify(original))
      clone.id = newId
      clone.position = { x: original.position.x + 40, y: original.position.y + 40 }
      clone.next = null
      clone.routes = {}
      clone.isWelcomeMessage = false // duplicates are never the welcome message

      // Clear condition routing stored inside config
      if (clone.type === 'condition' && clone.config) {
        clone.config.else_next = null
        if (clone.config.conditions) {
          clone.config.conditions.forEach(cond => { cond.next_node = null })
        }
      }

      this.flowDefinition.nodes.push(clone)
      this.selectedNodeId = newId
      this.multiSelectMode = true
      this.selectedNodeIds = [newId]
      this.saveToHistory()
    },

    duplicateNodeTree (rootId) {
      const nodeMap = {}
      this.flowDefinition.nodes.forEach(n => { nodeMap[n.id] = n })

      // BFS forward traversal to collect the full subtree
      const visited = new Set()
      const queue = [rootId]
      while (queue.length) {
        const id = queue.shift()
        if (visited.has(id) || !nodeMap[id]) continue
        visited.add(id)
        const node = nodeMap[id]
        if (node.next) queue.push(node.next)
        if (node.routes) Object.values(node.routes).forEach(t => t && queue.push(t))
        if (node.type === 'condition' && node.config) {
          if (node.config.conditions) node.config.conditions.forEach(c => c.next_node && queue.push(c.next_node))
          if (node.config.else_next) queue.push(node.config.else_next)
        }
      }

      // Build old→new ID map and clone each node
      const idMap = {}
      visited.forEach(oldId => {
        idMap[oldId] = `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
      })

      const newNodes = []
      visited.forEach(oldId => {
        const original = nodeMap[oldId]
        const clone = JSON.parse(JSON.stringify(original))
        clone.id = idMap[oldId]
        clone.name = (clone.name || '') + '_1'
        clone.position = { x: original.position.x + 350, y: original.position.y + 350 }
        clone.isWelcomeMessage = false

        // Remap next
        if (clone.next) clone.next = idMap[clone.next] || null

        // Remap routes
        if (clone.routes) {
          Object.keys(clone.routes).forEach(k => {
            clone.routes[k] = clone.routes[k] ? (idMap[clone.routes[k]] || null) : null
          })
        }

        // Remap condition routing
        if (clone.type === 'condition' && clone.config) {
          if (clone.config.conditions) {
            clone.config.conditions.forEach(c => {
              c.next_node = c.next_node ? (idMap[c.next_node] || null) : null
            })
          }
          if (clone.config.else_next) clone.config.else_next = idMap[clone.config.else_next] || null
        }

        newNodes.push(clone)
      })

      newNodes.forEach(n => this.flowDefinition.nodes.push(n))
      this.selectedNodeId = idMap[rootId]
      this.multiSelectMode = true
      this.selectedNodeIds = newNodes.map(n => n.id)
      this.saveToHistory()
    },

    varRef (name) {
      return '\u007B\u007B' + name + '\u007D\u007D'
    },

    getNodeOrphanedVars (node) {
      if (this.orphanedVarNames.size === 0) return []
      const configStr = JSON.stringify(node.config || {})
      return [...this.orphanedVarNames].filter(v => configStr.includes(`{{${v}}}`))
    },

    removeOrphanedVar (varName) {
      const pattern = new RegExp(`\\{\\{${varName}\\}\\}`, 'g')
      this.flowDefinition.nodes.forEach(node => {
        const before = JSON.stringify(node.config || {})
        if (!before.includes(`{{${varName}}}`)) return
        // Replace in stringified config then re-parse back
        const after = before.replace(pattern, '')
        node.config = JSON.parse(after)
      })
      this.saveToHistory()
    },

    getNodeProducedVarNames (node) {
      const vars = []
      const c = node.config || {}

      if (c.variable_name) {
        vars.push(c.variable_name)
        if (['button_selection', 'text_with_buttons', 'list_selection'].includes(node.type)) {
          vars.push(`${c.variable_name}_label`)
        }
        if (node.type === 'image_input') vars.push(`${c.variable_name}_caption`)
        if (node.type === 'location_input') {
          const short = c.variable_name.replace(/_location$/, '')
          vars.push(`raw_${c.variable_name}`, `${short}_latitude`, `${short}_longitude`,
            `${c.variable_name}_maps_url`, `${c.variable_name}_address`, `${c.variable_name}_name`)
        }
      }

      if (c.result_variable) {
        vars.push(c.result_variable)
        if (node.type === 'google_sheets_read') vars.push(`${c.result_variable}_count`)
        if (node.type === 'google_drive_upload') vars.push(`${c.result_variable}_url`)
        if (node.type === 'store_locator') {
          ['_name', '_address', '_lat', '_lng', '_distance_km', '_maps_url', '_directions_url']
            .forEach(s => vars.push(c.result_variable + s))
        }
        if (node.type === 'currency_exchange') {
          vars.push(`${c.result_variable}_rate`, `${c.result_variable}_date`)
        }
        if (node.type === 'book_appointment') {
          ['_id', '_date', '_time', '_payment_status', '_summary'].forEach(s => vars.push(c.result_variable + s))
        }
        if (node.type === 'cancel_appointment') {
          ['_date', '_time', '_summary'].forEach(s => vars.push(c.result_variable + s))
        }
        if (node.type === 'get_customer_appointments') {
          ['_count', '_next_date', '_next_time', '_summary'].forEach(s => vars.push(c.result_variable + s))
        }
        if (node.type === 'get_services') vars.push(`${c.result_variable}_name`)
        if (node.type === 'get_departments') vars.push(`${c.result_variable}_name`)
        if (node.type === 'check_available_dates') {
          vars.push(`${c.result_variable}_formatted`, `${c.result_variable}_day`)
        }
        if (node.type === 'submit_rating') vars.push(`${c.result_variable}_text`)
      }

      if (['openai_call', 'claude_call', 'gemini_call'].includes(node.type) && c.mode === 'extract') {
        ;(c.extract_fields || []).forEach(f => { if (f.var_name) vars.push(f.var_name) })
      }

      if (node.type === 'api_call') {
        Object.values(c.response_mapping || {}).forEach(v => { if (v) vars.push(v) })
        if (c.idmsg_mode && c.idmsg_mode !== 'off') vars.push('idmsg')
      }

      if (node.type === 'set_variable' && c.variable_name) {
        vars.push(c.variable_name)
      }

      if (node.type === 'calculator') {
        ;(c.calculations || []).forEach(calc => { if (calc.result_variable) vars.push(calc.result_variable) })
      }

      if (node.type === 'current_datetime' && c.variable_prefix) {
        const p = c.variable_prefix
        vars.push(`${p}_date`, `${p}_time`, `${p}_datetime`, `${p}_day`, `${p}_day_number`, `${p}_hour`, `${p}_minute`)
      }

      if (node.type === 'get_lead_data') {
        ;(c.mappings || []).forEach(m => { if (m.variable_name) vars.push(m.variable_name) })
      }

      return [...new Set(vars.filter(Boolean))]
    },

    scanVarReferences (varNames, excludeNodeId) {
      const results = []
      const seen = new Set()
      this.flowDefinition.nodes
        .filter(n => n.id !== excludeNodeId)
        .forEach(node => {
          const configStr = JSON.stringify(node.config || {})
          varNames.forEach(varName => {
            const key = `${varName}::${node.id}`
            if (!seen.has(key) && configStr.includes(`{{${varName}}}`)) {
              seen.add(key)
              results.push({ varName, nodeName: node.name || node.type, nodeType: node.type })
            }
          })
        })
      return results
    },

    deleteNode (nodeId) {
      // Check if it's the welcome message
      const node = this.flowDefinition.nodes.find(n => n.id === nodeId)
      if (node && node.isWelcomeMessage) {
        this.$toast.warning('Welcome Message cannot be deleted')
        return
      }

      const producedVars = this.getNodeProducedVarNames(node)
      const affectedVars = producedVars.length ? this.scanVarReferences(producedVars, nodeId) : []

      this.deleteNodeDialog = {
        show: true,
        nodeId,
        nodeName: node.name || node.type,
        producedVars,
        affectedVars
      }
    },

    confirmDeleteNode () {
      const nodeId = this.deleteNodeDialog.nodeId
      this.deleteNodeDialog.show = false

      // Remove node
      this.flowDefinition.nodes = this.flowDefinition.nodes.filter(n => n.id !== nodeId)

      // Remove connections to this node
      this.flowDefinition.nodes.forEach(node => {
        if (node.next === nodeId) {
          node.next = null
        }
        if (node.routes) {
          Object.keys(node.routes).forEach(key => {
            if (node.routes[key] === nodeId) {
              node.routes[key] = null
            }
          })
        }
        // Clear condition node refs
        if (node.type === 'condition' && node.config) {
          if (node.config.else_next === nodeId) node.config.else_next = null
          if (node.config.conditions) {
            node.config.conditions.forEach(cond => {
              if (cond.next_node === nodeId) cond.next_node = null
            })
          }
        }
        // Clear advertiser message slot refs
        if (node.type === 'advertiser' && node.config?.message_nodes) {
          node.config.message_nodes = node.config.message_nodes.map(id => id === nodeId ? null : id)
        }
        // Clear AI tool refs
        if (node.config?.tools) {
          node.config.tools = node.config.tools.filter(t => t.node_id !== nodeId)
        }
      })

      // Clear selection
      if (this.selectedNodeId === nodeId) {
        this.selectedNodeId = null
      }

      // Remove produced vars from flowDefinition.variables (custom/preset vars)
      const producedVarNames = this.deleteNodeDialog.producedVars || []
      producedVarNames.forEach(varName => {
        if (this.flowDefinition.variables?.[varName] !== undefined) {
          delete this.flowDefinition.variables[varName]
        }
        if (this.flowDefinition.variable_meta?.[varName] !== undefined) {
          delete this.flowDefinition.variable_meta[varName]
        }
      })

      this.saveToHistory()
    },

    selectSubtree (nodeId) {
      // BFS from nodeId through all outgoing edges to collect the full subtree
      const visited = new Set()
      const queue = [nodeId]
      while (queue.length) {
        const id = queue.shift()
        if (visited.has(id)) continue
        visited.add(id)
        this.edges.forEach(edge => {
          if (edge.from === id && !visited.has(edge.to)) queue.push(edge.to)
        })
      }
      this.multiSelectMode = true
      this.selectedNodeIds = [...visited]
    },

    toggleMultiSelect () {
      this.multiSelectMode = !this.multiSelectMode
      if (!this.multiSelectMode) {
        this.selectedNodeIds = []
      }
    },

    selectNode (nodeId) {
      // In multi-select mode: toggle node in/out of selection, don't open properties panel
      if (this.multiSelectMode) {
        const idx = this.selectedNodeIds.indexOf(nodeId)
        if (idx !== -1) {
          this.selectedNodeIds.splice(idx, 1)
        } else {
          this.selectedNodeIds.push(nodeId)
        }
        return
      }

      console.log('Selecting node:', nodeId)
      this.selectedNodeId = nodeId
      this.selectedEdge = null
      console.log('Selected node data:', this.selectedNode)

      // Ensure buttons have description field (backwards-compat for older saved flows)
      const selNode = this.selectedNode
      if (selNode && ['button_selection', 'text_with_buttons'].includes(selNode.type)) {
        (selNode.config?.buttons || []).forEach(btn => {
          if (!btn.description) btn.description = { en: '', ar: '' }
        })
      }

      // Auto-connect upstream nodes
      this.$nextTick(() => {
        const node = this.selectedNode
        console.log('[auto-connect] node type:', node?.type, 'config:', JSON.stringify(node?.config))

        if (node?.type === 'check_available_slots' && !node.config?.date_var) {
          const upstream = this.upstreamDateNode
          if (upstream?.config?.result_variable) {
            node.config.date_var = upstream.config.result_variable
            this.onNodePropertyChange()
          }
        }
        if (node?.type === 'book_appointment') {
          const slotsNode = this.upstreamSlotsNode
          console.log('[auto-connect] book_appointment slotsNode:', slotsNode?.id, slotsNode?.config)
          if (slotsNode) {
            if (!node.config?.time_var && slotsNode.config?.result_variable) {
              node.config.time_var = slotsNode.config.result_variable
              this.onNodePropertyChange()
            }
            if (!node.config?.date_var) {
              // Try slots node's own date_var first, then look further upstream
              const dateVar = slotsNode.config?.date_var || this.upstreamDatesNodeForBooking?.config?.result_variable
              if (dateVar) {
                node.config.date_var = dateVar
                this.onNodePropertyChange()
              }
            }
          }
          try {
            const servicesNode = this.upstreamServicesNode
            if (servicesNode && !node.config?.service_id_var && servicesNode.config?.result_variable) {
              node.config.service_id_var = servicesNode.config.result_variable
              this.onNodePropertyChange()
            }
          } catch (e) { /* services auto-connect failed silently */ }
        }
      })
    },

    deselectAll () {
      this.selectedNodeId = null
      this.selectedEdge = null
      this.duplicateMenuNodeId = null
    },

    getDefaultNodeConfig (nodeType) {
      const defaults = {
        message: {
          text: { en: '', ar: '' }
        },
        list_message: {
          array_variable: '',
          item_template: { en: '', ar: '' },
          header: { en: '', ar: '' },
          footer: { en: '', ar: '' },
          separator: 'compact',
          max_items: 0,
          empty_fallback: { en: 'No items found.', ar: 'لا توجد عناصر.' }
        },
        image_message: {
          image_url: '',
          caption: { en: '', ar: '' }
        },
        text_with_buttons: {
          header: { en: '', ar: '' },
          text: { en: '', ar: '' },
          footer: { en: '', ar: '' },
          buttons: [],
          use_dynamic_buttons: false,
          dynamic_array_variable: '',
          button_text_field: '',
          button_id_field: '',
          dynamic_routing_mode: 'end', // 'end' or 'conditional'
          save_selection_id_as: '',
          save_selection_text_as: '',
          empty_array_fallback: { en: 'No options available', ar: 'لا توجد خيارات متاحة' },
          show_back_button: true
        },
        button_selection: {
          header: { en: '', ar: '' },
          body: { en: '', ar: '' },
          footer: { en: '', ar: '' },
          buttons: [],
          use_dynamic_buttons: false,
          dynamic_array_variable: '',
          button_text_field: '',
          button_id_field: '',
          dynamic_routing_mode: 'end', // 'end' or 'conditional'
          save_selection_id_as: '',
          save_selection_text_as: '',
          empty_array_fallback: { en: 'No options available', ar: 'لا توجد خيارات متاحة' },
          show_back_button: true
        },
        list_selection: {
          header: { en: '', ar: '' },
          body: { en: '', ar: '' },
          button_text: { en: 'Select', ar: 'اختر' },
          rows: []
        },
        text_input: {
          prompt: { en: '', ar: '' },
          variable_name: '',
          input_type: 'custom',
          validation: { required: true }
        },
        api_call: {
          api_url: '',
          http_method: 'POST',
          headers: {},
          query_params: {},
          request_body_json: '',
          response_mapping: {},
          api_node_name: ''
        },
        action: {
          action_type: 'save_submission',
          success_message: { en: '', ar: '' }
        },
        restart_flow: {
          message: { en: '', ar: '' }
        },
        assign_department: {
          department_id_var: '',
          department_id_static: '',
          message: { en: '', ar: '' },
          resolve_action: 'restart',
          post_resolve_enabled: false,
          post_resolve_first_node_id: null
        },
        csat_rating: {
          question: { en: 'How would you rate your experience?', ar: '' },
          scale: 5,
          variable_name: 'customer_csat_rating'
        },
        send_email: {
          to: '',
          subject: '',
          body: '',
          from_name: '',
          reply_to: '',
          html_mode: true
        },
        currency_exchange: {
          amount: '',
          from_currency: 'USD',
          to_currency: 'EGP',
          result_variable: 'converted_amount',
          decimal_places: 2
        },
        send_template: {
          template_name: '',
          template_language: 'en',
          template_components: [],
          header_type: '',
          header_params: [],
          header_media_url: '',
          body_params: [],
          button_params: []
        },
        document_message: {
          document_url: '',
          caption: { en: '', ar: '' }
        },
        date_selection: {
          prompt: { en: '', ar: '' },
          button_label: 'View Dates',
          variable_name: '',
          max_future_days: 7,
          max_past_days: 0,
          skip_days: [5, 6],
          skip_today: false,
          display_format: 'D, d M Y',
          output_format: 'd/m/Y'
        },
        time_selection: {
          prompt: { en: '', ar: '' },
          variable_name: '',
          time_format: '12h',
          validation: { required: true }
        },
        location_input: {
          prompt: { en: '', ar: '' },
          variable_name: 'customer_location'
        },
        store_locator: {
          user_location_variable: 'customer_location',
          store_source: 'manual',
          stores: [{ name: '', address: '', lat: '', lng: '' }],
          stores_array_variable: '',
          name_field: 'name',
          address_field: 'address',
          lat_field: 'latitude',
          lng_field: 'longitude',
          result_variable: 'nearest_store'
        },
        image_input: {
          prompt: { en: '', ar: '' },
          invalid_message: { en: '', ar: '' },
          variable_name: 'customer_image'
        },
        google_sheets_read: {
          google_connection_id: '',
          spreadsheet_id: '',
          sheet_name: 'Sheet1',
          range: '',
          filter_column: '',
          filter_operator: '==',
          filter_value: '',
          result_variable: 'sheet_rows',
          limit: 10
        },
        google_sheets_append: {
          google_connection_id: '',
          spreadsheet_id: '',
          sheet_name: 'Sheet1',
          row_data_json: '{}',
          result_variable: ''
        },
        google_sheets_update: {
          google_connection_id: '',
          spreadsheet_id: '',
          sheet_name: 'Sheet1',
          match_column: '',
          match_value: '',
          set_data_json: '{}',
          result_variable: ''
        },
        google_drive_upload: {
          google_connection_id: '',
          file_url: '',
          file_name: '',
          folder_id: '',
          result_variable: 'uploaded_file_id'
        },
        google_drive_create_folder: {
          google_connection_id: '',
          folder_name: '',
          parent_folder_id: '',
          result_variable: 'new_folder_id'
        },
        google_docs_create: {
          google_connection_id: '',
          doc_title: '',
          initial_content: '',
          folder_id: '',
          result_variable: 'created_doc_id'
        },
        google_docs_append: {
          google_connection_id: '',
          doc_id: '',
          append_text: ''
        },
        excel_read: {
          microsoft_connection_id: '',
          workbook_id: '',
          worksheet_name: 'Sheet1',
          filter_column: '',
          filter_operator: '==',
          filter_value: '',
          result_variable: 'excel_rows',
          limit: 10
        },
        excel_append: {
          microsoft_connection_id: '',
          workbook_id: '',
          worksheet_name: 'Sheet1',
          row_data_json: '{}',
          result_variable: ''
        },
        excel_update: {
          microsoft_connection_id: '',
          workbook_id: '',
          worksheet_name: 'Sheet1',
          match_column: '',
          match_value: '',
          set_data_json: '{}',
          result_variable: ''
        },
        onedrive_upload: {
          microsoft_connection_id: '',
          file_url: '',
          file_name: '',
          folder_id: '',
          result_variable: 'uploaded_file_id'
        },
        onedrive_create_folder: {
          microsoft_connection_id: '',
          folder_name: '',
          parent_folder_id: '',
          result_variable: 'new_folder_id'
        },
        openai_call: {
          api_key: '',
          model: 'gpt-4o-mini',
          mode: 'generate',
          inject_session: true,
          inject_history: true,
          history_limit: 5,
          system_prompt: '',
          user_prompt: '',
          intents: [],
          extract_fields: [],
          tools: [],
          result_variable: 'ai_response',
          send_to_customer: false,
          json_mode: false,
          max_tokens: 500,
          temperature: 0.7,
          include_image: false,
          image_url_input: ''
        },
        claude_call: {
          api_key: '',
          model: 'claude-3-5-sonnet-20241022',
          mode: 'generate',
          inject_session: true,
          inject_history: true,
          history_limit: 5,
          system_prompt: '',
          user_prompt: '',
          intents: [],
          extract_fields: [],
          tools: [],
          result_variable: 'ai_response',
          send_to_customer: false,
          json_mode: false,
          max_tokens: 500,
          temperature: 0.7,
          include_image: false,
          image_url_input: ''
        },
        gemini_call: {
          api_key: '',
          model: 'gemini-2.0-flash',
          mode: 'generate',
          inject_session: true,
          inject_history: true,
          history_limit: 5,
          system_prompt: '',
          user_prompt: '',
          intents: [],
          extract_fields: [],
          tools: [],
          result_variable: 'ai_response',
          send_to_customer: false,
          json_mode: false,
          max_tokens: 500,
          temperature: 0.7,
          include_image: false,
          image_url_input: ''
        },
        calculator: {
          calculations: [
            { operand_a: '', operator: '*', operand_b: '', result_variable: '' }
          ]
        },
        delay: {
          delay_value: 5,
          delay_unit: 'seconds'
        },
        current_datetime: {
          variable_prefix: 'now',
          date_format: 'd/m/Y',
          time_format: 'H:i',
          offset_days: 0,
          skip_weekends: false,
          weekend_days: [0, 6]
        },
        qr_generator: {
          qr_data: '',
          size: 300,
          result_variable: 'qr_code_url',
          send_to_customer: true,
          caption: ''
        },
        set_session_timeout: {
          timeout_value: 24,
          timeout_unit: 'hours',
          never_expire: false
        },
        check_24h_window: {
          hours_limit: 24,
          result_variable: ''
        },
        check_working_hours: {
          timezone: 'Africa/Cairo',
          result_variable: ''
        },
        crm_add_note: {
          note: ''
        },
        crm_update_category: {
          category_id_static: '',
          category_id_var: ''
        },
        crm_update_customer: {
          full_name: '',
          address: '',
          vip: '',
          case_resolved: ''
        },
        assign_tag: { tag_id: '' },
        remove_tag: { tag_id: '' },
        check_tag: {},
        notify_team: {
          send_to: 'specific_agent',
          agent_id_static: '',
          agent_id_var: '',
          message: ''
        },
        todo_create: {
          title: '',
          description: '',
          priority: 'medium',
          agent_mode: 'specific',
          agent_id_static: '',
          agent_id_var: '',
          deadline_mode: 'none',
          deadline_days: 3,
          deadline_var: '',
          result_variable: 'todo_id'
        },
        save_lead_field: {
          field_id: '',
          value_var: ''
        },
        check_lead_complete: {
          missing_fields_var: ''
        },
        get_lead_data: {
          mappings: [{ field_id: '', variable_name: '' }]
        },
        get_services: {
          message: 'Please choose a service:',
          button_text: 'View Services',
          result_variable: 'service'
        },
        get_departments: {
          message: 'Please choose a department:',
          button_text: 'View Departments',
          result_variable: 'department'
        },
        check_available_dates: {
          days_ahead: 14,
          max_dates: 7,
          message: 'Please choose a date for your appointment:',
          button_text: 'View Dates',
          result_variable: 'selected_date'
        },
        check_available_slots: {
          date_var: '',
          message: 'Please choose a time slot:',
          button_text: 'View Slots',
          result_variable: 'slots'
        },
        book_appointment: {
          date_var: '',
          time_var: '',
          service_id_static: '',
          service_id_var: '',
          result_variable: 'appointment'
        },
        cancel_appointment: {
          message: 'Which appointment would you like to cancel?',
          button_text: 'My Appointments',
          message_none: 'You have no upcoming appointments to cancel.',
          message_cancelled: '',
          result_variable: 'cancelled'
        },
        get_customer_appointments: {
          max_shown: 5,
          message_none: 'You have no upcoming appointments.',
          result_variable: 'my_appointments'
        },
        submit_rating: {
          rating_var: '',
          feedback_var: '',
          result_variable: 'rating'
        },
        set_variable: {
          variable_name: '',
          value: '',
          transform: 'none',
          transform_param: '',
          transform_param2: '',
          transform_format: 'g:i A'
        },
        condition: {
          conditions: [
            { id: `cond_${Date.now()}`, variable: '', operator: '==', value: '', next_node: null }
          ],
          else_next: null
        },
        advertiser: {
          mode: 'random',
          count: 2,
          message_nodes: [],
          weights: [50, 50],
          slots: [],
          result_variable: ''
        }
      }

      return defaults[nodeType] || {}
    },

    getNodeIcon (nodeType) {
      const node = this.nodeTypes.find(n => n.type === nodeType)
      return node ? node.icon : '📦'
    },

    getNodeTypeName (nodeType) {
      const node = this.nodeTypes.find(n => n.type === nodeType)
      return node ? node.name : nodeType
    },

    getNodeTypeIcon (nodeType) {
      const node = this.nodeTypes.find(n => n.type === nodeType)
      return node ? node.icon : '📦'
    },

    getNodeIconSrcs (nodeType) {
      const chat = require('@/assets/chat-comment-conversation-svgrepo-com.svg')
      const btn = require('@/assets/white-square-button-svgrepo-com.svg')
      const map = {
        message: [chat],
        text_with_buttons: [chat, btn],
        button_selection: [btn],
        send_email: [require('@/assets/mail-svgrepo-com.svg')],
        openai_call: [require('@/assets/openai.svg')],
        claude_call: [require('@/assets/claude-logo.svg')],
        gemini_call: [require('@/assets/google-gemini.svg')],
        excel_read: [require('@/assets/excel.svg')],
        excel_append: [require('@/assets/excel.svg')],
        excel_update: [require('@/assets/excel.svg')],
        onedrive_upload: [require('@/assets/onedrive.svg')],
        onedrive_create_folder: [require('@/assets/onedrive.svg')]
      }
      return map[nodeType] || []
    },

    getNodeIconSrc (nodeType) {
      const srcs = this.getNodeIconSrcs(nodeType)
      return srcs.length ? srcs[0] : null
    },

    getNodePreview (node) {
      if (node.type === 'message' && node.config && node.config.text) {
        return node.config.text?.en || node.config.text?.ar || 'Empty message'
      }
      if (node.type === 'list_message' && node.config) {
        const arrVar = node.config.array_variable || '?'
        const tpl = node.config.item_template?.en || node.config.item_template?.ar || ''
        return tpl ? `📋 ${arrVar} → "${tpl.substring(0, 30)}"` : `📋 ${arrVar} (no template)`
      }
      if (node.type === 'image_message' && node.config) {
        const url = node.config.image_url || ''
        const caption = node.config.caption?.en || node.config.caption?.ar || ''
        if (url && caption) return `📷 ${caption.substring(0, 25)}...`
        if (url) return '📷 Image'
        return '📷 No image URL'
      }
      if (node.type === 'text_with_buttons' && node.config) {
        const text = node.config.text?.en || node.config.text?.ar || ''
        const btnCount = node.config.buttons?.length || 0
        return text ? `${text.substring(0, 30)}... (${btnCount} btns)` : `${btnCount} buttons`
      }
      if (node.type === 'button_selection' && node.config && node.config.buttons) {
        return `${node.config.buttons.length} buttons`
      }
      if (node.type === 'list_selection' && node.config && node.config.rows) {
        return `List with ${node.config.rows.length} items`
      }
      if (node.type === 'text_input' && node.config && node.config.variable_name) {
        return `→ ${node.config.variable_name}`
      }
      if (node.type === 'api_call' && node.config && node.config.api_url) {
        return node.config.api_url
      }
      if (node.type === 'condition' && node.config?.conditions) {
        return `${node.config.conditions.length} condition(s) + else`
      }
      if (node.type === 'crm_add_note') {
        const note = (node.config?.note || '').trim()
        return note ? `"${note.substring(0, 35)}..."` : 'No note text set'
      }
      if (node.type === 'crm_update_category') {
        if (node.config?.category_id_var) return `Category ← {{${node.config.category_id_var}}}`
        if (node.config?.category_id_static) {
          const cat = this.customerCategories.find(c => String(c.id) === String(node.config.category_id_static))
          return cat ? `Move to: ${cat.name}` : `Category ID: ${node.config.category_id_static}`
        }
        return 'No category set'
      }
      if (node.type === 'crm_update_customer') {
        const parts = []
        if ((node.config?.full_name || '').trim()) parts.push('name')
        if ((node.config?.address || '').trim()) parts.push('address')
        if (node.config?.vip !== '') parts.push('vip')
        if (node.config?.case_resolved !== '') parts.push('resolved')
        return parts.length ? `Update: ${parts.join(', ')}` : 'No fields set'
      }
      if (node.type === 'todo_create') {
        const title = (node.config?.title || '').trim()
        const priority = node.config?.priority || 'medium'
        const icons = { low: '🟢', medium: '🟡', high: '🟠', urgent: '🔴' }
        return title ? `${icons[priority]} ${title.substring(0, 30)}` : 'No title set'
      }
      if (node.type === 'get_services') {
        const rv = node.config?.result_variable || 'service'
        return `🛎️ → {{${rv}_name}}`
      }
      if (node.type === 'get_departments') {
        const rv = node.config?.result_variable || 'department'
        return `🏢 → {{${rv}_name}}`
      }
      if (node.type === 'check_available_dates') {
        const rv = node.config?.result_variable || 'selected_date'
        const days = node.config?.days_ahead || 14
        return `📅 Next ${days} days → {{${rv}}}`
      }
      if (node.type === 'check_available_slots') {
        const dv = node.config?.date_var || ''
        const rv = node.config?.result_variable || 'slots'
        return dv ? `🗓️ {{${dv}}} → {{${rv}}}` : 'No date variable set'
      }
      if (node.type === 'book_appointment') {
        const dv = node.config?.date_var || ''
        const tv = node.config?.time_var || ''
        return (dv && tv) ? `✅ {{${dv}}} at {{${tv}}}` : 'Set date + time variables'
      }
      if (node.type === 'cancel_appointment') {
        return '❌ Show list → cancel selected'
      }
      if (node.type === 'get_customer_appointments') {
        const n = node.config?.max_shown || 5
        return `📋 Send up to ${n} upcoming appointments`
      }
      if (node.type === 'submit_rating') {
        const rv = node.config?.rating_var || ''
        return rv ? `⭐ {{${rv}}}` : 'No rating variable set'
      }
      if (node.type === 'csat_rating') {
        const scale = node.config?.scale || 5
        const varName = node.config?.variable_name || 'customer_csat_rating'
        return `⭐ 1–${scale} → {{${varName}}}`
      }
      if (node.type === 'save_lead_field') {
        const field = this.allLeadFields.find(f => String(f.id) === String(node.config?.field_id))
        const fieldName = field ? field.name : (node.config?.field_id ? `#${node.config.field_id}` : '—')
        const varName = node.config?.value_var || ''
        return varName ? `💾 {{${varName}}} → ${fieldName}` : 'No variable selected'
      }
      if (node.type === 'check_lead_complete') {
        return '📋 All required fields filled?'
      }
      if (node.type === 'get_lead_data') {
        const count = (node.config?.mappings || []).filter(m => m.field_id).length
        return count > 0 ? `📥 Load ${count} field${count > 1 ? 's' : ''}` : 'No mappings set'
      }
      if (node.type === 'notify_team') {
        const sendTo = node.config?.send_to || 'specific_agent'
        if (sendTo === 'all_team') return '🔔 Notify all team members'
        if (sendTo === 'random_active') return '🔔 Notify random active agent'
        if (node.config?.agent_id_var) return `🔔 Notify {{${node.config.agent_id_var}}}`
        if (node.config?.agent_id_static) {
          const agent = this.teamAgents.find(a => String(a.id) === String(node.config.agent_id_static))
          return agent ? `🔔 Notify: ${agent.name}` : `🔔 Agent #${node.config.agent_id_static}`
        }
        return '🔔 No recipient set'
      }
      if (node.type === 'calculator' && node.config) {
        const rows = (node.config.calculations || []).filter(c => c.result_variable)
        if (rows.length === 0) return '🧮 No calculations yet'
        const opLabel = { '+': '+', '-': '−', '*': '×', '/': '÷', '%': '%', round: '↻', floor: '⌊⌋', ceil: '⌈⌉', abs: '|x|', min: 'min', max: 'max' }
        if (rows.length === 1) {
          const r = rows[0]
          const a = r.operand_a || '?'
          const b = r.operand_b ? ` ${r.operand_b}` : ''
          return `🧮 ${r.result_variable} = ${a} ${opLabel[r.operator] || r.operator}${b}`
        }
        return `🧮 ${rows.length} calculations`
      }
      if (node.type === 'set_variable' && node.config) {
        const name = node.config.variable_name || '?'
        const val = node.config.value || '?'
        const transform = node.config.transform || 'none'
        if (transform === 'remove_prefix') {
          const param = node.config.transform_param || '…'
          return `📦 ${name} = strip(${param}, ${val.substring(0, 18)})`
        }
        if (transform !== 'none') return `📦 ${name} = ${transform}(${val.substring(0, 20)})`
        return `📦 ${name} = ${val.substring(0, 28)}`
      }
      if (node.type === 'delay') {
        const val = node.config?.delay_value || 5
        const unit = node.config?.delay_unit || 'seconds'
        return `Wait ${val} ${unit}`
      }
      if (node.type === 'current_datetime') {
        const p = node.config?.variable_prefix || 'now'
        const offset = parseInt(node.config?.offset_days || 0) || 0
        const skip = node.config?.skip_weekends ? ' · skip weekends' : ''
        const label = this.offsetDayLabel(offset)
        return `→ {{${p}_date}}, {{${p}_time}} (${label}${skip})`
      }
      if (node.type === 'check_24h_window') {
        const h = node.config?.hours_limit || 24
        return `Within ${h}h or over?`
      }
      if (node.type === 'check_working_hours') {
        const tz = node.config?.timezone || 'UTC'
        return `Company hours check · ${tz}`
      }
      if (node.type === 'send_template') {
        const name = node.config?.template_name || ''
        const lang = node.config?.template_language || ''
        if (name) return `📋 ${name} (${lang})`
        return '📋 Select a template'
      }
      if (node.type === 'qr_generator' && node.config) {
        const data = node.config.qr_data || '?'
        const short = data.length > 28 ? data.slice(0, 28) + '…' : data
        const send = node.config.send_to_customer ? ' · sends to customer' : ''
        return `🔲 ${short}${send}`
      }
      if (node.type === 'restart_flow') {
        const msg = node.config?.message?.en || node.config?.message?.ar || ''
        return msg ? `"${msg.substring(0, 30)}..." then restart` : 'Restarts the flow'
      }
      if (node.type === 'set_session_timeout') {
        if (node.config?.never_expire) return '⏱ Session: never expires'
        const v = node.config?.timeout_value ?? 24
        const u = node.config?.timeout_unit || 'hours'
        return `⏱ Session: ${v} ${u} after last reply`
      }
      if (node.type === 'assign_department') {
        if (node.config?.department_id_var) return `Assign dept: {{${node.config.department_id_var}}}`
        if (node.config?.department_id_static) {
          const dept = this.departments.find(d => String(d.id) === String(node.config.department_id_static))
          return dept ? `Assign: ${dept.name}` : `Assign dept ID: ${node.config.department_id_static}`
        }
        return 'No department configured'
      }
      if (node.type === 'currency_exchange') {
        const amt = node.config?.amount || '?'
        const from = node.config?.from_currency || 'USD'
        const to = node.config?.to_currency || 'EGP'
        const rv = node.config?.result_variable || 'converted_amount'
        return `💱 ${amt} ${from} → ${to} · {{${rv}}}`
      }
      if (node.type === 'send_email') {
        const to = node.config?.to || ''
        const subject = node.config?.subject || ''
        if (to && subject) return `📧 ${to.substring(0, 22)} · "${subject.substring(0, 22)}"`
        if (to) return `📧 To: ${to.substring(0, 30)}`
        return '📧 No recipient configured'
      }
      if (node.type === 'document_message' && node.config) {
        const url = node.config.document_url || ''
        const caption = node.config.caption?.en || node.config.caption?.ar || ''
        if (url && caption) return `📄 ${caption.substring(0, 25)}...`
        if (url) return '📄 ' + (url.split('/').pop() || 'Document')
        return '📄 No document URL'
      }
      if (node.type === 'google_sheets_read' && node.config) {
        const sid = node.config.spreadsheet_id || ''
        const rv = node.config.result_variable || ''
        return sid ? `Read → ${rv || 'array'}` : 'Configure spreadsheet'
      }
      if (node.type === 'google_sheets_append' && node.config) {
        const sid = node.config.spreadsheet_id || ''
        return sid ? `Add row → ${node.config.sheet_name || 'Sheet1'}` : 'Configure spreadsheet'
      }
      if (node.type === 'google_sheets_update' && node.config) {
        const sid = node.config.spreadsheet_id || ''
        const col = node.config.match_column || ''
        return sid && col ? `Update where ${col} matches` : 'Configure spreadsheet'
      }
      if (node.type === 'google_drive_upload' && node.config) {
        const fn = node.config.file_name || ''
        return fn ? `Upload: ${fn.substring(0, 30)}` : 'Configure file upload'
      }
      if (node.type === 'google_drive_create_folder' && node.config) {
        const fn = node.config.folder_name || ''
        return fn ? `Create folder: ${fn.substring(0, 30)}` : 'Configure folder'
      }
      if (node.type === 'google_docs_create' && node.config) {
        const title = node.config.doc_title || ''
        return title ? `Create doc: ${title.substring(0, 30)}` : 'Configure document'
      }
      if (node.type === 'google_docs_append' && node.config) {
        const docId = node.config.doc_id || ''
        return docId ? `Append to doc: ${docId.substring(0, 20)}...` : 'Configure document'
      }
      if (node.type === 'store_locator' && node.config) {
        const rv = node.config.result_variable || 'nearest_store'
        const src = node.config.store_source || 'manual'
        const count = src === 'manual' ? (node.config.stores || []).filter(s => s.lat && s.lng).length : '?'
        const loc = node.config.user_location_variable || '?'
        return `🗺️ {{${loc}}} → ${count} store(s) → {{${rv}_name}}`
      }
      if (['openai_call', 'claude_call', 'gemini_call'].includes(node.type) && node.config) {
        const icons = { openai_call: '🤖', claude_call: '🧠', gemini_call: '✨' }
        const icon = icons[node.type]
        const model = node.config.model || ''
        const mode = node.config.mode || 'generate'
        if (mode === 'classify') {
          const intentCount = (node.config.intents || []).length
          return intentCount ? `${icon} classify → ${intentCount} intents` : `${icon} ${model} — classify`
        }
        if (mode === 'extract') {
          const fieldCount = (node.config.extract_fields || []).length
          return fieldCount ? `${icon} extract ${fieldCount} fields` : `${icon} ${model} — extract`
        }
        const rv = node.config.result_variable || 'ai_response'
        return node.config.user_prompt ? `${icon} ${model} → {{${rv}}}` : `${icon} ${model} — configure`
      }
      if (node.type === 'advertiser' && node.config) {
        const mode = node.config.mode || 'random'
        const connected = (node.config.message_nodes || []).filter(Boolean).length
        const total = node.config.count || 2
        const modeLabel = mode === 'sequential' ? 'Sequential' : mode === 'weighted' ? 'Weighted' : 'Random'
        return `📢 ${modeLabel} — ${connected}/${total} slots connected`
      }
      return 'Configure this node'
    },

    storeLocatorHintHtml (rv) {
      const v = (s) => `<code>{{${rv}_${s}}}</code>`
      return `Saves: ${v('name')} · ${v('address')}<br>${v('lat')} · ${v('lng')} · ${v('distance_km')}<br>${v('maps_url')} · ${v('directions_url')}`
    },

    onImageError (event) {
      event.target.style.display = 'none'
      event.target.parentElement.innerHTML = '<div class="wa-image-error">❌ Invalid image URL</div>'
    },

    ensureCaption (node) {
      if (!node.config.caption) {
        node.config.caption = { en: '', ar: '' }
      }
      return node.config.caption
    },

    ensureHeader (node) {
      if (!node.config.header) {
        node.config.header = { en: '', ar: '' }
      }
      return node.config.header
    },

    ensureFooter (node) {
      if (!node.config.footer) {
        node.config.footer = { en: '', ar: '' }
      }
      return node.config.footer
    },

    insertVariableAtCursor (varName) {
      if (this.activeVarEditor) {
        this.activeVarEditor.insertVariable(varName)
      }
    },

    insertListField (fieldName) {
      if (this.activeVarEditor) {
        this.activeVarEditor.insertVariable(fieldName)
        this.onNodePropertyChange()
      }
    },

    ensureValidation (node) {
      if (!node.config.validation) {
        node.config.validation = { required: true }
      }
      return node.config.validation
    },

    ensurePrompt (node) {
      if (!node.config.prompt) {
        node.config.prompt = { en: '', ar: '' }
      }
      return node.config.prompt
    },

    ensureApiCallConfig (node) {
      if (!node.config.headers) {
        node.config.headers = {}
      }
      if (!node.config.response_mapping) {
        node.config.response_mapping = {}
      }
      if (!node.config.request_body_json) {
        node.config.request_body_json = ''
      }
      if (!node.config.api_url) {
        node.config.api_url = ''
      }
      if (!node.config.http_method) {
        node.config.http_method = 'POST'
      }
      if (!node.config.query_params) {
        node.config.query_params = {}
      }
      if (!node.config.api_node_name) {
        node.config.api_node_name = ''
      }
      if (!node.config.body_format) {
        node.config.body_format = 'json'
      }
      if (!node.config.form_params) {
        node.config.form_params = {}
      }
      if (!node.config.idmsg_mode) {
        node.config.idmsg_mode = 'off'
      }
      if (node.config.idmsg_custom_value === undefined) {
        node.config.idmsg_custom_value = ''
      }
      if (node.config.skip_ssl_verify === undefined) {
        node.config.skip_ssl_verify = false
      }
      return node.config
    },

    // ── AI node helpers ──────────────────────────────────────────────

    ensureAiConfig (node) {
      const c = node.config
      if (!c.mode) c.mode = 'generate'
      if (c.inject_session === undefined) c.inject_session = true
      if (c.inject_history === undefined) c.inject_history = true
      if (c.history_limit === undefined) c.history_limit = 5
      if (c.system_prompt === undefined) c.system_prompt = ''
      if (c.user_prompt === undefined) c.user_prompt = ''
      if (!c.intents) c.intents = []
      if (!c.extract_fields) c.extract_fields = []
      if (!c.tools) c.tools = []
      if (!c.result_variable) c.result_variable = 'ai_response'
      if (c.send_to_customer === undefined) c.send_to_customer = false
      if (c.json_mode === undefined) c.json_mode = false
      if (!c.max_tokens) c.max_tokens = 500
      if (c.temperature === undefined) c.temperature = 0.7
      if (c.include_image === undefined) c.include_image = false
      if (c.image_url_input === undefined) c.image_url_input = ''
      return c
    },

    getAiBannerClass (type) {
      return { openai_call: 'openai-banner', claude_call: 'claude-banner', gemini_call: 'gemini-banner' }[type] || ''
    },

    getAiProviderName (type) {
      return { openai_call: 'OpenAI — ChatGPT', claude_call: 'Anthropic — Claude', gemini_call: 'Google — Gemini' }[type] || 'AI'
    },

    getAiNodeIconSrc (nodeType) {
      return this.getNodeIconSrc(nodeType)
    },

    getAiKeyPlaceholder (type) {
      return { openai_call: 'sk-... or {{openai_api_key}}', claude_call: 'sk-ant-... or {{claude_api_key}}', gemini_call: 'AIza... or {{gemini_api_key}}' }[type] || ''
    },

    getAiModelOptions (type) {
      if (type === 'openai_call') {
        return [
          { value: 'gpt-4o-mini', label: 'gpt-4o-mini' },
          { value: 'gpt-4o', label: 'gpt-4o' },
          { value: 'gpt-4.1-nano', label: 'gpt-4.1-nano' },
          { value: 'gpt-4.1-mini', label: 'gpt-4.1-mini' },
          { value: 'gpt-4.1', label: 'gpt-4.1' },
          { value: 'gpt-5.2', label: 'gpt-5.2' },
          { value: 'gpt-5.1', label: 'gpt-5.1' },
          { value: 'gpt-5-mini', label: 'gpt-5-mini' },
          { value: 'gpt-5-nano', label: 'gpt-5-nano' },
          { value: 'gpt-5-pro', label: '⚠ gpt-5-pro (expensive)' },
          { value: 'gpt-5.2-pro', label: '⚠⚠ gpt-5.2-pro (very expensive)' }
        ]
      }
      if (type === 'claude_call') {
        return [
          { value: 'claude-opus-4-5', label: 'claude-opus-4-5' },
          { value: 'claude-sonnet-4-5', label: 'claude-sonnet-4-5' },
          { value: 'claude-haiku-4-5', label: 'claude-haiku-4-5' },
          { value: 'claude-3-5-sonnet-20241022', label: 'claude-3-5-sonnet-20241022' },
          { value: 'claude-3-5-haiku-20241022', label: 'claude-3-5-haiku-20241022' },
          { value: 'claude-3-opus-20240229', label: 'claude-3-opus-20240229' }
        ]
      }
      if (type === 'gemini_call') {
        return [
          { value: 'gemini-2.0-flash', label: 'gemini-2.0-flash' },
          { value: 'gemini-2.0-flash-lite', label: 'gemini-2.0-flash-lite' },
          { value: 'gemini-1.5-pro', label: 'gemini-1.5-pro' },
          { value: 'gemini-1.5-flash', label: 'gemini-1.5-flash' }
        ]
      }
      return []
    },

    addAiIntent (node) {
      const cfg = this.ensureAiConfig(node)
      cfg.intents = [...cfg.intents, { name: '', description: '' }]
      this.onNodePropertyChange()
    },

    removeAiIntent (node, idx) {
      const cfg = this.ensureAiConfig(node)
      cfg.intents = cfg.intents.filter((_, i) => i !== idx)
      this.onNodePropertyChange()
    },

    addAiExtractField (node) {
      const cfg = this.ensureAiConfig(node)
      cfg.extract_fields = [...cfg.extract_fields, { var_name: '', description: '' }]
      this.onNodePropertyChange()
    },

    removeAiExtractField (node, idx) {
      const cfg = this.ensureAiConfig(node)
      cfg.extract_fields = cfg.extract_fields.filter((_, i) => i !== idx)
      this.onNodePropertyChange()
    },

    // ── Calculator helpers ────────────────────────────────
    addCalcRow (node) {
      if (!node.config.calculations) node.config.calculations = []
      node.config.calculations.push({ operand_a: '', operator: '*', operand_b: '', result_variable: '' })
      this.onNodePropertyChange()
    },

    removeCalcRow (node, idx) {
      node.config.calculations.splice(idx, 1)
      this.onNodePropertyChange()
    },

    calcOpNeedsB (op) {
      return !['floor', 'ceil', 'abs'].includes(op)
    },

    insertCalcVar (varName) {
      if (!this.activeCalcInput || !this.selectedNode) return
      const { idx, field } = this.activeCalcInput
      const calc = (this.selectedNode.config.calculations || [])[idx]
      if (!calc) return
      calc[field === 'b' ? 'operand_b' : 'operand_a'] = `{{${varName}}}`
      this.onNodePropertyChange()
    },
    // ── End calculator helpers ────────────────────────────

    // ── Advertiser helpers ────────────────────────────────
    setAdvSlotCount (node, n) {
      node.config.count = n
      // Trim or extend message_nodes array
      if (!node.config.message_nodes) node.config.message_nodes = []
      while (node.config.message_nodes.length < n) node.config.message_nodes.push(null)
      node.config.message_nodes = node.config.message_nodes.slice(0, n)
      // Same for weights
      if (!node.config.weights) node.config.weights = []
      while (node.config.weights.length < n) node.config.weights.push(1)
      node.config.weights = node.config.weights.slice(0, n)
      this.onNodePropertyChange()
    },

    getAdvSlotLabel (node, idx) {
      const nodeId = (node.config?.message_nodes || [])[idx]
      if (!nodeId) return 'Not connected'
      const targetNode = this.flowDefinition.nodes.find(n => n.id === nodeId)
      if (!targetNode) return 'Unknown node'
      return targetNode.name || targetNode.type.replace(/_/g, ' ')
    },

    setAdvWeight (node, idx, val) {
      if (!node.config.weights) node.config.weights = []
      while (node.config.weights.length <= idx) node.config.weights.push(1)
      node.config.weights[idx] = Math.max(1, parseInt(val) || 1)
      this.onNodePropertyChange()
    },

    getAdvWeightPct (node, idx) {
      const weights = node.config?.weights || []
      const total = weights.reduce((s, w) => s + Math.max(1, parseInt(w) || 1), 0)
      if (total === 0) return 0
      const w = Math.max(1, parseInt(weights[idx]) || 1)
      return Math.round((w / total) * 100)
    },

    clearAdvSlot (node, idx) {
      if (node.config?.message_nodes) {
        node.config.message_nodes[idx] = null
      }
      this.onNodePropertyChange()
    },

    ensureAdvSlot (node, idx) {
      if (!node.config.slots) node.config.slots = []
      while (node.config.slots.length <= idx) node.config.slots.push({})
      if (!node.config.slots[idx]) node.config.slots[idx] = {}
      return node.config.slots[idx]
    },

    toggleAdvCountry (node, idx, code) {
      const slot = this.ensureAdvSlot(node, idx)
      if (!slot.countries) slot.countries = []
      const pos = slot.countries.indexOf(code)
      if (pos === -1) slot.countries.push(code)
      else slot.countries.splice(pos, 1)
      this.onNodePropertyChange()
    },

    getAdvSlotBadges (node, idx) {
      const slot = (node.config?.slots || [])[idx]
      if (!slot) return ''
      const parts = []
      const countries = slot.countries || []
      if (countries.length > 0) parts.push(`🌍${countries.length}`)
      if (slot.time_from || slot.time_to) parts.push('⏰')
      const cap = parseInt(slot.freq_cap) || 0
      if (cap > 0) parts.push(`🔁${cap}/${(slot.freq_period || 'lifetime').slice(0, 3)}`)
      return parts.join(' ')
    },
    // ── End advertiser helpers ────────────────────────────

    nodeNameToFuncName (name) {
      return (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || 'tool'
    },

    isAiToolEnabled (node, toolNodeId) {
      const cfg = this.ensureAiConfig(node)
      return cfg.tools.some(t => t.node_id === toolNodeId)
    },

    getAiToolConfig (node, toolNodeId) {
      const cfg = this.ensureAiConfig(node)
      return cfg.tools.find(t => t.node_id === toolNodeId) || null
    },

    toggleAiTool (node, toolNode) {
      const cfg = this.ensureAiConfig(node)
      const idx = cfg.tools.findIndex(t => t.node_id === toolNode.id)
      if (idx >= 0) {
        cfg.tools.splice(idx, 1)
      } else {
        const baseName = toolNode.config?.label || toolNode.config?.title || toolNode.type.replace(/_/g, ' ')
        cfg.tools.push({
          node_id: toolNode.id,
          name: this.nodeNameToFuncName(baseName),
          description: `Use this to ${baseName.toLowerCase()}`
        })
      }
      this.onNodePropertyChange()
    },

    getToolNodeTypeName (aiNode, toolNodeId) {
      const n = (this.flowDefinition?.nodes || []).find(n => n.id === toolNodeId)
      return n ? this.getNodeTypeName(n.type) : '(disconnected)'
    },

    onSkipWeekendsToggle (node) {
      // Ensure weekend_days is a proper array when the feature is first enabled
      if (!Array.isArray(node.config.weekend_days)) {
        node.config.weekend_days = [0, 6]
      }
    },

    toggleWeekendDay (node, dayValue, event) {
      if (!Array.isArray(node.config.weekend_days)) {
        node.config.weekend_days = []
      }
      const days = node.config.weekend_days.map(Number)
      if (event.target.checked) {
        if (!days.includes(dayValue)) days.push(dayValue)
      } else {
        const idx = days.indexOf(dayValue)
        if (idx !== -1) days.splice(idx, 1)
      }
      node.config.weekend_days = days
      this.onNodePropertyChange()
    },

    toggleSkipDay (node, dayValue, event) {
      if (!Array.isArray(node.config.skip_days)) {
        node.config.skip_days = []
      }
      const days = node.config.skip_days.map(Number)
      if (event.target.checked) {
        if (!days.includes(dayValue)) days.push(dayValue)
      } else {
        const idx = days.indexOf(dayValue)
        if (idx !== -1) days.splice(idx, 1)
      }
      node.config.skip_days = days
      this.onNodePropertyChange()
    },

    offsetDayLabel (n) {
      n = parseInt(n) || 0
      if (n === 0) return 'today'
      if (n === 1) return 'tomorrow'
      if (n === 2) return 'day after tomorrow'
      if (n === -1) return 'yesterday'
      if (n === -2) return 'day before yesterday'
      return n > 0 ? `${n} days from now` : `${Math.abs(n)} days ago`
    },

    // Generates live sample values for the current_datetime node preview
    datetimeSampleValues (node) {
      const base = new Date()
      const offsetDays = parseInt(node.config?.offset_days || 0) || 0
      const skipWeekends = !!node.config?.skip_weekends

      // Apply day offset
      const now = new Date(base)
      now.setDate(now.getDate() + offsetDays)

      // Skip configured days: keep advancing by 1 until we land on a non-skipped day
      const weekendDays = Array.isArray(node.config?.weekend_days) ? node.config.weekend_days.map(Number) : []
      if (skipWeekends && weekendDays.length > 0) {
        let safety = 7
        while (weekendDays.includes(now.getDay()) && safety-- > 0) {
          now.setDate(now.getDate() + 1)
        }
      }

      const pad = n => String(n).padStart(2, '0')
      const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
      const monthsShort = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

      const d = pad(now.getDate())
      const j = String(now.getDate())
      const m = pad(now.getMonth() + 1)
      const Y = String(now.getFullYear())
      const M = monthsShort[now.getMonth()]
      const F = months[now.getMonth()]
      const H = pad(now.getHours())
      const G = String(now.getHours())
      const i = pad(now.getMinutes())
      const s = pad(now.getSeconds())
      const h12 = now.getHours() % 12 || 12
      const h = pad(h12)
      const A = now.getHours() < 12 ? 'AM' : 'PM'
      const N = now.getDay() === 0 ? 7 : now.getDay() // ISO: Mon=1 … Sun=7

      const phpFormat = fmt => fmt
        .replace(/d/g, d).replace(/j/g, j).replace(/m/g, m).replace(/Y/g, Y)
        .replace(/M/g, M).replace(/F/g, F)
        .replace(/H/g, H).replace(/G/g, G).replace(/i/g, i).replace(/s/g, s)
        .replace(/h/g, h).replace(/A/g, A)

      const dateFmt = node.config?.date_format || 'd/m/Y'
      const timeFmt = node.config?.time_format || 'H:i'
      const p = node.config?.variable_prefix || 'now'

      return [
        { key: `${p}_date`, val: phpFormat(dateFmt) },
        { key: `${p}_time`, val: phpFormat(timeFmt) },
        { key: `${p}_datetime`, val: phpFormat(dateFmt) + ' ' + phpFormat(timeFmt) },
        { key: `${p}_day`, val: days[now.getDay()] },
        { key: `${p}_day_number`, val: String(N) },
        { key: `${p}_hour`, val: G },
        { key: `${p}_minute`, val: String(now.getMinutes()) }
      ]
    },

    removeAiTool (node, idx) {
      const cfg = this.ensureAiConfig(node)
      cfg.tools.splice(idx, 1)
      this.onNodePropertyChange()
    },

    updateAiToolField (node, toolNodeId, field, value) {
      const cfg = this.ensureAiConfig(node)
      const tool = cfg.tools.find(t => t.node_id === toolNodeId)
      if (tool) {
        tool[field] = value
        this.onNodePropertyChange()
      }
    },

    addHeader (node) {
      const config = this.ensureApiCallConfig(node)
      // Use a unique key so multiple headers can be added without collision
      let newKey = 'Authorization'
      if (newKey in config.headers) {
        newKey = `Header_${Date.now()}`
      }
      const headers = { ...config.headers, [newKey]: '' }
      node.config.headers = headers
      this.onNodePropertyChange()
    },

    removeHeader (node, key) {
      delete node.config.headers[key]
      this.onNodePropertyChange()
    },

    updateHeaderKey (node, oldKey, newKey) {
      if (oldKey === newKey || !newKey) return
      const value = node.config.headers[oldKey]
      delete node.config.headers[oldKey]
      node.config.headers[newKey] = value
      this.onNodePropertyChange()
    },

    addQueryParam (node) {
      const config = this.ensureApiCallConfig(node)
      const newKey = 'param_name'
      const params = { ...config.query_params, [newKey]: '' }
      node.config.query_params = params
      this.onNodePropertyChange()
    },

    removeQueryParam (node, key) {
      delete node.config.query_params[key]
      this.onNodePropertyChange()
    },

    updateQueryParam (node, oldKey, newKey, value) {
      if (oldKey !== newKey && newKey) {
        delete node.config.query_params[oldKey]
        node.config.query_params[newKey] = value
      } else {
        node.config.query_params[oldKey] = value
      }
      this.onNodePropertyChange()
    },

    addFormParam (node) {
      const config = this.ensureApiCallConfig(node)
      const newKey = 'field_name'
      node.config.form_params = { ...config.form_params, [newKey]: '' }
      this.onNodePropertyChange()
    },

    removeFormParam (node, key) {
      delete node.config.form_params[key]
      this.onNodePropertyChange()
    },

    updateFormParam (node, oldKey, newKey, value) {
      if (oldKey !== newKey && newKey) {
        delete node.config.form_params[oldKey]
        node.config.form_params[newKey] = value
      } else {
        node.config.form_params[oldKey] = value
      }
      this.onNodePropertyChange()
    },

    addResponseMapping (node) {
      const config = this.ensureApiCallConfig(node)
      const newKey = 'response.key'
      const mapping = { ...config.response_mapping, [newKey]: 'variable_name' }
      node.config.response_mapping = mapping
      this.onNodePropertyChange()
    },

    removeResponseMapping (node, key) {
      delete node.config.response_mapping[key]
      this.onNodePropertyChange()
    },

    updateResponseMapping (node, oldKey, newKey, value) {
      if (oldKey === newKey || !newKey) return
      delete node.config.response_mapping[oldKey]
      node.config.response_mapping[newKey] = value
      this.onNodePropertyChange()
    },

    prepareApiTest (node) {
      const config = node.config
      // Collect all text blobs that might contain {{variables}}
      const allText = [
        config.api_url || '',
        config.request_body_json || '',
        ...Object.values(config.headers || {}),
        ...Object.values(config.query_params || {})
      ].join(' ')

      const pattern = /\{\{([^}]+)\}\}/g
      const found = new Set()
      let m
      while ((m = pattern.exec(allText)) !== null) {
        found.add(m[1].trim())
      }

      const varList = Array.from(found)

      if (varList.length === 0) {
        // No variables — run immediately
        this.apiTestVarsDetected = []
        this.runApiTest(node)
        return
      }

      // Pre-fill with preset values if available
      varList.forEach(varName => {
        if (this.testSampleValues[varName] === undefined || this.testSampleValues[varName] === '') {
          const preset = this.flowDefinition.variables?.[varName]
          this.testSampleValues[varName] = (preset !== undefined && preset !== null) ? String(preset) : ''
        }
      })

      this.apiTestVarsDetected = varList
    },

    async runApiTest (node) {
      this.testingApi = true
      this.apiTestResponse = null
      this.apiTestError = null
      this.apiTestNodeId = node.id

      try {
        const config = node.config
        const samples = this.testSampleValues
        let url = this.replaceVariables(config.api_url || '', samples)

        if (!url) {
          throw new Error('API URL is required')
        }

        // Add query parameters for GET requests
        if (config.http_method === 'GET' && config.query_params) {
          const params = new URLSearchParams()
          Object.keys(config.query_params).forEach(key => {
            const value = this.replaceVariables(config.query_params[key], samples)
            if (value) params.append(key, value)
          })
          const queryString = params.toString()
          if (queryString) {
            url = url.includes('?') ? `${url}&${queryString}` : `${url}?${queryString}`
          }
        }

        const headers = { 'Content-Type': 'application/json' }
        Object.keys(config.headers || {}).forEach(key => {
          // Normalize non-breaking spaces (\u00a0) to regular spaces
          headers[key] = this.replaceVariables(config.headers[key], samples).replace(/\u00a0/g, ' ')
        })

        const method = config.http_method || 'POST'
        const bodyJson = this.replaceVariables(config.request_body_json || '{}', samples)

        // Route through server proxy so http:// URLs work (browsers block mixed content)
        const result = await DashboardService.proxyApiTest({
          url,
          method,
          headers,
          body: ['POST', 'PUT', 'PATCH'].includes(method) ? bodyJson : null,
          timeout: config.timeout_seconds || 30,
          skip_ssl_verify: !!config.skip_ssl_verify,
          body_format: config.body_format || 'json',
          form_params: config.form_params || {}
        })

        if (result.error && !result.status_code) {
          throw new Error(result.error)
        }

        const data = result.response

        this.apiTestResponse = data

        if (data && typeof data === 'object') {
          this.detectedResponseKeys = this.detectJsonKeys(data, config.api_node_name || '')
          this.$toast.success(`API call successful! (HTTP ${result.status_code}) Detected ${this.detectedResponseKeys.length} response keys.`)
        } else {
          this.detectedResponseKeys = []
          this.$toast.success(`API call successful! (HTTP ${result.status_code})`)
        }
      } catch (error) {
        this.apiTestError = error.message
        this.$toast.error('API call failed: ' + error.message)
      } finally {
        this.testingApi = false
      }
    },

    replaceVariables (text, sampleValues = {}) {
      if (typeof text !== 'string') return text
      return text.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
        const key = varName.trim()
        if (sampleValues[key] !== undefined && sampleValues[key] !== '') return sampleValues[key]
        if (this.flowDefinition.variables && this.flowDefinition.variables[key] !== undefined) {
          return this.flowDefinition.variables[key]
        }
        return `[${key}]`
      })
    },

    detectJsonKeys (data, nodeNamePrefix) {
      const keys = []
      const prefix = nodeNamePrefix ? `${nodeNamePrefix.replace(/[^a-zA-Z0-9_]/g, '_')}_` : ''

      const detectRecursive = (obj, path = '') => {
        if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
          Object.keys(obj).forEach(key => {
            const currentPath = path ? `${path}.${key}` : key
            const value = obj[key]
            const type = Array.isArray(value) ? 'array' : typeof value

            const keyData = {
              key: currentPath,
              type: type,
              value: value,
              suggestedName: `${prefix}${currentPath.replace(/[^a-zA-Z0-9_]/g, '_')}`,
              varName: `${prefix}${currentPath.replace(/[^a-zA-Z0-9_]/g, '_')}`,
              saved: false
            }

            // Enhanced array metadata
            if (Array.isArray(value)) {
              keyData.arrayLength = value.length
              keyData.arrayItemType = value.length > 0 ? (Array.isArray(value[0]) ? 'array' : typeof value[0]) : 'unknown'
              keyData.sampleItems = value.slice(0, 3) // First 3 items for preview

              // For arrays of objects, detect available fields
              if (value.length > 0 && typeof value[0] === 'object' && !Array.isArray(value[0])) {
                keyData.objectFields = Object.keys(value[0])
              }
            }

            keys.push(keyData)

            // Recursively detect nested keys for objects
            if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
              detectRecursive(value, currentPath)
            }

            // If it's an array and has items, detect keys from the first item
            if (Array.isArray(value) && value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
              detectRecursive(value[0], currentPath + '[0]')
            }
          })
        }
      }

      detectRecursive(data)
      return keys
    },

    saveDetectedVariable (node, item) {
      if (!item.varName) return

      // Sanitize: replace any invalid chars with underscore
      item.varName = item.varName.replace(/[^a-zA-Z0-9_]/g, '_')

      if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(item.varName)) {
        this.$toast.error('Variable name must start with a letter or underscore and contain only letters, numbers, and underscores')
        return
      }

      if (!this.flowDefinition.variables) {
        this.flowDefinition.variables = {}
      }
      if (!this.flowDefinition.variable_meta) {
        this.flowDefinition.variable_meta = {}
      }

      this.flowDefinition.variables[item.varName] = item.value
      this.flowDefinition.variable_meta[item.varName] = {
        ...(this.flowDefinition.variable_meta[item.varName] || {}),
        api_output: true
      }
      item.saved = true
      this.$toast.success(`Saved variable: ${item.varName}`)
      this.saveToHistory()
    },

    saveAllDetectedVariables (node) {
      if (!this.flowDefinition.variables) {
        this.flowDefinition.variables = {}
      }
      if (!this.flowDefinition.variable_meta) {
        this.flowDefinition.variable_meta = {}
      }

      let savedCount = 0
      this.detectedResponseKeys.forEach(item => {
        if (item.varName) {
          item.varName = item.varName.replace(/[^a-zA-Z0-9_]/g, '_')
          if (/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(item.varName)) {
            this.flowDefinition.variables[item.varName] = item.value
            this.flowDefinition.variable_meta[item.varName] = {
              ...(this.flowDefinition.variable_meta[item.varName] || {}),
              api_output: true
            }
            item.saved = true
            savedCount++
          }
        }
      })

      this.$toast.success(`Saved ${savedCount} variables to flow!`)
      this.saveToHistory()
    },

    unsaveDetectedVariable (item) {
      if (!item.varName || !this.flowDefinition.variables) return
      delete this.flowDefinition.variables[item.varName]
      if (this.flowDefinition.variable_meta) {
        delete this.flowDefinition.variable_meta[item.varName]
      }
      item.saved = false
      this.$toast.success(`Removed variable: ${item.varName}`)
      this.saveToHistory()
    },

    getArrayVariables () {
      // Return only array variables from flow variables and detected keys
      const arrayVars = {}

      // Check flow definition variables
      if (this.flowDefinition.variables) {
        Object.keys(this.flowDefinition.variables).forEach(varName => {
          const value = this.flowDefinition.variables[varName]
          if (Array.isArray(value)) {
            arrayVars[varName] = {
              arrayLength: value.length,
              arrayItemType: value.length > 0 ? (typeof value[0]) : 'unknown',
              sampleItems: value.slice(0, 3),
              objectFields: value.length > 0 && typeof value[0] === 'object' && !Array.isArray(value[0]) ? Object.keys(value[0]) : []
            }
          }
        })
      }

      // Also check detected response keys from API test
      this.detectedResponseKeys.forEach(item => {
        if (item.type === 'array' && item.varName && !arrayVars[item.varName]) {
          arrayVars[item.varName] = {
            arrayLength: item.arrayLength || 0,
            arrayItemType: item.arrayItemType || 'unknown',
            sampleItems: item.sampleItems || [],
            objectFields: item.objectFields || []
          }
        }
      })

      return arrayVars
    },

    getSelectedArrayMetadata () {
      // Get metadata for the currently selected array variable in dynamic button config
      if (!this.selectedNode || !this.selectedNode.config || !this.selectedNode.config.dynamic_array_variable) {
        return null
      }

      const varName = this.selectedNode.config.dynamic_array_variable
      const arrayVars = this.getArrayVariables()
      return arrayVars[varName] || null
    },

    getDynamicButtonsPreview () {
      // Get actual button preview data from saved variables
      if (!this.selectedNode || !this.selectedNode.config || !this.selectedNode.config.dynamic_array_variable) {
        return []
      }

      const varName = this.selectedNode.config.dynamic_array_variable
      const textField = this.selectedNode.config.button_text_field
      const idField = this.selectedNode.config.button_id_field

      // Get the actual array data from flow variables
      let arrayData = null
      if (this.flowDefinition.variables && this.flowDefinition.variables[varName]) {
        arrayData = this.flowDefinition.variables[varName]
      }

      if (!arrayData || !Array.isArray(arrayData) || arrayData.length === 0) {
        return []
      }

      // Generate preview buttons from actual data
      return arrayData.slice(0, 3).map((item, index) => {
        if (typeof item === 'object' && item !== null) {
          // For object arrays, use the specified fields
          const buttonText = textField ? item[textField] : `Item ${index + 1}`
          const buttonId = idField ? item[idField] : index
          return { text: buttonText, id: buttonId }
        } else {
          // For primitive arrays (strings, numbers), use the value itself
          return { text: item.toString(), id: item }
        }
      })
    },

    getAutoVarName (node, suffix) {
      // Auto-generate variable name based on node name and suffix
      // Example: node.name = "Select Department" -> "select_department_id" or "select_department_name"
      if (!node || !node.name) {
        return `selection${suffix}`
      }

      // Convert node name to snake_case and add suffix
      const baseName = node.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')

      return `${baseName}${suffix}`
    },

    async copyVariableToClipboard (varName) {
      const textToCopy = `{{${varName}}}`
      try {
        await navigator.clipboard.writeText(textToCopy)
        this.$toast.success(`Copied: ${textToCopy}`)
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = textToCopy
        document.body.appendChild(textArea)
        textArea.select()
        try {
          document.execCommand('copy')
          this.$toast.success(`Copied: ${textToCopy}`)
        } catch (e) {
          this.$toast.error('Failed to copy')
        }
        document.body.removeChild(textArea)
      }
    },

    openVariableModal (varName = null, varValue = '', isSecret = false) {
      if (varName) {
        // Edit mode
        this.editingVariableName = varName
        this.newVariableName = varName
        // For secret variables, never pre-fill the value — user must re-enter to update
        this.newVariableValue = isSecret ? '' : varValue
        this.newVariableSecret = isSecret
      } else {
        // Add mode
        this.editingVariableName = null
        this.newVariableName = ''
        this.newVariableValue = ''
        this.newVariableSecret = false
      }
      this.showVariableModal = true
    },

    onSecretToggle () {
      // When switching the input type from text → password some browsers clear the field.
      // Restore the value on the next tick so nothing is silently lost.
      const saved = this.newVariableValue
      this.$nextTick(() => {
        if (this.newVariableValue !== saved) {
          this.newVariableValue = saved
        }
      })
    },

    closeVariableModal () {
      this.showVariableModal = false
      this.newVariableName = ''
      this.newVariableValue = ''
      this.newVariableSecret = false
      this.editingVariableName = null
    },

    addCustomVariable () {
      if (!this.newVariableName.trim()) {
        this.$toast.warning('Please enter a variable name')
        return
      }

      // Validate variable name (alphanumeric and underscore only)
      if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(this.newVariableName)) {
        this.$toast.error('Variable name must start with a letter or underscore and contain only letters, numbers, and underscores')
        return
      }

      const vars = this.flowDefinition.variables || {}
      const isEditing = !!this.editingVariableName

      // For a new variable, name must not already exist
      if (vars[this.newVariableName] !== undefined && !isEditing) {
        this.$toast.error('Variable already exists')
        return
      }

      // For new secret variables, value is required
      if (this.newVariableSecret && !isEditing && !this.newVariableValue.trim()) {
        this.$toast.error('Please enter a value for the secret variable')
        return
      }

      const isNameChanged = isEditing && this.editingVariableName !== this.newVariableName

      // Determine the stored value:
      // - For secret edit with blank value → keep existing encrypted value
      // - Otherwise use the entered value (backend will encrypt if secret)
      let storedValue = this.newVariableValue
      if (this.newVariableSecret && isEditing && !this.newVariableValue.trim()) {
        // Keep whatever is already stored (the ENC:... ciphertext)
        storedValue = vars[this.editingVariableName] ?? ''
      }

      // Build new variables object
      const updated = { ...vars, [this.newVariableName]: storedValue }
      if (isNameChanged) delete updated[this.editingVariableName]
      this.flowDefinition.variables = updated

      // Update variable_meta for secret flag
      const meta = { ...(this.flowDefinition.variable_meta || {}) }
      if (this.newVariableSecret) {
        meta[this.newVariableName] = { secret: true }
      } else {
        delete meta[this.newVariableName]
      }
      // If name changed, clean up old meta key
      if (isNameChanged) delete meta[this.editingVariableName]
      this.flowDefinition.variable_meta = meta

      const action = isEditing ? 'updated' : 'added'
      const secretNote = this.newVariableSecret ? ' (will be encrypted on save)' : ''
      this.$toast.success(`Variable "${this.newVariableName}" ${action}${secretNote}`)

      // Close modal and reset form
      this.closeVariableModal()

      this.saveToHistory()
    },

    editCustomVariable (varName, varValue, isSecret = false) {
      this.openVariableModal(varName, varValue, isSecret)
    },

    deleteCustomVariable (varName) {
      if (!confirm(`Delete variable "${varName}"?`)) return

      if (this.flowDefinition.variables && this.flowDefinition.variables[varName] !== undefined) {
        const { [varName]: _removed, ...rest } = this.flowDefinition.variables
        this.flowDefinition.variables = rest
        // Also remove from variable_meta
        if (this.flowDefinition.variable_meta && this.flowDefinition.variable_meta[varName]) {
          const { [varName]: _m, ...restMeta } = this.flowDefinition.variable_meta
          this.flowDefinition.variable_meta = restMeta
        }
        this.$toast.success(`Variable "${varName}" deleted`)
        this.saveToHistory()
      }
    },

    getNodeValidationStatus (nodeId) {
      const validation = this.flowValidation
      if (!validation) {
        return { hasError: false, hasWarning: false }
      }
      const hasError = validation.errors && validation.errors.some(e => e.nodeId === nodeId)
      const hasWarning = validation.warnings && validation.warnings.some(w => w.nodeId === nodeId)
      return { hasError, hasWarning }
    },

    hasConnection (node) {
      if (node.type === 'action') return true // Action nodes are endpoints
      if (node.type === 'condition') {
        return !!(node.config?.else_next || node.config?.conditions?.some(c => c.next_node))
      }
      return node.next || (node.routes && Object.keys(node.routes).length > 0)
    },

    isStartNode (nodeId) {
      return this.flowDefinition.start_node === nodeId
    },

    getReachableNodeIds () {
      const startId = this.flowDefinition.start_node
      if (!startId) return new Set()
      const visited = new Set()
      const queue = [startId]
      while (queue.length) {
        const id = queue.shift()
        if (visited.has(id)) continue
        visited.add(id)
        const node = this.flowDefinition.nodes.find(n => n.id === id)
        if (!node) continue
        if (node.next) queue.push(node.next)
        if (node.routes) Object.values(node.routes).filter(Boolean).forEach(t => queue.push(t))
        if (node.config?.conditions) node.config.conditions.forEach(c => c.next_node && queue.push(c.next_node))
        if (node.config?.else_next) queue.push(node.config.else_next)
      }
      return visited
    },

    canHaveOutput (node) {
      // assign_department in dept handoff mode
      if (node.type === 'assign_department' && this.flowDefinition?.settings?.department_handoff_mode) {
        // post_resolve_enabled replaces the normal output with the "On Resolve" port
        if (node.config?.post_resolve_enabled) return false
        return node.config?.resolve_action === 'continue'
      }
      return !['action', 'restart_flow'].includes(node.type)
    },

    hasMultipleOutputs (node) {
      // Dynamic button nodes: use a single output port instead of per-button ports
      if ((node.type === 'text_with_buttons' || node.type === 'button_selection') &&
          node.config?.use_dynamic_buttons) {
        return false
      }
      // AI classify mode gets one port per intent + fallback
      if (['openai_call', 'claude_call', 'gemini_call'].includes(node.type) &&
          node.config?.mode === 'classify') {
        return true
      }
      return ['text_with_buttons', 'button_selection', 'list_selection', 'condition', 'google_sheets_read', 'excel_read', 'image_input', 'check_lead_complete', 'get_services', 'get_departments', 'check_available_dates', 'check_available_slots', 'book_appointment', 'cancel_appointment', 'submit_rating', 'check_24h_window', 'store_locator', 'send_email', 'currency_exchange', 'check_working_hours', 'check_tag'].includes(node.type)
    },

    isPortConnected (node, portId) {
      if (node.type === 'condition') {
        if (portId === 'else') return !!node.config?.else_next
        const cond = (node.config?.conditions || []).find(c => c.id === portId)
        return !!(cond?.next_node)
      }
      return node.routes && node.routes[portId]
    },

    isInputPortConnected (nodeId) {
      // Check if any edge connects TO this node
      return this.edges?.some(edge => edge.to === nodeId) || false
    },

    getNodeCenterY (node) {
      // Calculate the Y position of the center of the node where ports are positioned
      // Ports use CSS: top: 50%; transform: translateY(-50%);
      // So we need to calculate 50% of the actual node height

      // Header height: padding 10px + 10px + content (~20px) + 1px border = ~41px
      let totalHeight = 41

      // Name section height if present: padding 10px + 10px + content (~15px) + 1px border = ~36px
      if (node.name) {
        totalHeight += 36
      }

      // Content height: padding 12px + 12px + min-height 50px = 74px
      // Note: API call nodes don't show content section
      if (node.type !== 'api_call') {
        totalHeight += 74
      }

      // Return 50% of total height
      return node.position.y + (totalHeight / 2)
    },

    getNodeOptions (node) {
      if (node.type === 'text_with_buttons' || node.type === 'button_selection') {
        // Dynamic buttons now use single port (hasMultipleOutputs returns false for them)
        if (node.config?.use_dynamic_buttons) {
          return []
        }
        // Static buttons - show individual ports
        const buttons = node.config?.buttons || []
        return buttons.map((btn, idx) => ({
          id: btn.id,
          label: btn.text?.en || btn.text?.ar || `Button ${idx + 1}`
        }))
      }
      if (node.type === 'list_selection') {
        const rows = node.config?.rows || []
        return rows.map((row, idx) => ({
          id: row.id,
          label: row.title?.en || row.title?.ar || `Item ${idx + 1}`
        }))
      }
      if (node.type === 'google_sheets_read' || node.type === 'excel_read') {
        return [
          { id: 'found', label: '✓ Found' },
          { id: 'not_found', label: '✗ Not Found' }
        ]
      }
      if (node.type === 'image_input') {
        return [
          { id: 'received', label: '🖼 Image received' },
          { id: 'invalid', label: '✗ Not an image' }
        ]
      }
      if (node.type === 'check_lead_complete') {
        return [
          { id: 'complete', label: '✅ Complete' },
          { id: 'incomplete', label: '❌ Incomplete' }
        ]
      }
      if (node.type === 'check_24h_window') {
        const h = node.config?.hours_limit || 24
        return [
          { id: 'within', label: `✅ Within ${h}h` },
          { id: 'over', label: `⏰ Over ${h}h` }
        ]
      }
      if (node.type === 'check_working_hours') {
        return [
          { id: 'open', label: '✅ Open (within hours)' },
          { id: 'closed', label: '🔴 Closed (outside hours)' }
        ]
      }
      if (node.type === 'get_services') {
        return [
          { id: 'selected', label: '✅ Service selected' },
          { id: 'none', label: '❌ No services found' }
        ]
      }
      if (node.type === 'get_departments') {
        return [
          { id: 'selected', label: '✅ Department selected' },
          { id: 'none', label: '❌ No departments found' }
        ]
      }
      if (node.type === 'check_available_dates') {
        return [
          { id: 'selected', label: '✅ Date selected' },
          { id: 'none', label: '❌ No dates available' }
        ]
      }
      if (node.type === 'check_available_slots') {
        return [
          { id: 'selected', label: '✅ Slot selected' },
          { id: 'none', label: '❌ No slots available' }
        ]
      }
      if (node.type === 'check_tag') {
        const ports = this.availableTags.map(t => ({ id: String(t.id), label: t.name }))
        ports.push({ id: 'none', label: 'No Tag' })
        return ports
      }
      if (node.type === 'send_email') {
        return [
          { id: 'success', label: '✅ Sent' },
          { id: 'failed', label: '❌ Failed' }
        ]
      }
      if (node.type === 'currency_exchange') {
        return [
          { id: 'success', label: '✅ Converted' },
          { id: 'failed', label: '❌ Failed' }
        ]
      }
      if (node.type === 'book_appointment') {
        return [
          { id: 'success', label: '✅ Booked' },
          { id: 'failed', label: '❌ Failed' }
        ]
      }
      if (node.type === 'cancel_appointment') {
        return [
          { id: 'cancelled', label: '✅ Cancelled' },
          { id: 'none', label: '❌ No appointments found' }
        ]
      }
      if (node.type === 'get_customer_appointments') {
        return [
          { id: 'found', label: '📅 Has appointments' },
          { id: 'none', label: '❌ No appointments' }
        ]
      }
      if (node.type === 'submit_rating') {
        return [
          { id: 'saved', label: '✅ Rating saved' },
          { id: 'no_appointment', label: '❌ No appointment to rate' }
        ]
      }
      if (node.type === 'store_locator') {
        return [
          { id: 'found', label: '✅ Store found' },
          { id: 'not_found', label: '❌ No location / no stores' }
        ]
      }
      if (node.type === 'condition') {
        const conditions = node.config?.conditions || []
        const options = conditions.map((cond, idx) => ({
          id: cond.id || `cond_${idx}`,
          label: cond.variable && cond.operator
            ? `If ${cond.variable} ${cond.operator}${!['empty', 'not_empty'].includes(cond.operator) ? ' ' + cond.value : ''}`
            : `Condition ${idx + 1}`
        }))
        options.push({ id: 'else', label: 'Else' })
        return options
      }
      // AI classify mode: one port per defined intent + fallback
      if (['openai_call', 'claude_call', 'gemini_call'].includes(node.type) &&
          node.config?.mode === 'classify') {
        const intents = node.config?.intents || []
        const opts = intents
          .filter(i => i.name)
          .map(i => ({ id: i.name, label: i.name }))
        opts.push({ id: 'fallback', label: '⚠ Fallback' })
        return opts
      }
      return []
    },

    getDynamicButtonOptions (node) {
      // Generate button options from dynamic array variable
      const varName = node.config.dynamic_array_variable
      const textField = node.config.button_text_field
      const idField = node.config.button_id_field

      // Get the actual array data from flow variables
      let arrayData = null
      if (this.flowDefinition.variables && this.flowDefinition.variables[varName]) {
        arrayData = this.flowDefinition.variables[varName]
      }

      if (!arrayData || !Array.isArray(arrayData) || arrayData.length === 0) {
        return []
      }

      // Generate options from array data
      const options = arrayData.map((item, index) => {
        if (typeof item === 'object' && item !== null) {
          // For object arrays, use the specified fields
          const buttonText = textField ? item[textField] : `Item ${index + 1}`
          const buttonId = idField ? item[idField] : index
          return { id: buttonId, label: buttonText }
        } else {
          // For primitive arrays (strings, numbers), use the value itself
          return { id: item, label: item.toString() }
        }
      })

      return options
    },

    getNodeTotalHeight (node) {
      let h = 41 // header
      if (node.name) h += 36
      if (node.type !== 'api_call') h += 74
      return h
    },

    getToolPortStyle (node, idx, isAdd) {
      // Total slots = configured tools + 1 add port
      const toolCount = (node.config?.tools || []).length
      const total = toolCount + 1
      const portSize = 14
      const nodeWidth = 280
      const spacing = nodeWidth / (total + 1)
      const left = spacing * (idx + 1) - portSize / 2
      return {
        left: `${left}px`,
        bottom: '-8px',
        transform: 'none'
      }
    },

    getAdvPortStyle (node, idx) {
      const total = node.config?.count || 2
      const portSize = 14
      const nodeWidth = 280
      const spacing = nodeWidth / (total + 1)
      const left = spacing * (idx + 1) - portSize / 2
      return { left: `${left}px`, bottom: '-8px', transform: 'none' }
    },

    createToolCurvePath (x1, y1, x2, y2) {
      const dy = Math.abs(y2 - y1)
      const offset = Math.min(dy / 2, 80)
      return `M ${x1} ${y1} C ${x1} ${y1 + offset}, ${x2} ${y2 - offset}, ${x2} ${y2}`
    },

    getMultiPortStyle (node, index) {
      const options = this.getNodeOptions(node)
      const count = options.length
      if (count === 0) return {}

      // Distribute ports vertically along the right edge
      const nodeHeight = 120 // Approximate node height
      const spacing = nodeHeight / (count + 1)
      const top = spacing * (index + 1)

      return {
        top: `${top}px`,
        transform: 'translateY(-50%)'
      }
    },

    onNodePropertyChange () {
      this.saveToHistory()
    },

    onAgentHandleUnitChange () {
      const max = this.flowDefinition.settings.max_agent_handle_unit === 'minutes' ? 1440 : 24
      if (this.flowDefinition.settings.max_agent_handle_value > max) {
        this.flowDefinition.settings.max_agent_handle_value = max
      }
      this.onNodePropertyChange()
    },

    checkWelcomeMessageButtonType () {
      if (!this.selectedNode || !this.selectedNode.isWelcomeMessage) return

      const buttonCount = this.selectedNode.config.buttons?.length || 0
      const rowCount = this.selectedNode.config.rows?.length || 0
      const totalCount = Math.max(buttonCount, rowCount)

      // 1-3 buttons: use button_selection (reply buttons)
      if (totalCount <= 3 && this.selectedNode.type !== 'button_selection') {
        this.convertWelcomeToButtonSelection()
      } else if (totalCount > 3 && this.selectedNode.type !== 'list_selection') {
        // 4+ buttons: use list_selection
        this.convertWelcomeToListSelection()
      }
    },

    convertWelcomeToButtonSelection () {
      const node = this.selectedNode
      const existingButtons = node.config.buttons || []
      const existingRows = node.config.rows || []

      // Convert rows to buttons if coming from list
      let buttons = existingButtons
      if (existingRows.length > 0 && existingButtons.length === 0) {
        buttons = existingRows.slice(0, 3).map(row => ({
          id: row.id || `btn_${Date.now()}_${Math.random()}`,
          text: row.title || { en: '', ar: '' }
        }))
      }

      node.type = 'button_selection'
      node.config = {
        body: node.config.body || node.config.text || { en: '', ar: '' },
        buttons: buttons.slice(0, 3)
      }

      this.$toast.info('Converted to Reply Buttons (up to 3 buttons)')
    },

    convertWelcomeToListSelection () {
      const node = this.selectedNode
      const existingButtons = node.config.buttons || []
      const existingRows = node.config.rows || []

      // Convert buttons to rows if coming from button_selection
      let rows = existingRows
      if (existingButtons.length > 0 && existingRows.length === 0) {
        rows = existingButtons.map(btn => ({
          id: btn.id || `row_${Date.now()}_${Math.random()}`,
          title: btn.text || { en: '', ar: '' },
          description: { en: '', ar: '' }
        }))
      }

      node.type = 'list_selection'
      node.config = {
        header: node.config.header || { en: '', ar: '' },
        body: node.config.body || node.config.text || { en: '', ar: '' },
        button_text: node.config.button_text || { en: 'View Menu', ar: 'عرض القائمة' },
        rows: rows
      }

      this.$toast.info('Converted to List Menu (4+ items)')
    },

    addButton () {
      if (!this.selectedNode.config.buttons) {
        this.selectedNode.config.buttons = []
      }

      this.selectedNode.config.buttons.push({
        id: `btn_${Date.now()}`,
        text: { en: '', ar: '' },
        description: { en: '', ar: '' }
      })
      if (this.selectedNode.isWelcomeMessage) {
        this.checkWelcomeMessageButtonType()
      }
      this.onNodePropertyChange()
    },

    removeButton (index) {
      this.selectedNode.config.buttons.splice(index, 1)

      // For Welcome Message, check if we need to convert back to button_selection
      if (this.selectedNode.isWelcomeMessage) {
        this.checkWelcomeMessageButtonType()
      }

      this.onNodePropertyChange()
    },

    onButtonDragStart (index) {
      this.dragButtonFromIndex = index
    },

    onButtonDragOver (index) {
      if (this.dragButtonFromIndex === null || this.dragButtonFromIndex === index) return
      const buttons = this.selectedNode.config.buttons
      const moved = buttons.splice(this.dragButtonFromIndex, 1)[0]
      buttons.splice(index, 0, moved)
      this.dragButtonFromIndex = index
    },

    onButtonDragEnd () {
      this.dragButtonFromIndex = null
      this.onNodePropertyChange()
    },

    addListRow () {
      if (!this.selectedNode.config.rows) {
        this.selectedNode.config.rows = []
      }

      // For Welcome Message, allow unlimited rows with pagination
      if (this.selectedNode.isWelcomeMessage) {
        this.selectedNode.config.rows.push({
          id: `row_${Date.now()}`,
          title: { en: '', ar: '' },
          description: { en: '', ar: '' }
        })
        this.checkWelcomeMessageButtonType()
        this.onNodePropertyChange()
      } else if (this.selectedNode.config.rows.length < 10) {
        this.selectedNode.config.rows.push({
          id: `row_${Date.now()}`,
          title: { en: '', ar: '' },
          description: { en: '', ar: '' }
        })
        this.onNodePropertyChange()
      }
    },

    removeListRow (index) {
      this.selectedNode.config.rows.splice(index, 1)

      // For Welcome Message, check if we need to convert back to button_selection
      if (this.selectedNode.isWelcomeMessage) {
        this.checkWelcomeMessageButtonType()
      }

      this.onNodePropertyChange()
    },

    addCondition () {
      // Fix if config is an array (PHP empty-object → [] corruption)
      if (Array.isArray(this.selectedNode.config) || typeof this.selectedNode.config !== 'object') {
        this.selectedNode.config = { conditions: [], else_next: null }
      }
      if (!this.selectedNode.config.conditions) {
        this.selectedNode.config.conditions = []
      }
      this.selectedNode.config.conditions.push({
        id: `cond_${Date.now()}`,
        variable: '',
        operator: '==',
        value: '',
        next_node: null
      })
      this.onNodePropertyChange()
    },

    removeCondition (index) {
      this.selectedNode.config.conditions.splice(index, 1)
      this.onNodePropertyChange()
    },

    getVariableType (nodeType) {
      const types = {
        text_input: 'text',
        number_input: 'number',
        email_input: 'text',
        date_selection: 'date',
        button_selection: 'text',
        list_selection: 'text',
        image_input: 'text'
      }
      return types[nodeType] || 'text'
    },

    // ==================== NODE DRAGGING ====================

    startNodeDrag (event, nodeId) {
      if (event.target.classList.contains('port') ||
          event.target.closest('.btn-delete-node') ||
          event.target.closest('.duplicate-menu-wrap')) {
        return
      }

      this.draggingNode = nodeId
      const node = this.flowDefinition.nodes.find(n => n.id === nodeId)
      const rect = this.$refs.canvas.getBoundingClientRect()
      const scale = this.zoomLevel

      // Calculate the mouse position relative to the canvas in canvas coordinates
      const mouseX = (event.clientX - rect.left - this.canvasPan.x) / scale
      const mouseY = (event.clientY - rect.top - this.canvasPan.y) / scale

      // Store offset as the difference between mouse position and node position
      this.dragOffset = {
        x: mouseX - node.position.x,
        y: mouseY - node.position.y
      }

      // If dragging a selected node in multi-select mode, set up offsets for all selected nodes
      if (this.multiSelectMode && this.selectedNodeIds.includes(nodeId) && this.selectedNodeIds.length > 1) {
        this.multiDragOffsets = {}
        this.selectedNodeIds.forEach(id => {
          const n = this.flowDefinition.nodes.find(n => n.id === id)
          if (n) this.multiDragOffsets[id] = { x: mouseX - n.position.x, y: mouseY - n.position.y }
        })
      } else {
        this.multiDragOffsets = null
      }
    },

    onMouseMove (event) {
      if (this.isPanning) {
        const dx = event.clientX - this.panStart.x
        const dy = event.clientY - this.panStart.y

        // Apply pan delta with boundaries (can't pan to show negative coordinates)
        const newPanX = this.canvasPan.x + dx
        const newPanY = this.canvasPan.y + dy

        // Limit panning: can only pan right (positive X) and down (positive Y)
        // This prevents showing area beyond 0,0 origin
        this.canvasPan.x = Math.min(0, newPanX)
        this.canvasPan.y = Math.min(0, newPanY)

        this.panStart = { x: event.clientX, y: event.clientY }
        return
      }

      if (this.draggingNode) {
        const rect = this.$refs.canvas.getBoundingClientRect()
        const scale = this.zoomLevel
        const mouseX = (event.clientX - rect.left - this.canvasPan.x) / scale
        const mouseY = (event.clientY - rect.top - this.canvasPan.y) / scale

        if (this.multiDragOffsets && this.selectedNodeIds.includes(this.draggingNode)) {
          // Move all selected nodes together
          this.selectedNodeIds.forEach(id => {
            const n = this.flowDefinition.nodes.find(n => n.id === id)
            if (n && this.multiDragOffsets[id]) {
              n.position.x = Math.max(0, mouseX - this.multiDragOffsets[id].x)
              n.position.y = Math.max(0, mouseY - this.multiDragOffsets[id].y)
            }
          })
        } else {
          const node = this.flowDefinition.nodes.find(n => n.id === this.draggingNode)
          if (node) {
            node.position.x = Math.max(0, mouseX - this.dragOffset.x)
            node.position.y = Math.max(0, mouseY - this.dragOffset.y)
          }
        }
      }

      if (this.connectingFrom) {
        const rect = this.$refs.canvas.getBoundingClientRect()
        const scale = this.zoomLevel
        const x2 = (event.clientX - rect.left - this.canvasPan.x) / scale
        const y2 = (event.clientY - rect.top - this.canvasPan.y) / scale

        const fromNode = this.flowDefinition.nodes.find(n => n.id === this.connectingFrom.nodeId)
        if (fromNode) {
          let x1, y1, path
          if (this.connectingFrom.portId?.startsWith('tool__')) {
            const toolIdx = this.connectingFrom.portId === 'tool__new'
              ? (fromNode.config?.tools || []).length
              : parseInt(this.connectingFrom.portId.split('__')[1])
            const toolCount = (fromNode.config?.tools || []).length
            const total = toolCount + 1
            const spacing = 280 / (total + 1)
            x1 = fromNode.position.x + spacing * (toolIdx + 1)
            y1 = fromNode.position.y + this.getNodeTotalHeight(fromNode) + 8
            path = this.createToolCurvePath(x1, y1, x2, y2)
          } else {
            x1 = fromNode.position.x + 280
            y1 = this.getNodeCenterY(fromNode)
            path = this.createCurvePath(x1, y1, x2, y2)
          }
          this.tempConnection = { path }
        }
      }
    },

    onCanvasMouseDown (event) {
      // Close duplicate menu on any canvas click
      if (this.duplicateMenuNodeId) this.duplicateMenuNodeId = null
      // Only start panning if clicking on the canvas background (not on a node)
      if (event.target.classList.contains('canvas-container') ||
          event.target.classList.contains('flow-canvas')) {
        // Clear multi-selection when clicking empty canvas
        if (this.multiSelectMode) {
          this.selectedNodeIds = []
          return
        }
        this.isPanning = true
        this.panStart = { x: event.clientX, y: event.clientY }
        event.preventDefault()
      }
    },

    onMouseUp (event) {
      if (this.isPanning) {
        this.isPanning = false
      }

      if (this.draggingNode) {
        this.saveToHistory()
        this.draggingNode = null
        this.multiDragOffsets = null
      }

      if (this.connectingFrom) {
        const fromNode = this.flowDefinition.nodes.find(n => n.id === this.connectingFrom.nodeId)
        const isToolPort = this.connectingFrom.portId?.startsWith('tool__')
        const isAdvMsgPort = this.connectingFrom.portId?.startsWith('msg__')

        // For tool/advertiser ports: accept drop on the node body OR its input port
        // For regular ports: only accept drop on .port-input
        const targetPort = event.target.closest('.port-input')
        const targetNodeEl = (isToolPort || isAdvMsgPort) ? event.target.closest('.flow-node') : null
        const targetNodeId = targetPort?.dataset.nodeId || targetNodeEl?.dataset?.nodeId

        if (targetNodeId && fromNode && targetNodeId !== this.connectingFrom.nodeId) {
          if (this.connectingFrom.portId) {
            if (fromNode.type === 'condition') {
              if (this.connectingFrom.portId === 'else') {
                fromNode.config.else_next = targetNodeId
              } else {
                const cond = (fromNode.config.conditions || []).find(c => c.id === this.connectingFrom.portId)
                if (cond) cond.next_node = targetNodeId
              }
            } else if (isAdvMsgPort) {
              // Advertiser message slot — store connected node ID in config
              const slotIdx = parseInt(this.connectingFrom.portId.split('__')[1])
              if (!fromNode.config.message_nodes) fromNode.config.message_nodes = []
              while (fromNode.config.message_nodes.length <= slotIdx) fromNode.config.message_nodes.push(null)
              fromNode.config.message_nodes[slotIdx] = targetNodeId
            } else if (isToolPort) {
              // Ensure tools array exists reactively
              if (!fromNode.config.tools) fromNode.config.tools = []
              if (this.connectingFrom.portId === 'tool__new') {
                const targetNode = this.flowDefinition.nodes.find(n => n.id === targetNodeId)
                const baseName = (targetNode?.type || 'tool').replace(/_/g, '_')
                fromNode.config.tools.push({ node_id: targetNodeId, name: baseName, description: `Use this to ${(targetNode?.type || 'tool').replace(/_/g, ' ')}` })
              } else {
                const toolIdx = parseInt(this.connectingFrom.portId.split('__')[1])
                if (fromNode.config.tools[toolIdx]) fromNode.config.tools[toolIdx].node_id = targetNodeId
              }
            } else if (this.connectingFrom.portId === 'post_resolve') {
              // assign_department On Resolve chain — store in node config, not routes
              fromNode.config.post_resolve_first_node_id = targetNodeId
            } else {
              if (!fromNode.routes || Array.isArray(fromNode.routes)) fromNode.routes = {}
              fromNode.routes[this.connectingFrom.portId] = targetNodeId
            }
          } else {
            fromNode.next = targetNodeId
          }
          this.saveToHistory()
        }

        this.connectingFrom = null
        this.tempConnection = null
      }
    },

    startConnectionDrag (event, nodeId, portId = null) {
      this.connectingFrom = { nodeId, portId }
    },

    // ==================== EDGE RENDERING ====================

    getEdgePath (edge) {
      const fromNode = this.flowDefinition.nodes.find(n => n.id === edge.from)
      const toNode = this.flowDefinition.nodes.find(n => n.id === edge.to)

      if (!fromNode || !toNode) return ''

      // Tool port: start from bottom of AI node
      if (edge.portId?.startsWith('tool__')) {
        const toolIdx = parseInt(edge.portId.split('__')[1])
        const toolCount = (fromNode.config?.tools || []).length
        const total = toolCount + 1
        const spacing = 280 / (total + 1)
        const x1 = fromNode.position.x + spacing * (toolIdx + 1)
        const y1 = fromNode.position.y + this.getNodeTotalHeight(fromNode) + 8
        const x2 = toNode.position.x
        const y2 = this.getNodeCenterY(toNode)
        return this.createToolCurvePath(x1, y1, x2, y2)
      }

      // Advertiser msg port: start from bottom port, connect to input (left) of target
      if (edge.portId?.startsWith('msg__')) {
        const msgIdx = parseInt(edge.portId.split('__')[1])
        const total = fromNode.config?.count || 2
        const spacing = 280 / (total + 1)
        const x1 = fromNode.position.x + spacing * (msgIdx + 1)
        const y1 = fromNode.position.y + this.getNodeTotalHeight(fromNode) + 8
        const x2 = toNode.position.x
        const y2 = this.getNodeCenterY(toNode)
        return this.createToolCurvePath(x1, y1, x2, y2)
      }

      // Calculate start position
      const x1 = fromNode.position.x + 280 // Right edge of node
      let y1 = this.getNodeCenterY(fromNode) // Center of node (dynamically calculated)

      // If connecting from a specific port (button/list item), adjust Y position
      if (edge.portId && this.hasMultipleOutputs(fromNode)) {
        const options = this.getNodeOptions(fromNode)
        const portIndex = options.findIndex(opt => opt.id === edge.portId)
        if (portIndex >= 0) {
          const count = options.length
          const nodeHeight = 120
          const spacing = nodeHeight / (count + 1)
          y1 = fromNode.position.y + spacing * (portIndex + 1)
        }
      }

      // Calculate end position (input port on left side)
      const x2 = toNode.position.x
      const y2 = this.getNodeCenterY(toNode) // Center of node (dynamically calculated)

      return this.createCurvePath(x1, y1, x2, y2)
    },

    createCurvePath (x1, y1, x2, y2) {
      const dx = Math.abs(x2 - x1)
      const offset = Math.min(dx / 2, 100)

      return `M ${x1} ${y1} C ${x1 + offset} ${y1}, ${x2 - offset} ${y2}, ${x2} ${y2}`
    },

    selectEdge (edge) {
      this.selectedEdge = edge
      this.selectedNodeId = null
    },

    deleteSelectedEdge () {
      if (!this.selectedEdge) return

      const fromNode = this.flowDefinition.nodes.find(n => n.id === this.selectedEdge.from)
      if (!fromNode) return

      if (this.selectedEdge.portId?.startsWith('tool__')) {
        const toolIdx = parseInt(this.selectedEdge.portId.split('__')[1])
        const tools = fromNode.config?.tools || []
        if (tools[toolIdx]) tools[toolIdx].node_id = null
      } else if (this.selectedEdge.portId?.startsWith('msg__')) {
        const msgIdx = parseInt(this.selectedEdge.portId.split('__')[1])
        if (fromNode.config?.message_nodes) fromNode.config.message_nodes[msgIdx] = null
      } else if (this.selectedEdge.portId && fromNode.type === 'condition') {
        // Condition node: remove from config
        if (this.selectedEdge.portId === 'else') {
          fromNode.config.else_next = null
        } else {
          const cond = (fromNode.config.conditions || []).find(c => c.id === this.selectedEdge.portId)
          if (cond) cond.next_node = null
        }
      } else if (this.selectedEdge.portId && fromNode.routes) {
        // Route-based connection (button/list)
        delete fromNode.routes[this.selectedEdge.portId]
      } else {
        // Regular next connection
        fromNode.next = null
      }

      this.selectedEdge = null
      this.saveToHistory()
    },

    deleteEdge (edge) {
      const fromNode = this.flowDefinition.nodes.find(n => n.id === edge.from)
      if (!fromNode) return

      if (edge.portId?.startsWith('tool__')) {
        const toolIdx = parseInt(edge.portId.split('__')[1])
        const tools = fromNode.config?.tools || []
        if (tools[toolIdx]) tools[toolIdx].node_id = null
      } else if (edge.portId && fromNode.type === 'condition') {
        // Condition node: remove from config
        if (edge.portId === 'else') {
          fromNode.config.else_next = null
        } else {
          const cond = (fromNode.config.conditions || []).find(c => c.id === edge.portId)
          if (cond) cond.next_node = null
        }
      } else if (edge.portId && fromNode.routes) {
        // Route-based connection (button/list)
        delete fromNode.routes[edge.portId]
      } else {
        // Regular next connection
        fromNode.next = null
      }

      this.saveToHistory()
    },

    getEdgeDeleteButtonPosition (edge) {
      const fromNode = this.flowDefinition.nodes.find(n => n.id === edge.from)
      const toNode = this.flowDefinition.nodes.find(n => n.id === edge.to)

      if (!fromNode || !toNode) return { display: 'none' }

      // Calculate start position
      const x1 = fromNode.position.x + 300
      let y1 = fromNode.position.y + 60

      // Adjust Y for multi-port connections
      if (edge.portId && this.hasMultipleOutputs(fromNode)) {
        const options = this.getNodeOptions(fromNode)
        const portIndex = options.findIndex(opt => opt.id === edge.portId)
        if (portIndex >= 0) {
          const count = options.length
          const nodeHeight = 120
          const spacing = nodeHeight / (count + 1)
          y1 = fromNode.position.y + spacing * (portIndex + 1)
        }
      }

      // Calculate end position
      const x2 = toNode.position.x
      const y2 = toNode.position.y + 60

      // Calculate midpoint
      const midX = (x1 + x2) / 2
      const midY = (y1 + y2) / 2

      return {
        left: `${midX - 12}px`,
        top: `${midY - 12}px`
      }
    },

    onKeyDown (event) {
      // Delete selected edge with Delete or Backspace
      if ((event.key === 'Delete' || event.key === 'Backspace') && this.selectedEdge) {
        // Don't delete if user is typing in an input field
        if (event.target.tagName === 'INPUT' || event.target.tagName === 'TEXTAREA') {
          return
        }
        event.preventDefault()
        this.deleteSelectedEdge()
      }
    },

    // ==================== CANVAS CONTROLS ====================

    zoomIn () {
      this.zoomLevel = Math.min(this.zoomLevel + 0.1, 2.5)
      this.applyZoom()
    },

    zoomOut () {
      this.zoomLevel = Math.max(this.zoomLevel - 0.1, 0.25)
      this.applyZoom()
    },

    resetZoom () {
      this.zoomLevel = 1
      this.applyZoom()
    },

    onCanvasWheel (event) {
      // Mouse wheel zoom with limits
      const delta = event.deltaY > 0 ? -0.05 : 0.05
      this.zoomLevel = Math.max(0.25, Math.min(2.5, this.zoomLevel + delta))
      this.applyZoom()
    },

    applyZoom () {
      if (this.$refs.canvas) {
        this.$refs.canvas.style.transform = `scale(${this.zoomLevel})`
      }
    },

    // ==================== PROPERTIES PANEL RESIZE ====================

    startPanelResize () {
      this.isResizingPanel = true
    },

    handlePanelResize (e) {
      if (!this.isResizingPanel) return

      const newWidth = window.innerWidth - e.clientX
      // Min width 380px, max width 800px
      this.propertiesPanelWidth = Math.max(380, Math.min(800, newWidth))
    },

    stopPanelResize () {
      this.isResizingPanel = false
    },

    // ==================== AUTO-ARRANGE ====================

    autoArrange () {
      // Auto-arrange nodes in flow order with collision detection
      // Ensures nodes never go outside top (y >= 50) and left (x >= 50) edges
      const startX = 50
      const startY = 50
      const horizontalSpacing = 400
      const verticalSpacing = 200
      const minX = 50 // Minimum left edge
      const minY = 50 // Minimum top edge

      const visited = new Set()
      const occupiedPositions = new Map() // Track occupied Y positions per X column

      const isPositionOccupied = (x, y) => {
        const column = occupiedPositions.get(x) || []
        return column.some(occupiedY => Math.abs(occupiedY - y) < verticalSpacing)
      }

      const findAvailableY = (x, preferredY) => {
        let y = Math.max(preferredY, minY) // Never go above top edge
        while (isPositionOccupied(x, y)) {
          y += verticalSpacing
        }
        return y
      }

      const markPositionOccupied = (x, y) => {
        if (!occupiedPositions.has(x)) {
          occupiedPositions.set(x, [])
        }
        occupiedPositions.get(x).push(y)
      }

      const layoutNode = (nodeId, preferredX, preferredY, depth = 0) => {
        if (visited.has(nodeId)) return
        if (depth > 50) return // Prevent infinite loops

        const node = this.flowDefinition.nodes.find(n => n.id === nodeId)
        if (!node) return

        // Find available position, ensuring we stay within bounds
        const x = Math.max(preferredX, minX) // Never go past left edge
        const y = findAvailableY(x, preferredY)

        node.position = { x, y }
        visited.add(nodeId)
        markPositionOccupied(x, y)

        // Layout single next node
        if (node.next) {
          layoutNode(node.next, x + horizontalSpacing, y, depth + 1)
        }

        // Layout multiple branches (routes)
        if (node.routes) {
          const routes = Object.values(node.routes).filter(Boolean)
          const totalBranches = routes.length

          routes.forEach((targetId, index) => {
            if (targetId) {
              // Distribute branches vertically around current node
              const branchOffset = (index - (totalBranches - 1) / 2) * verticalSpacing
              const branchY = Math.max(y + branchOffset, minY) // Keep within top edge
              layoutNode(targetId, x + horizontalSpacing, branchY, depth + 1)
            }
          })
        }
      }

      if (this.flowDefinition.start_node) {
        layoutNode(this.flowDefinition.start_node, startX, startY, 0)
      }

      this.saveToHistory()
    },

    // ==================== HISTORY (UNDO/REDO) ====================

    saveToHistory () {
      const state = JSON.parse(JSON.stringify(this.flowDefinition))

      if (this.historyIndex < this.history.length - 1) {
        this.history = this.history.slice(0, this.historyIndex + 1)
      }

      this.history.push(state)
      this.historyIndex = this.history.length - 1

      // Limit history to 50 states
      if (this.history.length > 50) {
        this.history.shift()
        this.historyIndex--
      }

      // Trigger autosave
      this.scheduleAutosave()
    },

    scheduleAutosave () {
      if (!this.autosaveEnabled) return

      // Clear existing timer
      if (this.autosaveTimer) {
        clearTimeout(this.autosaveTimer)
      }

      // Schedule autosave after 2 seconds of inactivity
      this.autosaveTimer = setTimeout(() => {
        this.autosave()
      }, 2000)
    },

    async autosave () {
      if (!this.autosaveEnabled || this.saving) return

      try {
        const flowData = {
          flow_name: this.flowName,
          flow_definition: this.flowDefinition,
          enabled: this.flowEnabled ? 1 : 0
        }

        if (this.flowId) {
          // Update existing flow
          await DashboardService.updateCustomFlow(this.flowId, flowData)
        } else {
          // Create new flow on first autosave
          const response = await DashboardService.createCustomFlow(flowData)
          if (response && response.success && response.flow_id) {
            this.flowId = response.flow_id
            this.$router.replace({ params: { id: this.flowId } })
          }
        }

        this.lastSaved = new Date()
        this.autosaveFeedback = true
        setTimeout(() => { this.autosaveFeedback = false }, 2000)
      } catch (error) {
        console.error('Autosave failed:', error)
      }
    },

    openRenameModal () {
      this.renameFlowName = this.flowName || ''
      this.showRenameModal = true
      this.$nextTick(() => {
        if (this.$refs.renameInput) this.$refs.renameInput.select()
      })
    },

    confirmRename () {
      const name = (this.renameFlowName || '').trim()
      if (name) {
        this.flowName = name
        this.saveFlowName()
      }
      this.showRenameModal = false
    },

    toggleAutosave () {
      this.autosaveEnabled = !this.autosaveEnabled
      if (this.autosaveEnabled) {
        this.scheduleAutosave()
      } else {
        if (this.autosaveTimer) {
          clearTimeout(this.autosaveTimer)
        }
      }
    },

    getTimeSince (date) {
      const seconds = Math.floor((new Date() - date) / 1000)
      if (seconds < 60) return 'just now'
      const minutes = Math.floor(seconds / 60)
      if (minutes < 60) return `${minutes}m ago`
      const hours = Math.floor(minutes / 60)
      return `${hours}h ago`
    },

    undoAction () {
      if (this.canUndo) {
        this.historyIndex--
        this.flowDefinition = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      }
    },

    redoAction () {
      if (this.canRedo) {
        this.historyIndex++
        this.flowDefinition = JSON.parse(JSON.stringify(this.history[this.historyIndex]))
      }
    },

    // ==================== AI FLOW BUILDER ====================
    /* eslint-disable camelcase */

    async loadAiSettings () {
      try {
        const res = await DashboardService.getAiSettings()
        if (res && res.success) {
          this.aiSettings.model = res.model || 'claude-haiku-4-5'
          this.aiSettings.apiKeyHint = res.api_key_hint || ''
          this.aiSettingsForm.model = this.aiSettings.model
        }
      } catch (e) { /* silently ignore */ }
    },

    async saveAiSettings () {
      try {
        const payload = { model: this.aiSettingsForm.model }
        if (this.aiSettingsForm.apiKey) payload.apiKey = this.aiSettingsForm.apiKey
        const res = await DashboardService.saveAiSettings(payload)
        if (res && res.success) {
          this.aiSettings.model = this.aiSettingsForm.model
          if (res.api_key_hint) this.aiSettings.apiKeyHint = res.api_key_hint
          this.aiSettingsForm.apiKey = ''
          this.aiSettingsOpen = false
        } else {
          alert((res && res.message) || 'Failed to save AI settings')
        }
      } catch (e) {
        alert('Failed to save AI settings: ' + e.message)
      }
    },

    async sendAiMessage () {
      const text = this.aiInput.trim()
      if (!text || this.aiLoading) return
      if (!this.aiSettings.apiKeyHint) {
        this.aiSettingsOpen = true
        return
      }

      this.aiInput = ''
      this.aiLoading = true

      this.aiDisplayMessages.push({ role: 'user', text })
      this.aiMessages.push({ role: 'user', content: text })
      this.$nextTick(() => this._scrollAiToBottom())

      try {
        // Serialize the active plan into the system prompt context so the AI
        // never loses its checklist regardless of how far the history is pruned.
        const contextAnchor = this.aiPlan.length
          ? this.aiPlan.map(s => {
            const icon = s.status === 'done' ? '✅' : s.status === 'in_progress' ? '🔄' : s.status === 'skipped' ? '⏭' : '⬜'
            return `${icon} [${s.status}] ${s.description}${s.detail ? ' — ' + s.detail : ''}`
          }).join('\n')
          : null

        let iteration = 0
        let rateLimitRetries = 0
        while (iteration < 60) {
          iteration++
          const res = await DashboardService.sendAiChat(this.aiMessages, contextAnchor)
          if (!res || !res.success) {
            if (res && res.rate_limited && rateLimitRetries < 5) {
              rateLimitRetries++
              // Show / update live countdown and auto-retry after 65 s
              let secs = 65
              const countdownObj = { role: 'system', text: `⏳ Rate limited (${rateLimitRetries}/5) — retrying in ${secs}s…`, isSystem: true }
              this.aiDisplayMessages.push(countdownObj)
              const msgIdx = this.aiDisplayMessages.length - 1
              this.$nextTick(() => this._scrollAiToBottom())
              await new Promise(resolve => {
                const timer = setInterval(() => {
                  secs--
                  if (secs > 0) {
                    this.aiDisplayMessages.splice(msgIdx, 1, { role: 'system', text: `⏳ Rate limited (${rateLimitRetries}/5) — retrying in ${secs}s…`, isSystem: true })
                  } else {
                    this.aiDisplayMessages.splice(msgIdx, 1, { role: 'system', text: `🔄 Retrying… (attempt ${rateLimitRetries}/5)`, isSystem: true })
                    clearInterval(timer)
                    resolve()
                  }
                }, 1000)
              })
              iteration-- // don't count the failed attempt against the tool-loop cap
              continue
            }
            if (res && res.rate_limited) {
              this.aiDisplayMessages.push({ role: 'assistant', text: 'Still rate limited after 5 retries. Please wait a few minutes, then type "continue".', isError: true })
            } else {
              this.aiDisplayMessages.push({ role: 'assistant', text: (res && res.message) || 'Error communicating with AI. Check your API key in AI Settings.', isError: true })
            }
            break
          }
          rateLimitRetries = 0 // successful call — reset counter

          const { stop_reason: stopReason, content } = res // eslint-disable-line camelcase

          // Append assistant full content block to message history
          this.aiMessages.push({ role: 'assistant', content })

          // Collect tool uses and text blocks
          const toolUseBlocks = Array.isArray(content) ? content.filter(b => b.type === 'tool_use') : []
          const textBlocks = Array.isArray(content) ? content.filter(b => b.type === 'text') : []

          // Display any text blocks from Claude
          for (const tb of textBlocks) {
            if (tb.text && tb.text.trim()) {
              this.aiDisplayMessages.push({ role: 'assistant', text: tb.text })
            }
          }

          this.$nextTick(() => this._scrollAiToBottom())

          if (stopReason === 'end_turn' || !toolUseBlocks.length) {
            // If the plan still has pending/in_progress steps, the AI is just
            // "narrating" before acting — push a silent continue and loop again.
            const hasMore = this.aiPlan.length && this.aiPlan.some(s => s.status === 'pending' || s.status === 'in_progress')
            if (hasMore && iteration < 60) {
              this.aiMessages.push({ role: 'user', content: 'continue' })
              continue
            }
            break
          }

          // Execute tools and collect results
          const toolResults = []
          for (const toolUse of toolUseBlocks) {
            let result
            try {
              result = await this.executeAiTool(toolUse.name, toolUse.input || {})
            } catch (e) {
              result = { error: e.message }
            }
            toolResults.push({
              type: 'tool_result',
              tool_use_id: toolUse.id,
              content: JSON.stringify(result)
            })
          }

          // Feed tool results back as user message
          this.aiMessages.push({ role: 'user', content: toolResults })
          this.$nextTick(() => this._scrollAiToBottom())
        }
      } catch (e) {
        this.aiDisplayMessages.push({ role: 'assistant', text: 'Error: ' + e.message, isError: true })
      }

      this.aiLoading = false
      this.$nextTick(() => this._scrollAiToBottom())
    },

    aiFixErrors () {
      const errors = (this.flowValidation && this.flowValidation.errors) || []
      if (!errors.length) return
      this.aiPanelOpen = true
      const nodeMap = {}
      ;(this.flowDefinition.nodes || []).forEach(n => { nodeMap[n.id] = n })
      const lines = errors.map((err, i) => {
        const node = nodeMap[err.nodeId]
        const label = node ? `"${node.label || node.type}" (${err.nodeId})` : (err.nodeId || 'unknown node')
        return `${i + 1}. ${label}: ${err.message}`
      })
      const userText = `The flow has ${errors.length} validation error${errors.length > 1 ? 's' : ''}. Please fix all of them:\n\n${lines.join('\n')}`
      this.$nextTick(() => {
        this.aiInput = userText
        this.sendAiMessage()
      })
    },

    clearAiChat () {
      this.aiMessages = []
      this.aiDisplayMessages = []
      this.aiPlan = []
      try {
        const fid = this.flowId
        localStorage.removeItem(fid ? `ai_flow_messages_${fid}` : 'ai_flow_messages')
        localStorage.removeItem(fid ? `ai_flow_display_${fid}` : 'ai_flow_display')
      } catch (e) { /* */ }
    },

    _scrollAiToBottom () {
      const el = this.$refs.aiChatMessages
      if (el) el.scrollTop = el.scrollHeight
    },

    aiFormatMessage (text) {
      if (!text) return ''
      // Basic markdown: bold, code, newlines
      return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/`([^`]+)`/g, '<code>$1</code>')
        .replace(/\n/g, '<br>')
    },

    // ── AI Tool Executor ──────────────────────────────────────────────

    executeAiTool (name, input) {
      switch (name) {
        case 'get_flow_overview': return Promise.resolve(this.aiToolGetFlowOverview())
        case 'get_node': return Promise.resolve(this.aiToolGetNode(input))
        case 'find_nodes': return Promise.resolve(this.aiToolFindNodes(input))
        case 'create_node': return Promise.resolve(this.aiToolCreateNode(input))
        case 'update_node': return Promise.resolve(this.aiToolUpdateNode(input))
        case 'add_button': return Promise.resolve(this.aiToolAddButton(input))
        case 'add_list_row': return Promise.resolve(this.aiToolAddListRow(input))
        case 'delete_node': return Promise.resolve(this.aiToolDeleteNode(input))
        case 'link_nodes': return Promise.resolve(this.aiToolLinkNodes(input))
        case 'unlink_nodes': return Promise.resolve(this.aiToolUnlinkNodes(input))
        case 'set_start_node': return Promise.resolve(this.aiToolSetStartNode(input))
        case 'move_node': return Promise.resolve(this.aiToolMoveNode(input))
        case 'build_plan': return Promise.resolve(this.aiToolBuildPlan(input))
        case 'update_plan': return Promise.resolve(this.aiToolUpdatePlan(input))
        case 'mark_step': return Promise.resolve(this.aiToolMarkStep(input))
        case 'ask_user': return Promise.resolve(this.aiToolAskUser(input))
        case 'validate_flow': return Promise.resolve(this.aiToolValidateFlow())
        default: return Promise.resolve({ error: 'Unknown tool: ' + name })
      }
    },

    aiToolGetFlowOverview () {
      const nodes = this.flowDefinition.nodes || []
      return {
        flow_id: this.flowId,
        flow_name: this.flowName,
        start_node: this.flowDefinition.start_node,
        node_count: nodes.length,
        nodes: nodes.map(n => ({
          id: n.id,
          type: n.type,
          name: n.name,
          position: { x: Math.round(n.position ? n.position.x : 0), y: Math.round(n.position ? n.position.y : 0) },
          next: n.next || null,
          routes: n.routes || null,
          config_keys: n.config ? Object.keys(n.config) : []
        }))
      }
    },

    aiToolGetNode ({ node_id }) {
      const node = (this.flowDefinition.nodes || []).find(n => n.id === node_id)
      if (!node) return { error: 'Node ' + node_id + ' not found' }
      // Return full node but exclude position to keep response lean
      const { position, ...rest } = JSON.parse(JSON.stringify(node)) // eslint-disable-line no-unused-vars
      return { node: rest }
    },

    aiToolFindNodes ({ type, name_contains } = {}) {
      let nodes = this.flowDefinition.nodes || []
      if (type) nodes = nodes.filter(n => n.type === type)
      if (name_contains) {
        const q = name_contains.toLowerCase()
        nodes = nodes.filter(n => (n.name || '').toLowerCase().includes(q))
      }
      return {
        count: nodes.length,
        nodes: nodes.map(n => ({ id: n.id, type: n.type, name: n.name }))
      }
    },

    aiToolCreateNode ({ type, name, config, position } = {}) {
      if (!type) return { error: 'type is required' }
      this._pushAiUndo()
      const id = 'node_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7)
      const nodes = this.flowDefinition.nodes || []

      // Auto-position below lowest existing node
      let px = 400
      let py = 200
      if (position) {
        px = position.x != null ? position.x : px
        py = position.y != null ? position.y : py
      } else if (nodes.length > 0) {
        const maxY = Math.max(...nodes.map(n => (n.position ? n.position.y : 0) || 0))
        py = maxY + 200
      }

      const nodeType = this.nodeTypes.find(nt => nt.type === type)
      const newNode = {
        id,
        type,
        name: name || (nodeType ? nodeType.name : type),
        position: { x: px, y: py },
        config: this._aiMergeConfig(type, config || {}),
        next: null
      }

      this.flowDefinition.nodes.push(newNode)

      if (nodes.length === 0) {
        this.flowDefinition.start_node = id
      }

      this._sanitizeAllNodeConfigs()
      this.saveToHistory()
      return { created: true, node_id: id, type, name: newNode.name }
    },

    aiToolUpdateNode ({ node_id, name, config, config_patch } = {}) { // eslint-disable-line camelcase
      const idx = (this.flowDefinition.nodes || []).findIndex(n => n.id === node_id)
      if (idx < 0) return { error: 'Node ' + node_id + ' not found' }
      this._pushAiUndo()
      const node = this.flowDefinition.nodes[idx]
      if (name !== undefined) node.name = name
      const patch = config || config_patch // PHP schema uses 'config', legacy uses 'config_patch'
      if (patch && typeof patch === 'object') {
        node.config = this._aiMergeConfig(node.type, patch, node.config)
      }
      this._sanitizeAllNodeConfigs()
      this.saveToHistory()
      // Return updated node snapshot so AI can verify without an extra get_node call
      const snap = JSON.parse(JSON.stringify(node))
      delete snap.position
      return { updated: true, node_id, node: snap }
    },

    aiToolAddButton ({ node_id, button_id, text_en, text_ar } = {}) { // eslint-disable-line camelcase
      const node = (this.flowDefinition.nodes || []).find(n => n.id === node_id)
      if (!node) return { error: 'Node ' + node_id + ' not found' }
      if (node.type !== 'button_selection' && node.type !== 'text_with_buttons') {
        return { error: 'Node type ' + node.type + ' does not have buttons. Use update_node with rows for list_selection.' }
      }
      if (!node.config.buttons) node.config.buttons = []
      if (node.config.buttons.length >= 3) {
        return { error: 'button_selection supports max 3 buttons. Current count: ' + node.config.buttons.length + '. Remove one first or use list_selection for more options.' }
      }
      const id = button_id || ('btn_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6))
      this._pushAiUndo()
      node.config.buttons.push({ id, text: { en: text_en || '', ar: text_ar || '' } })
      this.saveToHistory()
      return { added: true, button_id: id, total_buttons: node.config.buttons.length, buttons: node.config.buttons }
    },

    aiToolAddListRow ({ node_id, row_id, title_en, title_ar, description_en, description_ar } = {}) { // eslint-disable-line camelcase
      const node = (this.flowDefinition.nodes || []).find(n => n.id === node_id)
      if (!node) return { error: 'Node ' + node_id + ' not found' }
      if (node.type !== 'list_selection') {
        return { error: 'Node type ' + node.type + ' does not support list rows. Use add_button for button_selection.' }
      }
      if (!node.config.rows) node.config.rows = []
      const id = row_id || ('row_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6))
      this._pushAiUndo()
      node.config.rows.push({
        id,
        title: { en: title_en || '', ar: title_ar || '' },
        description: { en: description_en || '', ar: description_ar || '' }
      })
      this.saveToHistory()
      return { added: true, row_id: id, total_rows: node.config.rows.length }
    },

    aiToolDeleteNode ({ node_id } = {}) { // eslint-disable-line camelcase
      if (node_id === this.flowDefinition.start_node) {
        return { error: 'Cannot delete the start/welcome node. Update its content instead.' }
      }
      const idx = (this.flowDefinition.nodes || []).findIndex(n => n.id === node_id)
      if (idx < 0) return { error: 'Node ' + node_id + ' not found' }
      this._pushAiUndo()
      this.flowDefinition.nodes.splice(idx, 1)
      // Remove all references to deleted node
      this.flowDefinition.nodes.forEach(n => {
        if (n.next === node_id) n.next = null
        if (n.routes && typeof n.routes === 'object') {
          Object.keys(n.routes).forEach(k => {
            if (n.routes[k] === node_id) n.routes[k] = null
          })
        }
        if (Array.isArray(n.conditions)) {
          n.conditions.forEach(c => { if (c.next === node_id) c.next = null })
        }
      })
      if (this.flowDefinition.start_node === node_id) this.flowDefinition.start_node = null
      if (this.selectedNodeId === node_id) this.selectedNodeId = null
      this.saveToHistory()
      return { deleted: true, node_id }
    },

    aiToolLinkNodes ({ source_node_id, target_node_id, source_handle } = {}) { // eslint-disable-line camelcase
      const from = (this.flowDefinition.nodes || []).find(n => n.id === source_node_id)
      const to = (this.flowDefinition.nodes || []).find(n => n.id === target_node_id)
      if (!from) return { error: 'Source node ' + source_node_id + ' not found' }
      if (!to) return { error: 'Target node ' + target_node_id + ' not found' }
      this._pushAiUndo()
      if (source_handle && source_handle !== 'output') {
        if (!from.routes) from.routes = {}
        from.routes[source_handle] = target_node_id
      } else {
        from.next = target_node_id
      }
      this.saveToHistory()
      return { linked: true, from: source_node_id, to: target_node_id, handle: source_handle || 'output' }
    },

    aiToolUnlinkNodes ({ source_node_id, source_handle } = {}) { // eslint-disable-line camelcase
      const from = (this.flowDefinition.nodes || []).find(n => n.id === source_node_id)
      if (!from) return { error: 'Node ' + source_node_id + ' not found' }
      this._pushAiUndo()
      if (source_handle && source_handle !== 'output') {
        if (from.routes) delete from.routes[source_handle]
      } else {
        from.next = null
      }
      this.saveToHistory()
      return { unlinked: true, from: source_node_id, handle: source_handle || 'output' }
    },

    aiToolSetStartNode ({ node_id } = {}) {
      const node = (this.flowDefinition.nodes || []).find(n => n.id === node_id)
      if (!node) return { error: 'Node ' + node_id + ' not found' }
      this._pushAiUndo()
      this.flowDefinition.start_node = node_id
      this.saveToHistory()
      return { start_node_set: true, node_id }
    },

    aiToolMoveNode ({ node_id, x, y } = {}) {
      const node = (this.flowDefinition.nodes || []).find(n => n.id === node_id)
      if (!node) return { error: 'Node ' + node_id + ' not found' }
      this._pushAiUndo()
      if (!node.position) node.position = { x: 0, y: 0 }
      if (x != null) node.position.x = x
      if (y != null) node.position.y = y
      this.saveToHistory()
      return { moved: true, node_id, x: node.position.x, y: node.position.y }
    },

    // ── AI Plan Tools ─────────────────────────────────────────────────

    aiToolBuildPlan ({ steps } = {}) {
      if (!Array.isArray(steps) || !steps.length) return { error: 'steps must be a non-empty array' }
      this.aiPlan = steps.map(s => ({ id: s.id, description: s.description, status: 'pending', detail: '' }))
      this.aiPlanVisible = true
      return { plan_created: true, step_count: steps.length }
    },

    aiToolUpdatePlan ({ steps } = {}) {
      if (!Array.isArray(steps) || !steps.length) return { error: 'steps must be a non-empty array' }
      const prevById = {}
      this.aiPlan.forEach(s => { prevById[s.id] = s })
      this.aiPlan = steps.map(s => ({
        id: s.id,
        description: s.description,
        // preserve done/skipped status for matching IDs so completed work stays marked
        status: (prevById[s.id] && ['done', 'skipped'].includes(prevById[s.id].status))
          ? prevById[s.id].status
          : (prevById[s.id] ? prevById[s.id].status : 'pending'),
        detail: prevById[s.id] ? prevById[s.id].detail : ''
      }))
      this.aiPlanVisible = true
      return { plan_updated: true, step_count: steps.length }
    },

    aiToolMarkStep ({ step_id, status, detail } = {}) { // eslint-disable-line camelcase
      const step = this.aiPlan.find(s => s.id === step_id)
      if (!step) return { error: 'Step ' + step_id + ' not found in plan' }
      step.status = status
      if (detail) step.detail = detail
      return { marked: true, step_id, status }
    },

    aiToolValidateFlow () {
      const v = this.flowValidation
      const issues = [
        ...v.errors.map(e => ({ severity: 'error', node_id: e.nodeId, node_name: e.nodeName || e.nodeId, message: e.message })),
        ...v.warnings.map(w => ({ severity: 'warning', node_id: w.nodeId, node_name: w.nodeName || w.nodeId, message: w.message }))
      ]
      return {
        valid: v.isValid,
        error_count: v.errors.length,
        warning_count: v.warnings.length,
        issues
      }
    },

    aiToolAskUser ({ question, context, options } = {}) {
      const text = context ? `${question}\n\n*Context: ${context}*` : question
      const opts = Array.isArray(options) && options.length ? options : []
      this.aiDisplayMessages.push({ role: 'assistant', text, isQuestion: true, options: opts })
      this.$nextTick(() => this._scrollAiToBottom())
      return { question_displayed: true, note: 'Stop using tools now and wait for the user to reply in the chat.' }
    },

    selectAiOption (text, msgIdx) {
      // Mark question bubble as answered
      const msg = this.aiDisplayMessages[msgIdx]
      if (!msg || msg.answered) return
      this.aiDisplayMessages.splice(msgIdx, 1, { ...msg, answered: true, selectedOption: text || 'Something else…' })
      if (text) {
        // Option chosen — send immediately
        this.aiInput = text
        this.sendAiMessage()
      } else {
        // "Something else…" — focus textarea so user can type freely
        this.$nextTick(() => {
          const el = this.$el.querySelector('.ai-chat-textarea')
          if (el) el.focus()
        })
      }
    },

    // ── AI Config Helpers ─────────────────────────────────────────────

    /** Repair all nodes in the flow that have structurally broken configs. */
    _sanitizeAllNodeConfigs () {
      const fixI18nObj = (obj, def) => {
        // Ensure all keys from the default object exist (e.g. en, ar)
        if (obj && typeof obj === 'object' && def && typeof def === 'object') {
          Object.keys(def).forEach(k => { if (!(k in obj)) obj[k] = def[k] })
        }
      }
      const fixTextLike = (container, key, def) => {
        const v = container[key]
        if (v === undefined || v === null) {
          container[key] = Object.assign({}, def)
        } else if (typeof v === 'string') {
          container[key] = Object.assign({}, def)
          if ('en' in def) container[key].en = v
        } else if (Array.isArray(v) && !Array.isArray(def)) {
          container[key] = Object.assign({}, def)
        } else if (typeof v === 'object' && !Array.isArray(v)) {
          fixI18nObj(v, def)
        }
      }
      ;(this.flowDefinition.nodes || []).forEach(node => {
        if (!node.config) { node.config = this.getDefaultNodeConfig(node.type) || {}; return }
        const defaults = this.getDefaultNodeConfig(node.type) || {}
        Object.keys(defaults).forEach(key => {
          const defVal = defaults[key]
          if (defVal !== null && typeof defVal === 'object' && !Array.isArray(defVal)) {
            fixTextLike(node.config, key, defVal)
          }
        })
        // Deep-sanitize button/row arrays (AI may send text as a plain string)
        if (node.config.buttons && Array.isArray(node.config.buttons)) {
          node.config.buttons.forEach(btn => {
            fixTextLike(btn, 'text', { en: '', ar: '' })
            fixTextLike(btn, 'description', { en: '', ar: '' })
          })
        }
        if (node.config.rows && Array.isArray(node.config.rows)) {
          node.config.rows.forEach(row => {
            fixTextLike(row, 'title', { en: '', ar: '' })
            fixTextLike(row, 'description', { en: '', ar: '' })
          })
        }
      })
    },

    /**
     * Merge AI-supplied config onto defaults for a node type.
     * Ensures nested objects required by the template (e.g. text:{en,ar})
     * are never replaced by a plain string from the AI.
     */
    _aiMergeConfig (type, patch, base) {
      const defaults = this.getDefaultNodeConfig(type) || {}
      // Smart base merge: deep-merge object fields so base can't wipe out en/ar from defaults
      const result = Object.assign({}, defaults)
      Object.keys(base || {}).forEach(k => {
        const dv = defaults[k]
        const bv = (base || {})[k]
        if (dv !== null && dv !== undefined && typeof dv === 'object' && !Array.isArray(dv) &&
            bv !== null && bv !== undefined && typeof bv === 'object' && !Array.isArray(bv)) {
          result[k] = Object.assign({}, dv, bv)
        } else {
          result[k] = bv
        }
      })
      Object.keys(patch).forEach(key => {
        const defVal = defaults[key]
        const patchVal = patch[key]
        if (defVal !== null && typeof defVal === 'object' && !Array.isArray(defVal)) {
          // Default expects an object — coerce AI strings into the right shape
          if (typeof patchVal === 'string') {
            // e.g. AI sent text:"Hello" → convert to text:{en:"Hello", ar:""}
            result[key] = Object.assign({}, defVal)
            if ('en' in defVal) result[key].en = patchVal
          } else if (patchVal && typeof patchVal === 'object' && !Array.isArray(patchVal)) {
            result[key] = Object.assign({}, defVal, patchVal)
          }
          // else: ignore null / unexpected types, keep default
        } else {
          result[key] = patchVal
        }
      })
      return result
    },

    // ── AI Undo Stack ─────────────────────────────────────────────────

    _pushAiUndo () {
      this.aiUndoStack.push(JSON.stringify(this.flowDefinition))
      if (this.aiUndoStack.length > 20) this.aiUndoStack.shift()
    },

    revertLastAiChange () {
      if (!this.aiUndoStack.length) return
      const snapshot = this.aiUndoStack.pop()
      this.flowDefinition = JSON.parse(snapshot)
      this._sanitizeAllNodeConfigs()
      this.saveToHistory()
      this.aiDisplayMessages.push({ role: 'system', text: 'Last AI change was reverted.', isSystem: true })
      this.$nextTick(() => this._scrollAiToBottom())
    }

    /* eslint-enable camelcase */

  }
}
</script>

<style scoped>
.flow-builder-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--bg-primary);
  overflow: hidden;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
}

/* ─── Top Header ─────────────────────────────────────────────── */
.flow-header {
  height: 56px;
  background: var(--card-background);
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  flex-shrink: 0;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0;
  min-width: 0;
}

.header-divider {
  width: 1px;
  height: 22px;
  background: var(--border-primary);
  margin: 0 12px;
  flex-shrink: 0;
}

/* Logo */
.header-logo {
  height: 28px;
  width: auto;
  object-fit: contain;
  flex-shrink: 0;
  display: block;
}

/* Back button */
.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 7px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
}
.btn-back:hover {
  background: var(--hover-background);
  color: var(--text-primary);
  border-color: var(--text-tertiary);
}

/* Flow title area */
.flow-title-container {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.flow-name-display {
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 260px;
}

.btn-rename {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 5px;
  background: transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  flex-shrink: 0;
}
.btn-rename:hover {
  background: var(--hover-background);
  color: var(--text-primary);
}

.flow-id-badge {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary);
  background: var(--hover-background);
  border: 1px solid var(--border-primary);
  padding: 2px 7px;
  border-radius: 20px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  flex-shrink: 0;
}

.target-user-banner {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 500;
  color: #fff;
  background: linear-gradient(135deg, #7c3aed, #5b21b6);
  padding: 4px 10px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

.target-user-banner strong {
  font-weight: 700;
}

/* Right-side actions */
.header-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* Grouped icon buttons (undo/redo) */
.btn-group {
  display: flex;
  align-items: center;
  border: 1px solid var(--border-primary);
  border-radius: 7px;
  overflow: hidden;
}
.btn-group .hdr-btn {
  border: none;
  border-radius: 0;
  border-right: 1px solid var(--border-primary);
}
.btn-group .hdr-btn:last-child {
  border-right: none;
}

/* Universal header button */
.hdr-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 34px;
  padding: 0 12px;
  background: var(--card-background);
  border: 1px solid var(--border-primary);
  border-radius: 7px;
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s, box-shadow 0.15s;
  white-space: nowrap;
}
.hdr-btn:hover:not(:disabled) {
  background: var(--hover-background);
  color: var(--text-primary);
  border-color: var(--text-tertiary);
}
.hdr-btn:disabled {
  opacity: 0.38;
  cursor: not-allowed;
}

/* Icon-only variant (undo/redo) */
.hdr-btn-icon {
  width: 34px;
  padding: 0;
}

/* Save button */
.hdr-btn-save {
  background: var(--primary-color, #3b82f6);
  border-color: var(--primary-color, #3b82f6);
  color: #fff;
}
.hdr-btn-save:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 2px 10px rgba(59, 130, 246, 0.35);
}
.hdr-btn-save:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Simulate button */
.hdr-btn-simulate {
  background: linear-gradient(135deg, #128C7E 0%, #075E54 100%);
  border-color: transparent;
  color: #fff;
}
.hdr-btn-simulate:hover:not(:disabled) {
  background: linear-gradient(135deg, #0f7267 0%, #064d44 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 10px rgba(18, 140, 126, 0.4);
}
.hdr-btn-simulate:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Fix errors button */
.hdr-btn-fix-errors {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-color: transparent;
  color: #fff;
}
.hdr-btn-fix-errors:hover {
  background: linear-gradient(135deg, #d97706 0%, #b45309 100%);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 2px 10px rgba(245, 158, 11, 0.4);
}

/* Autosave group */
.autosave-group {
  display: flex;
  align-items: center;
  gap: 7px;
}

.autosave-label {
  font-size: 13px;
  color: var(--text-secondary);
  font-weight: 500;
  user-select: none;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  cursor: pointer;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.3s;
  border-radius: 24px;
}

.toggle-slider:before {
  position: absolute;
  content: "";
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.3s;
  border-radius: 50%;
}

.toggle-switch input:checked + .toggle-slider {
  background-color: #25D366;
}

.toggle-switch input:checked + .toggle-slider:before {
  transform: translateX(20px);
}

.toggle-slider:hover {
  background-color: #bbb;
}

.toggle-switch input:checked + .toggle-slider:hover {
  background-color: #1fb354;
}

.autosave-saved-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #25D366;
  background: rgba(37, 211, 102, 0.1);
  border: 1px solid rgba(37, 211, 102, 0.25);
  border-radius: 20px;
  padding: 2px 8px;
  white-space: nowrap;
}

/* Rename flow modal */
.rename-flow-modal {
  width: 420px;
  border-radius: 10px;
  overflow: hidden;
}

/* Autosave flash transition */
.autosave-flash-enter-active {
  transition: opacity 0.2s ease;
}
.autosave-flash-leave-active {
  transition: opacity 0.6s ease;
}
.autosave-flash-enter-from,
.autosave-flash-leave-to {
  opacity: 0;
}

.btn-secondary,
.btn-primary,
.btn-success {
  padding: 8px 16px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
  border: none;
}

.btn-secondary {
  background: var(--hover-background);
  color: var(--text-primary);
  border: 1px solid var(--border-primary);
}

.btn-secondary:hover {
  background: var(--border-primary);
}

.btn-primary {
  background: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-success {
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: white;
}

.btn-success:hover {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
}

.btn-success:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Main Content */
.flow-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* Left Sidebar – Activity Bar + Panel */
.left-sidebar-wrapper {
  display: flex;
  flex-shrink: 0;
  border-right: 1px solid var(--border-primary);
}

/* Vertical icon tab strip */
.activity-bar {
  width: 72px;
  background: var(--bg-secondary);
  border-right: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  padding: 6px 0;
  flex-shrink: 0;
}

.activity-tab {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 10px 4px;
  width: 100%;
  background: transparent;
  border: none;
  border-left: 2px solid transparent;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: color 0.15s, background 0.15s, border-color 0.15s;
  font-size: 10px;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.activity-tab:hover {
  color: var(--text-primary);
  background: var(--hover-background);
}

.activity-tab.active {
  color: #667eea;
  border-left-color: #667eea;
  background: rgba(102, 126, 234, 0.08);
}

/* Shared panel area */
.sidebar-panel {
  width: 384px;
  background: var(--card-background);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.panel-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}

.sidebar-header {
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex-shrink: 0;
}

.sidebar-header-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sidebar-header h3 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  flex: 1;
  text-transform: uppercase;
  color: var(--text-tertiary);
}

.node-search {
  width: 100%;
  padding: 8px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 14px;
}

.node-search:focus {
  outline: none;
  border-color: var(--primary-color);
}

.node-categories {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.node-category {
  border-bottom: 1px solid var(--border-primary);
}

.category-header {
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.2s;
  color: var(--text-primary);
  font-weight: 500;
}

.category-header:hover {
  background: var(--hover-background);
}

.category-header span {
  flex: 1;
}

.chevron {
  transition: transform 0.2s;
}

.chevron.rotated {
  transform: rotate(180deg);
}

.category-nodes {
  padding: 8px;
}

.node-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  margin-bottom: 6px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  cursor: grab;
  transition: all 0.2s;
}

.node-item:hover {
  border-color: var(--primary-color);
  transform: translateX(4px);
}

.node-item:active {
  cursor: grabbing;
}

.node-icon {
  font-size: 20px;
}

.node-icon-img {
  width: 22px;
  height: 22px;
  object-fit: contain;
  flex-shrink: 0;
}
.node-icon-multi {
  display: flex;
  align-items: center;
  gap: 3px;
  flex-shrink: 0;
}

.google-brand-icon {
  width: 16px;
  height: 16px;
  object-fit: contain;
  flex-shrink: 0;
}

.node-info {
  flex: 1;
}

.node-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 2px;
}

.node-desc {
  font-size: 11px;
  color: var(--text-tertiary);
}

/* Canvas Container */
.canvas-container {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: var(--bg-secondary);
}

.flow-canvas {
  width: 10000px;
  height: 10000px;
  position: absolute;
  transform-origin: 0 0;
  background: var(--bg-secondary);
  cursor: grab;
}

.flow-canvas:active {
  cursor: grabbing;
}

.flow-canvas.show-grid {
  background: var(--bg-secondary);
  background-image: radial-gradient(circle, var(--border-primary) 1px, transparent 1px);
  background-size: 20px 20px;
}

.flow-node {
  position: absolute;
  width: 280px;
  background: var(--card-background, #ffffff);
  border: 1.5px solid var(--border-primary, #e5e7eb);
  border-radius: 10px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06);
  cursor: move;
  transition: all 0.2s ease;
  z-index: 1;
  overflow: visible;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .flow-node {
    background: var(--card-background, #1f2937);
    border-color: var(--border-primary, #374151);
  }
}

.flow-node:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15), 0 2px 4px rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.flow-node.selected {
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.2), 0 8px 24px rgba(102, 126, 234, 0.25);
  transform: translateY(-2px);
  z-index: 10;
}

.flow-node.node-has-error {
  border-color: #e53e3e;
  box-shadow: 0 0 0 2px rgba(229, 62, 62, 0.3), 0 4px 12px rgba(229, 62, 62, 0.2);
}

.flow-node.node-has-error.selected {
  border-color: #e53e3e;
  box-shadow: 0 0 0 3px rgba(229, 62, 62, 0.35), 0 8px 24px rgba(229, 62, 62, 0.25);
}

.flow-node.node-has-warning {
  border-color: #d97706;
  box-shadow: 0 0 0 2px rgba(217, 119, 6, 0.3), 0 4px 12px rgba(217, 119, 6, 0.15);
}

.flow-node.node-has-warning.selected {
  border-color: #d97706;
  box-shadow: 0 0 0 3px rgba(217, 119, 6, 0.35), 0 8px 24px rgba(217, 119, 6, 0.2);
}

.flow-node.multi-selected {
  border-color: #f59e0b;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.3), 0 8px 24px rgba(245, 158, 11, 0.2);
  z-index: 10;
}

.node-header {
  padding: 10px 14px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.06) 100%);
  border-bottom: 1px solid var(--border-primary);
  border-radius: 10px 10px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;
  position: relative;
  overflow: hidden;
}

.node-header::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px 0 0 0;
}

/* AI node per-provider colors */
.node-type-openai_call .node-header {
  background: linear-gradient(135deg, rgba(16, 163, 127, 0.12) 0%, rgba(16, 163, 127, 0.04) 100%);
}
.node-type-openai_call .node-header::before {
  background: linear-gradient(180deg, #10a37f 0%, #0d8a6a 100%);
}
.node-type-openai_call {
  border-color: rgba(16, 163, 127, 0.35);
}

.node-type-claude_call .node-header {
  background: linear-gradient(135deg, rgba(215, 126, 72, 0.12) 0%, rgba(215, 126, 72, 0.04) 100%);
}
.node-type-claude_call .node-header::before {
  background: linear-gradient(180deg, #d97706 0%, #b45309 100%);
}
.node-type-claude_call {
  border-color: rgba(215, 126, 72, 0.35);
}

.node-type-gemini_call .node-header {
  background: linear-gradient(135deg, rgba(66, 133, 244, 0.12) 0%, rgba(66, 133, 244, 0.04) 100%);
}
.node-type-gemini_call .node-header::before {
  background: linear-gradient(180deg, #4285f4 0%, #1a73e8 100%);
}
.node-type-gemini_call {
  border-color: rgba(66, 133, 244, 0.35);
}

.node-type-label {
  flex: 1;
  font-size: 10px;
  font-weight: 800;
  color: #667eea;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  padding-left: 6px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.canvas-node-icon-img {
  width: 27px;
  height: 27px;
  flex-shrink: 0;
  object-fit: contain;
}
.canvas-node-icon-emoji {
  font-size: 22px;
  line-height: 1;
  flex-shrink: 0;
}

.node-name-section {
  padding: 10px 14px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  background: var(--card-background);
}

.node-name-label {
  width: 100%;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.node-icon-small {
  font-size: 16px;
}

.node-title {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.node-status-badge {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 8px;
  border-radius: 50%;
  margin-right: 4px;
}

.node-status-badge.error {
  background: rgba(239, 68, 68, 0.15);
  animation: pulse-error 2s ease-in-out infinite;
}

.node-status-badge.warning {
  background: rgba(251, 191, 36, 0.15);
  animation: pulse-warning 2s ease-in-out infinite;
}

.node-status-badge.success {
  background: rgba(34, 197, 94, 0.15);
}

@keyframes pulse-error {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
}

@keyframes pulse-warning {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(0.95); }
}

.btn-select-subtree {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  border: none;
  border-radius: 6px;
  color: var(--text-tertiary);
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
}

.flow-node:hover .btn-select-subtree {
  opacity: 1;
}

.btn-select-subtree:hover {
  background: #f59e0b;
  color: white;
  opacity: 1;
  transform: scale(1.05);
}

.btn-duplicate-node {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  border: none;
  border-radius: 6px;
  color: var(--text-tertiary);
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
}

.flow-node:hover .btn-duplicate-node {
  opacity: 1;
}

.btn-duplicate-node:hover,
.btn-duplicate-node.active {
  background: #3b82f6;
  color: white;
  opacity: 1;
  transform: scale(1.05);
}

.duplicate-dropdown {
  position: fixed;
  z-index: 99999;
  background: var(--bg-primary, #fff);
  border: 1px solid var(--border-primary, #e5e7eb);
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
  min-width: 120px;
  overflow: hidden;
}

.duplicate-dropdown button {
  display: block;
  width: 100%;
  padding: 7px 12px;
  text-align: left;
  background: none;
  border: none;
  font-size: 12px;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.15s;
}

.duplicate-dropdown button:hover {
  background: rgba(99, 102, 241, 0.1);
  color: #6366f1;
}

.btn-delete-node {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.04);
  border: none;
  border-radius: 6px;
  color: var(--text-tertiary);
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s ease;
}

.flow-node:hover .btn-delete-node {
  opacity: 1;
}

.btn-delete-node:hover {
  background: #ef4444;
  color: white;
  transform: scale(1.05);
}

.welcome-badge {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  background: rgba(102, 126, 234, 0.1);
  border-radius: 6px;
  cursor: help;
}

.node-content {
  padding: 12px 14px;
  min-height: 50px;
}

.node-preview {
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.6;
  max-height: 70px;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
}

.node-ports {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
}

.port {
  position: absolute;
  width: 14px;
  height: 14px;
  background: white;
  border: 2.5px solid #667eea;
  border-radius: 50%;
  pointer-events: auto;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 4px rgba(102, 126, 234, 0.2);
}

.port:hover {
  transform: scale(1.4);
  box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.15), 0 2px 8px rgba(102, 126, 234, 0.3);
  border-width: 3px;
}

.port-input {
  left: -8px;
  top: 50%;
  transform: translateY(-50%);
}

.port-output {
  right: -8px;
  top: 50%;
  transform: translateY(-50%);
}

.port-multi {
  position: absolute;
  right: -8px;
}

.port-label {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
  background: var(--card-background);
  border: 1px solid rgba(102, 126, 234, 0.2);
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 10px;
  font-weight: 600;
  color: #667eea;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  backdrop-filter: blur(8px);
}

.port-multi:hover .port-label {
  opacity: 1;
}

.port-connected {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.4);
}

.port-connected:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* Connections */
.connections-layer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;
}

.connection-path {
  fill: none;
  stroke: var(--primary-color, #3b82f6);
  stroke-width: 3;
  pointer-events: stroke;
  cursor: pointer;
  transition: all 0.2s;
  filter: drop-shadow(0 2px 4px rgba(59, 130, 246, 0.3));
}

.connection-path:hover {
  stroke-width: 4;
  stroke: #2563eb;
  filter: drop-shadow(0 3px 6px rgba(37, 99, 235, 0.5));
}

.connection-path.selected {
  stroke-width: 4;
  stroke: #ef4444;
  filter: drop-shadow(0 3px 6px rgba(239, 68, 68, 0.5));
  animation: pulse-edge 2s ease-in-out infinite;
}

@keyframes pulse-edge {
  0%, 100% {
    stroke-width: 4;
  }
  50% {
    stroke-width: 5;
  }
}

.connection-path.temp {
  stroke: var(--text-tertiary, #9ca3af);
  stroke-dasharray: 8,8;
  stroke-width: 2;
  animation: dash 1s linear infinite;
}

@keyframes dash {
  to {
    stroke-dashoffset: -16;
  }
}

/* Connection Delete Buttons */
.connection-delete-btn {
  position: absolute;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  transition: all 0.2s;
  z-index: 10;
  pointer-events: auto;
}

.connection-delete-btn svg {
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
}

.connection-delete-btn circle {
  fill: white;
  stroke: #ef4444;
}

.connection-delete-btn line {
  stroke: #ef4444;
}

.connection-delete-btn:hover {
  transform: scale(1.2);
}

.connection-delete-btn:hover circle {
  fill: #ef4444;
}

.connection-delete-btn:hover line {
  stroke: white;
}

.canvas-container:hover .connection-delete-btn {
  opacity: 1;
}

/* Canvas Controls */
.canvas-controls {
  position: absolute;
  bottom: 20px;
  right: 20px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 12px;
  padding: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.btn-zoom {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #333;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 13px;
  font-weight: 500;
}

.btn-zoom:hover {
  background: #e8e8e8;
  border-color: #d0d0d0;
  transform: translateY(-1px);
}

.btn-zoom:active {
  transform: translateY(0);
}

.btn-zoom svg {
  stroke: #555;
}

.zoom-level {
  padding: 0 12px;
  font-size: 13px;
  color: #666;
  font-weight: 600;
  min-width: 50px;
  text-align: center;
}

.canvas-controls-divider {
  width: 1px;
  height: 24px;
  background: #e0e0e0;
  margin: 0 4px;
}

.multiselect-controls {
  display: flex;
  align-items: center;
  gap: 4px;
}

.btn-multiselect {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  height: 36px;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  color: #333;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-multiselect:hover {
  background: #e8e8e8;
  border-color: #d0d0d0;
}

.btn-multiselect.active {
  background: #fef3c7;
  border-color: #f59e0b;
  color: #92400e;
}

.btn-multiselect svg {
  flex-shrink: 0;
}

.btn-multiselect-clear {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 6px;
  color: #991b1b;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  transition: all 0.2s;
  padding: 0;
}

.btn-multiselect-clear:hover {
  background: #fecaca;
  border-color: #f87171;
}

/* Right Sidebar - Properties */
.right-sidebar {
  position: relative;
  background: var(--card-background);
  border-left: 1px solid var(--border-primary);
  overflow-y: auto;
}

.resize-handle {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  background: transparent;
  transition: background 0.2s;
  z-index: 10;
}

.resize-handle:hover {
  background: rgba(249, 115, 22, 0.3);
}

.resize-handle:active {
  background: rgba(249, 115, 22, 0.5);
}

.properties-panel {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.panel-header {
  padding: 16px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.panel-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
}

.btn-close-panel {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 4px;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: all 0.2s;
}

.btn-close-panel:hover {
  background: var(--hover-background);
  color: var(--text-primary);
}

.properties-content {
  flex: 1;
  padding: 16px;
  overflow-y: auto;
}

.property-group {
  margin-bottom: 20px;
}

.node-validation-errors {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(229, 62, 62, 0.4);
  background: rgba(229, 62, 62, 0.06);
}

.node-validation-error-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 12px;
  font-size: 12.5px;
  color: #c53030;
  border-bottom: 1px solid rgba(229, 62, 62, 0.15);
  line-height: 1.4;
}

.node-validation-error-item:last-child {
  border-bottom: none;
}

.node-validation-warnings {
  margin-bottom: 16px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid rgba(217, 119, 6, 0.4);
  background: rgba(251, 191, 36, 0.08);
}

.node-validation-warning-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 9px 12px;
  font-size: 12.5px;
  color: #92400e;
  border-bottom: 1px solid rgba(217, 119, 6, 0.15);
  line-height: 1.4;
}

.node-validation-warning-item:last-child {
  border-bottom: none;
}

.btn-text-wrap {
  position: relative;
  display: flex;
  align-items: center;
  gap: 6px;
}

.btn-text-wrap .property-input {
  flex: 1;
}

.input-over-limit {
  border-color: #f59e0b !important;
  background: rgba(251, 191, 36, 0.07) !important;
}

.btn-char-error {
  font-size: 11px;
  color: #d97706;
  white-space: nowrap;
  font-weight: 600;
  flex-shrink: 0;
}

.property-label {
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-secondary);
  margin-bottom: 8px;
}

.field-optional {
  font-size: 10px;
  font-weight: 400;
  color: #94a3b8;
  margin-left: 4px;
}

.property-hint {
  display: block;
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 4px;
  font-style: italic;
}

.property-row-2 {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.day-skip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
}

.day-skip-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 4px 8px;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.day-skip-item.active {
  background: rgba(220, 38, 38, 0.08);
  border-color: #dc2626;
  color: #dc2626;
}

.day-skip-item input[type="checkbox"] {
  width: 13px;
  height: 13px;
  cursor: pointer;
}

.property-hint-box {
  font-size: 12px;
  color: #0369a1;
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 16px;
  line-height: 1.5;
}

.property-input {
  width: 100%;
  padding: 8px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 14px;
  user-select: text;
  -webkit-user-select: text;
}

.property-input:focus {
  outline: none;
  border-color: var(--primary-color);
}

.property-value-readonly {
  padding: 8px 12px;
  background: var(--hover-background);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-tertiary);
  font-size: 13px;
}

.property-textarea {
  width: 100%;
  padding: 8px 12px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  color: var(--text-primary);
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
  user-select: text;
  -webkit-user-select: text;
}

.property-textarea:focus {
  outline: none;
  border-color: var(--primary-color);
}

.property-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 14px;
  color: var(--text-primary);
}

.property-checkbox input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.property-checkbox.dynamic-toggle {
  padding: 12px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.06) 100%);
  border-radius: 8px;
  border: 1.5px solid rgba(102, 126, 234, 0.2);
  margin-bottom: 16px;
}

.dynamic-config-section {
  padding: 16px;
  background: rgba(102, 126, 234, 0.05);
  border: 1.5px solid rgba(102, 126, 234, 0.15);
  border-radius: 8px;
  margin-bottom: 16px;
}

.dynamic-config-section .property-group:last-child {
  margin-bottom: 0;
}

.routing-option {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 14px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  background: white;
}

.routing-option:hover {
  border-color: #f97316;
  background: rgba(249, 115, 22, 0.03);
}

.routing-option.active {
  border-color: #f97316;
  background: rgba(249, 115, 22, 0.08);
}

.routing-option input[type="radio"] {
  margin-top: 3px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #f97316;
}

.routing-option-content {
  flex: 1;
}

.routing-option-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.routing-option-content small {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.4;
}

.button-item {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.button-item .property-input {
  flex: 1;
}

.drag-handle {
  cursor: grab;
  color: #9ca3af;
  font-size: 16px;
  padding: 0 4px;
  user-select: none;
  flex-shrink: 0;
}
.drag-handle:active {
  cursor: grabbing;
}

.btn-remove {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fee;
  border: 1px solid #fcc;
  border-radius: 6px;
  color: #ef4444;
  cursor: pointer;
  font-size: 20px;
  font-weight: bold;
  transition: all 0.2s;
}

.btn-remove:hover {
  background: #ef4444;
  color: white;
}

.btn-add {
  width: 100%;
  padding: 8px 12px;
  background: var(--bg-primary);
  border: 1px dashed var(--border-primary);
  border-radius: 6px;
  color: var(--text-secondary);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-add:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  background: rgba(59, 130, 246, 0.05);
}

/* Key-Value Pairs for Headers and Mappings */
.kv-pair {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  align-items: center;
}

.kv-key {
  flex: 1;
}

.kv-value {
  flex: 2;
}

.kv-vte {
  flex: 2;
  min-width: 0;
}

.kv-pair .btn-remove {
  flex-shrink: 0;
}

/* Header Type/Value Pairs */
.header-pair {
  display: grid;
  grid-template-columns: 180px 1fr 32px;
  gap: 8px;
  align-items: center;
}

.header-type-select {
  min-width: 180px;
}

/* Restart Flow node */
.restart-flow-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fff7ed;
  border: 1.5px solid #fed7aa;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.restart-flow-icon {
  font-size: 24px;
  flex-shrink: 0;
  line-height: 1;
}
.restart-flow-info strong {
  display: block;
  font-size: 13px;
  color: #9a3412;
  margin-bottom: 4px;
}
.restart-flow-info p {
  font-size: 12px;
  color: #c2410c;
  margin: 0;
  line-height: 1.5;
}

/* CSAT Rating node — scale selector cards */
.csat-scale-cards {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}
.csat-scale-card {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px 10px;
  border: 1.5px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  text-align: center;
}
.csat-scale-card:hover:not(.csat-scale-card--active) {
  border-color: #fbbf24;
  background: #fffbeb;
}
.csat-scale-card--active {
  border-color: #f59e0b;
  background: #fffbeb;
  box-shadow: 0 0 0 3px rgba(245, 158, 11, 0.15);
}
.csat-scale-stars {
  font-size: 16px;
  line-height: 1.2;
  letter-spacing: 1px;
}
.csat-scale-stars--ten {
  font-size: 11px;
  letter-spacing: 0;
  color: var(--text-secondary);
  font-weight: 600;
}
.csat-scale-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-primary);
}
.csat-scale-desc {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.3;
}
.csat-scale-card--active .csat-scale-label {
  color: #b45309;
}
.csat-scale-card--active .csat-scale-desc {
  color: #92400e;
}

/* CSAT Rating node — info banner */
.csat-info-banner {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #fefce8;
  border: 1.5px solid #fde047;
  border-radius: 8px;
  padding: 12px 14px;
  margin-top: 10px;
}
.csat-info-icon {
  font-size: 22px;
  flex-shrink: 0;
  line-height: 1;
}
.csat-info-banner strong {
  display: block;
  font-size: 13px;
  color: #713f12;
  margin-bottom: 4px;
}
.csat-info-banner p {
  font-size: 12px;
  color: #92400e;
  margin: 0;
  line-height: 1.5;
}

/* Delay node */
.delay-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #f0f9ff;
  border: 1.5px solid #bae6fd;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.delay-icon {
  font-size: 24px;
  flex-shrink: 0;
  line-height: 1;
}
.delay-info strong {
  display: block;
  font-size: 13px;
  color: #0c4a6e;
  margin-bottom: 4px;
}
.delay-info p {
  font-size: 12px;
  color: #0369a1;
  margin: 0;
  line-height: 1.5;
}
.delay-input-row {
  display: flex;
  gap: 8px;
}
.delay-value-input {
  width: 80px;
  flex-shrink: 0;
}
.delay-unit-select {
  flex: 1;
}
.deadline-days-label {
  display: flex;
  align-items: center;
  padding: 0 8px;
  font-size: 13px;
  color: #6b7280;
  white-space: nowrap;
}
.delay-note {
  display: block;
  font-size: 11px;
  color: var(--text-muted, #888);
  margin-top: 6px;
}

.datetime-preview-table {
  border: 1px solid var(--border-color, #e2e8f0);
  border-radius: 6px;
  overflow: hidden;
  margin-top: 4px;
}
.datetime-preview-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 5px 10px;
  font-size: 12px;
  border-bottom: 1px solid var(--border-color, #e2e8f0);
}
.datetime-preview-row:last-child {
  border-bottom: none;
}
.datetime-preview-key {
  color: var(--primary-color, #3b82f6);
  font-size: 11px;
  background: none;
  padding: 0;
}
.datetime-preview-val {
  font-weight: 600;
  color: var(--text-primary, #1e293b);
  font-size: 12px;
}

/* CRM nodes */
.node-category-crm .category-header-crm {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(5, 150, 105, 0.05) 100%);
  border-bottom-color: rgba(16, 185, 129, 0.2);
}
.node-category-crm .category-header-crm:hover {
  background: linear-gradient(135deg, rgba(16, 185, 129, 0.14) 0%, rgba(5, 150, 105, 0.10) 100%);
}

/* Appointments nodes */
.node-category-appointments .category-header-appointments {
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.08) 0%, rgba(2, 132, 199, 0.05) 100%);
  border-bottom-color: rgba(14, 165, 233, 0.2);
}
.node-category-appointments .category-header-appointments:hover {
  background: linear-gradient(135deg, rgba(14, 165, 233, 0.14) 0%, rgba(2, 132, 199, 0.10) 100%);
}
.appt-node-banner {
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 8px;
  margin-bottom: 14px;
  font-size: 13px;
}
.appt-node-banner > span { font-size: 22px; flex-shrink: 0; }
.appt-node-banner p { margin: 2px 0 0; color: #6b7280; font-size: 12px; line-height: 1.4; }
.appt-banner-slots  { background: rgba(14, 165, 233, 0.07); border: 1px solid rgba(14, 165, 233, 0.2); }
.appt-banner-services { background: rgba(139, 92, 246, 0.07); border: 1px solid rgba(139, 92, 246, 0.2); }
.appt-banner-dates    { background: rgba(14, 165, 233, 0.07); border: 1px solid rgba(14, 165, 233, 0.2); }
.auto-connect-hint {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(14, 165, 233, 0.08);
  border: 1px solid rgba(14, 165, 233, 0.3);
  border-radius: 6px;
  padding: 6px 10px;
  margin-bottom: 6px;
  font-size: 12px;
  color: #0369a1;
}
.auto-connect-hint span { flex: 1; }
.auto-connect-btn {
  background: #0ea5e9;
  color: #fff;
  border: none;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.auto-connect-btn:hover { background: #0284c7; }
.auto-connect-badge {
  display: inline-block;
  margin-left: 6px;
  background: rgba(16, 185, 129, 0.12);
  border: 1px solid rgba(16, 185, 129, 0.3);
  color: #059669;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 600;
  vertical-align: middle;
}
.appt-banner-book   { background: rgba(16, 185, 129, 0.07);  border: 1px solid rgba(16, 185, 129, 0.2);  }
.appt-banner-cancel { background: rgba(239, 68, 68, 0.07);   border: 1px solid rgba(239, 68, 68, 0.2);   }
.appt-banner-list   { background: rgba(99, 102, 241, 0.07);  border: 1px solid rgba(99, 102, 241, 0.2);  }
.appt-banner-rating { background: rgba(245, 158, 11, 0.07);  border: 1px solid rgba(245, 158, 11, 0.2);  }
.appt-vars-preview {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 12px;
}
.appt-var-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  padding: 3px 0;
  font-size: 12px;
}
.appt-var-row code {
  font-size: 11px;
  background: #e2e8f0;
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
  flex-shrink: 0;
}
.appt-var-row span { color: #6b7280; }
.appt-var-highlight code { background: #d1fae5; color: #065f46; }
.appt-var-highlight span { color: #047857; font-weight: 500; }
.branch-found { color: #059669; }
.branch-none  { color: #dc2626; }

/* Leads nodes */
.node-category-leads .category-header-leads {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.08) 0%, rgba(79, 70, 229, 0.05) 100%);
  border-bottom-color: rgba(99, 102, 241, 0.2);
}
.node-category-leads .category-header-leads:hover {
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.14) 0%, rgba(79, 70, 229, 0.10) 100%);
}

/* Lead mapping rows (Get Lead Data node) */
.lead-mapping-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}
.lead-mapping-row .mapping-field-select {
  flex: 1;
}
.mapping-arrow {
  color: #9ca3af;
  font-size: 14px;
  flex-shrink: 0;
}
.lead-mapping-row .mapping-var-input {
  flex: 1;
}
.btn-remove-mapping {
  background: none;
  border: 1px solid #fca5a5;
  color: #ef4444;
  border-radius: 4px;
  width: 24px;
  height: 24px;
  cursor: pointer;
  font-size: 11px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.btn-remove-mapping:hover {
  background: #fef2f2;
}
.btn-add-mapping {
  background: none;
  border: 1px dashed #a5b4fc;
  color: #6366f1;
  border-radius: 6px;
  padding: 5px 10px;
  font-size: 12px;
  cursor: pointer;
  width: 100%;
  text-align: center;
  margin-top: 4px;
}
.btn-add-mapping:hover {
  background: #eef2ff;
}

/* Branch info (Check Lead Complete node) */
.branch-info-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}
.branch-info-item {
  padding: 7px 10px;
  border-radius: 6px;
  font-size: 12px;
}
.branch-complete {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  color: #065f46;
}
.branch-incomplete {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}
.crm-node-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #f0fdf4;
  border: 1.5px solid #bbf7d0;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.working-hours-setup-hint {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  background: #fffbeb;
  border: 1.5px solid #fde68a;
  border-radius: 8px;
  padding: 10px 14px;
  margin-top: 12px;
  font-size: 12px;
  color: #92400e;
}
.btn-goto-profile {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #d97706;
  text-decoration: none;
  border: 1.5px solid #fcd34d;
  border-radius: 6px;
  padding: 4px 10px;
  background: #fff;
  transition: background 0.15s, color 0.15s;
}
.btn-goto-profile:hover {
  background: #fef3c7;
  color: #92400e;
}
.crm-node-icon {
  font-size: 24px;
  flex-shrink: 0;
  line-height: 1;
}
.crm-node-info strong {
  display: block;
  font-size: 13px;
  color: #065f46;
  margin-bottom: 4px;
}
.crm-node-info p {
  font-size: 12px;
  color: #047857;
  margin: 0;
  line-height: 1.5;
}
.required-star {
  color: #e53e3e;
}

/* Secret variable styles */
.type-secret {
  background: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}
.variable-value-masked {
  font-size: 12px;
  color: #9ca3af;
  letter-spacing: 2px;
  padding: 2px 0;
}
.secret-toggle-group {
  background: #1e293b;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px;
  margin-top: 4px;
}
.secret-toggle-label {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  cursor: pointer;
}
.secret-checkbox {
  margin-top: 2px;
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  cursor: pointer;
}
.secret-toggle-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.secret-toggle-text strong {
  font-size: 13px;
  color: #fbbf24;
}
.secret-toggle-text small {
  font-size: 11px;
  color: #9ca3af;
  line-height: 1.4;
}

/* Send Template node */
.property-group-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 12px 0 6px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e5e7eb;
}
.template-node-preview {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  border-radius: 8px;
  padding: 10px 12px;
  margin-bottom: 12px;
}
.template-comp-row {
  display: flex;
  gap: 8px;
  align-items: baseline;
  font-size: 12px;
  margin-bottom: 4px;
}
.template-comp-label {
  font-weight: 700;
  color: #065f46;
  flex-shrink: 0;
  min-width: 60px;
}
.template-comp-text {
  color: #374151;
  word-break: break-word;
}
.template-comp-media {
  color: #6b7280;
  font-style: italic;
}

/* Assign Department node */
.assign-dept-info {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  background: #eff6ff;
  border: 1.5px solid #bfdbfe;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 12px;
}
.assign-dept-icon {
  font-size: 24px;
  flex-shrink: 0;
  line-height: 1;
}
.assign-dept-info strong {
  display: block;
  font-size: 13px;
  color: #1e40af;
  margin-bottom: 4px;
}
.assign-dept-info p {
  font-size: 12px;
  color: #1d4ed8;
  margin: 0;
  line-height: 1.5;
}

/* Currency Exchange Node */
.weekend-days-picker {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 6px;
}
.weekend-day-chip {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border-radius: 6px;
  border: 1.5px solid #d1d5db;
  background: #f9fafb;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
}
.weekend-day-chip.active {
  background: #ede9fe;
  border-color: #6366f1;
  color: #4f46e5;
}
.weekend-day-chip input[type="checkbox"] {
  accent-color: #6366f1;
  width: 13px;
  height: 13px;
  cursor: pointer;
}

.setvar-section-label {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.setvar-step {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: #fff;
  background: #6366f1;
  border-radius: 4px;
  padding: 2px 7px;
  white-space: nowrap;
}
.setvar-step-desc {
  font-size: 11px;
  color: #6b7280;
}

.currency-node-banner {
  display: flex;
  gap: 12px;
  align-items: center;
  background: linear-gradient(135deg, #f0fdf4, #dcfce7);
  border: 1.5px solid #86efac;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.currency-node-icon {
  font-size: 26px;
  flex-shrink: 0;
  line-height: 1;
}
.currency-node-banner strong {
  display: block;
  font-size: 13px;
  color: #14532d;
  margin-bottom: 2px;
}
.currency-node-banner small {
  font-size: 11px;
  color: #166534;
}
.currency-row {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  margin-bottom: 4px;
}
.currency-col {
  flex: 1;
  margin-bottom: 0 !important;
}
.currency-arrow {
  font-size: 18px;
  color: #16a34a;
  font-weight: 700;
  padding-bottom: 8px;
  flex-shrink: 0;
}

/* Send Email Node */
.email-node-banner {
  display: flex;
  gap: 12px;
  align-items: center;
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  border: 1.5px solid #fcd34d;
  border-radius: 8px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.email-node-logo {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}
.email-node-banner strong {
  display: block;
  font-size: 13px;
  color: #92400e;
  margin-bottom: 2px;
}
.email-node-banner small {
  font-size: 11px;
  color: #b45309;
}
.email-html-toggle {
  display: flex;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  font-weight: 400;
}
.email-html-toggle input[type="checkbox"] {
  width: 14px;
  height: 14px;
  accent-color: #d97706;
  cursor: pointer;
}
.email-html-toggle-label {
  font-size: 11px;
  color: #92400e;
  background: #fef3c7;
  border: 1px solid #fcd34d;
  border-radius: 4px;
  padding: 1px 6px;
}

/* Variables Helper */
.variables-helper {
  background: #f0f9ff;
  border: 1px solid #bae6fd;
  border-radius: 8px;
  padding: 12px;
}

.variables-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 8px;
}

.variable-chip {
  display: inline-flex;
  align-items: center;
  padding: 5px 10px;
  background: white;
  border: 1.5px solid #0ea5e9;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 11px;
  color: #0369a1;
  cursor: pointer;
  transition: all 0.2s;
  user-select: none;
}

/* ── Variable insert row (inside node editors) ── */
.var-pick-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  padding: 8px 0 2px;
  margin-top: 2px;
  border-top: 1px dashed #e2e8f0;
}
.api-var-picker {
  border-top: 1px dashed var(--border-primary);
  padding-top: 10px;
  margin-top: 4px;
}
.api-var-picker-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}
.api-var-count {
  font-size: 10px;
  color: var(--text-tertiary);
  font-variant-numeric: tabular-nums;
}
.api-var-search {
  width: 100%;
  padding: 5px 9px;
  font-size: 12px;
  border: 1px solid var(--border-primary);
  border-radius: 5px;
  background: var(--bg-secondary);
  color: var(--text-primary);
  outline: none;
  margin-bottom: 8px;
  box-sizing: border-box;
  transition: border-color 0.15s;
}
.api-var-search:focus {
  border-color: #6366f1;
  box-shadow: 0 0 0 2px rgba(99,102,241,0.12);
}
.api-var-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  max-height: 120px;
  overflow-y: auto;
}
.api-var-empty {
  font-size: 11px;
  color: var(--text-tertiary);
  font-style: italic;
  padding: 4px 0;
}

.var-pick-label {
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  flex-shrink: 0;
  margin-right: 2px;
}

.var-pick-chip {
  font-size: 10px;
  font-family: monospace;
  padding: 2px 7px;
  border-radius: 3px;
  border: 1px solid;
  cursor: pointer;
  user-select: none;
  line-height: 1.5;
  transition: background 0.12s, color 0.12s, transform 0.1s;
}
.var-pick-chip:hover {
  transform: translateY(-1px);
  opacity: 0.9;
}
.var-pick-chip:active {
  transform: translateY(0);
}

/* Chip color variants by variable source */
.chip-src-session {
  border-color: #3b82f6;
  color: #1e40af;
  background: #eff6ff;
}
.chip-src-session:hover { background: #3b82f6 !important; color: white !important; box-shadow: 0 2px 6px rgba(59,130,246,0.35) !important; }

.chip-src-auto {
  border-color: #10b981;
  color: #065f46;
  background: #f0fdf4;
}
.chip-src-auto:hover { background: #10b981 !important; color: white !important; box-shadow: 0 2px 6px rgba(16,185,129,0.35) !important; }

.chip-src-node {
  border-color: #6366f1;
  color: #3730a3;
  background: #eef2ff;
}
.chip-src-node:hover { background: #6366f1 !important; color: white !important; box-shadow: 0 2px 6px rgba(99,102,241,0.35) !important; }

.chip-src-api {
  border-color: #f59e0b;
  color: #92400e;
  background: #fffbeb;
}
.chip-src-api:hover { background: #f59e0b !important; color: white !important; box-shadow: 0 2px 6px rgba(245,158,11,0.35) !important; }

.chip-src-custom {
  border-color: #94a3b8;
  color: #475569;
  background: #f8fafc;
}
.chip-src-custom:hover { background: #64748b !important; color: white !important; box-shadow: 0 2px 6px rgba(100,116,139,0.35) !important; }

.variable-chip:hover {
  background: #0ea5e9;
  color: white;
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(14, 165, 233, 0.3);
}
/* All source-specific hovers also lift */
.chip-src-session:hover,
.chip-src-auto:hover,
.chip-src-node:hover,
.chip-src-api:hover,
.chip-src-custom:hover {
  transform: translateY(-1px);
}

.variable-chip:active {
  transform: translateY(0);
}

/* API Test — sample variable values */
.api-test-samples {
  background: #fffbeb;
  border: 1px solid #fde68a;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 16px;
}

.api-test-samples-header {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-bottom: 10px;
}

.api-test-samples-header span {
  font-size: 13px;
  font-weight: 600;
  color: #92400e;
}

.api-test-samples-header small {
  font-size: 11px;
  color: #b45309;
}

.api-test-samples-header code {
  background: rgba(0,0,0,0.07);
  padding: 1px 4px;
  border-radius: 3px;
  font-family: monospace;
}

.api-test-sample-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.api-test-sample-tag {
  font-size: 10px;
  font-family: monospace;
  padding: 2px 7px;
  border-radius: 3px;
  border: 1px solid #10b981;
  color: #065f46;
  background: #f0fdf4;
  white-space: nowrap;
  flex-shrink: 0;
}

.api-test-sample-input {
  flex: 1;
  margin-bottom: 0 !important;
}

/* API Test Response */
.api-test-result {
  background: #f0fdf4;
  border: 1px solid #86efac;
  border-radius: 8px;
  padding: 12px;
}

.api-test-error {
  background: #fef2f2;
  border: 1px solid #fca5a5;
  border-radius: 8px;
  padding: 12px;
}

.api-response-preview,
.api-error-preview {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 12px;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  max-height: 300px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
  color: #1f2937;
  user-select: text;
  cursor: text;
}

.property-hint.success {
  color: #16a34a;
  font-weight: 500;
}

.detected-key-row {
  display: flex;
  gap: 8px;
  align-items: center;
  padding: 8px;
  background: #f9fafb;
  border-radius: 6px;
  margin-bottom: 8px;
}

.detected-key-info {
  flex: 0 0 180px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.detected-key-name {
  font-weight: 600;
  color: #1f2937;
  font-size: 13px;
}

.detected-key-type {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.detected-var-input {
  flex: 1;
  min-width: 0;
}

.btn-save-var {
  padding: 6px 12px;
  background: #10b981;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  transition: all 0.2s;
  white-space: nowrap;
}

.btn-save-var:hover:not(:disabled) {
  background: #059669;
}

.btn-save-var:disabled {
  background: #d1d5db;
  cursor: not-allowed;
}

.btn-unsave-var {
  background: #ef4444 !important;
}
.btn-unsave-var:hover {
  background: #dc2626 !important;
}

.empty-state {
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px;
  color: var(--text-tertiary);
}

.empty-state p {
  margin-top: 16px;
  font-size: 14px;
}

/* Variables panel content */
.variable-count {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  flex-shrink: 0;
}

.vars-sidebar-content {
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  padding: 12px;
  gap: 10px;
}

/* ── Settings panel ── */
.settings-panel-inner {
  overflow-y: auto !important;
}
.settings-sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: var(--card-background);
}
.settings-content {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.settings-error-banner {
  display: flex;
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
  background: rgba(220, 38, 38, 0.1);
  border: 1px solid rgba(220, 38, 38, 0.35);
  border-radius: 7px;
  color: #ef4444;
  font-size: 12px;
  line-height: 1.5;
}
.settings-error-item + .settings-error-item {
  margin-top: 3px;
}
.settings-section {
  background: var(--bg-secondary, #1f2937);
  border: 1px solid var(--border-color, #374151);
  border-radius: 8px;
  overflow: hidden;
}
.settings-section-header {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border-color, #374151);
  background: rgba(255,255,255,0.03);
}
.settings-section-icon {
  font-size: 18px;
  line-height: 1.3;
  flex-shrink: 0;
}
.settings-section-header strong {
  display: block;
  color: var(--text-primary, #f9fafb);
  font-size: 13px;
  margin-bottom: 3px;
}
.settings-section-header p {
  margin: 0;
  font-size: 11px;
  color: var(--text-secondary, #9ca3af);
  line-height: 1.4;
}
.settings-field {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.settings-label {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-secondary, #9ca3af);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.vars-actions {
  flex-shrink: 0;
}

.vars-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

/* Variable group sections */
.var-group {
  margin-bottom: 8px;
}

.var-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 10px;
  border-radius: 7px;
  margin-bottom: 5px;
  background: #f1f5f9;
  border-left: 3px solid #cbd5e1;
}
.var-group-header.vg-session{ background: #eff6ff; border-left-color: #3b82f6; }
.var-group-header.vg-auto  { background: #f0fdf4; border-left-color: #10b981; }
.var-group-header.vg-node  { background: #eef2ff; border-left-color: #6366f1; }
.var-group-header.vg-api   { background: #fffbeb; border-left-color: #f59e0b; }
.var-group-header.vg-custom{ background: #f8fafc; border-left-color: #94a3b8; }

.var-group-icon {
  font-size: 16px;
  flex-shrink: 0;
}
.var-group-info {
  flex: 1;
  min-width: 0;
}
.var-group-label {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: #1e293b;
  line-height: 1.3;
}
.var-group-desc {
  display: block;
  font-size: 10px;
  color: #475569;
  line-height: 1.3;
}
.var-group-count {
  font-size: 11px;
  font-weight: 700;
  background: rgba(0,0,0,0.12);
  border-radius: 10px;
  padding: 1px 7px;
  color: #334155;
  flex-shrink: 0;
}

/* Card left-border accent by source */
.variable-card.card-src-session { border-left: 3px solid #3b82f6; }
.variable-card.card-src-auto   { border-left: 3px solid #10b981; }
.variable-card.card-src-node   { border-left: 3px solid #6366f1; }
.variable-card.card-src-api    { border-left: 3px solid #f59e0b; }
.variable-card.card-src-custom { border-left: 3px solid #94a3b8; }

.vars-empty {
  text-align: center;
  padding: 32px 12px;
  color: var(--text-secondary);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.vars-empty svg {
  color: var(--text-tertiary);
}

.vars-empty p {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.vars-empty small {
  font-size: 11px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.btn-add-variable {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 9px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.25);
}

.btn-add-variable:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.35);
}

.btn-add-variable:active {
  transform: translateY(0);
}

.variable-card {
  background: var(--bg-secondary);
  border: 1.5px solid var(--border-primary);
  border-radius: 8px;
  padding: 10px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.variable-card:hover {
  border-color: #667eea;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15), 0 2px 4px rgba(102, 126, 234, 0.1);
  transform: translateY(-1px);
}

.variable-card-header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 6px;
}

.variable-title {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}

.variable-syntax {
  font-family: 'Courier New', Consolas, monospace;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  background: rgba(102, 126, 234, 0.08);
  padding: 6px 10px;
  border-radius: 6px;
  border: 1px solid rgba(102, 126, 234, 0.2);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.btn-copy-var {
  padding: 6px;
  background: transparent;
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  cursor: pointer;
  color: var(--text-secondary);
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-copy-var:hover {
  background: #667eea;
  border-color: #667eea;
  color: white;
  transform: scale(1.05);
}

.variable-meta {
  display: flex;
  gap: 6px;
  align-items: center;
  flex-shrink: 0;
}

.type-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.type-badge.type-string {
  background: #dbeafe;
  color: #1e40af;
}

.type-badge.type-number {
  background: #fef3c7;
  color: #92400e;
}

.type-badge.type-object {
  background: #e9d5ff;
  color: #6b21a8;
}

.source-badge {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  padding: 4px 8px;
  border-radius: 4px;
  letter-spacing: 0.5px;
}

.source-badge.source-custom {
  background: #fef3c7;
  color: #92400e;
}

.source-badge.source-auto {
  background: #d1fae5;
  color: #065f46;
}

.variable-description {
  font-size: 12px;
  color: var(--text-secondary);
  margin-bottom: 8px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.5;
}

.variable-description svg {
  flex-shrink: 0;
  margin-top: 2px;
  color: var(--text-tertiary);
}

.array-info {
  margin-top: 8px;
  padding: 12px;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(167, 139, 250, 0.06) 100%);
  border: 1.5px solid rgba(139, 92, 246, 0.2);
  border-radius: 8px;
}

.array-info-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.array-info-header svg {
  color: #8b5cf6;
  flex-shrink: 0;
}

.array-info-text {
  font-size: 12px;
  font-weight: 600;
  color: #8b5cf6;
}

.array-sample {
  margin-top: 8px;
}

.array-sample-items {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 6px;
}

.array-sample-item {
  font-family: 'Courier New', Consolas, monospace;
  font-size: 11px;
  padding: 6px 8px;
  background: rgba(255, 255, 255, 0.5);
  border: 1px solid rgba(139, 92, 246, 0.15);
  border-radius: 4px;
  color: var(--text-primary);
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.variable-preview {
  margin-top: 8px;
  padding: 10px;
  background: var(--hover-background);
  border-radius: 6px;
  border: 1px solid var(--border-primary);
}

.preview-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 6px;
}

.preview-value {
  font-family: 'Courier New', Consolas, monospace;
  font-size: 12px;
  color: var(--text-primary);
  display: block;
  word-break: break-all;
}

.variable-card-actions {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid var(--border-primary);
}

.btn-action {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-action.btn-edit {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
}

.btn-action.btn-edit:hover {
  background: #dbeafe;
  border-color: #3b82f6;
  transform: translateY(-1px);
}

.btn-action.btn-delete {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
}

.btn-action.btn-delete:hover {
  background: #fee2e2;
  border-color: #ef4444;
  transform: translateY(-1px);
}

.empty-state {
  text-align: center;
  padding: 48px 24px;
  max-width: 500px;
  margin: 0 auto;
}

.empty-state svg {
  color: #d1d5db;
  margin-bottom: 20px;
}

.empty-state h4 {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 12px 0;
}

.empty-state p {
  font-size: 13px;
  color: var(--text-secondary);
  line-height: 1.6;
  margin: 0 0 24px 0;
}

.empty-state .btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.empty-state .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(102, 126, 234, 0.4);
}

/* ── Variable source node pill ─────────────────────────────────── */
.var-source-node-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 5px;
  padding: 3px 8px;
  border-radius: 5px;
  border: 1px solid var(--border-color, rgba(255,255,255,0.1));
  background: rgba(99, 102, 241, 0.08);
  color: #818cf8;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}
.var-source-node-btn:hover {
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
}

/* ── Broken / Orphaned Variables ───────────────────────────────── */
.vg-broken {
  background: rgba(239, 68, 68, 0.08);
  border-left: 3px solid #ef4444;
}
.vg-broken .var-group-label { color: #f87171; }
.vg-broken .var-group-count {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}
.card-broken {
  border: 1px solid rgba(239, 68, 68, 0.3) !important;
  background: rgba(239, 68, 68, 0.05) !important;
}
.broken-syntax {
  color: #f87171 !important;
  text-decoration: line-through;
  opacity: 0.8;
}
.type-broken {
  background: rgba(239, 68, 68, 0.15);
  color: #f87171;
  border: 1px solid rgba(239, 68, 68, 0.3);
}
.node-status-badge.broken-badge {
  cursor: default;
  font-size: 12px;
  line-height: 1;
}

/* ── Delete Node Modal ─────────────────────────────────────────── */
.delete-node-overlay {
  backdrop-filter: blur(2px);
}
.delete-node-modal {
  max-width: 460px;
  width: 90%;
  border-radius: 14px;
  overflow: hidden;
  animation: slideUpFade 0.18s ease-out;
  background: var(--card-background, #1f2937) !important;
}
.delete-node-header,
.delete-node-body,
.delete-node-footer {
  background: var(--card-background, #1f2937);
}
@keyframes slideUpFade {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}
.delete-node-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--border-color, rgba(255,255,255,0.08));
}
.delete-node-header h2 {
  font-size: 15px;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
}
.delete-node-body {
  padding: 18px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.delete-node-name {
  margin: 0;
  font-size: 13.5px;
  color: var(--text-primary);
  line-height: 1.5;
}
.delete-node-safe {
  margin: 0;
  font-size: 12.5px;
  color: var(--text-secondary);
}
.delete-node-warning {
  background: rgba(245, 158, 11, 0.08);
  border: 1px solid rgba(245, 158, 11, 0.25);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.delete-warning-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  color: #f59e0b;
}
.delete-affected-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 160px;
  overflow-y: auto;
}
.delete-affected-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
}
.delete-var-chip {
  background: rgba(99, 102, 241, 0.15);
  color: #818cf8;
  padding: 2px 7px;
  border-radius: 5px;
  font-size: 11.5px;
  white-space: nowrap;
}
.delete-affected-arrow {
  color: var(--text-secondary);
  font-size: 11px;
}
.delete-affected-node {
  color: var(--text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.delete-warning-note {
  margin: 0;
  font-size: 11.5px;
  color: var(--text-secondary);
}
.delete-node-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 20px;
  border-top: 1px solid var(--border-color, rgba(255,255,255,0.08));
}
.btn-cancel-delete {
  padding: 8px 18px;
  border-radius: 8px;
  border: 1px solid var(--border-color, rgba(255,255,255,0.12));
  background: transparent;
  color: var(--text-primary);
  font-size: 13px;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-cancel-delete:hover {
  background: rgba(255,255,255,0.06);
}
.btn-confirm-delete {
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: #ef4444;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background 0.15s;
}
.btn-confirm-delete:hover {
  background: #dc2626;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-content {
  background: var(--card-background);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 20px 24px;
  border-bottom: 1px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.btn-close {
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--text-tertiary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
}

.btn-close:hover {
  background: var(--hover-background);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

/* Variable Modal Specific Styles */
.variable-modal-overlay {
  background: rgba(0, 0, 0, 0.7) !important;
  backdrop-filter: blur(2px);
}

.variable-modal {
  max-width: 500px;
  background: var(--card-background) !important;
  animation: modalSlideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.variable-modal .modal-header {
  background: var(--card-background);
}

.variable-modal .modal-body {
  background: var(--card-background);
}

.variable-modal .modal-footer {
  background: var(--bg-secondary);
}

@keyframes modalSlideIn {
  from {
    transform: translateY(-20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  background: #374151;
  color: #f9fafb;
  transition: all 0.2s;
  font-family: 'Courier New', monospace;
}

.form-input:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-input::placeholder {
  color: #9ca3af;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.form-hint {
  display: block;
  font-size: 12px;
  color: var(--text-secondary);
  margin-top: 6px;
  line-height: 1.5;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid var(--border-primary);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: var(--bg-secondary);
}

.btn-primary {
  display: flex;
  align-items: center;
  padding: 10px 24px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(102, 126, 234, 0.4);
  background: linear-gradient(135deg, #7c93f5 0%, #8b5bb8 100%);
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.btn-secondary {
  padding: 10px 24px;
  background: var(--bg-primary);
  color: var(--text-primary);
  border: 2px solid var(--border-primary);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}

.btn-secondary:hover {
  background: var(--hover-background);
  border-color: var(--text-tertiary);
}

/* List Row Item Styling */
.list-row-item {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  align-items: flex-start;
}

.list-row-fields {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ─── Google Node Styles ─── */
.node-category-google .category-header,
.category-header-google {
  border-left: 3px solid #4285F4;
}

.node-item-google {
  border-left: 2px solid #4285F4;
}

.node-editor-google {
  border-top: 3px solid #4285F4;
}

.google-node-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  font-weight: 600;
  font-size: 13px;
}

.sheets-banner {
  background: rgba(52, 168, 83, 0.12);
  color: #34A853;
  border: 1px solid rgba(52, 168, 83, 0.3);
}

.drive-banner {
  background: rgba(66, 133, 244, 0.12);
  color: #4285F4;
  border: 1px solid rgba(66, 133, 244, 0.3);
}

.docs-banner {
  background: rgba(66, 133, 244, 0.10);
  color: #4285F4;
  border: 1px solid rgba(66, 133, 244, 0.25);
}

.filter-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

/* ─── AI Node Styles ─── */
.node-category-ai .category-header,
.category-header-ai {
  border-left: 3px solid #7c3aed;
}

.node-item-ai {
  border-left: 2px solid #7c3aed;
}

.node-editor-ai {
  border-top: 3px solid #7c3aed;
}

.ai-node-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 14px;
}

.ai-node-banner strong {
  display: block;
  font-size: 13px;
  font-weight: 600;
}

.ai-node-banner small {
  display: block;
  font-size: 11px;
  opacity: 0.8;
  margin-top: 1px;
}

.ai-node-logo-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
  flex-shrink: 0;
}

.ai-icon-img {
  width: 18px;
  height: 18px;
  object-fit: contain;
  flex-shrink: 0;
}

.openai-banner {
  background: rgba(16, 163, 127, 0.12);
  color: #10a37f;
  border: 1px solid rgba(16, 163, 127, 0.3);
}

.claude-banner {
  background: rgba(215, 126, 72, 0.12);
  color: #c96d2e;
  border: 1px solid rgba(215, 126, 72, 0.3);
}

.gemini-banner {
  background: rgba(66, 133, 244, 0.12);
  color: #4285F4;
  border: 1px solid rgba(66, 133, 244, 0.3);
}

.ai-advanced {
  opacity: 0.85;
}

.chip-src-ai {
  background: rgba(124, 58, 237, 0.12);
  color: #7c3aed;
  border-color: rgba(124, 58, 237, 0.4);
}

/* Post-resolve toggle row */
.post-resolve-toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.toggle-switch-label {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
  user-select: none;
}

.toggle-switch-label input[type="checkbox"] {
  display: none;
}

.toggle-switch-track {
  display: inline-block;
  width: 32px;
  height: 18px;
  border-radius: 9px;
  background: var(--border-primary);
  position: relative;
  transition: background 0.2s;
  flex-shrink: 0;
}

.toggle-switch-label input:checked + .toggle-switch-track {
  background: #0ea5e9;
}

.toggle-switch-track::after {
  content: '';
  position: absolute;
  left: 2px;
  top: 2px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #fff;
  transition: transform 0.2s;
}

.toggle-switch-label input:checked + .toggle-switch-track::after {
  transform: translateX(14px);
}

.post-resolve-active-hint {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 8px 10px;
  background: rgba(14, 165, 233, 0.06);
  border: 1px solid rgba(14, 165, 233, 0.25);
  border-radius: 6px;
  font-size: 12px;
  color: var(--text-secondary);
  line-height: 1.5;
  margin-top: 4px;
}

.post-resolve-hint-icon {
  flex-shrink: 0;
  margin-top: 1px;
}

/* Resolve Action Pills (assign_department node) */
.resolve-action-pills {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.resolve-action-pill {
  flex: 1;
  padding: 6px 8px;
  border: 1.5px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.resolve-action-pill.active {
  border-color: #0ea5e9;
  background: rgba(14, 165, 233, 0.08);
  color: #0ea5e9;
}

.resolve-action-pill:hover:not(.active) {
  border-color: #7dd3fc;
}

/* AI Mode Pills */
.ai-mode-pills {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.ai-mode-pill {
  flex: 1;
  padding: 6px 8px;
  border: 1.5px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.ai-mode-pill--active {
  border-color: #7c3aed;
  background: rgba(124, 58, 237, 0.08);
  color: #7c3aed;
}

.ai-mode-pill:hover:not(.ai-mode-pill--active) {
  border-color: #a78bfa;
  color: #7c3aed;
}

/* AI Field Rows (intents + extract fields) */
.ai-field-row {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  align-items: center;
}

.ai-field-name {
  flex: 1;
  min-width: 0;
}

.ai-field-desc {
  flex: 2;
  min-width: 0;
}

.btn-remove-field {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border: none;
  background: none;
  color: var(--text-tertiary);
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.btn-remove-field:hover {
  background: #fee2e2;
  color: #dc2626;
}

.btn-add-field {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border: 1.5px dashed var(--border-primary);
  border-radius: 5px;
  background: none;
  color: var(--text-secondary);
  font-size: 12px;
  cursor: pointer;
  margin-top: 2px;
  transition: border-color 0.15s, color 0.15s;
}
.store-locator-row {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 6px;
  padding: 8px 10px;
  margin-bottom: 8px;
}

.btn-add-field:hover {
  border-color: #7c3aed;
  color: #7c3aed;
}

/* Conversation history limit row */
.history-limit-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  font-size: 12px;
  color: var(--text-secondary);
}

.history-limit-input {
  width: 60px !important;
  text-align: center;
  padding: 4px 6px !important;
}

.property-hint--warn {
  color: #b45309;
  background: rgba(251, 191, 36, 0.12);
  border: 1px solid rgba(251, 191, 36, 0.35);
  border-radius: 4px;
  padding: 4px 8px;
  display: block;
}

.dark .property-hint--warn {
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.08);
  border-color: rgba(251, 191, 36, 0.25);
}

/* AI tool connection edge */
.connection-path.connection-tool {
  stroke: #7c3aed;
  stroke-width: 2;
  stroke-dasharray: 7,4;
  filter: drop-shadow(0 1px 3px rgba(124, 58, 237, 0.35));
}
.connection-path.connection-tool:hover {
  stroke-width: 3;
  stroke: #6d28d9;
}

/* AI bottom tool ports */
.port-tool {
  position: absolute;
  width: 14px;
  height: 14px;
  background: white;
  border: 2.5px solid #7c3aed;
  border-radius: 50%;
  cursor: crosshair;
  transition: all 0.2s;
  z-index: 3;
  box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12);
}
.port-tool:hover {
  background: rgba(124, 58, 237, 0.1);
  transform: scale(1.25);
  box-shadow: 0 0 0 4px rgba(124, 58, 237, 0.2);
}
.port-tool.port-connected {
  background: linear-gradient(135deg, #7c3aed, #6d28d9);
  border-color: #7c3aed;
  box-shadow: 0 2px 6px rgba(124, 58, 237, 0.45);
}
.port-tool-label {
  position: absolute;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  background: #7c3aed;
  color: white;
  border-radius: 5px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  box-shadow: 0 2px 8px rgba(124, 58, 237, 0.3);
}
.port-tool:hover .port-tool-label {
  opacity: 1;
}
.port-tool-add {
  border-style: dashed;
  opacity: 0.6;
}
.port-tool-add:hover {
  opacity: 1;
  border-style: solid;
}

/* AI Tools */
.ai-tools-empty {
  font-size: 12px;
  color: #9ca3af;
  font-style: italic;
  padding: 4px 0;
}
.ai-tools-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}
.ai-tool-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: rgba(0,0,0,0.03);
  border: 1px solid rgba(0,0,0,0.08);
  border-radius: 6px;
  padding: 6px 8px;
}
.dark .ai-tool-row {
  background: rgba(255,255,255,0.03);
  border-color: rgba(255,255,255,0.08);
}
.ai-tool-row-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}
.ai-tool-type-badge {
  font-size: 12px;
  color: #6b7280;
  text-transform: capitalize;
}
.dark .ai-tool-type-badge {
  color: #9ca3af;
}
.ai-tool-fields {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 20px;
}
.ai-tool-name-input {
  font-family: monospace;
  font-size: 12px;
}
.ai-tool-desc-input {
  font-size: 12px;
}

/* ─── Advertiser Node Styles ─── */
.node-category-advertiser .category-header,
.category-header-advertiser {
  border-left: 3px solid #f97316;
}

.node-item-advertiser {
  border-left: 2px solid #f97316;
}

.node-editor-advertiser {
  border-top: 3px solid #f97316;
}

.advertiser-node-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 14px;
  background: rgba(249, 115, 22, 0.10);
  border: 1px solid rgba(249, 115, 22, 0.3);
}

.advertiser-banner-icon {
  font-size: 24px;
  flex-shrink: 0;
}

.advertiser-node-banner strong {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #ea580c;
}

.advertiser-node-banner small {
  display: block;
  font-size: 11px;
  color: #f97316;
  opacity: 0.85;
  margin-top: 1px;
}

.adv-mode-pills {
  display: flex;
  gap: 6px;
  margin-top: 4px;
}

.adv-mode-pill {
  flex: 1;
  padding: 6px 8px;
  border: 1.5px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.adv-mode-pill--active {
  border-color: #f97316;
  background: rgba(249, 115, 22, 0.08);
  color: #ea580c;
}

.adv-mode-pill:hover:not(.adv-mode-pill--active) {
  border-color: #fdba74;
  color: #ea580c;
}

.adv-count-row {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 4px;
}

.adv-count-btn {
  width: 32px;
  height: 32px;
  border: 1.5px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.adv-count-btn--active {
  border-color: #f97316;
  background: rgba(249, 115, 22, 0.08);
  color: #ea580c;
}

.adv-count-btn:hover:not(.adv-count-btn--active) {
  border-color: #fdba74;
  color: #ea580c;
}

.adv-slots-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 4px;
}

.adv-slot-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  font-size: 12px;
}

.adv-slot-row.adv-slot-connected {
  border-color: rgba(249, 115, 22, 0.4);
  background: rgba(249, 115, 22, 0.06);
}

.adv-slot-num {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--border-primary);
  color: var(--text-secondary);
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.adv-slot-connected .adv-slot-num {
  background: #f97316;
  color: white;
}

.adv-slot-name {
  flex: 1;
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.adv-slot-connected .adv-slot-name {
  color: var(--text-primary);
  font-weight: 500;
}

.adv-weight-input {
  width: 52px;
  padding: 3px 5px;
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 12px;
  text-align: center;
}

.adv-weight-pct {
  width: 34px;
  font-size: 11px;
  color: #f97316;
  font-weight: 600;
  text-align: right;
  flex-shrink: 0;
}

.adv-slot-clear {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border: none;
  background: none;
  color: var(--text-tertiary);
  font-size: 12px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
}

.adv-slot-clear:hover {
  background: #fee2e2;
  color: #dc2626;
}

/* Advertiser port — orange to distinguish from AI purple */
.port-adv-msg {
  border-color: #f97316 !important;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.12) !important;
}

.port-adv-msg:hover {
  background: rgba(249, 115, 22, 0.1) !important;
  box-shadow: 0 0 0 4px rgba(249, 115, 22, 0.2) !important;
}

.port-adv-msg.port-connected {
  background: linear-gradient(135deg, #f97316, #ea580c) !important;
  border-color: #f97316 !important;
  box-shadow: 0 2px 6px rgba(249, 115, 22, 0.45) !important;
}

.port-adv-msg .port-tool-label {
  background: #f97316 !important;
  box-shadow: 0 2px 8px rgba(249, 115, 22, 0.3) !important;
}

/* ─── Advertiser Slot Card (targeting) ─── */
.adv-slot-card {
  border: 1px solid rgba(249, 115, 22, 0.2);
  border-radius: 8px;
  overflow: hidden;
  background: var(--bg-secondary);
  margin-bottom: 8px;
}

.adv-slot-card.adv-slot-connected {
  border-color: rgba(249, 115, 22, 0.4);
}

.adv-slot-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: rgba(249, 115, 22, 0.05);
  min-height: 36px;
}

.adv-targeting-badges {
  font-size: 11px;
  background: rgba(249, 115, 22, 0.12);
  border: 1px solid rgba(249, 115, 22, 0.25);
  border-radius: 10px;
  padding: 1px 7px;
  color: #ea580c;
  white-space: nowrap;
}

.adv-target-btn {
  margin-left: auto;
  padding: 3px 9px;
  font-size: 11px;
  border: 1px solid rgba(249, 115, 22, 0.3);
  border-radius: 5px;
  background: transparent;
  color: #f97316;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s;
}

.adv-target-btn:hover,
.adv-target-btn--active {
  background: rgba(249, 115, 22, 0.12);
  border-color: #f97316;
}

.adv-slot-targeting {
  padding: 10px;
  border-top: 1px solid rgba(249, 115, 22, 0.15);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.adv-target-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.adv-target-label {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.adv-target-hint {
  font-size: 11px;
  color: var(--text-muted, var(--text-secondary));
  opacity: 0.75;
}

.adv-country-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.adv-country-chip {
  padding: 3px 8px;
  font-size: 11px;
  border: 1px solid rgba(249, 115, 22, 0.25);
  border-radius: 12px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}

.adv-country-chip:hover {
  border-color: #f97316;
  color: var(--text-primary);
}

.adv-country-chip--on {
  background: rgba(249, 115, 22, 0.15);
  border-color: #f97316;
  color: #ea580c;
  font-weight: 600;
}

.adv-time-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.adv-time-label {
  font-size: 12px;
  color: var(--text-secondary);
  white-space: nowrap;
}

.adv-time-input {
  flex: 1;
  min-width: 90px;
  max-width: 130px;
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-primary, #fff);
  color: var(--text-primary);
}

.adv-time-input:focus {
  outline: none;
  border-color: #f97316;
}

.adv-clear-time {
  padding: 2px 7px;
  font-size: 12px;
  border: 1px solid rgba(249, 115, 22, 0.3);
  border-radius: 4px;
  background: transparent;
  color: #f97316;
  cursor: pointer;
}

.adv-clear-time:hover {
  background: #fee2e2;
  color: #dc2626;
  border-color: #dc2626;
}

.adv-freq-row {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.adv-freq-input {
  width: 60px;
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-primary, #fff);
  color: var(--text-primary);
  text-align: center;
}

.adv-freq-input:focus {
  outline: none;
  border-color: #f97316;
}

.adv-freq-select {
  padding: 4px 6px;
  font-size: 12px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: var(--bg-primary, #fff);
  color: var(--text-primary);
}

.adv-freq-select:focus {
  outline: none;
  border-color: #f97316;
}

/* ─── QR Generator Node Styles ─── */
.node-editor-qr {
  border-top: 3px solid #6366f1;
}

.qr-presets {
  display: flex;
  gap: 6px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.qr-preset-btn {
  flex: 1;
  min-width: 60px;
  padding: 6px 8px;
  border: 1.5px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  text-align: center;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  white-space: nowrap;
}

.qr-preset-btn:hover {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.08);
  color: #6366f1;
}

/* Size pills */
.qr-size-pills {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.qr-size-pill {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 6px;
  border: 1.5px solid var(--border-primary);
  border-radius: 8px;
  background: var(--bg-primary);
  color: var(--text-secondary);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
}

.qr-size-pill--active {
  border-color: #6366f1;
  background: rgba(99, 102, 241, 0.08);
  color: #6366f1;
}

.qr-size-pill:hover:not(.qr-size-pill--active) {
  border-color: #a5b4fc;
  color: #6366f1;
}

.qr-size-px {
  font-size: 10px;
  font-weight: 400;
  opacity: 0.7;
  margin-top: 1px;
}

/* Send toggle */
.qr-send-toggle {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1.5px solid var(--border-primary);
  background: var(--bg-secondary);
  transition: border-color 0.15s;
}

.qr-send-toggle:hover {
  border-color: #6366f1;
}

.qr-toggle-track {
  flex-shrink: 0;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: var(--border-primary);
  position: relative;
  transition: background 0.2s;
}

.qr-toggle-track.qr-toggle-on {
  background: #6366f1;
}

.qr-toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: white;
  transition: transform 0.2s;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
}

.qr-toggle-on .qr-toggle-thumb {
  transform: translateX(16px);
}

.qr-toggle-text span {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.qr-toggle-text small {
  display: block;
  font-size: 11px;
  color: var(--text-tertiary);
  margin-top: 1px;
}

.qr-caption-row {
  margin-top: 8px;
}

/* ─── Calculator Node Styles ─── */
.node-editor-calc {
  border-top: 3px solid #0d9488;
  padding: 14px;
}

.calc-cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}

.calc-card {
  background: var(--bg-secondary);
  border: 1.5px solid var(--border-primary);
  border-radius: 10px;
  padding: 10px;
  transition: border-color 0.15s;
}

.calc-card:focus-within {
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.1);
}

/* Equation row: A [op] B */
.calc-eq-row {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
}

.calc-operand {
  flex: 1;
  min-width: 0;
  padding: 6px 8px;
  border: 1.5px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 12px;
  font-family: monospace;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.calc-operand:focus,
.calc-operand--active {
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.15);
}

.calc-op-select {
  flex-shrink: 0;
  padding: 6px 4px;
  border: 1.5px solid var(--border-primary);
  border-radius: 6px;
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  outline: none;
  min-width: 0;
  width: 110px;
}

.calc-op-select:focus {
  border-color: #0d9488;
}

/* Result row: save as [var_name] [✕] */
.calc-result-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.calc-save-label {
  font-size: 11px;
  font-weight: 600;
  color: #0d9488;
  white-space: nowrap;
  flex-shrink: 0;
}

.calc-result-input {
  flex: 1;
  min-width: 0;
  padding: 5px 8px;
  border: 1.5px dashed rgba(13, 148, 136, 0.5);
  border-radius: 6px;
  background: rgba(13, 148, 136, 0.05);
  color: var(--text-primary);
  font-size: 12px;
  font-family: monospace;
  outline: none;
  transition: border-color 0.15s;
}

.calc-result-input:focus {
  border-style: solid;
  border-color: #0d9488;
}

.calc-remove-btn {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border: none;
  background: none;
  color: var(--text-tertiary);
  font-size: 13px;
  cursor: pointer;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  line-height: 1;
}

.calc-remove-btn:hover {
  background: #fee2e2;
  color: #dc2626;
}

.calc-empty {
  text-align: center;
  font-size: 12px;
  color: var(--text-tertiary);
  padding: 18px 0 8px;
  font-style: italic;
}

/* Add button */
.btn-add-calc {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 8px;
  border: 1.5px dashed rgba(13, 148, 136, 0.5);
  border-radius: 8px;
  background: rgba(13, 148, 136, 0.04);
  color: #0d9488;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  margin-bottom: 14px;
}

.btn-add-calc:hover {
  background: rgba(13, 148, 136, 0.10);
  border-color: #0d9488;
}

/* Variable picker */
.calc-var-picker {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  padding: 10px;
  margin-bottom: 10px;
}

.calc-var-picker-label {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--text-tertiary);
  margin-bottom: 8px;
  font-weight: 500;
}

.calc-var-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
}

.calc-var-pill {
  padding: 3px 9px;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 20px;
  font-size: 11px;
  font-family: monospace;
  color: var(--text-secondary);
  cursor: default;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  user-select: none;
}

.calc-var-pill--ready {
  cursor: pointer;
  border-color: rgba(13, 148, 136, 0.4);
  color: #0d9488;
}

.calc-var-pill--ready:hover {
  background: rgba(13, 148, 136, 0.10);
  border-color: #0d9488;
  color: #0d9488;
}

.calc-chain-hint {
  display: block;
  font-size: 11px;
  color: var(--text-tertiary);
  text-align: center;
  padding: 0 4px 4px;
}

/* Google — No Account Connected panel */
.google-not-connected-panel {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 36px 24px;
  gap: 10px;
}

.google-not-connected-panel .gnc-icon {
  color: #4285F4;
  opacity: 0.6;
  margin-bottom: 4px;
}

.google-not-connected-panel h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: var(--text-primary, #f9fafb);
}

.google-not-connected-panel p {
  margin: 0;
  font-size: 13px;
  color: var(--text-secondary, #9ca3af);
  max-width: 260px;
}

.btn-go-connect {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  background: #4285F4;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: background 0.15s;
}

.btn-go-connect:hover { background: #3267d6; }

/* Google Sheets dynamic pickers */
.gs-picker-row {
  display: flex;
  gap: 6px;
  align-items: center;
}

.gs-picker-row select { flex: 1; }

.property-label-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.btn-gs-refresh {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  background: var(--bg-secondary, #1a1a2e);
  border: 1px solid var(--border-primary, #2a2a3e);
  border-radius: 6px;
  color: var(--text-secondary, #888);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-gs-refresh:hover:not(:disabled) {
  border-color: #4285F4;
  color: #4285F4;
}

.btn-gs-refresh:disabled { opacity: 0.4; cursor: not-allowed; }

/* Row data / set data builder */
.row-builder {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.row-builder-item {
  display: flex;
  gap: 6px;
  align-items: center;
}

.row-builder-col { flex: 0 0 38%; min-width: 0; }
.row-builder-val { flex: 1; min-width: 0; }

.btn-row-remove {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  background: transparent;
  border: 1px solid var(--border-primary, #2a2a3e);
  border-radius: 5px;
  color: var(--text-secondary, #888);
  font-size: 15px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.btn-row-remove:hover { border-color: #ef4444; color: #ef4444; background: rgba(239,68,68,0.07); }

.btn-row-add {
  align-self: flex-start;
  background: transparent;
  border: 1px dashed var(--border-primary, #2a2a3e);
  border-radius: 6px;
  color: #4285F4;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 12px;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 2px;
}

.btn-row-add:hover { background: rgba(66,133,244,0.07); border-color: #4285F4; }

.link-open-sheet {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: #4285F4;
  text-decoration: none;
  margin-top: 5px;
  opacity: 0.85;
  transition: opacity 0.15s;
}

.link-open-sheet:hover { opacity: 1; text-decoration: underline; }

/* WhatsApp Preview Styling */
.whatsapp-preview-section {
  margin-top: 32px;
  padding-top: 24px;
  border-top: 2px solid var(--border-primary);
}

.preview-header {
  margin-bottom: 16px;
}

.preview-header h4 {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.whatsapp-mockup {
  background: #e5ddd5;
  background-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><circle cx="20" cy="20" r="2" fill="%23ffffff" opacity="0.3"/><circle cx="80" cy="80" r="2" fill="%23ffffff" opacity="0.3"/></svg>');
  border-radius: 12px;
  padding: 16px;
  min-height: 200px;
  box-shadow: inset 0 2px 8px rgba(0, 0, 0, 0.1);
}

.whatsapp-message {
  max-width: 85%;
}

.wa-bubble {
  background: white;
  border-radius: 8px;
  padding: 8px 12px;
  margin-bottom: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
}

.wa-bubble::before {
  content: '';
  position: absolute;
  left: -6px;
  top: 0;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 0 6px 6px 0;
  border-color: transparent white transparent transparent;
}

.wa-bubble p {
  margin: 0;
  font-size: 14px;
  color: #000;
  line-height: 1.5;
  word-wrap: break-word;
}

.wa-header {
  font-weight: 600;
  font-size: 13px;
  color: #00a884;
  margin-bottom: 6px !important;
}

.wa-footer {
  font-size: 11px;
  color: #8696a0;
  margin-top: 4px !important;
}

.wa-bubble small {
  font-size: 12px;
  color: #667781;
  display: block;
  margin-top: 4px;
}

.wa-bubble.wa-generic {
  background: #f0f2f5;
  border-left: 3px solid #00a884;
}

.wa-buttons {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.wa-button {
  background: white;
  border: 1px solid #d1d7db;
  border-radius: 8px;
  padding: 10px 16px;
  text-align: center;
  font-size: 14px;
  color: #00a884;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.wa-button:hover {
  background: #f0f2f5;
}

.wa-list-button {
  margin-bottom: 8px;
}

.wa-list-btn {
  width: 100%;
  text-align: left;
  color: #00a884;
  border-color: #00a884;
}

.wa-list-preview {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
}

.wa-list-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f0f2f5;
  transition: background 0.2s;
}

.wa-list-item:last-child {
  border-bottom: none;
}

.wa-list-item:hover {
  background: #f0f2f5;
}

.wa-list-title {
  font-size: 14px;
  font-weight: 500;
  color: #111b21;
  margin-bottom: 2px;
}

.wa-list-desc {
  font-size: 13px;
  color: #667781;
  line-height: 1.4;
}

.wa-input-placeholder {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e9edef;
}

.wa-input-placeholder input {
  width: 100%;
  padding: 8px 12px;
  background: white;
  border: 1px solid #d1d7db;
  border-radius: 6px;
  font-size: 14px;
  color: #667781;
}

.wa-input-placeholder input:disabled {
  cursor: not-allowed;
  opacity: 0.7;
}

/* WhatsApp Image Preview */
.wa-media-bubble {
  margin-bottom: 8px;
}

.wa-image-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  position: relative;
}

.wa-image {
  width: 100%;
  max-height: 300px;
  object-fit: cover;
  display: block;
}

.wa-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  background: #f0f2f5;
  color: #667781;
  text-align: center;
}

.wa-image-placeholder svg {
  margin-bottom: 12px;
  opacity: 0.5;
}

.wa-image-placeholder p {
  margin: 0;
  font-size: 13px;
}

.wa-image-error {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: #fee;
  color: #ef4444;
  font-size: 13px;
  text-align: center;
}

.wa-media-caption {
  padding: 8px 12px;
  background: white;
}

.wa-media-caption p {
  margin: 0;
  font-size: 14px;
  color: #000;
  line-height: 1.5;
  word-wrap: break-word;
}

/* Document message preview */
.wa-document-container {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.wa-document-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 12px;
  background: #f0f2f5;
}

.wa-document-icon {
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #25d366;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.wa-document-info {
  flex: 1;
  min-width: 0;
}

.wa-document-name {
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  color: #111b21;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.wa-document-placeholder {
  margin: 0;
  font-size: 13px;
  color: #667781;
  font-style: italic;
}

/* ==================== CONDITION NODE STYLES ==================== */

.condition-rule-row {
  background: rgba(99, 102, 241, 0.06);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
}

.condition-else-row {
  background: rgba(239, 68, 68, 0.06);
  border-color: rgba(239, 68, 68, 0.2);
}

.condition-rule-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.condition-rule-badge {
  font-size: 11px;
  font-weight: 700;
  color: #6366f1;
  background: rgba(99, 102, 241, 0.12);
  padding: 3px 10px;
  border-radius: 20px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.else-badge {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.12);
}

.condition-rule-fields {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 8px;
}

.condition-operator-select {
  font-size: 12px;
}

.condition-port-hint {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--text-secondary);
  opacity: 0.8;
}

.condition-port-hint strong {
  color: #6366f1;
}

.condition-else-row .condition-port-hint strong {
  color: #ef4444;
}

.btn-remove-condition {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: #ef4444;
  cursor: pointer;
  font-size: 16px;
  font-weight: bold;
  line-height: 1;
  padding: 0;
}

.btn-remove-condition:hover {
  background: rgba(239, 68, 68, 0.2);
}

/* Condition node on canvas preview */
.node-type-condition .node-content {
  background: rgba(99, 102, 241, 0.05);
}

/* ── List Message node ─────────────────────────────────── */
.list-msg-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 8px;
  margin-bottom: 16px;
  background: rgba(16, 185, 129, 0.1);
  border: 1px solid rgba(16, 185, 129, 0.25);
  color: #10b981;
  font-size: 0.8rem;
  font-weight: 500;
}

.list-field-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.list-field-chip {
  display: inline-block;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-family: 'Courier New', monospace;
  cursor: pointer;
  background: rgba(16, 185, 129, 0.12);
  color: #34d399;
  border: 1px solid rgba(16, 185, 129, 0.25);
  transition: background 0.15s, transform 0.1s;
  user-select: none;
}

.list-field-chip:hover {
  background: rgba(16, 185, 129, 0.25);
  transform: translateY(-1px);
}

.list-field-chip--index {
  background: rgba(139, 92, 246, 0.12);
  color: #a78bfa;
  border-color: rgba(139, 92, 246, 0.25);
}

.list-field-chip--index:hover {
  background: rgba(139, 92, 246, 0.25);
}

.list-template-input {
  font-family: 'Courier New', monospace;
  font-size: 0.82rem;
  line-height: 1.5;
}

.list-separator-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.list-sep-option {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.82rem;
  color: rgba(255, 255, 255, 0.75);
}

.list-sep-option small {
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.72rem;
}

.warn-hint {
  color: #f59e0b;
}

/* List message WhatsApp bubble preview */
.list-msg-preview-lines {
  font-size: 0.85rem;
  line-height: 1.5;
}

.list-preview-item {
  padding: 1px 0;
  font-family: 'Courier New', monospace;
  font-size: 0.78rem;
  white-space: pre-wrap;
  word-break: break-all;
}

.list-preview-item--dim {
  opacity: 0.5;
}

.list-preview-item--dimmer {
  opacity: 0.25;
}

.list-preview-more {
  margin-top: 4px;
  font-size: 0.7rem;
  color: rgba(255, 255, 255, 0.4);
  font-style: italic;
}

/* ─── AI Flow Builder ───────────────────────────────────────── */

.hdr-btn-ai {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.15), rgba(59, 130, 246, 0.15));
  border: 1px solid rgba(124, 58, 237, 0.35);
  color: #a78bfa;
}
.hdr-btn-ai:hover {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.3), rgba(59, 130, 246, 0.3));
  color: #c4b5fd;
}
.hdr-btn-ai--active {
  background: linear-gradient(135deg, rgba(124, 58, 237, 0.4), rgba(59, 130, 246, 0.4)) !important;
  color: #e9d5ff !important;
  border-color: rgba(167, 139, 250, 0.6) !important;
}

.ai-panel {
  position: fixed;
  top: 56px;
  right: 0;
  width: 380px;
  height: calc(100vh - 56px);
  background: #111827;
  border-left: 1px solid rgba(124, 58, 237, 0.3);
  display: flex;
  flex-direction: column;
  z-index: 200;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.5);
}

.ai-panel-slide-enter-active,
.ai-panel-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.ai-panel-slide-enter-from,
.ai-panel-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.ai-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px;
  height: 48px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  background: rgba(124, 58, 237, 0.08);
}
.ai-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.85rem;
  font-weight: 600;
  color: #a78bfa;
}
.ai-model-badge {
  font-size: 0.65rem;
  background: rgba(124, 58, 237, 0.2);
  border: 1px solid rgba(124, 58, 237, 0.4);
  color: #c4b5fd;
  padding: 2px 6px;
  border-radius: 10px;
  text-transform: capitalize;
  font-weight: 500;
}
.ai-panel-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.ai-icon-btn {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  transition: color 0.15s, background 0.15s;
}
.ai-icon-btn:hover {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.08);
}
.ai-revert-btn {
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #f87171;
  cursor: pointer;
  padding: 3px 8px;
  border-radius: 4px;
  font-size: 0.72rem;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: background 0.15s;
}
.ai-revert-btn:hover {
  background: rgba(239, 68, 68, 0.2);
}

.ai-chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-behavior: smooth;
}
.ai-chat-messages::-webkit-scrollbar { width: 4px; }
.ai-chat-messages::-webkit-scrollbar-track { background: transparent; }
.ai-chat-messages::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 2px; }

.ai-empty-state {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: rgba(255, 255, 255, 0.35);
  padding: 20px;
  font-size: 0.82rem;
  line-height: 1.5;
  margin: auto;
}
.ai-hint-text {
  font-style: italic;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.25);
  margin-top: 8px;
}

.ai-msg { display: flex; }
.ai-msg--user { justify-content: flex-end; }
.ai-msg--assistant, .ai-msg--system { justify-content: flex-start; }

.ai-msg-bubble {
  max-width: 88%;
  padding: 9px 12px;
  border-radius: 12px;
  font-size: 0.82rem;
  line-height: 1.55;
  word-break: break-word;
}
.ai-msg--user .ai-msg-bubble {
  background: linear-gradient(135deg, #5b21b6, #3b82f6);
  color: #fff;
  border-bottom-right-radius: 3px;
}
.ai-msg--assistant .ai-msg-bubble {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.9);
  border-bottom-left-radius: 3px;
}
.ai-msg--system .ai-msg-bubble {
  background: rgba(59, 130, 246, 0.1);
  border: 1px solid rgba(59, 130, 246, 0.25);
  color: #93c5fd;
  border-radius: 8px;
  font-size: 0.75rem;
  font-style: italic;
}
.ai-msg--error .ai-msg-bubble {
  background: rgba(239, 68, 68, 0.1) !important;
  border-color: rgba(239, 68, 68, 0.3) !important;
  color: #fca5a5 !important;
}
.ai-msg-bubble code {
  background: rgba(255,255,255,0.1);
  padding: 1px 4px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 0.85em;
}

.ai-typing {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 10px 14px !important;
}
.ai-typing span {
  width: 6px;
  height: 6px;
  background: rgba(167, 139, 250, 0.6);
  border-radius: 50%;
  display: inline-block;
  animation: ai-bounce 1.2s infinite;
}
.ai-typing span:nth-child(2) { animation-delay: 0.2s; }
.ai-typing span:nth-child(3) { animation-delay: 0.4s; }
@keyframes ai-bounce {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.6; }
  40% { transform: translateY(-6px); opacity: 1; }
}

.ai-chat-input-area {
  flex-shrink: 0;
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
}
.ai-chat-textarea {
  flex: 1;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.82rem;
  padding: 8px 10px;
  resize: none;
  line-height: 1.5;
  outline: none;
  font-family: inherit;
  transition: border-color 0.2s;
}
.ai-chat-textarea:focus { border-color: rgba(124, 58, 237, 0.5); }
.ai-chat-textarea::placeholder { color: rgba(255, 255, 255, 0.25); }
.ai-chat-textarea:disabled { opacity: 0.5; }

.ai-send-btn {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  background: linear-gradient(135deg, #7c3aed, #3b82f6);
  border: none;
  border-radius: 8px;
  color: #fff;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s, transform 0.1s;
}
.ai-send-btn:hover:not(:disabled) { opacity: 0.88; }
.ai-send-btn:active:not(:disabled) { transform: scale(0.95); }
.ai-send-btn:disabled { opacity: 0.35; cursor: not-allowed; }

.ai-settings-modal { width: 420px; }

.ai-length-warning {
  flex-shrink: 0;
  padding: 4px 12px;
  font-size: 0.72rem;
  color: #fbbf24;
  background: rgba(251, 191, 36, 0.08);
  border-top: 1px solid rgba(251, 191, 36, 0.2);
}

.ai-setup-key-btn {
  margin-top: 12px;
  background: linear-gradient(135deg, #7c3aed, #3b82f6);
  border: none;
  border-radius: 6px;
  color: #fff;
  padding: 7px 16px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: opacity 0.2s;
}
.ai-setup-key-btn:hover { opacity: 0.85; }

/* ── AI Plan Checklist ──────────────────────────────────────────── */
.ai-plan-section {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  max-height: 220px;
  background: rgba(0, 0, 0, 0.15);
}
.ai-plan-header {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 7px 12px;
  cursor: pointer;
  user-select: none;
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
}
.ai-plan-header:hover { background: rgba(255,255,255,0.04); }
.ai-plan-title { color: rgba(255, 255, 255, 0.7); }
.ai-plan-progress {
  background: rgba(124, 58, 237, 0.3);
  color: #c4b5fd;
  font-size: 0.65rem;
  padding: 1px 6px;
  border-radius: 8px;
  border: 1px solid rgba(124, 58, 237, 0.4);
}
.ai-plan-list {
  overflow-y: auto;
  padding: 4px 0 6px;
}
.ai-plan-list::-webkit-scrollbar { width: 3px; }
.ai-plan-list::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 2px; }

.ai-plan-step {
  display: flex;
  align-items: flex-start;
  gap: 7px;
  padding: 4px 14px;
  font-size: 0.76rem;
  line-height: 1.45;
  transition: background 0.15s;
}
.ai-plan-step-icon {
  flex-shrink: 0;
  width: 14px;
  height: 14px;
  margin-top: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ai-plan-step--pending { color: rgba(255, 255, 255, 0.4); }
.ai-plan-step--in_progress {
  color: #93c5fd;
  font-weight: 600;
  background: rgba(59, 130, 246, 0.07);
}
.ai-plan-step--done {
  color: rgba(255, 255, 255, 0.25);
  text-decoration: line-through;
}
.ai-plan-step--skipped { color: rgba(255, 255, 255, 0.2); text-decoration: line-through; }
.ai-plan-step-detail {
  font-style: italic;
  opacity: 0.7;
}

/* Ask-user question bubble */
.ai-msg--question .ai-msg-bubble {
  background: rgba(234, 179, 8, 0.08) !important;
  border: 1px solid rgba(234, 179, 8, 0.35) !important;
  color: #fde68a !important;
  border-radius: 10px !important;
}
.ai-question-label {
  font-size: 0.65rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #f59e0b;
  margin-bottom: 5px;
  opacity: 0.9;
}
.ai-question-options {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
  padding: 0 2px;
}
.ai-option-btn {
  background: rgba(255,255,255,0.07);
  border: 1px solid rgba(255,255,255,0.18);
  color: #e2e8f0;
  border-radius: 6px;
  padding: 5px 12px;
  font-size: 0.8rem;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
  white-space: nowrap;
}
.ai-option-btn:hover {
  background: rgba(99,102,241,0.25);
  border-color: rgba(99,102,241,0.6);
  color: #fff;
}
.ai-option-btn--other {
  border-style: dashed;
  opacity: 0.7;
}
.ai-option-btn--other:hover {
  opacity: 1;
  background: rgba(255,255,255,0.1);
  border-color: rgba(255,255,255,0.4);
}
.ai-question-answered {
  margin-top: 6px;
  font-size: 0.75rem;
  color: rgba(134,239,172,0.8);
  padding: 0 2px;
}
</style>
